import { useState, useEffect, useRef, useCallback } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, useParams } from "react-router-dom";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from "axios";
import AdminPanel from "./AdminPanel";
import ProgressDashboard from "./components/ProgressDashboard";
import WeightTracker from "./components/WeightTracker";
import PeriodTracker from "./components/PeriodTracker";
import GiftSubscriptionModal from "./components/GiftSubscriptionModal";
import GiftSuccessPage from "./components/GiftSuccessPage";
import ErrorBoundary from "./components/ErrorBoundary";
import NotFound from "./components/NotFound";
import LoadingSpinner from "./components/LoadingSpinner";
import EmptyState from "./components/EmptyState";
import TermsOfService from "./components/TermsOfService";
import PrivacyPolicy from "./components/PrivacyPolicy";
import BlogPillarPage from "./BlogPillarPage";
import FAQPage from "./FAQPage";
import WellnessAssessment from "./components/WellnessAssessment";
import WellnessAssessmentPage from "./components/WellnessAssessmentPage";
import ProgressiveOnboarding from "./components/ProgressiveOnboarding";
import OnboardingChecklist from "./components/OnboardingChecklist";
import PersonalizedWelcome from "./components/PersonalizedWelcome";

// Analytics Tracking Utility for GA4 and Meta Pixel
const Analytics = {
  // Track sign up event
  trackSignUp: (userData = {}) => {
    // GA4
    if (window.gtag) {
      window.gtag('event', 'sign_up', {
        method: 'email',
        user_id: userData.id || undefined
      });
    }
    // Meta Pixel
    if (window.fbq) {
      window.fbq('track', 'CompleteRegistration', {
        content_name: 'Luminelle Membership',
        status: 'registered'
      });
    }
    console.log('📊 Analytics: sign_up tracked');
  },
  
  // Track login event
  trackLogin: (userData = {}) => {
    // GA4
    if (window.gtag) {
      window.gtag('event', 'login', {
        method: 'email',
        user_id: userData.id || undefined
      });
    }
    console.log('📊 Analytics: login tracked');
  },
  
  // Track subscription purchase
  trackPurchase: (subscriptionData = {}) => {
    // GA4
    if (window.gtag) {
      window.gtag('event', 'purchase', {
        transaction_id: subscriptionData.subscription_id || Date.now().toString(),
        value: subscriptionData.amount || 19,
        currency: 'USD',
        items: [{
          item_name: subscriptionData.plan || 'Luminelle Membership',
          price: subscriptionData.amount || 19
        }]
      });
    }
    // Meta Pixel
    if (window.fbq) {
      window.fbq('track', 'Purchase', {
        value: subscriptionData.amount || 19,
        currency: 'USD',
        content_name: subscriptionData.plan || 'Luminelle Membership',
        content_type: 'subscription'
      });
    }
    console.log('📊 Analytics: purchase tracked');
  },
  
  // Track subscription cancellation
  trackCancellation: (userData = {}) => {
    // GA4
    if (window.gtag) {
      window.gtag('event', 'cancel_subscription', {
        user_id: userData.id || undefined,
        reason: userData.reason || 'user_initiated'
      });
    }
    // Meta Pixel
    if (window.fbq) {
      window.fbq('trackCustom', 'SubscriptionCancelled', {
        content_name: 'Luminelle Membership'
      });
    }
    console.log('📊 Analytics: cancel_subscription tracked');
  },
  
  // Track trial expiration
  trackTrialExpired: (userData = {}) => {
    // GA4
    if (window.gtag) {
      window.gtag('event', 'trial_ended', {
        user_id: userData.id || undefined
      });
    }
    // Meta Pixel
    if (window.fbq) {
      window.fbq('trackCustom', 'TrialEnded', {
        content_name: 'Luminelle Trial'
      });
    }
    console.log('📊 Analytics: trial_ended tracked');
  },
  
  // Track checkout initiated
  trackCheckoutInitiated: (planData = {}) => {
    // GA4
    if (window.gtag) {
      window.gtag('event', 'begin_checkout', {
        value: planData.amount || 19,
        currency: 'USD',
        items: [{
          item_name: planData.plan || 'Luminelle Membership',
          price: planData.amount || 19
        }]
      });
    }
    // Meta Pixel
    if (window.fbq) {
      window.fbq('track', 'InitiateCheckout', {
        value: planData.amount || 19,
        currency: 'USD',
        content_name: planData.plan || 'Luminelle Membership'
      });
    }
    console.log('📊 Analytics: begin_checkout tracked');
  }
};


// Import shadcn components
import { Button } from "./components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Badge } from "./components/ui/badge";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "./components/ui/dropdown-menu";

// Import icons
import { MessageCircle, BookOpen, Calculator, Calendar, ShoppingBag, Users, Heart, Sparkles, TrendingUp, Clock, Award, Shield, Star, Menu, X, User, 
         CreditCard, Bookmark, ChevronDown, Plus, LogOut, FileText, Trash2, MessageSquare, Search, Camera, Save, AlertTriangle, Gift, Share2, Link2, Copy, ArrowLeft, Loader2, Lock, CheckCircle, ExternalLink } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Access Control Helper Functions
const isMember = (subscriptionStatus) => {
  return ['active', 'member', 'forever_free', 'referral_free'].includes(subscriptionStatus);
};

const isTrial = (subscriptionStatus) => {
  return subscriptionStatus === 'trial';
};

const isPublic = (user) => {
  return !user;
};

// Helper function to format user names for privacy (FirstName L.)
const formatDisplayName = (fullName) => {
  if (!fullName || typeof fullName !== 'string') {
    return 'Anonymous User';
  }
  
  const nameParts = fullName.trim().split(' ').filter(part => part.length > 0);
  
  if (nameParts.length === 0) {
    return 'Anonymous User';
  } else if (nameParts.length === 1) {
    return nameParts[0]; // Just return the single name
  } else {
    const firstName = nameParts[0];
    const lastInitial = nameParts[nameParts.length - 1].charAt(0).toUpperCase();
    return `${firstName} ${lastInitial}.`;
  }
};

// Utility functions for video URL handling
const getVideoEmbedUrl = (url) => {
  if (!url) return null;
  
  // YouTube URL patterns
  if (url.includes('youtube.com/watch?v=')) {
    const videoId = url.split('v=')[1]?.split('&')[0];
    return `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}`;
  }
  
  if (url.includes('youtu.be/')) {
    const videoId = url.split('youtu.be/')[1]?.split('?')[0];
    return `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}`;
  }
  
  // Vimeo URL patterns
  if (url.includes('vimeo.com/')) {
    const videoId = url.split('vimeo.com/')[1]?.split('?')[0];
    return `https://player.vimeo.com/video/${videoId}?autoplay=1&muted=1&loop=1&background=1`;
  }
  
  // Direct video file URLs (MP4, WebM, etc.)
  if (url.match(/\.(mp4|webm|ogg|mov)(\?.*)?$/i)) {
    return url;
  }
  
  // Return as-is for other embeddable URLs
  return url;
};

const isDirectVideoFile = (url) => {
  return url && url.match(/\.(mp4|webm|ogg|mov)(\?.*)?$/i);
};

// Footer Component
const Footer = () => {
  return (
    <footer className="bg-burgundy text-cream py-8 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="text-center space-y-4">
          <p className="text-lg font-semibold">
            Copyright © 2026 Luminelle, a What's Next LLC Company
          </p>
          <div className="text-xs text-cream/80 max-w-4xl mx-auto leading-relaxed">
            <p>
              Luminelle is an educational community and does not provide medical advice, diagnosis, or treatment. 
              Information shared here — including articles, resources, and discussions — is for informational purposes 
              only and should not replace guidance from a licensed healthcare provider. Always consult a qualified 
              professional before starting, stopping, or combining any therapies, supplements, or lifestyle changes.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Upgrade to Member Modal Component
const UpgradeModal = ({ isOpen, onClose, feature = "this premium content" }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-burgundy/50 hover:text-burgundy"
        >
          <X className="h-6 w-6" />
        </button>
        
        <div className="text-center">
          <div className="mx-auto w-16 h-16 bg-gradient-to-br from-burgundy to-dark-teal rounded-full flex items-center justify-center mb-4">
            <Shield className="h-8 w-8 text-cream" />
          </div>
          
          <h2 className="text-2xl font-bold text-burgundy mb-2 font-serif">
            Upgrade to Member
          </h2>
          
          <p className="text-burgundy/70 mb-6">
            Access to this product is available exclusively to our members.
          </p>
          
          <div className="bg-cream/50 rounded-lg p-4 mb-6 text-left">
            <h3 className="font-semibold text-burgundy mb-3">Member Benefits:</h3>
            <ul className="space-y-2 text-sm text-burgundy/80">
              <li className="flex items-start gap-2">
                <span className="text-teal mt-0.5">✓</span>
                <span>Full access to The Lab (advanced wellness content)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-teal mt-0.5">✓</span>
                <span>Advanced tools and trackers</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-teal mt-0.5">✓</span>
                <span>All peptide products and protocols</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-teal mt-0.5">✓</span>
                <span>Priority support</span>
              </li>
            </ul>
          </div>
          
          <div className="space-y-3">
            <Button
              onClick={() => {
                onClose();
                // Store in sessionStorage and reload to dashboard with subscription tab
                sessionStorage.setItem('openSubscriptionTab', 'true');
                window.location.href = '/';
              }}
              className="w-full bg-gradient-to-r from-burgundy to-dark-teal text-cream hover:opacity-90 transition-opacity py-6"
            >
              View Membership Plans
            </Button>
            
            <Button
              variant="outline"
              onClick={onClose}
              className="w-full border-burgundy/30 text-burgundy hover:bg-burgundy/5"
            >
              Maybe Later
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Content view tracking helper
const trackContentView = async (contentType, contentId, contentTitle, token) => {
  try {
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    await axios.post(`${BACKEND_URL}/api/track/view`, {
      content_type: contentType,
      content_id: contentId,
      content_title: contentTitle
    }, { headers });
  } catch (e) { /* silent */ }
};

const trackPublicView = async (contentId, contentTitle) => {
  try {
    await axios.post(`${BACKEND_URL}/api/track/public-view`, {
      content_type: 'article',
      content_id: contentId,
      content_title: contentTitle
    });
  } catch (e) { /* silent */ }
};

// Founding 48 Landing Page
const Founding48Page = () => {
  const [showAuth, setShowAuth] = useState(false);

  return (
    <div className="min-h-screen bg-cream">
      {/* Header */}
      <header className="bg-burgundy text-cream py-4 px-4 md:px-8">
        <div className="max-w-5xl mx-auto flex justify-between items-center">
          <a href="/" className="flex items-center">
            <img src="/assets/luminelle-logo.png" alt="Luminelle" className="h-10 w-auto" />
          </a>
          <button
            onClick={() => setShowAuth(true)}
            className="px-4 py-2 bg-gold text-burgundy font-semibold rounded-full hover:bg-gold/90 transition-colors text-sm"
            data-testid="founding48-header-join-btn"
          >
            Join Now
          </button>
        </div>
      </header>

      {/* Hero Section - Logo replaces text title */}
      <section className="py-8 md:py-12 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <img 
            src="/icons/founding48-logo.png" 
            alt="Luminelle Founding 48" 
            className="max-w-[280px] sm:max-w-[360px] md:max-w-[440px] mx-auto mb-4"
            data-testid="founding48-title"
          />
          <p className="text-lg md:text-xl text-burgundy/70 font-light italic">
            A small group helping shape what Luminelle becomes.
          </p>
        </div>
      </section>

      {/* Story Section */}
      <section className="pb-6 md:pb-8 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-2xl shadow-sm border border-burgundy/10 p-6 md:p-10">
            <p className="text-base md:text-lg text-burgundy/80 leading-relaxed">
              I turn 48 this year. And instead of celebrating with something traditional, I decided to build something I wish had existed when I started trying to understand how to navigate this phase of life with more intention, energy, and clarity. That became Luminelle — a place where women can learn, track, experiment intelligently, and connect with others designing their next chapter.
            </p>
            <p className="text-base md:text-lg text-burgundy/80 leading-relaxed mt-4">
              To mark this beginning, I'm inviting 48 founding members to join me at the very start. Not just as members. But as early builders helping shape what this becomes.
            </p>
          </div>
        </div>
      </section>

      {/* Why Founding Members Matter */}
      <section className="py-6 md:py-8 px-4 bg-burgundy/5">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-serif font-bold text-burgundy mb-4 text-center">
            Why Founding Members Matter
          </h2>
          <p className="text-base md:text-lg text-burgundy/80 leading-relaxed mb-6 text-center">
            Every meaningful community starts with a small group of thoughtful people who believe in the idea before it's finished. The Founding 48 aren't just early users. They are the women helping create the culture, conversations, and direction of Luminelle.
          </p>
          <p className="text-base md:text-lg text-burgundy/80 leading-relaxed mb-4 text-center">
            If you're someone who:
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl mx-auto">
            {[
              "Likes understanding how things actually work",
              "Believes reinvention is possible at any stage",
              "Wants to be proactive instead of reactive about health and life",
              "Enjoys being part of smart, thoughtful communities"
            ].map((item, i) => (
              <div key={`persona-${item.slice(0,20)}`} className="flex items-start gap-3 bg-white rounded-xl p-4 shadow-sm border border-burgundy/10">
                <span className="text-dark-teal mt-0.5 font-bold">&#10003;</span>
                <span className="text-burgundy/80 text-sm md:text-base">{item}</span>
              </div>
            ))}
          </div>
          <p className="text-base md:text-lg text-burgundy/70 leading-relaxed mt-6 text-center italic">
            You'll probably feel very at home here.
          </p>
        </div>
      </section>

      {/* What Founding Members Receive */}
      <section className="py-6 md:py-8 px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-serif font-bold text-burgundy mb-6 text-center">
            Founding 48 Members Receive
          </h2>
          <div className="space-y-4">
            {[
              { title: "Lifetime Founding Member Access", desc: "No future membership fees. Ever." },
              { title: "Founding Member Status", desc: "Recognition inside the community as part of the original founding group." },
              { title: "Early Notification to Everything New", desc: "New tools, trackers, research, and features will always be shared with founding members first." },
              { title: "Founder Notes", desc: "Regular behind-the-scenes updates on what I'm learning, testing, and building as Luminelle grows." },
              { title: "A Voice in What We Build", desc: "Founding members will help influence future tools, topics, and features through feedback and early discussions." }
            ].map((benefit, i) => (
              <div key={benefit.title} className="flex items-start gap-4 bg-white rounded-xl p-4 md:p-5 shadow-sm border border-burgundy/10">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-dark-teal flex items-center justify-center text-cream font-bold text-sm">
                  {i + 1}
                </div>
                <div>
                  <h3 className="font-serif font-bold text-burgundy text-lg mb-1">{benefit.title}</h3>
                  <p className="text-burgundy/70 text-sm md:text-base">{benefit.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="py-6 md:py-8 px-4 bg-burgundy/5">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-serif font-bold text-burgundy mb-4">
            Who This Is For
          </h2>
          <p className="text-base md:text-lg text-burgundy/80 leading-relaxed">
            Luminelle is being built for women who see this stage of life as a redesign opportunity, not a slow decline. Women who are curious. Intentional. Still building. Still learning. Still evolving. If that sounds like you, you're exactly who I'm building this for.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-8 md:py-12 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-serif font-bold text-burgundy mb-3">
            Founding Member Invitation
          </h2>
          <p className="text-base md:text-lg text-burgundy/80 leading-relaxed mb-6">
            To celebrate this launch, I'm opening access to The Founding 48. Once these spots are filled, membership will move to standard paid tiers as the platform grows. If you'd like to be part of this early group:
          </p>
          
          <div className="bg-white rounded-2xl shadow-lg border border-burgundy/10 p-6 md:p-8 mb-6">
            <p className="text-burgundy/70 mb-2 text-sm">Use code:</p>
            <div className="bg-cream rounded-lg px-6 py-3 inline-block mb-4">
              <span className="font-mono text-2xl md:text-3xl font-bold text-burgundy tracking-widest" data-testid="founding48-code">FOUNDING48</span>
            </div>
            <p className="text-burgundy/60 text-sm mb-4">Apply code in the coupon code field when signing up</p>
            
            <button
              onClick={() => setShowAuth(true)}
              className="w-full sm:w-auto px-10 py-4 bg-gradient-to-r from-burgundy to-dark-teal text-cream font-semibold rounded-full hover:opacity-90 transition-opacity text-lg shadow-md"
              data-testid="founding48-join-btn"
            >
              Join as a Founding Member
            </button>
            
            <p className="mt-3 text-burgundy font-semibold text-sm">First 48 only</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />

      {/* Auth Modal - opens in signup mode with FOUNDING48 pre-filled */}
      {showAuth && <AuthModal onClose={() => setShowAuth(false)} defaultMode="signup" defaultCoupon="FOUNDING48" />}
    </div>
  );
};

// Standalone Wellness Assessment Page wrapper
const WellnessAssessmentStandalone = () => {
  const [showAuth, setShowAuth] = useState(false);
  return (
    <>
      <WellnessAssessmentPage onOpenSignup={() => setShowAuth(true)} />
      {showAuth && <AuthModal onClose={() => setShowAuth(false)} defaultMode="signup" />}
    </>
  );
};


// Main Landing Page Component
const HomePage = () => {
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState('login'); // 'login' or 'signup'
  const [homeContent, setHomeContent] = useState({
    hero_title: "Radiant Body, Balanced Mind, Enduring Vitality",
    hero_subtitle: "Navigate your perimenopause and menopause journey with confidence through our comprehensive wellness platform designed exclusively for sophisticated women.",
    hero_cta_text: "Begin Your Journey",
    feature1_title: "Exclusive Community",
    feature1_description: "Connect with like-minded women in our premium, moderated wellness community",
    feature2_title: "Curated Lessons", 
    feature2_description: "Access lessons on relevant, timely wellness topics",
    feature3_title: "Wellness Tracker",
    feature3_description: "Set goals, track progress, access coaching to move to the next level",
    testimonial_text: "Luminelle transformed my understanding of wellness. For the first time, I feel truly supported in my journey through perimenopause.",
    testimonial_author: "Sarah M.",
    testimonial_title: "Wellness Community Member"
  });

  const [contentBoxes, setContentBoxes] = useState([]);
  
  // Helper functions to open auth modal in specific mode
  const openSignIn = () => {
    setAuthMode('login');
    setShowAuth(true);
  };
  
  const openSignUp = () => {
    setAuthMode('signup');
    setShowAuth(true);
  };

  useEffect(() => {
    fetchHomeContent();
    fetchContentBoxes();
    
    // Check if URL has #signup hash to trigger auth modal
    if (window.location.hash === '#signup') {
      openSignUp();
      // Remove hash from URL after triggering modal
      window.history.replaceState(null, null, window.location.pathname);
    }
  }, []);

  const fetchHomeContent = async () => {
    try {
      const response = await axios.get(`${API}/home-content`);
      setHomeContent(response.data);
    } catch (error) {
      console.error('Error fetching home content:', error);
    }
  };

  const fetchContentBoxes = async () => {
    try {
      const response = await axios.get(`${API}/admin/home-content-boxes`);
      setContentBoxes(response.data.content_boxes || []);
    } catch (error) {
      console.error('Error fetching content boxes:', error);
      // Use default content boxes if fetch fails
      setContentBoxes([
        {
          id: "community",
          title: "Community",
          description: "Engage in meaningful discussions with curated expert-led conversations",
          icon: "MessageCircle",
          accent: "burgundy",
          order: 1
        },
        {
          id: "research",
          title: "Research Library", 
          description: "Access peer-reviewed studies and expert-authored wellness content",
          icon: "BookOpen",
          accent: "dark-teal",
          order: 2
        },
        {
          id: "calculator",
          title: "Precision Calculator",
          description: "Professional peptide dosing with visual guidance and safety protocols",
          icon: "Heart",
          accent: "gold",
          order: 3
        },
        {
          id: "tracker",
          title: "Wellness Tracker",
          description: "Track your daily wellness metrics and progress over time",
          icon: "TrendingUp", 
          accent: "burgundy",
          order: 4
        },
        {
          id: "marketplace",
          title: "Curated Marketplace",
          description: "Vetted, premium supplements and wellness products",
          icon: "ShoppingBag",
          accent: "dark-teal",
          order: 5
        },
        {
          id: "coaching",
          title: "Personal Coaching",
          description: "One-on-one guidance from certified wellness specialists",
          icon: "Star",
          accent: "gold",
          order: 6
        }
      ]);
    }
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-cream via-warm-white to-gold-50 overflow-x-hidden">
      {/* Navigation */}
      <nav className="bg-burgundy backdrop-blur-md border-b border-gold-200 sticky top-0 z-50 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div 
              className="flex items-center cursor-pointer" 
              onClick={() => window.location.href = '/dashboard'}
            >
              <img 
                src="/assets/luminelle-logo.png"
                alt="Luminelle"
                className="h-10 w-auto hover:opacity-80 transition-opacity"
              />
            </div>
            <div className="flex items-center space-x-1 sm:space-x-4">
              <Button 
                variant="ghost"
                onClick={() => window.location.href = '/faq'}
                className="text-cream hover:text-gold hover:bg-cream/10 font-medium px-2 sm:px-4 text-sm sm:text-base"
              >
                FAQ
              </Button>
              <Button 
                data-testid="sign-in-btn"
                onClick={openSignIn}
                variant="ghost"
                className="text-cream hover:text-gold hover:bg-cream/10 font-medium px-2 sm:px-6 py-1 sm:py-2 text-sm sm:text-base"
              >
                Sign&nbsp;In
              </Button>
              <Button 
                data-testid="sign-up-btn"
                onClick={openSignUp}
                className="bg-gradient-to-r from-gold to-gold-600 hover:from-gold-600 hover:to-gold-700 text-burgundy font-semibold px-2 sm:px-6 py-1 sm:py-2 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 border border-gold-300 text-sm sm:text-base"
              >
                Sign&nbsp;Up
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-20 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-burgundy/10 to-gold/10 opacity-50"></div>
        <div className="max-w-7xl mx-auto text-center relative">
          <div className="mb-12">
            <Badge className="mb-6 bg-burgundy/20 text-burgundy border-burgundy/30 hover:bg-burgundy/30 px-4 py-2 text-sm font-semibold">
              Science-backed wellness community for women 40+
            </Badge>
            <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold mb-8 text-burgundy font-serif leading-tight">
              Radiant Body,
              <br />
              <span className="bg-gradient-to-r from-gold to-gold-600 bg-clip-text text-transparent">Balanced Mind</span>
              <br />
              <span className="text-dark-teal">Enduring Vitality</span>
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl text-burgundy/80 max-w-4xl mx-auto leading-relaxed font-light px-4">
              {homeContent.hero_subtitle}
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center px-4 sm:px-0">
            <div className="order-2 lg:order-1">
              {/* Dynamic Media Display - 16:9 Aspect Ratio */}
              {homeContent.hero_media_type === 'video' && homeContent.hero_video_url ? (
                <div className="rounded-2xl overflow-hidden shadow-2xl border-4 border-gold/30 bg-black aspect-[16/9] w-full">
                  {isDirectVideoFile(homeContent.hero_video_url) ? (
                    <video 
                      src={homeContent.hero_video_url}
                      className="w-full h-full object-cover"
                      autoPlay
                      muted
                      loop
                      playsInline
                    />
                  ) : (
                    <iframe 
                      src={getVideoEmbedUrl(homeContent.hero_video_url)}
                      className="w-full h-full"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      title="Hero Video"
                    />
                  )}
                </div>
              ) : homeContent.hero_media_type === 'url' && homeContent.hero_external_url ? (
                <div className="rounded-2xl overflow-hidden shadow-2xl border-4 border-gold/30 aspect-[16/9] w-full">
                  <iframe 
                    src={homeContent.hero_external_url}
                    className="w-full h-full"
                    frameBorder="0"
                    title="Hero External Content"
                  />
                </div>
              ) : (
                <div 
                  onClick={openSignUp}
                  className="cursor-pointer group relative"
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && openSignUp()}
                >
                  <img 
                    src={homeContent.hero_image_url || "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e"}
                    alt="Confident professional woman - Click to create account"
                    className="rounded-2xl shadow-2xl w-full aspect-[16/9] object-cover border-4 border-gold/30 transition-all duration-300 group-hover:border-gold group-hover:shadow-gold/20"
                  />
                  <div className="absolute inset-0 bg-burgundy/0 group-hover:bg-burgundy/10 rounded-2xl transition-all duration-300 flex items-center justify-center">
                    <span className="opacity-0 group-hover:opacity-100 bg-burgundy text-cream px-6 py-3 rounded-full font-semibold shadow-lg transition-all duration-300 transform scale-90 group-hover:scale-100">
                      Click to Get Started
                    </span>
                  </div>
                </div>
              )}
            </div>
            <div className="order-1 lg:order-2 text-left">
              <div className="grid grid-cols-1 gap-6">
                <FeatureHighlight
                  icon={<Users className="h-8 w-8 text-gold" />}
                  title={homeContent.feature1_title}
                  description={homeContent.feature1_description}
                  gradient="from-burgundy to-burgundy/80"
                  textColor="text-gold"
                />
                <FeatureHighlight
                  icon={<BookOpen className="h-8 w-8 text-gold" />}
                  title={homeContent.feature2_title}
                  description={homeContent.feature2_description}
                  gradient="from-dark-teal to-dark-teal/80"
                  textColor="text-gold"
                />
                <FeatureHighlight
                  icon={<TrendingUp className="h-8 w-8 text-gold" />}
                  title={homeContent.feature3_title}
                  description={homeContent.feature3_description}
                  gradient="from-burgundy to-dark-teal"
                  textColor="text-gold"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Three Pillars Section */}
      <section id="pillars" className="py-20 bg-gradient-to-r from-burgundy to-dark-teal">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-cream mb-4 font-serif">
              {homeContent.pillars_heading || "Three Pillars of Wellness"}
            </h2>
            <p className="text-xl text-cream/90 max-w-3xl mx-auto font-light">
              {homeContent.pillars_subheading || "Our holistic approach addresses your complete wellness journey"}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <PillarCardWithPosts
              icon={<Sparkles className="h-12 w-12" />}
              title={homeContent.pillar1_title || "Radiant Body"}
              description={homeContent.pillar1_description || "Optimize your physical health with personalized nutrition guidance, supplement recommendations, and body-positive wellness strategies."}
              image={homeContent.pillar1_image || "https://images.unsplash.com/photo-1575633660454-cd44c9538d91"}
              pillarSlug="radiant_body"
              pillarUrl="/blog/radiant-body"
            />
            <PillarCardWithPosts
              icon={<Heart className="h-12 w-12" />}
              title={homeContent.pillar2_title || "Balanced Mind"}
              description={homeContent.pillar2_description || "Cultivate emotional resilience through mindfulness practices, stress management techniques, and community support."}
              image={homeContent.pillar2_image || "https://images.unsplash.com/photo-1590650046871-92c887180603"}
              pillarSlug="balanced_mind"
              pillarUrl="/blog/balanced-mind"
            />
            <PillarCardWithPosts
              icon={<TrendingUp className="h-12 w-12" />}
              title={homeContent.pillar3_title || "Enduring Vitality"}
              description={homeContent.pillar3_description || "Sustain long-term wellness with evidence-based protocols, lifestyle optimization, and continuous health monitoring."}
              image={homeContent.pillar3_image || "https://images.pexels.com/photos/2381069/pexels-photo-2381069.jpeg"}
              pillarSlug="enduring_vitality"
              pillarUrl="/blog/enduring-vitality"
            />
          </div>
        </div>
      </section>

      {/* Wellness Assessment CTA Section */}
      <section className="py-16 bg-cream" data-testid="homepage-quiz-cta">
        <div className="max-w-4xl mx-auto text-center px-4">
          <div className="bg-dark-teal rounded-2xl p-8 md:p-12 shadow-lg">
            <div className="w-14 h-14 bg-white/15 backdrop-blur rounded-2xl flex items-center justify-center mx-auto mb-5">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 text-gold" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" /></svg>
            </div>
            <h2 className="text-2xl md:text-3xl font-serif font-bold text-white mb-3">
              Not Sure Where to Start?
            </h2>
            <p className="text-white/80 max-w-xl mx-auto mb-8 text-base font-light">
              Take our free 2-minute wellness assessment and get a personalized plan matched to your symptoms and goals.
            </p>
            <a
              href="/wellness-assessment"
              className="inline-flex items-center gap-2 bg-gold text-burgundy hover:bg-gold/90 transition-colors px-8 py-4 rounded-full text-base font-semibold shadow-lg"
              data-testid="homepage-quiz-cta-btn"
            >
              Take the Free Assessment
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
            </a>
            <p className="text-white/50 text-xs mt-4">No account needed · Takes about 2 minutes</p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-cream">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-burgundy mb-4 font-serif">
              Your Complete Wellness Ecosystem
            </h2>
            <p className="text-xl text-burgundy/70 max-w-2xl mx-auto font-light">
              Everything you need to thrive during your transformation
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {contentBoxes
              .sort((a, b) => a.order - b.order)
              .map((box) => {
                // Dynamic icon rendering
                const getIcon = (iconName) => {
                  const iconProps = { className: "h-8 w-8" };
                  switch(iconName) {
                    case 'MessageCircle': return <MessageCircle {...iconProps} />;
                    case 'BookOpen': return <BookOpen {...iconProps} />;
                    case 'Calculator': return <Calculator {...iconProps} />;
                    case 'TrendingUp': return <TrendingUp {...iconProps} />;
                    case 'ShoppingBag': return <ShoppingBag {...iconProps} />;
                    case 'Star': return <Star {...iconProps} />;
                    case 'Heart': return <Heart {...iconProps} />;
                    case 'Sparkles': return <Sparkles {...iconProps} />;
                    case 'Calendar': return <Calendar {...iconProps} />;
                    default: return <Star {...iconProps} />;
                  }
                };

                return (
                  <LuxuryFeatureCard
                    key={box.id}
                    icon={getIcon(box.icon)}
                    title={box.title}
                    description={box.description}
                    accent={box.accent}
                  />
                );
              })
            }
          </div>
        </div>
      </section>

      {/* Testimonial Section - Conditionally displayed */}
      {homeContent.show_testimonial && (
        <section className="py-20 bg-gradient-to-br from-burgundy/10 to-gold/10">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center">
              <Badge className="mb-6 bg-gold/20 text-burgundy border-gold/50">
                Trusted by Thousands
              </Badge>
              <blockquote className="text-2xl md:text-3xl font-light text-burgundy mb-8 italic font-serif">
                "{homeContent.testimonial_text}"
              </blockquote>
              <div className="flex items-center justify-center">
                <img 
                  src={homeContent.testimonial_image || "https://images.unsplash.com/photo-1575633660454-cd44c9538d91"} 
                  alt={homeContent.testimonial_author}
                  className="w-16 h-16 rounded-full border-2 border-gold mr-4"
                />
                <div className="text-left">
                  <p className="font-semibold text-burgundy">{homeContent.testimonial_author}</p>
                  <p className="text-burgundy/70">{homeContent.testimonial_title}</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-burgundy to-dark-teal">
        <div className="max-w-4xl mx-auto text-center px-4">
          <h2 className="text-4xl md:text-5xl font-bold text-cream mb-6 font-serif">
            Begin Your Transformation Today
          </h2>
          <p className="text-xl text-cream/90 mb-8 font-light">
            Join our exclusive community of empowered women taking control of their wellness journey.
          </p>
          <Button 
            data-testid="join-community-btn"
            onClick={openSignUp}
            size="lg"
            className="bg-gradient-to-r from-gold to-gold-600 hover:from-gold-600 hover:to-gold-700 text-burgundy px-8 sm:px-12 py-3 sm:py-4 text-base sm:text-lg font-semibold rounded-lg shadow-xl hover:shadow-2xl transition-all duration-300 border-2 border-gold-300"
          >
            <span className="whitespace-nowrap">Begin Your Journey</span>
          </Button>
        </div>
      </section>

      {/* Footer */}
      <Footer />

      {/* Auth Modal */}
      {showAuth && <AuthModal onClose={() => setShowAuth(false)} defaultMode={authMode} />}
    </div>
  );
};

// Feature Highlight Component
const FeatureHighlight = ({ icon, title, description, gradient, textColor = "text-cream" }) => {
  return (
    <div className={`bg-gradient-to-r ${gradient} p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1`}>
      <div className="flex items-start space-x-4">
        <div className="flex-shrink-0 bg-cream/20 p-3 rounded-lg">
          {icon}
        </div>
        <div>
          <h3 className={`text-lg font-semibold mb-2 ${textColor}`}>{title}</h3>
          <p className={`text-sm opacity-90 ${textColor}`}>{description}</p>
        </div>
      </div>
    </div>
  );
};

// Pillar Card Component
const PillarCard = ({ icon, title, description, image }) => {
  return (
    <Card className="bg-cream border-0 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden">
      <div className="relative h-48 overflow-hidden">
        <img 
          src={image}
          alt={title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-burgundy/80 to-transparent"></div>
        <div className="absolute bottom-4 left-4 text-gold">
          {icon}
        </div>
      </div>
      <CardHeader>
        <CardTitle className="text-xl text-burgundy font-serif">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-burgundy/70 font-light">{description}</p>
      </CardContent>
    </Card>
  );
};

// Pillar Card with Recent Posts
const PillarCardWithPosts = ({ icon, title, description, image, pillarSlug, pillarUrl }) => {
  const [recentPosts, setRecentPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRecentPosts = async () => {
      try {
        const response = await axios.get(`${API}/blog/public`, {
          params: { pillar: pillarSlug, limit: 3 }
        });
        setRecentPosts(response.data || []);
      } catch (error) {
        console.error('Error fetching recent posts:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchRecentPosts();
  }, [pillarSlug]);

  return (
    <Card className="bg-cream border-0 shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden flex flex-col">
      <div className="relative h-48 overflow-hidden">
        <img 
          src={image}
          alt={title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-burgundy/80 to-transparent"></div>
        <div className="absolute bottom-4 left-4 text-gold">
          {icon}
        </div>
      </div>
      <CardHeader>
        <CardTitle className="text-xl text-burgundy font-serif">{title}</CardTitle>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col">
        <p className="text-burgundy/70 font-light mb-4">{description}</p>
        
        {/* Recent Posts */}
        {!loading && recentPosts.length > 0 && (
          <div className="mb-4 space-y-2">
            <p className="text-sm font-medium text-burgundy/80">Recent Posts:</p>
            <ul className="space-y-1.5">
              {recentPosts.slice(0, 3).map((post) => (
                <li key={post.id} className="text-sm text-burgundy/60 line-clamp-1">
                  • {post.title}
                </li>
              ))}
            </ul>
          </div>
        )}
        
        {/* Read Articles Button */}
        <div className="mt-auto pt-4">
          <a 
            href={pillarUrl}
            className="inline-flex items-center justify-center w-full px-4 py-2 bg-burgundy hover:bg-burgundy/80 text-cream rounded-md font-medium transition-colors"
          >
            Read Articles
          </a>
        </div>
      </CardContent>
    </Card>
  );
};

// Luxury Feature Card Component
const LuxuryFeatureCard = ({ icon, title, description, accent }) => {
  const accentColors = {
    burgundy: "from-burgundy to-burgundy/80 text-cream",
    "dark-teal": "from-dark-teal to-dark-teal/80 text-cream", 
    gold: "from-gold to-gold/80 text-burgundy"
  };

  return (
    <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 bg-white">
      <CardHeader>
        <div className={`w-16 h-16 bg-gradient-to-r ${accentColors[accent]} rounded-xl flex items-center justify-center mb-4`}>
          {icon}
        </div>
        <CardTitle className="text-xl text-burgundy font-serif">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-burgundy/70 font-light">{description}</p>
      </CardContent>
    </Card>
  );
};

// Auth Modal Component
const AuthModal = ({ onClose, onAuthSuccess, defaultMode = 'login', defaultCoupon = '' }) => {
  const [isLogin, setIsLogin] = useState(defaultMode === 'login');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    age: '',
    location: '',
    referred_by_email: '',
    coupon_code: defaultCoupon,
    gift_code: '',
    agreesToDisclaimer: false,
    agreesToCommunityGuidelines: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [googleLoading, setGoogleLoading] = useState(false);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleSignIn = () => {
    setGoogleLoading(true);
    const redirectUrl = window.location.origin;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Check disclaimer agreement for registration
    if (!isLogin && !formData.agreesToDisclaimer) {
      setError('Please read and agree to the disclaimer before proceeding.');
      setLoading(false);
      return;
    }

    // Check community guidelines agreement for registration
    if (!isLogin && !formData.agreesToCommunityGuidelines) {
      setError('Please read and agree to the community guidelines before proceeding.');
      setLoading(false);
      return;
    }

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const payload = isLogin 
        ? { email: formData.email, password: formData.password }
        : {
            email: formData.email,
            password: formData.password,
            name: formData.name,
            age: formData.age ? parseInt(formData.age) : null,
            location: formData.location || null,
            referred_by_email: formData.referred_by_email || null,
            coupon_code: formData.coupon_code || null
          };

      const response = await axios.post(`${API}${endpoint}`, payload);
      
      if (response.data.success) {
        // Check trial expiration before proceeding
        const userData = response.data.user;
        const isTrialExpired = checkTrialExpiration(userData);
        
        // Track analytics events
        if (isLogin) {
          Analytics.trackLogin(userData);
        } else {
          Analytics.trackSignUp(userData);
        }
        
        // Store token and user data
        localStorage.setItem('auth_token', response.data.token);
        localStorage.setItem('user_data', JSON.stringify(userData));
        
        if (isTrialExpired) {
          // Track trial expired event
          Analytics.trackTrialExpired(userData);
          // Show trial expired message and redirect to upgrade
          toast.error('Your trial period has ended. Please upgrade to continue using Luminelle.');
          onClose();
          // Set a flag to show upgrade modal after page reload
          localStorage.setItem('show_trial_expired', 'true');
          window.location.reload();
        } else {
          // Show success message for active users
          toast.success(response.data.message);
          
          // If this is a new registration with a coupon code, store it for later use at checkout
          if (!isLogin && formData.coupon_code && formData.coupon_code.trim()) {
            // Store coupon code in user's localStorage for later use at checkout
            localStorage.setItem('pending_coupon_code', formData.coupon_code.trim().toUpperCase());
            toast.success('Coupon code saved! It will be applied when you upgrade.');
          }
          
          // If this is a new registration with a gift code, redeem it
          if (!isLogin && formData.gift_code && formData.gift_code.trim()) {
            try {
              await axios.post(`${API}/gift-subscriptions/redeem`, {
                gift_code: formData.gift_code.trim(),
                user_id: userData.id
              });
              toast.success('Gift code redeemed successfully!');
            } catch (giftError) {
              console.error('Error redeeming gift code:', giftError);
              toast.error('Gift code could not be redeemed. You can try again from your profile.');
            }
          }
          
          // If this is a new registration and user hasn't completed onboarding, flag it
          if (!isLogin && !userData.onboarding_completed) {
            localStorage.setItem('show_onboarding', 'true');
          }
          
          // Close modal and redirect to dashboard
          onClose();
          window.location.href = '/dashboard';
        }
      } else {
        setError(response.data.message);
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      age: '',
      location: '',
      referred_by_email: '',
      coupon_code: '',
      gift_code: '',
      agreesToDisclaimer: false,
      agreesToCommunityGuidelines: false
    });
    setError('');
  };

  const toggleMode = () => {
    setIsLogin(!isLogin);
    resetForm();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <Card className="w-full max-w-md mx-4 border-0 shadow-2xl bg-cream my-8 max-h-[90vh] overflow-y-auto">
        <CardHeader className="text-center pb-6">
          <div className="w-16 h-16 rounded-full overflow-hidden mx-auto mb-4">
            <img src="/icons/luminelle-butterfly.png" alt="Luminelle" className="w-full h-full object-cover" />
          </div>
          <CardTitle className="text-2xl font-serif text-burgundy">
            {isLogin ? 'Welcome Back' : 'Join Luminelle'}
          </CardTitle>
          <CardDescription className="text-burgundy/70">
            {isLogin ? 'Continue your wellness journey' : 'Begin your transformation today'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div>
                <label className="block text-sm font-medium text-burgundy mb-2">
                  Full Name *
                </label>
                <Input
                  data-testid="name-input"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="border-burgundy/30 focus:border-gold focus:ring-gold"
                  required={!isLogin}
                />
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium text-burgundy mb-2">
                Email Address *
              </label>
              <Input
                data-testid="email-input"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="border-burgundy/30 focus:border-gold focus:ring-gold"
                required
              />
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-burgundy">
                  Password *
                </label>
                {isLogin && (
                  <button
                    type="button"
                    onClick={() => {
                      onClose();
                      window.dispatchEvent(new CustomEvent('openForgotPassword'));
                    }}
                    className="text-xs text-burgundy/70 hover:text-gold transition-colors"
                  >
                    Forgot Password?
                  </button>
                )}
              </div>
              <Input
                data-testid="password-input"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                className="border-burgundy/30 focus:border-gold focus:ring-gold"
                required
                minLength={6}
              />
              {!isLogin && (
                <p className="text-xs text-burgundy/50 mt-1">Minimum 6 characters</p>
              )}
            </div>

            {!isLogin && (
              <>
                <div>
                  <label className="block text-sm font-medium text-burgundy mb-2">
                    Age <span className="text-burgundy/50">(Optional)</span>
                  </label>
                  <Input
                    data-testid="age-input"
                    type="number"
                    min="18"
                    max="100"
                    value={formData.age}
                    onChange={(e) => setFormData({...formData, age: e.target.value})}
                    className="border-burgundy/30 focus:border-gold focus:ring-gold"
                    placeholder="45"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-burgundy mb-2">
                    Location <span className="text-burgundy/50">(Optional)</span>
                  </label>
                  <Input
                    data-testid="location-input"
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({...formData, location: e.target.value})}
                    className="border-burgundy/30 focus:border-gold focus:ring-gold"
                    placeholder="City, State"
                  />
                  <p className="text-xs text-burgundy/50 mt-1">
                    Age and location help build a sense of community
                  </p>
                </div>

                {/* Referral Field */}
                <div>
                  <label className="block text-sm font-medium text-burgundy mb-2">
                    Referred by? <span className="text-burgundy/50">(Optional)</span>
                  </label>
                  <Input
                    data-testid="referral-input"
                    type="email"
                    value={formData.referred_by_email}
                    onChange={(e) => setFormData({...formData, referred_by_email: e.target.value})}
                    className="border-burgundy/30 focus:border-gold focus:ring-gold"
                    placeholder="friend@example.com"
                  />
                  <p className="text-xs text-burgundy mt-1">
                    Enter your friend's email for an extended 14-day trial!
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-burgundy mb-2">
                    Coupon Code <span className="text-burgundy/50">(Optional)</span>
                  </label>
                  <Input
                    type="text"
                    value={formData.coupon_code}
                    onChange={(e) => setFormData({...formData, coupon_code: e.target.value.toUpperCase()})}
                    className="border-burgundy/30 focus:border-gold focus:ring-gold"
                    placeholder="Enter coupon code"
                  />
                  <p className="text-xs text-burgundy mt-1">
                    Have a coupon code? Enter it here for a discount on your subscription!
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-burgundy mb-2">
                    Gift Code <span className="text-burgundy/50">(Optional)</span>
                  </label>
                  <Input
                    type="text"
                    value={formData.gift_code}
                    onChange={(e) => setFormData({...formData, gift_code: e.target.value})}
                    className="border-burgundy/30 focus:border-gold focus:ring-gold"
                    placeholder="Enter gift code"
                  />
                  <p className="text-xs text-burgundy mt-1">
                    Have a gift code? Enter it here to activate your subscription!
                  </p>
                </div>
                
                {/* Disclaimer Checkbox for Registration */}
                <div className="w-full min-h-[120px] mt-4 p-4 bg-burgundy/5 border border-burgundy/20 rounded-lg">
                  <div className="flex items-start space-x-3 w-full">
                    <input
                      type="checkbox"
                      id="disclaimer-checkbox"
                      checked={formData.agreesToDisclaimer}
                      onChange={(e) => setFormData({...formData, agreesToDisclaimer: e.target.checked})}
                      className="mt-1 h-4 w-4 text-burgundy focus:ring-burgundy border-burgundy/30 rounded"
                      required
                    />
                    <label htmlFor="disclaimer-checkbox" className="text-xs text-burgundy/80 leading-relaxed">
                      By checking this box, I confirm that I understand Luminelle is an educational community, not a medical provider. 
                      I acknowledge that any information shared within the platform — including discussions, resources, or suggested 
                      wellness practices — is for general educational purposes only and not a substitute for professional medical advice, 
                      diagnosis, or treatment. I agree to consult a licensed healthcare provider before acting on any information I learn here.
                    </label>
                  </div>
                </div>

                {/* Community Guidelines Checkbox for Registration */}
                <div className="community-guidelines-container w-full min-h-[200px] mt-4 p-4 bg-gold/5 border border-gold/30 rounded-lg">
                  <div className="flex items-start space-x-3 w-full">
                    <input
                      type="checkbox"
                      id="community-guidelines-checkbox"
                      checked={formData.agreesToCommunityGuidelines}
                      onChange={(e) => setFormData({...formData, agreesToCommunityGuidelines: e.target.checked})}
                      className="mt-1 h-4 w-4 text-burgundy focus:ring-burgundy border-burgundy/30 rounded"
                      required
                    />
                    <div className="flex-1">
                      <label htmlFor="community-guidelines-checkbox" className="text-xs text-burgundy/80 leading-relaxed block mb-2">
                        <strong>Before you jump in, a few house vibes:</strong>
                      </label>
                      <div className="text-xs text-burgundy/70 space-y-1 mb-3">
                        <p><strong>Be Kind</strong> – Lift each other up. No snark, no shade.</p>
                        <p><strong>Share, Don't Prescribe</strong> – "Here's what helped me" &gt; "You should do this."</p>
                        <p><strong>No Spam or Sales</strong> – We're here to support, not to sell.</p>
                        <p><strong>Stay Curious, Not Combative</strong> – Questions are gold. Be cool in the comments.</p>
                        <p><strong>Keep It Private</strong> – What's shared here, stays here.</p>
                        <p><strong>We're Not Your Doctor</strong> – Educational only, not medical advice.</p>
                      </div>
                      <p className="text-xs text-burgundy/80">
                        By posting, you agree to keep this space kind, supportive, and spam-free.
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-red-600 text-sm font-medium">{error}</p>
              </div>
            )}

            <Button
              data-testid="auth-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-burgundy to-dark-teal hover:from-burgundy/80 hover:to-dark-teal/80 text-cream font-semibold py-3 rounded-lg shadow-lg"
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-cream mr-2"></div>
                  {isLogin ? 'Signing In...' : 'Creating Account...'}
                </div>
              ) : (
                isLogin ? 'Sign In' : 'Begin Journey'
              )}
            </Button>
          </form>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-burgundy/20"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-3 bg-cream text-burgundy/50">or</span>
            </div>
          </div>

          {/* Google Sign-In Button */}
          <button
            data-testid="google-signin-btn"
            onClick={handleGoogleSignIn}
            disabled={googleLoading}
            className="w-full flex items-center justify-center gap-3 bg-white border border-burgundy/20 hover:bg-burgundy/5 text-burgundy font-medium py-3 px-4 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {googleLoading ? (
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-burgundy border-t-transparent"></div>
                Redirecting...
              </div>
            ) : (
              <>
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </>
            )}
          </button>

          <div className="mt-6 text-center">
            <button
              data-testid="toggle-auth-mode"
              onClick={toggleMode}
              className="text-burgundy hover:text-dark-teal font-medium transition-colors"
            >
              {isLogin ? "New to Luminelle? Create account" : "Already a member? Sign in"}
            </button>
          </div>
          
          <div className="mt-4 text-center">
            <Button
              data-testid="close-auth-modal"
              variant="outline"
              onClick={onClose}
              className="w-full border-burgundy/30 text-burgundy hover:bg-burgundy/10"
            >
              Close
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Forgot Password Modal Component
const ForgotPasswordModal = ({ onClose, isAdmin = false }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent multiple submissions
    if (loading || submitted) {
      return;
    }
    
    setLoading(true);
    setSubmitted(true);
    setError('');

    try {
      const response = await axios.post(`${API}/auth/forgot-password`, { email });
      
      if (response.data.success) {
        setSuccess(true);
        
        // Auto-close modal after 3 seconds
        setTimeout(() => {
          onClose();
        }, 3000);
      } else {
        setError(response.data.message || 'Failed to send reset email');
        setSubmitted(false); // Allow retry on error
      }
    } catch (err) {
      console.error('Forgot password error:', err);
      setError('An error occurred. Please try again.');
      setSubmitted(false); // Allow retry on error
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md border-0 shadow-2xl bg-cream">
        <CardHeader className="text-center">
          <div className="w-16 h-16 bg-gradient-to-r from-burgundy to-dark-teal rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="h-8 w-8 text-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
          </div>
          <CardTitle className="text-2xl font-serif text-burgundy">
            Forgot Password?
          </CardTitle>
          <CardDescription className="text-burgundy/70">
            {isAdmin ? "Enter admin email to receive reset instructions" : "Enter your email to receive reset instructions"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {success ? (
            <div className="text-center space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center justify-center mb-3">
                  <svg className="h-12 w-12 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <p className="text-green-700 font-medium mb-2">Email Sent Successfully!</p>
                <p className="text-green-600 text-sm mb-2">
                  Check your email inbox for password reset instructions.
                </p>
                <p className="text-amber-600 text-sm font-semibold mb-2">
                  ⚠️ Make sure to check your spam/junk folder!
                </p>
                <p className="text-green-500 text-xs italic">
                  This window will close automatically in 3 seconds...
                </p>
              </div>
              <Button
                onClick={onClose}
                className="w-full bg-burgundy hover:bg-burgundy/80 text-cream"
              >
                Close Now
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-burgundy mb-2">
                  Email Address
                </label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="border-burgundy/30 focus:border-gold focus:ring-gold"
                  placeholder={isAdmin ? "hello@luminellewellness.com" : "your@email.com"}
                  required
                />
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-red-600 text-sm">{error}</p>
                </div>
              )}

              <Button
                type="submit"
                disabled={loading || submitted}
                className="w-full bg-gradient-to-r from-burgundy to-dark-teal hover:from-burgundy/80 hover:to-dark-teal/80 text-cream font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-cream mr-2"></div>
                    Sending...
                  </div>
                ) : (
                  'Send Reset Link'
                )}
              </Button>

              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="w-full border-burgundy/30 text-burgundy hover:bg-burgundy/10"
              >
                Cancel
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Reset Password Modal Component
const ResetPasswordModal = ({ token, email, onClose, onSuccess }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);

  useEffect(() => {
    const verifyToken = async () => {
      try {
        const response = await axios.post(`${API}/auth/verify-reset-token`, { token, email });
        
        if (response.data.success) {
          setTokenValid(true);
        } else {
          setError(response.data.message || 'Invalid or expired reset token');
        }
      } catch (err) {
        console.error('Token verification error:', err);
        setError('Invalid or expired reset token');
      } finally {
        setVerifying(false);
      }
    };

    if (token && email) {
      verifyToken();
    } else {
      setError('Missing reset token or email');
      setVerifying(false);
    }
  }, [token, email]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: newPassword
      });
      
      if (response.data.success) {
        onSuccess();
      } else {
        setError(response.data.message || 'Failed to reset password');
      }
    } catch (err) {
      console.error('Password reset error:', err);
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md border-0 shadow-2xl bg-cream">
        <CardHeader className="text-center">
          <div className="w-16 h-16 bg-gradient-to-r from-burgundy to-dark-teal rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="h-8 w-8 text-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <CardTitle className="text-2xl font-serif text-burgundy">
            Reset Your Password
          </CardTitle>
          <CardDescription className="text-burgundy/70">
            Enter your new password below
          </CardDescription>
        </CardHeader>
        <CardContent>
          {verifying ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-burgundy mx-auto mb-4"></div>
              <p className="text-burgundy">Verifying reset token...</p>
            </div>
          ) : !tokenValid ? (
            <div className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-700 font-medium mb-2">Invalid Reset Link</p>
                <p className="text-red-600 text-sm">{error}</p>
              </div>
              <Button
                onClick={onClose}
                className="w-full bg-burgundy hover:bg-burgundy/80 text-cream"
              >
                Close
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-burgundy mb-2">
                  New Password
                </label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="border-burgundy/30 focus:border-gold focus:ring-gold"
                  required
                  minLength={6}
                />
                <p className="text-xs text-burgundy/50 mt-1">Minimum 6 characters</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-burgundy mb-2">
                  Confirm Password
                </label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="border-burgundy/30 focus:border-gold focus:ring-gold"
                  required
                  minLength={6}
                />
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-red-600 text-sm">{error}</p>
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-burgundy to-dark-teal hover:from-burgundy/80 hover:to-dark-teal/80 text-cream font-semibold"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-cream mr-2"></div>
                    Resetting...
                  </div>
                ) : (
                  'Reset Password'
                )}
              </Button>

              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="w-full border-burgundy/30 text-burgundy hover:bg-burgundy/10"
              >
                Cancel
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Survey Modal Component
const SurveyModal = ({ isOpen, onClose, surveyType, userId }) => {
  const [responses, setResponses] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const surveyQuestions = {
    cancellation: [
      "What was the main reason Luminelle wasn't the right fit for you right now?",
      "Was there anything you expected to find inside the membership that wasn't there?",
      "What's one thing we could improve that would've made you stay?"
    ],
    non_upgrade: [
      "What stopped you from upgrading today?",
      "Was there a feature, topic, or tool you were hoping to see but didn't?",
      "If we could change one thing to make the membership a no-brainer, what would it be?"
    ],
    upgrade: [
      "What convinced you that Luminelle was worth joining?",
      "Which part of the experience made the biggest difference in your decision?",
      "What are you hoping to get out of your membership next?"
    ]
  };

  const questions = surveyQuestions[surveyType] || [];
  const titles = {
    cancellation: "We're Sorry to See You Go",
    non_upgrade: "Help Us Improve",
    upgrade: "Welcome to Luminelle! 🎉"
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/surveys/submit?user_id=${userId}`, {
        survey_type: surveyType,
        responses: responses
      });
      toast.success('Thank you for your feedback!');
      onClose();
    } catch (error) {
      console.error('Error submitting survey:', error);
      toast.error('Error submitting survey');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkip = () => {
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto border-0 shadow-2xl bg-cream">
        <CardHeader className="border-b border-burgundy/10">
          <CardTitle className="text-2xl font-serif text-burgundy">
            {titles[surveyType]}
          </CardTitle>
          <CardDescription className="text-burgundy/70">
            Your feedback helps us create a better experience for everyone.
          </CardDescription>
        </CardHeader>

        <CardContent className="p-6">
          <div className="space-y-6">
            {questions.map((question, index) => (
              <div key={`q-${question.slice(0,30)}`}>
                <label className="block text-sm font-medium text-burgundy mb-2">
                  {index + 1}. {question}
                </label>
                <Textarea
                  value={responses[`question_${index + 1}`] || ''}
                  onChange={(e) => setResponses({
                    ...responses,
                    [`question_${index + 1}`]: e.target.value
                  })}
                  placeholder="Your answer..."
                  className="min-h-[100px] border-burgundy/30 focus:border-gold"
                />
              </div>
            ))}
          </div>

          <div className="flex gap-3 mt-8">
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="flex-1 bg-burgundy hover:bg-burgundy/80 text-cream"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin h-4 w-4 mr-2 border-2 border-white border-t-transparent rounded-full"></div>
                  Submitting...
                </>
              ) : (
                'Submit Feedback'
              )}
            </Button>
            <Button
              onClick={handleSkip}
              variant="outline"
              className="border-burgundy/30 text-burgundy hover:bg-burgundy/10"
            >
              Skip
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};


// Main Dashboard Component
const Dashboard = ({ user, onLogout, onUserUpdate, initialView, setShowUpgradeModal, setUpgradeFeature }) => {
  const location = useLocation();
  const params = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [defaultToolsTab, setDefaultToolsTab] = useState('overview'); // Track which tools tab should be default - Overview landing page is default
  const [educationResetTrigger, setEducationResetTrigger] = useState(0);
  const [userNotifications, setUserNotifications] = useState({
    warnings: [],
    unread_warnings: 0,
    total_warnings: 0
  });
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showSavedItems, setShowSavedItems] = useState(false);
  const [showWellnessAssessment, setShowWellnessAssessment] = useState(false);

  const [showGiftSubscriptionModal, setShowGiftSubscriptionModal] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [selectedUserProfile, setSelectedUserProfile] = useState(null);
  const [showUserProfileModal, setShowUserProfileModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [discussionToOpen, setDiscussionToOpen] = useState(null); // Track which discussion to open when navigating to community
  const [chatResetTrigger, setChatResetTrigger] = useState(0); // Trigger to reset chat section to rooms view
  const [contentToOpen, setContentToOpen] = useState(null); // Track which content to open when navigating to education
  const [categoryToOpen, setCategoryToOpen] = useState(null); // Track which category to open in classroom
  const [productToOpen, setProductToOpen] = useState(null); // Track which product to open when navigating to marketplace
  const [compoundToOpen, setCompoundToOpen] = useState(null); // Track which compound to open when navigating to dosing
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showNeedHelp, setShowNeedHelp] = useState(false);
  const [showSurveyModal, setShowSurveyModal] = useState(false);
  const [surveyType, setSurveyType] = useState(null);
  
  // Check for upgrade modal redirect
  useEffect(() => {
    const openSubscription = sessionStorage.getItem('openSubscriptionTab');
    if (openSubscription === 'true') {
      setActiveTab('subscription');
      sessionStorage.removeItem('openSubscriptionTab');
      return;
    }
  }, []);
  
  // Handle deep linking from URL (runs on initial mount and when route changes)
  const initialDeepLinkDone = useRef(false);
  useEffect(() => {
    // Skip deep-link processing during back/forward navigation.
    // The popstate handlers in EducationSection/ChatSection/etc. handle that directly.
    // Without this guard, React Router param changes re-trigger deep linking,
    // which calls fetchContentByCategory → pushState → duplicate history entries.
    if (isPopstateNavigation.current) return;

    if (initialView) {
      setActiveTab(initialView);
    } else {
      // No initialView = root route ("/") = dashboard
      setActiveTab('dashboard');
    }
    
    // Only process deep links once per route change (not on every re-render)
    const paramKey = `${params.categorySlug || ''}/${params.subcategorySlug || ''}/${params.contentId || ''}/${params.productId || ''}/${params.discussionId || ''}/${params.compoundName || ''}`;
    
    if (initialDeepLinkDone.current === paramKey) return;
    initialDeepLinkDone.current = paramKey;
    
    // Handle classroom deep link with subcategory support
    if (params.categorySlug && params.subcategorySlug && params.contentId) {
      setActiveTab('education');
      setContentToOpen({ 
        categorySlug: params.categorySlug, 
        subcategorySlug: params.subcategorySlug,
        contentId: params.contentId 
      });
    } else if (params.categorySlug && params.subcategorySlug) {
      setActiveTab('education');
      setContentToOpen({ 
        categorySlug: params.categorySlug, 
        subcategorySlug: params.subcategorySlug 
      });
    } else if (params.categorySlug && params.contentId) {
      setActiveTab('education');
      setContentToOpen({ categorySlug: params.categorySlug, contentId: params.contentId });
    } else if (params.categorySlug) {
      setActiveTab('education');
      setCategoryToOpen(params.categorySlug);
    }
    
    // Handle marketplace deep link
    if (params.productId) {
      setActiveTab('shop');
      setProductToOpen(params.productId);
    }
    
    // Handle community deep link
    if (params.discussionId) {
      setActiveTab('chat');
      setDiscussionToOpen(params.discussionId);
    }
    
    // Handle dosing database deep link
    if (params.compoundName) {
      setActiveTab('calculator');
      setDefaultToolsTab('dosing');
      setCompoundToOpen(params.compoundName);
    }
  }, [initialView, params.categorySlug, params.subcategorySlug, params.contentId, params.productId, params.discussionId, params.compoundName]);
  
  // Ref to track current tab for popstate handler (avoids stale closure)
  const activeTabRef = useRef(activeTab);
  useEffect(() => { activeTabRef.current = activeTab; }, [activeTab]);

  // Flag to prevent deep-link useEffect from firing during back/forward navigation
  // When popstate fires, React Router updates params, which would re-trigger deep linking
  // and create duplicate history entries. This flag prevents that.
  const isPopstateNavigation = useRef(false);

  // Handle browser back/forward for tab-level and tools sub-navigation
  useEffect(() => {
    const handlePopState = () => {
      // Mark this as a popstate-driven navigation so deep-link effect skips
      isPopstateNavigation.current = true;

      const path = window.location.pathname;
      
      // Tab-level navigation
      if (path === '/dashboard' || path === '/') {
        setActiveTab('dashboard');
      } else if (path.startsWith('/classroom')) {
        setActiveTab('education');
      } else if (path.startsWith('/community')) {
        setActiveTab('chat');
      } else if (path === '/marketplace' || path.startsWith('/marketplace/')) {
        setActiveTab('shop');
      } else if (path.startsWith('/tools')) {
        setActiveTab('calculator');
        if (path === '/tools/daily-wellness-tracker') setDefaultToolsTab('tracker');
        else if (path === '/tools/weight-tracker') setDefaultToolsTab('weight');
        else if (path === '/tools/period-tracker') setDefaultToolsTab('period');
        else if (path === '/tools/product-log') setDefaultToolsTab('product-log');
        else if (path === '/tools/dosing-calculator') setDefaultToolsTab('dosing');
        else setDefaultToolsTab('overview');
      } else if (path === '/subscription') {
        setActiveTab('subscription');
      }

      // Clear the flag after React has processed the re-render
      setTimeout(() => { isPopstateNavigation.current = false; }, 200);
    };
    
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);
  
  const [eventsEnabled, setEventsEnabled] = useState(() => {
    return localStorage.getItem('eventsEnabled') === 'true';
  }); // Global Events visibility control
  const [subscriptionPlans, setSubscriptionPlans] = useState({});

  const performGlobalSearch = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    setIsSearching(true);
    try {
      // Search across all content types
      const [classroomResponse, communityResponse, marketplaceResponse] = await Promise.all([
        axios.get(`${API}/content?search=${encodeURIComponent(query)}`),
        axios.get(`${API}/community-posts?search=${encodeURIComponent(query)}`),
        axios.get(`${API}/products?search=${encodeURIComponent(query)}`)
      ]);

      const results = [
        ...classroomResponse.data.map(item => ({ ...item, type: 'classroom', resultCategory: 'Classroom', originalCategory: item.category })),
        ...communityResponse.data.map(item => ({ ...item, type: 'community', resultCategory: 'Community' })),
        ...marketplaceResponse.data.map(item => ({ ...item, type: 'marketplace', resultCategory: 'Marketplace' }))
      ];

      setSearchResults(results);
      setShowSearchResults(true);
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    // Debounce search
    clearTimeout(window.searchTimeout);
    window.searchTimeout = setTimeout(() => {
      performGlobalSearch(query);
    }, 300);
  };

  const handleSearchResultClick = (result) => {
    setShowSearchResults(false);
    setSearchQuery('');
    
    // Navigate to appropriate section AND open specific content based on result type
    if (result.type === 'classroom') {
      setActiveTab('education');
      // Set the content to open - use _id or id depending on what's available
      const contentId = result._id || result.id;
      if (contentId) {
        // For classroom content, we need category info to build the deep link
        // Use originalCategory (preserved from API) or category field
        const category = result.originalCategory || result.category;
        const categorySlug = result.category_slug || result.categorySlug || 
          (category ? category.toLowerCase().replace(/\s+/g, '-') : null);
        const subcategorySlug = result.subcategory_slug || result.subcategorySlug ||
          (result.subcategory ? result.subcategory.toLowerCase().replace(/\s+/g, '-') : null);
        
        if (categorySlug) {
          setContentToOpen({ 
            categorySlug, 
            subcategorySlug,
            contentId 
          });
        } else {
          // Fallback: just set the content ID and let the education section figure it out
          setContentToOpen({ contentId });
        }
      }
    } else if (result.type === 'community') {
      setActiveTab('chat');
      // Set the discussion to open with room context
      // result contains: id, room_id, parent_message_id (if it's a reply)
      const messageId = result._id || result.id;
      const roomId = result.room_id;
      const parentId = result.parent_message_id;
      
      if (roomId) {
        // We have room context - can deep link properly
        // If this is a reply, open the parent thread; otherwise open this message
        const targetMessageId = parentId || messageId;
        setDiscussionToOpen({
          messageId: targetMessageId,
          roomId: roomId
        });
      }
    } else if (result.type === 'marketplace') {
      setActiveTab('shop');
      // Set the product to open
      const productId = result._id || result.id;
      if (productId) {
        setProductToOpen(productId);
      }
    }
  };

  // Handle tab navigation with URL updates (uses React Router navigate for proper history management)
  const handleTabChange = (tab) => {
    // Reset section state when switching to OR re-clicking tabs
    if (tab === 'education') {
      setEducationResetTrigger(prev => prev + 1);
      setContentToOpen(null);
      setCategoryToOpen(null);
    } else if (tab === 'chat') {
      setChatResetTrigger(prev => prev + 1);
      setDiscussionToOpen(null);
    } else if (tab === 'calculator') {
      setDefaultToolsTab('tracker');
    }
    
    setActiveTab(tab);
    let path = '/dashboard';
    if (tab === 'education') path = '/classroom';
    else if (tab === 'chat') path = '/community';
    else if (tab === 'shop') path = '/marketplace';
    else if (tab === 'calculator') {
      path = '/tools';
      setDefaultToolsTab('overview');
    }
    else if (tab === 'subscription') path = '/subscription';
    
    navigate(path);
  };

  // Fetch user notifications (warnings) - Dashboard specific
  const fetchUserNotifications = async () => {
    if (!user?.id) return;
    
    try {
      const response = await axios.get(`${BACKEND_URL}/api/user/${user.id}/warnings`);
      setUserNotifications(response.data);
    } catch (error) {
      console.error('Error fetching user notifications:', error);
      // Don't show error toast for notifications, fail silently
    }
  };

  // Fetch gamification data - REMOVED (gamification stripped)

  // Dashboard useEffect for notifications
  useEffect(() => {
    if (user?.id) {
      fetchUserNotifications();
      
      // Set up polling for user notifications every 60 seconds
      const notificationInterval = setInterval(fetchUserNotifications, 60000);
      return () => clearInterval(notificationInterval);
    }
  }, [user?.id]);

  // Check for non-upgrade survey trigger on login
  useEffect(() => {
    if (user?.id && user?.subscription_status === 'trial') {
      // Check if trial has expired
      const trialEndDate = user?.trial_end_date ? new Date(user.trial_end_date) : null;
      if (trialEndDate && trialEndDate < new Date()) {
        // Trial expired and user hasn't upgraded - show survey
        // Check if survey already shown (using sessionStorage to show once per session)
        const surveyShown = sessionStorage.getItem('non_upgrade_survey_shown');
        if (!surveyShown) {
          setSurveyType('non_upgrade');
          setShowSurveyModal(true);
          sessionStorage.setItem('non_upgrade_survey_shown', 'true');
        }
      }
    }
  }, [user]);

  // Listen for survey trigger events
  useEffect(() => {
    const handleSurveyEvent = (event) => {
      setSurveyType(event.detail.type);
      setShowSurveyModal(true);
    };

    window.addEventListener('show-survey', handleSurveyEvent);
    return () => window.removeEventListener('show-survey', handleSurveyEvent);
  }, []);

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showSearchResults && !event.target.closest('.search-container')) {
        setShowSearchResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSearchResults]);

  return (
    <div data-testid="main-dashboard" className="min-h-screen bg-gradient-to-br from-cream via-warm-white to-gold-50 overflow-x-hidden">
      {/* Dashboard Navigation */}
      <nav className="bg-burgundy backdrop-blur-md border-b border-gold-200 shadow-lg sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div 
              className="flex items-center cursor-pointer" 
              onClick={() => {
                setActiveTab('dashboard');
                navigate('/dashboard');
              }}
            >
              <img 
                src="/assets/luminelle-logo.png"
                alt="Luminelle"
                className="h-10 w-auto hover:opacity-80 transition-opacity"
              />
            </div>
            
            {/* Mobile Menu Button */}
            <div className="md:hidden">
              <button
                onClick={() => setShowMobileMenu(!showMobileMenu)}
                className="text-cream hover:bg-cream/10 p-2 rounded-md transition-colors"
              >
                {showMobileMenu ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-4">
              {/* Global Search */}
              <div className="relative search-container">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-cream/70" />
                  <Input
                    type="text"
                    placeholder="Search across platform..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    className="w-64 pl-10 bg-white/10 border-cream/30 text-cream placeholder:text-cream/70 focus:border-gold focus:ring-gold"
                  />
                  {isSearching && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-cream"></div>
                    </div>
                  )}
                </div>
                
                {/* Search Results Dropdown */}
                {showSearchResults && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-burgundy/20 rounded-lg shadow-xl z-50 max-h-96 overflow-y-auto">
                    {searchResults.length > 0 ? (
                      searchResults.map((result, index) => (
                        <div
                          key={`${result.type}-${result.id || index}`}
                          className="p-3 hover:bg-burgundy/5 cursor-pointer border-b border-burgundy/10 last:border-b-0"
                          onClick={() => handleSearchResultClick(result)}
                        >
                          <div className="flex justify-between items-start mb-1">
                            <h4 className="font-medium text-burgundy text-sm line-clamp-1">
                              {result.title || result.name || result.content?.substring(0, 50)}
                            </h4>
                            <Badge variant="outline" className="text-xs ml-2 flex-shrink-0">
                              {result.resultCategory}
                            </Badge>
                          </div>
                          <p className="text-xs text-burgundy/60 line-clamp-2">
                            {(result.description || result.content || '').replace(/<[^>]*>/g, '').substring(0, 100)}
                          </p>
                        </div>
                      ))
                    ) : (
                      <div className="p-6 text-center">
                        <div className="text-burgundy/50 mb-2">
                          <Search className="h-8 w-8 mx-auto mb-2" />
                        </div>
                        <p className="text-sm text-burgundy/60">No results found</p>
                        <p className="text-xs text-burgundy/40 mt-1">Try searching with different keywords</p>
                      </div>
                    )}
                  </div>
                )}
                
                {/* No Results Message */}
                {showSearchResults && searchResults.length === 0 && searchQuery.trim() && !isSearching && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-burgundy/20 rounded-lg shadow-xl z-50 p-4 text-center">
                    <p className="text-burgundy/60 text-sm">No results found for "{searchQuery}"</p>
                  </div>
                )}
              </div>
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="text-cream hover:bg-cream/10 flex items-center space-x-2 relative">
                    <User className="h-5 w-5" />
                    <span className="font-semibold">{user?.name}</span>
                    <ChevronDown className="h-4 w-4" />
                    {userNotifications.unread_warnings > 0 && (
                      <Badge className="absolute -top-1 -right-1 h-4 w-4 p-0 flex items-center justify-center bg-red-500 text-white text-xs rounded-full">
                        {userNotifications.unread_warnings}
                      </Badge>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end">
                  <div className="px-3 py-2">
                    <p className="text-sm font-medium">{user?.name}</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                    {user?.location && (
                      <p className="text-xs text-muted-foreground">{user.location}</p>
                    )}
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setShowProfileModal(true)}>
                    <User className="h-4 w-4 mr-2" />
                    <span>Profile & Settings</span>
                    {userNotifications.unread_warnings > 0 && (
                      <Badge className="ml-2 h-4 w-4 p-0 flex items-center justify-center bg-red-500 text-white text-xs rounded-full">
                        {userNotifications.unread_warnings}
                      </Badge>
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setShowSavedItems(true)}>
                    <Bookmark className="mr-2 h-4 w-4" />
                    <span>Saved Items & Favorites</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setShowWellnessAssessment(true)}>
                    <CheckCircle className="mr-2 h-4 w-4" />
                    <span>Wellness Assessment</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setShowNeedHelp(true)}>
                    <MessageCircle className="mr-2 h-4 w-4" />
                    <span>Need Help?</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setShowGiftSubscriptionModal(true)}>
                    <Gift className="mr-2 h-4 w-4" />
                    <span>Gift Subscription</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={onLogout}>
                    <LogOut className="h-4 w-4 mr-2" />
                    <span>Logout</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              
              {user?.subscription_status && (
                <div className="flex items-center gap-2">
                  <Badge className={`px-2 py-1 text-xs rounded-full cursor-default pointer-events-none ${
                    user.coupon_used === 'FOUNDING48'
                      ? 'bg-gold text-burgundy border-gold'
                    : user.subscription_status === 'trial'
                      ? 'bg-white text-burgundy border-burgundy/20'
                      : 'bg-dark-teal text-cream border-dark-teal'
                  }`}>
                    {user.coupon_used === 'FOUNDING48'
                      ? 'Founding 48'
                    : user.subscription_status === 'trial'
                      ? 'Trial Member'
                      : 'Member'}
                  </Badge>
                  
                  {(!user?.subscription_status || user.subscription_status === 'trial') && (
                    <button
                      onClick={() => handleTabChange('subscription')}
                      className="px-3 py-1 bg-burgundy hover:bg-burgundy/80 text-cream text-xs font-medium rounded-full transition-colors"
                    >
                      Upgrade
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu - Compact Slide-out - Rendered outside nav to avoid backdrop-blur stacking context issues */}
      {showMobileMenu && (
        <div className="fixed inset-0 md:hidden" style={{ zIndex: 88888 }}>
          {/* Background overlay */}
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setShowMobileMenu(false)}></div>
          
          {/* Slide-out menu */}
          <div className="fixed top-0 left-0 w-80 bg-burgundy shadow-2xl flex flex-col overflow-y-auto" style={{ zIndex: 88889, height: '100vh', maxHeight: '100vh' }}>
            {/* Header with Close Button */}
            <div className="flex items-center justify-between p-3 border-b border-cream/20">
              <img 
                src="/assets/luminelle-logo.png"
                alt="Luminelle"
                className="h-6 w-auto"
              />
              <button
                onClick={() => setShowMobileMenu(false)}
                className="text-cream hover:bg-cream/10 p-1 rounded-md transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Mobile Search */}
            <div className="p-3 search-container">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-cream/70" />
                <Input
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className="w-full pl-10 pr-3 py-2 text-base bg-white/10 border-cream/30 text-cream placeholder:text-cream/70 focus:border-gold focus:ring-gold rounded-md"
                />
                {isSearching && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-cream"></div>
                  </div>
                )}
              </div>
              
              {/* Mobile Search Results Dropdown */}
              {showSearchResults && (
                <div className="mt-2 bg-white border border-burgundy/20 rounded-lg shadow-xl max-h-64 overflow-y-auto" style={{ position: 'relative', zIndex: 100 }}>
                  {searchResults.length > 0 ? (
                    searchResults.map((result, index) => (
                      <div
                        key={`mobile-${result.type}-${result.id || index}`}
                        data-testid={`mobile-search-result-${index}`}
                        className="p-3 hover:bg-burgundy/5 active:bg-burgundy/10 cursor-pointer border-b border-burgundy/10 last:border-b-0"
                        onClick={() => {
                          handleSearchResultClick(result);
                          setShowMobileMenu(false);
                        }}
                      >
                        <div className="flex justify-between items-start mb-1">
                          <h4 className="font-medium text-burgundy text-sm line-clamp-1">
                            {result.title || result.name || result.content?.substring(0, 50)}
                          </h4>
                          <Badge variant="outline" className="text-xs ml-2 flex-shrink-0">
                            {result.resultCategory}
                          </Badge>
                        </div>
                        <p className="text-xs text-burgundy/60 line-clamp-2">
                          {(result.description || result.content || '').replace(/<[^>]*>/g, '').substring(0, 100)}
                        </p>
                      </div>
                    ))
                  ) : searchQuery.trim() && !isSearching ? (
                    <div className="p-4 text-center">
                      <Search className="h-6 w-6 mx-auto mb-2 text-burgundy/40" />
                      <p className="text-sm text-burgundy/60">No results found</p>
                      <p className="text-xs text-burgundy/40 mt-1">Try different keywords</p>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
            
            {/* Navigation Menu */}
            <div className="px-3 py-1">
              <button
                onClick={() => {handleTabChange('dashboard'); setShowMobileMenu(false);}}
                className={`w-full flex items-center px-3 py-2.5 rounded-md text-left transition-colors ${
                  activeTab === 'dashboard' ? 'bg-cream/20 text-cream' : 'text-cream/90 hover:bg-cream/10'
                }`}
              >
                <User className="h-5 w-5 mr-3" />
                <span className="text-sm font-medium">Dashboard</span>
              </button>
              
              <button
                onClick={() => {handleTabChange('education'); setShowMobileMenu(false);}}
                className={`w-full flex items-center px-3 py-2.5 rounded-md text-left transition-colors ${
                  activeTab === 'education' ? 'bg-cream/20 text-cream' : 'text-cream/90 hover:bg-cream/10'
                }`}
              >
                <BookOpen className="h-5 w-5 mr-3" />
                <span className="text-sm font-medium">Classroom</span>
              </button>
              
              <button
                onClick={() => {handleTabChange('chat'); setShowMobileMenu(false);}}
                className={`w-full flex items-center px-3 py-2.5 rounded-md text-left transition-colors ${
                  activeTab === 'chat' ? 'bg-cream/20 text-cream' : 'text-cream/90 hover:bg-cream/10'
                }`}
              >
                <MessageCircle className="h-5 w-5 mr-3" />
                <span className="text-sm font-medium">Community</span>
              </button>
              
              {eventsEnabled && (
                <button
                  onClick={() => {handleTabChange('events'); setShowMobileMenu(false);}}
                  className={`w-full flex items-center px-3 py-2.5 rounded-md text-left transition-colors ${
                    activeTab === 'events' ? 'bg-cream/20 text-cream' : 'text-cream/90 hover:bg-cream/10'
                  }`}
                >
                  <Calendar className="h-5 w-5 mr-3" />
                  <span className="text-sm font-medium">Events</span>
                </button>
              )}
              
              <button
                onClick={() => {handleTabChange('calculator'); setShowMobileMenu(false);}}
                className={`w-full flex items-center px-3 py-2.5 rounded-md text-left transition-colors ${
                  activeTab === 'calculator' ? 'bg-cream/20 text-cream' : 'text-cream/90 hover:bg-cream/10'
                }`}
              >
                <Calculator className="h-5 w-5 mr-3" />
                <span className="text-sm font-medium">Tools</span>
              </button>
              
              <button
                onClick={() => {handleTabChange('shop'); setShowMobileMenu(false);}}
                className={`w-full flex items-center px-3 py-2.5 rounded-md text-left transition-colors ${
                  activeTab === 'shop' ? 'bg-cream/20 text-cream' : 'text-cream/90 hover:bg-cream/10'
                }`}
              >
                <ShoppingBag className="h-5 w-5 mr-3" />
                <span className="text-sm font-medium">Marketplace</span>
              </button>
            </div>

            {/* User Profile Section */}
            <div className="border-t border-cream/20 mt-3 pt-2 px-3 pb-3">
              <div className="flex items-center px-2 py-1.5 mb-1">
                <UserAvatar 
                  user={user}
                  size="sm"
                />
                <div className="ml-3 flex-1">
                  <p className="text-cream font-medium text-sm">{user?.name}</p>
                  <div className="flex items-center gap-2">
                    {user?.subscription_status && (
                      <p className="text-gold text-xs font-medium">
                        {user.coupon_used === 'FOUNDING48' ? 'Founding 48'
                        : user.subscription_status === 'trial' ? 'Trial Member'
                        : 'Member'}
                      </p>
                    )}
                    
                    {(!user?.subscription_status || user.subscription_status === 'trial') && (
                      <button
                        onClick={() => {handleTabChange('subscription'); setShowMobileMenu(false);}}
                        className="px-2 py-0.5 bg-burgundy hover:bg-burgundy/80 text-cream text-xs font-medium rounded-full transition-colors"
                      >
                        Upgrade
                      </button>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="space-y-0.5">
                <button
                  onClick={() => {setShowProfileModal(true); setShowMobileMenu(false);}}
                  className="w-full flex items-center px-2 py-1.5 rounded-md text-left text-cream/90 hover:bg-cream/10 transition-colors"
                >
                  <User className="h-4 w-4 mr-3" />
                  <span className="text-sm">Profile & Settings</span>
                </button>
                
                <button
                  onClick={() => {setShowSavedItems(true); setShowMobileMenu(false);}}
                  className="w-full flex items-center px-2 py-1.5 rounded-md text-left text-cream/90 hover:bg-cream/10 transition-colors"
                >
                  <Bookmark className="h-4 w-4 mr-3" />
                  <span className="text-sm">Saved Items & Favorites</span>
                </button>
                
                <button
                  onClick={() => {setShowNeedHelp(true); setShowMobileMenu(false);}}
                  className="w-full flex items-center px-2 py-1.5 rounded-md text-left text-cream/90 hover:bg-cream/10 transition-colors"
                >
                  <MessageCircle className="h-4 w-4 mr-3" />
                  <span className="text-sm">Need Help?</span>
                </button>
                
                <button
                  onClick={onLogout}
                  className="w-full flex items-center px-2 py-1.5 rounded-md text-left text-cream/90 hover:bg-cream/10 transition-colors"
                >
                  <LogOut className="h-4 w-4 mr-3" />
                  <span className="text-sm">Logout</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 py-8 min-h-[calc(100vh-200px)]">
        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
          {/* Desktop Tab Navigation */}
          <TabsList className={`hidden md:grid ${eventsEnabled ? 'grid-cols-6' : 'grid-cols-5'} w-full mb-8 bg-white border border-burgundy/20 rounded-xl p-1 text-sm`}>
            <TabsTrigger data-testid="dashboard-tab" value="dashboard" className="data-[state=active]:bg-burgundy data-[state=active]:text-cream rounded-lg font-medium px-4"
              onClick={() => { if (activeTab === 'dashboard') handleTabChange('dashboard'); }}
            >
              Dashboard
            </TabsTrigger>
            <TabsTrigger 
              data-testid="education-tab" 
              value="education" 
              className="data-[state=active]:bg-burgundy data-[state=active]:text-cream rounded-lg font-medium px-4"
              onClick={() => { if (activeTab === 'education') handleTabChange('education'); }}
            >
              Classroom
            </TabsTrigger>
            <TabsTrigger 
              data-testid="chat-tab" 
              value="chat" 
              className="data-[state=active]:bg-burgundy data-[state=active]:text-cream rounded-lg font-medium px-4"
              onClick={() => { if (activeTab === 'chat') handleTabChange('chat'); }}
            >
              Community
            </TabsTrigger>
            {eventsEnabled && (
              <TabsTrigger data-testid="events-tab" value="events" className="data-[state=active]:bg-burgundy data-[state=active]:text-cream rounded-lg font-medium px-4"
                onClick={() => { if (activeTab === 'events') handleTabChange('events'); }}
              >
                Events
              </TabsTrigger>
            )}
            <TabsTrigger data-testid="calculator-tab" value="calculator" className="data-[state=active]:bg-burgundy data-[state=active]:text-cream rounded-lg font-medium px-4"
              onClick={() => { if (activeTab === 'calculator') handleTabChange('calculator'); }}
            >Tools</TabsTrigger>
            <TabsTrigger data-testid="shop-tab" value="shop" className="data-[state=active]:bg-burgundy data-[state=active]:text-cream rounded-lg font-medium px-4"
              onClick={() => { if (activeTab === 'shop') handleTabChange('shop'); }}
            >
              Marketplace
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="dashboard">
            <HomeSection 
              user={user} 
              showProfileModal={showProfileModal} 
              setShowProfileModal={setShowProfileModal} 
              onUserUpdate={onUserUpdate}
              setActiveTab={setActiveTab}
              setDefaultToolsTab={setDefaultToolsTab}
              setDiscussionToOpen={setDiscussionToOpen}
              setContentToOpen={setContentToOpen}
              setShowUpgradeModal={setShowUpgradeModal}
              setUpgradeFeature={setUpgradeFeature}
              setShowWellnessAssessment={setShowWellnessAssessment}
            />
          </TabsContent>
          
          <TabsContent value="education">
            <EducationSection resetTrigger={educationResetTrigger} user={user} contentToOpen={contentToOpen} setContentToOpen={setContentToOpen} categoryToOpen={categoryToOpen} setCategoryToOpen={setCategoryToOpen} setShowUpgradeModal={setShowUpgradeModal} setUpgradeFeature={setUpgradeFeature} />
          </TabsContent>
          
          <TabsContent value="chat">
            <ChatSection 
              user={user} 
              onViewProfile={setSelectedUserProfile}
              onShowProfileModal={setShowUserProfileModal}
              discussionToOpen={discussionToOpen}
              setDiscussionToOpen={setDiscussionToOpen}
              resetTrigger={chatResetTrigger}
            />
          </TabsContent>
          
          <TabsContent value="calculator">
            <PeptideCalculator user={user} defaultTab={defaultToolsTab} />
          </TabsContent>
          
          {eventsEnabled && (
            <TabsContent value="events">
              <EventsSection user={user} />
            </TabsContent>
          )}
          
          <TabsContent value="shop">
            <ShopSection user={user} productToOpen={productToOpen} setProductToOpen={setProductToOpen} setShowUpgradeModal={setShowUpgradeModal} setUpgradeFeature={setUpgradeFeature} />
          </TabsContent>

          <TabsContent value="subscription">
            <SubscriptionSection user={user} />
          </TabsContent>
        </Tabs>
      </div>
      
      {/* Footer */}
      <Footer />
      
      {/* Enhanced Profile Modal */}
      {showProfileModal && (
        <ProfileModal 
          user={user} 
          onClose={() => setShowProfileModal(false)}
          onUserUpdate={onUserUpdate}
          userNotifications={userNotifications}
          onRefreshNotifications={fetchUserNotifications}
          setMainActiveTab={setActiveTab}
        />
      )}

      {/* Other User Profile Modal */}
      {showUserProfileModal && selectedUserProfile && (
        <UserProfileModal 
          user={selectedUserProfile} 
          isOpen={showUserProfileModal}
          onClose={() => {
            setShowUserProfileModal(false);
            setSelectedUserProfile(null);
          }}
          isOwnProfile={false}
        />
      )}

      {/* Saved Items Modal */}
      {showSavedItems && (
        <SavedItemsModal 
          user={user}
          onClose={() => setShowSavedItems(false)}
          onNavigate={setActiveTab}
        />
      )}

      {/* Wellness Assessment Modal */}
      <WellnessAssessment
        isOpen={showWellnessAssessment}
        onClose={() => setShowWellnessAssessment(false)}
        user={user}
        onUserUpdate={onUserUpdate}
      />


      {/* Gift Subscription Modal */}
      {showGiftSubscriptionModal && (
        <GiftSubscriptionModal 
          user={user}
          isOpen={showGiftSubscriptionModal}
          onClose={() => setShowGiftSubscriptionModal(false)}
        />
      )}

      {/* Feedback Modal */}
      {showFeedbackModal && (
        <FeedbackModal
          user={user}
          onClose={() => setShowFeedbackModal(false)}
          currentPage={activeTab}
        />
      )}

      {/* Need Help Modal */}
      {showNeedHelp && (
        <NeedHelpModal
          user={user}
          onClose={() => setShowNeedHelp(false)}
        />
      )}


      {/* Survey Modal */}
      {showSurveyModal && (
        <SurveyModal
          isOpen={showSurveyModal}
          onClose={() => setShowSurveyModal(false)}
          surveyType={surveyType}
          userId={user?.id}
        />
      )}

      {/* Floating Feedback Button */}
      <button
        onClick={() => setShowFeedbackModal(true)}
        className="fixed bottom-6 left-6 bg-gold hover:bg-gold/80 text-burgundy rounded-full p-4 shadow-lg z-40 transition-all hover:scale-110 group"
        title="Send Feedback"
      >
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
        </svg>
        <span className="absolute left-16 top-1/2 -translate-y-1/2 bg-burgundy text-cream px-3 py-1.5 rounded-md text-sm font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
          Send Feedback
        </span>
      </button>
    </div>
  );
};

// Quiz Component
const QuizComponent = ({ contentId, userId, onClose }) => {
  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchQuiz();
  }, [contentId]);

  const fetchQuiz = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/quizzes/content/${contentId}`);
      if (response.data) {
        setQuiz(response.data);
        // Initialize answers
        const initialAnswers = {};
        response.data.questions.forEach((_, idx) => {
          initialAnswers[idx] = null;
        });
        setAnswers(initialAnswers);
      }
    } catch (error) {
      console.error('Error fetching quiz:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerSelect = (questionIndex, optionIndex) => {
    setAnswers({
      ...answers,
      [questionIndex]: optionIndex
    });
  };

  const handleSubmit = async () => {
    // Check if all questions are answered
    const allAnswered = Object.values(answers).every(answer => answer !== null);
    if (!allAnswered) {
      toast.error('Please answer all questions before submitting');
      return;
    }

    setSubmitting(true);
    try {
      const answerArray = Object.values(answers);
      const response = await axios.post(
        `${BACKEND_URL}/api/quizzes/submit?user_id=${userId}`,
        {
          quiz_id: quiz.id,
          answers: answerArray
        }
      );
      setResult(response.data);
      
      if (response.data.passed) {
        toast.success(`🎉 You passed! Score: ${response.data.percentage.toFixed(0)}%`);
      } else {
        toast.error(`You scored ${response.data.percentage.toFixed(0)}%. Try again to improve!`);
      }
    } catch (error) {
      console.error('Error submitting quiz:', error);
      toast.error('Error submitting quiz');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Card className="border-burgundy/20 mt-6">
        <CardContent className="py-8 text-center">
          <div className="animate-spin h-8 w-8 border-4 border-burgundy border-t-transparent rounded-full mx-auto"></div>
          <p className="text-burgundy/60 mt-4">Loading quiz...</p>
        </CardContent>
      </Card>
    );
  }

  if (!quiz) {
    return null; // No quiz for this content
  }

  return (
    <Card className="border-burgundy/20 mt-6 bg-gradient-to-br from-white to-cream/30">
      <CardHeader className="border-b border-burgundy/10">
        <div className="flex justify-between items-start">
          <div>
            <CardTitle className="text-2xl font-serif text-burgundy flex items-center gap-2">
              <img src="/badges/quiz.png" alt="Quiz" className="w-8 h-8" />
              {quiz.title}
            </CardTitle>
            <CardDescription className="mt-2">
              {result ? 'Quiz Results' : 'Test your knowledge! Pass with 70% or higher to earn points.'}
            </CardDescription>
          </div>
          {onClose && (
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="h-5 w-5" />
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="p-6">
        {!result ? (
          <div className="space-y-6">
            {quiz.questions.map((question, qIdx) => (
              <div key={qIdx} className="space-y-3">
                <h3 className="font-semibold text-burgundy">
                  {qIdx + 1}. {question.question}
                </h3>
                <div className="space-y-2">
                  {question.options.map((option, oIdx) => (
                    <button
                      key={oIdx}
                      onClick={() => handleAnswerSelect(qIdx, oIdx)}
                      className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                        answers[qIdx] === oIdx
                          ? 'border-burgundy bg-burgundy/10'
                          : 'border-burgundy/20 hover:border-burgundy/40 bg-white'
                      }`}
                    >
                      <span className="font-medium">{String.fromCharCode(65 + oIdx)}.</span> {option}
                    </button>
                  ))}
                </div>
              </div>
            ))}

            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="w-full bg-burgundy hover:bg-burgundy/80 text-cream"
            >
              {submitting ? 'Submitting...' : 'Submit Quiz'}
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Results Summary */}
            <div className={`p-6 rounded-lg text-center ${
              result.passed 
                ? 'bg-gradient-to-br from-gold/20 to-orange-500/10 border-2 border-gold'
                : 'bg-burgundy/5 border-2 border-burgundy/20'
            }`}>
              <div className="mb-4 flex justify-center">
                {result.passed ? (
                  <img src="/badges/congratulations.png" alt="Congratulations" className="w-24 h-24" />
                ) : (
                  <img src="/badges/keep-learning.png" alt="Keep Learning" className="w-24 h-24" />
                )}
              </div>
              <h3 className="text-2xl font-bold text-burgundy mb-2">
                {result.passed ? 'Congratulations!' : 'Keep Learning!'}
              </h3>
              <p className="text-xl font-semibold text-burgundy/80 mb-4">
                Score: {result.score}/{result.total_questions} ({result.percentage.toFixed(0)}%)
              </p>
              {result.passed && (
                <Badge className="bg-gold text-burgundy text-lg px-4 py-2">
                  +{result.points_earned} Points Earned!
                </Badge>
              )}
            </div>

            {/* Answers Review */}
            <div className="space-y-4">
              <h4 className="font-semibold text-burgundy">Review Your Answers:</h4>
              {quiz.questions.map((question, qIdx) => {
                const userAnswer = answers[qIdx];
                const correctAnswer = question.correct_answer_index;
                const isCorrect = userAnswer === correctAnswer;

                return (
                  <div key={qIdx} className={`p-4 rounded-lg border-2 ${
                    isCorrect 
                      ? 'border-teal-500 bg-teal-50'
                      : 'border-red-500 bg-red-50'
                  }`}>
                    <div className="flex items-start gap-2 mb-2">
                      <span className="text-xl">
                        {isCorrect ? '✅' : '❌'}
                      </span>
                      <div className="flex-1">
                        <p className="font-medium text-burgundy">
                          {qIdx + 1}. {question.question}
                        </p>
                        <p className="text-sm mt-2">
                          <span className="font-medium">Your answer:</span> {question.options[userAnswer]}
                        </p>
                        {!isCorrect && (
                          <p className="text-sm text-teal-700 mt-1">
                            <span className="font-medium">Correct answer:</span> {question.options[correctAnswer]}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <Button
              onClick={onClose}
              className="w-full bg-burgundy hover:bg-burgundy/80 text-cream"
            >
              Close Quiz
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Dashboard Poll Component
const DashboardPoll = ({ userId }) => {
  const [poll, setPoll] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);
  const [hasVoted, setHasVoted] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPoll();
  }, [userId]);

  const fetchPoll = async () => {
    try {
      const url = userId 
        ? `${BACKEND_URL}/api/polls/active?user_id=${userId}`
        : `${BACKEND_URL}/api/polls/active`;
      
      const response = await axios.get(url);
      if (response.data) {
        setPoll(response.data);
        setHasVoted(response.data.user_voted);
        setSelectedOption(response.data.user_vote_index);
      }
    } catch (error) {
      console.error('Error fetching poll:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (optionIndex) => {
    if (hasVoted || !userId) return;
    
    try {
      await axios.post(`${BACKEND_URL}/api/polls/vote?user_id=${userId}`, {
        poll_id: poll.id,
        option_index: optionIndex
      });
      
      setSelectedOption(optionIndex);
      setHasVoted(true);
      
      // Refresh poll to get updated results
      setTimeout(() => fetchPoll(), 500);
      
      toast.success('Vote recorded!');
    } catch (error) {
      console.error('Error submitting vote:', error);
      toast.error('Error submitting vote');
    }
  };

  if (loading || !poll) return null;

  const totalVotes = poll.total_votes;
  const getPercentage = (votes) => {
    if (totalVotes === 0) return 0;
    return Math.round((votes / totalVotes) * 100);
  };

  return (
    <Card className="border-burgundy/20 bg-white shadow-sm">
      <CardHeader>
        <CardTitle className="text-xl font-serif text-burgundy">
          Community Poll
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <p className="text-burgundy font-medium">{poll.question}</p>
          
          <div className="space-y-3">
            {poll.options.map((option, index) => {
              const votes = poll.vote_counts[index] || 0;
              const percentage = getPercentage(votes);
              const isSelected = selectedOption === index;
              
              return (
                <div key={`poll-opt-${option}`} className="relative">
                  <button
                    onClick={() => handleVote(index)}
                    disabled={hasVoted}
                    className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                      hasVoted
                        ? isSelected
                          ? 'border-gold bg-gold/10 cursor-default'
                          : 'border-burgundy/20 bg-burgundy/5 cursor-default'
                        : 'border-burgundy/30 hover:border-burgundy hover:bg-burgundy/5 cursor-pointer'
                    }`}
                  >
                    <div className="flex items-center justify-between relative z-10">
                      <span className={`font-medium ${isSelected ? 'text-burgundy' : 'text-burgundy/80'}`}>
                        {option}
                      </span>
                      {hasVoted && (
                        <span className={`text-sm font-bold ${isSelected ? 'text-burgundy' : 'text-burgundy/60'}`}>
                          {percentage}%
                        </span>
                      )}
                    </div>
                    
                    {hasVoted && (
                      <div 
                        className="absolute inset-0 bg-burgundy/10 rounded-lg transition-all"
                        style={{ width: `${percentage}%` }}
                      />
                    )}
                  </button>
                </div>
              );
            })}
          </div>
          
          {hasVoted && (
            <p className="text-xs text-burgundy/60 text-center mt-2">
              {totalVotes} {totalVotes === 1 ? 'vote' : 'votes'} • Results update in real-time
            </p>
          )}
          
          {!hasVoted && !userId && (
            <p className="text-xs text-burgundy/60 text-center mt-2">
              Please log in to vote
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Home Section Component
const HomeSection = ({ user, showProfileModal, setShowProfileModal, onUserUpdate, setActiveTab, setDefaultToolsTab, setDiscussionToOpen, setContentToOpen, setShowUpgradeModal, setUpgradeFeature, setShowWellnessAssessment }) => {
  // Debug: Log props on component mount
  useEffect(() => {
  }, []);
  
  const [welcomeVideo, setWelcomeVideo] = useState(null);
  const [carouselItems, setCarouselItems] = useState([]);
  const [currentCarouselIndex, setCurrentCarouselIndex] = useState(0);
  const [carouselHeadline, setCarouselHeadline] = useState('Take a tour of all the fantastic site features');
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [videoModalUrl, setVideoModalUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [recentContent, setRecentContent] = useState([]);
  const [recentDiscussions, setRecentDiscussions] = useState([]);
  const [recentMarketplace, setRecentMarketplace] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showProductModal, setShowProductModal] = useState(false);


  // Track product views for badge
  useEffect(() => {
    if (selectedProduct && user?.id) {
      const trackView = async () => {
        try {
          await axios.post(`${BACKEND_URL}/api/products/${selectedProduct.id}/view?user_id=${user.id}`);
        } catch (error) {
          console.error('Error tracking product view:', error);
        }
      };
      trackView();
      trackContentView('product', selectedProduct.id, selectedProduct.name, localStorage.getItem('token'));
    }
  }, [selectedProduct, user?.id]);

  useEffect(() => {
    fetchWelcomeVideo();
    fetchCarouselItems();
    fetchRecentContent();
    fetchRecentDiscussions();
    fetchRecentMarketplace();
  }, []);

  // Auto-rotation for carousel (only images, videos open in modal)
  useEffect(() => {
    if (carouselItems.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentCarouselIndex((prev) => (prev + 1) % carouselItems.length);
    }, 5000); // Rotate every 5 seconds

    return () => clearInterval(interval);
  }, [carouselItems.length, currentCarouselIndex]);


  const fetchWelcomeVideo = async () => {
    try {
      const response = await axios.get(`${API}/admin/welcome-video`);
      const videoData = response.data;
      
      // Only set video if it's enabled and has a URL
      if (videoData.enabled && videoData.video_url) {
        setWelcomeVideo(videoData);
      } else {
        setWelcomeVideo(null);
      }
    } catch (error) {
      console.error('Error fetching welcome video:', error);
      setWelcomeVideo(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchCarouselItems = async () => {
    try {
      const response = await axios.get(`${API}/homepage-carousel`);
      if (response.data.items && response.data.items.length > 0) {
        setCarouselItems(response.data.items);
      }
      
      // Fetch carousel headline
      const settingsResponse = await axios.get(`${API}/carousel-settings`);
      setCarouselHeadline(settingsResponse.data.headline || 'Take a tour of all the fantastic site features');
    } catch (error) {
      console.error('Error fetching carousel items:', error);
    }
  };

  const fetchRecentContent = async () => {
    try {
      const response = await axios.get(`${API}/content`);
      const sortedContent = response.data
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 3);
      setRecentContent(sortedContent);
    } catch (error) {
      console.error('Error fetching recent content:', error);
    }
  };

  const fetchRecentDiscussions = async () => {
    try {
      const response = await axios.get(`${API}/community-posts`);
      const sortedDiscussions = response.data
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 3);
      setRecentDiscussions(sortedDiscussions);
    } catch (error) {
      console.error('Error fetching recent discussions:', error);
    }
  };

  const fetchRecentMarketplace = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.get(`${API}/products`, { headers });
      const sortedProducts = response.data
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 3);
      setRecentMarketplace(sortedProducts);
    } catch (error) {
      console.error('Error fetching recent marketplace items:', error);
    }
  };

  const handleViewContent = async (item) => {
    // Track content view
    try {
      const viewUrl = user?.id 
        ? `${API}/content/${item.id}/view?user_id=${user.id}`
        : `${API}/content/${item.id}/view`;
      await axios.post(viewUrl);
    } catch (error) {
      console.error('Error tracking content view:', error);
    }
    // Track detailed view
    if (user?.id) {
      trackContentView('article', item.id, item.title, localStorage.getItem('token'));
    }
    
    if (item.content_type === 'url' && item.url) {
      window.open(item.url, '_blank');
    } else if (item.content_type === 'video' && item.url) {
      // Navigate to Classroom tab and open the video in modal
      setActiveTab('education');
      setContentToOpen(item);
      toast.success('Opening video in Classroom');
    } else if (item.content_type === 'audio') {
      // Navigate to Classroom tab and open the audio detail page
      setActiveTab('education');
      setContentToOpen(item);
      toast.success('Opening audio in Classroom');
    } else if (item.content_type === 'document') {
      // Open document URL in new tab if available
      if (item.url) {
        window.open(item.url, '_blank');
        toast.success('Opening document');
      } else {
        toast.error('Document URL not available');
      }
    } else if (item.content_type === 'article') {
      // Navigate to Classroom tab and open the specific article
      setActiveTab('education');
      // Pass the article to be opened via a state variable
      setContentToOpen(item);
      toast.success('Opening article in Classroom');
    } else {
      toast.error('Content not available');
    }
  };

  return (
    <div data-testid="home-section" className="space-y-8">
      {/* Personalized Welcome - shows when user has completed assessment */}
      {user && (
        <PersonalizedWelcome
          user={user}
          onNavigate={(target, data) => {
            if (target === 'classroom') setActiveTab('education');
            else if (target === 'classroom-url' && data) {
              window.location.href = data;
            }
          }}
        />
      )}

      {/* Onboarding Checklist */}
      {user && (
        <OnboardingChecklist
          user={user}
          onNavigate={(target) => {
            if (target === 'classroom') setActiveTab('education');
            else if (target === 'community') setActiveTab('community');
            else if (target === 'tracker') {
              setDefaultToolsTab('tracker');
              setActiveTab('calculator');
            }
            else if (target === 'profile') setActiveTab('profile');
            else if (target === 'assessment') {
              setShowWellnessAssessment(true);
            }
          }}
          onDismiss={() => {}}
        />
      )}

      {/* Homepage Carousel - Images at top */}
      {carouselItems.length > 0 && (
        <div>
            <div className="bg-white rounded-lg p-2 sm:p-4 lg:p-6 border border-burgundy/20 shadow-lg">
              
              {/* Carousel Content */}
              <div className="relative">
                <div 
                  className="aspect-video w-full max-w-6xl mx-auto relative cursor-pointer group"
                  onClick={() => {
                    const currentItem = carouselItems[currentCarouselIndex];
                    if (currentItem?.link_url) {
                      window.location.href = currentItem.link_url;
                    } else if (currentItem?.type === 'video') {
                      setVideoModalUrl(currentItem.url);
                      setShowVideoModal(true);
                    }
                  }}
                >
                  {carouselItems[currentCarouselIndex]?.type === 'video' ? (
                    // Video Thumbnail with Play Overlay
                    <div className="relative w-full h-full">
                      <img
                        src={
                          // Priority 1: Use thumbnail_url if provided
                          carouselItems[currentCarouselIndex]?.thumbnail_url ||
                          // Priority 2: Auto-generate for YouTube
                          (carouselItems[currentCarouselIndex]?.url.includes('youtube') 
                            ? `https://img.youtube.com/vi/${carouselItems[currentCarouselIndex].url.split('v=')[1]?.split('&')[0] || carouselItems[currentCarouselIndex].url.split('/').pop()}/maxresdefault.jpg`
                            : null) ||
                          // Priority 3: Fallback placeholder
                          'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="1920" height="1080" viewBox="0 0 1920 1080"%3E%3Crect fill="%238B4357" width="1920" height="1080"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="Arial" font-size="48" fill="%23FFF5E6"%3EClick to Watch Video%3C/text%3E%3C/svg%3E'
                        }
                        alt="Video thumbnail"
                        className="w-full h-full object-cover rounded-lg shadow-md"
                        loading="eager"
                        onError={(e) => {
                          // Fallback for thumbnail errors
                          e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="1920" height="1080" viewBox="0 0 1920 1080"%3E%3Crect fill="%238B4357" width="1920" height="1080"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="Arial" font-size="48" fill="%23FFF5E6"%3EClick to Watch Video%3C/text%3E%3C/svg%3E';
                        }}
                      />
                      {/* Play Button Overlay */}
                      <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition-colors rounded-lg">
                        <div className="w-20 h-20 bg-burgundy/90 group-hover:bg-burgundy rounded-full flex items-center justify-center shadow-2xl transition-all transform group-hover:scale-110">
                          <svg className="h-10 w-10 text-cream ml-1" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  ) : (
                    // Image Item
                    <img
                      src={carouselItems[currentCarouselIndex]?.url}
                      alt="Carousel image"
                      className="w-full h-full object-cover rounded-lg shadow-md"
                      loading="eager"
                    />
                  )}
                </div>

                {/* Navigation Arrows - Now positioned relative to the aspect-video container */}
                {carouselItems.length > 1 && (
                  <div className="absolute inset-0 pointer-events-none">
                    <button
                      onClick={() => setCurrentCarouselIndex((currentCarouselIndex - 1 + carouselItems.length) % carouselItems.length)}
                      className="pointer-events-auto absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 bg-burgundy/90 text-cream w-11 h-11 sm:w-12 sm:h-12 rounded-full shadow-lg z-10 flex items-center justify-center"
                      aria-label="Previous"
                    >
                      <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <button
                      onClick={() => setCurrentCarouselIndex((currentCarouselIndex + 1) % carouselItems.length)}
                      className="pointer-events-auto absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 bg-burgundy/90 text-cream w-11 h-11 sm:w-12 sm:h-12 rounded-full shadow-lg z-10 flex items-center justify-center"
                      aria-label="Next"
                    >
                      <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>

              {/* Dot Indicators */}
              {carouselItems.length > 1 && (
                <div className="flex justify-center items-center space-x-2 mt-4">
                  {carouselItems.map((_, index) => (
                    <button
                      key={`dot-${carouselItems[index]?.id || index}`}
                      onClick={() => setCurrentCarouselIndex(index)}
                      className={`h-2 rounded-full transition-all ${
                        index === currentCarouselIndex 
                          ? 'bg-burgundy w-8' 
                          : 'bg-burgundy/30 w-2 hover:bg-burgundy/50'
                      }`}
                      aria-label={`Go to slide ${index + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>
        </div>
      )}

        {/* Dashboard Poll */}
        <div className="mt-8">
          <DashboardPoll userId={user?.id} />
        </div>

        {/* Daily Tracking Section */}
        <div className="mt-8 bg-gradient-to-r from-dark-teal/10 to-burgundy/5 rounded-lg p-4 sm:p-6 border border-dark-teal/20">
          <div className="text-center">
            <div className="h-12 w-12 bg-dark-teal/10 rounded-full flex items-center justify-center mx-auto mb-3">
              <Calendar className="h-6 w-6 text-dark-teal" />
            </div>
            <h3 className="text-lg sm:text-xl font-semibold text-burgundy mb-4 font-serif">Daily Tracking</h3>
            <p className="text-burgundy/70 text-sm mb-4">How are you feeling today?</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-md mx-auto">
              <Button
                onClick={() => {
                  setDefaultToolsTab('tracker');
                  setActiveTab('calculator');
                  window.history.pushState({}, '', '/tools/daily-wellness-tracker');
                }}
                className="bg-dark-teal hover:bg-dark-teal/80 text-white w-full sm:w-auto min-h-[44px]"
              >
                Today's Wellness
              </Button>
              <Button
                onClick={() => {
                  setDefaultToolsTab('weight');
                  setActiveTab('calculator');
                  window.history.pushState({}, '', '/tools/weight-tracker');
                }}
                className="bg-dark-teal hover:bg-dark-teal/80 text-white w-full sm:w-auto min-h-[44px]"
              >
                Today's Weight
              </Button>
            </div>
          </div>
        </div>

      {/* Recent Content Sections */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 bg-white rounded-lg p-6 border border-burgundy/10">
        {/* Recent Classroom Posts */}
        <div>
          <h3 className="text-lg sm:text-xl font-bold text-burgundy font-serif mb-4 flex items-center">
            <BookOpen className="h-5 w-5 mr-2" />
            Recent Classroom
          </h3>
          <div className="space-y-4">
            {recentContent.length > 0 ? (
              recentContent.map((content) => {
                const isLocked = content.member_only && !isMember(user?.subscription_status);
                return (
                <Card key={content.id} className="hover:shadow-md transition-shadow bg-white border-burgundy/20 relative overflow-hidden">
                  {isLocked && (
                    <div 
                      className="absolute inset-0 bg-white/90 backdrop-blur-md z-10 flex items-center justify-center rounded-lg cursor-pointer hover:bg-white/95 transition-colors"
                      onClick={() => {
                        if (setUpgradeFeature && setShowUpgradeModal) {
                          setUpgradeFeature(content.title);
                          setShowUpgradeModal(true);
                        } else {
                          console.error('[UPGRADE] Functions not available!');
                        }
                      }}
                    >
                      <div className="text-center">
                        <Lock className="h-8 w-8 text-burgundy mx-auto mb-1" />
                        <p className="text-xs text-burgundy font-semibold">Members Only</p>
                        <p className="text-xs text-burgundy/70 mt-1">Tap to upgrade</p>
                      </div>
                    </div>
                  )}
                  <CardContent className={`p-4 ${isLocked ? 'blur-md' : ''}`}>
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-medium text-gray-900 text-sm line-clamp-2">{content.title}</h4>
                      <Badge variant="outline" className="border-gold text-gold text-xs ml-2">
                        {content.content_type === 'article' ? 'Article' : content.content_type === 'video' ? 'Video' : content.content_type === 'audio' ? 'Audio' : content.content_type === 'document' ? 'Document' : content.content_type?.charAt(0).toUpperCase() + content.content_type?.slice(1)}
                      </Badge>
                    </div>
                    <p className="text-xs text-gray-700 mb-3 line-clamp-2">
                      {content.description?.replace(/<[^>]*>/g, '') || ''}
                    </p>
                    <div className="flex justify-end">
                      <Button 
                        size="sm"
                        className="h-8 text-xs bg-burgundy hover:bg-burgundy/80"
                        onClick={() => {
                          if (isLocked) {
                            if (setUpgradeFeature && setShowUpgradeModal) {
                              setUpgradeFeature(content.title);
                              setShowUpgradeModal(true);
                            } else {
                              console.error('[DEBUG] Modal functions not available');
                            }
                            return;
                          }
                          handleViewContent(content);
                        }}
                      >
                        View
                      </Button>
                    </div>
                  </CardContent>
                </Card>
                );
              })
            ) : (
              <Card className="bg-cream/30 border-burgundy/20">
                <CardContent className="text-center py-6">
                  <BookOpen className="h-8 w-8 text-burgundy/30 mx-auto mb-2" />
                  <p className="text-xs text-burgundy/50">No recent content</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Recent Discussion Topics */}
        <div>
          <h3 className="text-xl font-bold text-burgundy font-serif mb-4 flex items-center">
            <MessageSquare className="h-5 w-5 mr-2" />
            Recent Discussions
          </h3>
          <div className="space-y-4">
            {recentDiscussions.length > 0 ? (
              recentDiscussions.map((discussion) => (
                <Card key={discussion.id} className="hover:shadow-md transition-shadow bg-white border-burgundy/20">
                  <CardContent className="p-4">
                    <h4 className="font-medium text-black text-sm mb-2 line-clamp-2">{discussion.headline || discussion.content?.substring(0, 50) + '...'}</h4>
                    <p className="text-xs text-black mb-3 line-clamp-2">{discussion.content}</p>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-burgundy/50">By {formatDisplayName(discussion.user_name)}</span>
                      <Button 
                        size="sm"
                        className="h-8 text-xs bg-dark-teal hover:bg-dark-teal/80"
                        onClick={() => {
                          setDiscussionToOpen(discussion);
                          setActiveTab('chat');
                        }}
                      >
                        View
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Card className="bg-cream/30 border-burgundy/20">
                <CardContent className="text-center py-6">
                  <MessageSquare className="h-8 w-8 text-burgundy/30 mx-auto mb-2" />
                  <p className="text-xs text-burgundy/50">No recent discussions</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Recent Marketplace Items */}
        <div>
          <h3 className="text-xl font-bold text-burgundy font-serif mb-4 flex items-center">
            <ShoppingBag className="h-5 w-5 mr-2" />
            Recent Marketplace
          </h3>
          <div className="space-y-4">
            {recentMarketplace.length > 0 ? (
              recentMarketplace.map((product) => {
                const isLocked = product.member_only && !isMember(user?.subscription_status);
                return (
                <Card key={product.id} className="hover:shadow-md transition-shadow bg-white border-burgundy/20 relative overflow-hidden">
                  {isLocked && (
                    <div 
                      className="absolute inset-0 bg-white/90 backdrop-blur-md z-10 flex items-center justify-center rounded-lg cursor-pointer hover:bg-white/95 transition-colors"
                      onClick={() => {
                        if (setUpgradeFeature && setShowUpgradeModal) {
                          setUpgradeFeature(product.name);
                          setShowUpgradeModal(true);
                        } else {
                          console.error('[UPGRADE] Functions not available!');
                        }
                      }}
                    >
                      <div className="text-center">
                        <Lock className="h-8 w-8 text-burgundy mx-auto mb-1" />
                        <p className="text-xs text-burgundy font-semibold">Members Only</p>
                        <p className="text-xs text-burgundy/70 mt-1">Tap to upgrade</p>
                      </div>
                    </div>
                  )}
                  <CardContent className={`p-4 ${isLocked ? 'blur-md' : ''}`}>
                    <div className="flex items-start space-x-3">
                      {product.image_url && (
                        <img 
                          src={product.image_url} 
                          alt={product.name}
                          className="w-12 h-12 rounded object-cover flex-shrink-0"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-gray-800 text-sm mb-1 line-clamp-1">{product.name}</h4>
                        <p className="text-xs text-gray-600 mb-2 line-clamp-2">
                          {product.description?.replace(/<[^>]*>/g, '') || ''}
                        </p>
                        <div className="flex justify-between items-center">
                          {product.price > 0 && (
                            <span className="text-sm font-semibold text-burgundy">${product.price}</span>
                          )}
                          <Button 
                            size="sm"
                            className="h-8 text-xs bg-gold hover:bg-gold/80 text-burgundy"
                            onClick={() => {
                              if (isLocked) {
                                if (setUpgradeFeature && setShowUpgradeModal) {
                                  setUpgradeFeature(product.name);
                                  setShowUpgradeModal(true);
                                } else {
                                  console.error('[DEBUG] Modal functions not available');
                                }
                                return;
                              }
                              setSelectedProduct(product);
                              setShowProductModal(true);
                            }}
                          >
                            View
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                );
              })
            ) : (
              <Card className="bg-cream/30 border-burgundy/20">
                <CardContent className="text-center py-6">
                  <ShoppingBag className="h-8 w-8 text-burgundy/30 mx-auto mb-2" />
                  <p className="text-xs text-burgundy/50">No recent products</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* 1-on-1 Personal Coaching Form */}
      <div className="mt-12 bg-gradient-to-r from-burgundy/5 to-dark-teal/5 rounded-xl p-8 border border-burgundy/10">
        <div className="text-center mb-6">
          <div className="h-16 w-16 bg-burgundy/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="h-8 w-8 text-burgundy" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a2 2 0 01-2-2v-6a2 2 0 012-2h8z" />
            </svg>
          </div>
          <h3 className="text-2xl font-bold text-burgundy font-serif mb-2">1-on-1 Personal Coaching</h3>
          <p className="text-burgundy/70">Get personalized guidance from our certified wellness specialists</p>
        </div>

        <CoachingForm user={user} />
      </div>

      {/* Video Modal */}
      {showVideoModal && (
        <div 
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={() => setShowVideoModal(false)}
        >
          <div 
            className="relative w-full max-w-5xl bg-black rounded-lg overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowVideoModal(false)}
              className="absolute top-4 right-4 z-10 bg-burgundy/90 hover:bg-burgundy text-cream rounded-full p-2 transition-colors"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            <div className="aspect-video w-full">
              {videoModalUrl.includes('youtube.com') || videoModalUrl.includes('youtu.be') ? (
                <iframe
                  src={videoModalUrl.includes('embed') ? videoModalUrl : `https://www.youtube.com/embed/${videoModalUrl.split('v=')[1]?.split('&')[0] || videoModalUrl.split('/').pop()}`}
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              ) : videoModalUrl.includes('vimeo.com') ? (
                <iframe
                  src={(() => {
                    // Handle different Vimeo URL formats
                    if (videoModalUrl.includes('player.vimeo.com')) {
                      return videoModalUrl;
                    }
                    // Extract video ID from various Vimeo formats
                    const vimeoMatch = videoModalUrl.match(/vimeo\.com\/(?:video\/)?(\d+)/);
                    const videoId = vimeoMatch ? vimeoMatch[1] : videoModalUrl.split('/').pop().split('?')[0];
                    return `https://player.vimeo.com/video/${videoId}`;
                  })()}
                  className="w-full h-full"
                  frameBorder="0"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                ></iframe>
              ) : (
                <video 
                  controls 
                  autoPlay
                  className="w-full h-full"
                >
                  <source src={videoModalUrl} type="video/mp4" />
                  <source src={videoModalUrl} type="video/webm" />
                  Your browser does not support the video tag.
                </video>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Product Modal */}
      {showProductModal && selectedProduct && (
        <div 
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
          onClick={() => setShowProductModal(false)}
        >
          <div 
            className="relative w-full max-w-2xl bg-white rounded-lg overflow-hidden shadow-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowProductModal(false)}
              className="sticky top-4 float-right z-10 bg-burgundy hover:bg-burgundy/80 text-cream rounded-full p-3 sm:p-2 transition-colors shadow-lg mr-4 sm:mr-0 sm:absolute sm:top-4 sm:right-4 min-h-[44px] min-w-[44px]"
              aria-label="Close"
            >
              <X className="h-5 w-5 sm:h-6 sm:w-6" />
            </button>
            
            <div className="p-8">
              <h2 className="text-3xl font-bold text-burgundy font-serif mb-4">{selectedProduct.name}</h2>
              
              {selectedProduct.image_url && (
                <img 
                  src={selectedProduct.image_url} 
                  alt={selectedProduct.name}
                  className="w-full h-64 object-cover rounded-lg mb-6"
                />
              )}
              
              {selectedProduct.price > 0 && (
                <h3 className="text-2xl font-bold text-red-600 mb-4">${selectedProduct.price}</h3>
              )}
              
              <div 
                className="text-gray-800 leading-relaxed mb-6 prose max-w-none [&_p]:text-gray-800 [&_h1]:text-gray-900 [&_h2]:text-gray-900 [&_h3]:text-gray-900 [&_strong]:text-gray-900 [&_ul]:text-gray-800 [&_ol]:text-gray-800 [&_li]:text-gray-800"
                dangerouslySetInnerHTML={{ __html: selectedProduct.description || 'No description available.' }}
              />
              
              <div className="flex gap-3">
                <Button
                  className="flex-1 bg-burgundy hover:bg-burgundy/80 text-cream"
                  onClick={() => window.open(selectedProduct.link, '_blank')}
                >
                  Shop Now
                </Button>
                
                {selectedProduct.learn_more_url && (
                  <Button
                    variant="outline"
                    className="flex-1 border-dark-teal text-dark-teal hover:bg-dark-teal/10"
                    onClick={() => {
                      setShowProductModal(false);
                      if (selectedProduct.learn_more_url.startsWith('/') || selectedProduct.learn_more_url.startsWith('#')) {
                        window.location.href = selectedProduct.learn_more_url;
                      } else {
                        window.open(selectedProduct.learn_more_url, '_blank');
                      }
                    }}
                  >
                    Learn More
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Coaching Form Component
const CoachingForm = ({ user }) => {
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    message: '',
    preferredTime: '',
    coachingType: 'wellness'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' or 'error'
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    setErrorMessage('');
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/coaching/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          message: formData.message,
          preferred_time: formData.preferredTime,
          coaching_type: formData.coachingType,
          user_id: user?.id || null
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to submit request');
      }
      
      setSubmitStatus('success');
      setFormData(prev => ({
        ...prev,
        message: '',
        preferredTime: ''
      }));
    } catch (error) {
      console.error('Coaching request error:', error);
      setSubmitStatus('error');
      setErrorMessage(error.message || 'Error submitting request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Success message view
  if (submitStatus === 'success') {
    return (
      <div className="max-w-2xl mx-auto text-center py-8">
        <div className="h-16 w-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="text-xl font-bold text-burgundy mb-2">Request Sent Successfully!</h3>
        <p className="text-burgundy/70 mb-4">
          Thank you for your interest in 1-on-1 coaching. We'll review your request and contact you within 24 hours to schedule your discovery session.
        </p>
        <p className="text-sm text-burgundy/60 mb-6">
          A confirmation email has been sent to <span className="font-medium">{formData.email}</span>
        </p>
        <Button 
          onClick={() => setSubmitStatus(null)}
          className="bg-burgundy hover:bg-burgundy/80 text-cream"
        >
          Submit Another Request
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="max-w-2xl mx-auto space-y-4">
      {/* Error message */}
      {submitStatus === 'error' && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <svg className="h-5 w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>{errorMessage}</span>
        </div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-burgundy mb-1">Name</label>
          <Input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData(prev => ({...prev, name: e.target.value}))}
            className="border-burgundy/30 focus:border-gold"
            required
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-burgundy mb-1">Email</label>
          <Input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData(prev => ({...prev, email: e.target.value}))}
            className="border-burgundy/30 focus:border-gold"
            required
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-burgundy mb-1">Coaching Focus</label>
          <select 
            value={formData.coachingType}
            onChange={(e) => setFormData(prev => ({...prev, coachingType: e.target.value}))}
            className="w-full border border-burgundy/30 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gold/50 focus:border-gold"
          >
            <option value="wellness">General Wellness</option>
            <option value="peptides">Peptides</option>
            <option value="life">Life Coaching</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-burgundy mb-1">Preferred Contact Time</label>
          <Input
            type="text"
            placeholder="e.g., Weekday mornings, Tuesday afternoons..."
            value={formData.preferredTime}
            onChange={(e) => setFormData(prev => ({...prev, preferredTime: e.target.value}))}
            className="border-burgundy/30 focus:border-gold"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-burgundy mb-1">Tell us about your goals</label>
        <Textarea
          placeholder="Share what you'd like to achieve and any specific challenges you're facing..."
          value={formData.message}
          onChange={(e) => setFormData(prev => ({...prev, message: e.target.value}))}
          className="min-h-[100px] border-burgundy/30 focus:border-gold"
          required
        />
      </div>

      <div className="text-center">
        <Button 
          type="submit" 
          disabled={isSubmitting}
          className="bg-burgundy hover:bg-burgundy/80 text-cream px-8 py-3"
        >
          {isSubmitting ? 'Submitting...' : 'Request 1-on-1 Coaching'}
        </Button>
      </div>

      <div className="text-center">
        <p className="text-xs text-burgundy/60">
          We will review your request and contact you within 24 hours to schedule your discovery session.
        </p>
      </div>
    </form>
  );
};

// User Profile Modal Component
const UserProfileModal = ({ user, isOpen, onClose, onUpdate, isOwnProfile = false, userNotifications = null, onRefreshNotifications = null }) => {
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    bio: user?.bio || '',
    location: user?.location || '',
    profile_photo: user?.profile_photo || '',
    fun_facts: user?.fun_facts || '',
    symptoms: user?.symptoms || [],
    wellness_goals: user?.wellness_goals || [],
    topics_of_interest: user?.topics_of_interest || [],
    experience_level: user?.experience_level || ''
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fullProfile, setFullProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeProfileTab, setActiveProfileTab] = useState('profile');
  const [emailPreferences, setEmailPreferences] = useState({
    email_notifications_enabled: true,
    email_new_content: true,
    email_community_replies: true,
    email_trial_reminders: true,
    email_weekly_digest: true
  });

  useEffect(() => {
    if (isOpen && (user?.id || user?.email)) {
      fetchFullProfile();
      if (isOwnProfile && activeProfileTab === 'email') {
        fetchEmailPreferences();
      }
    }
  }, [isOpen, user?.id, user?.email, activeProfileTab]);

  const fetchEmailPreferences = async () => {
    if (!isOwnProfile) return;
    try {
      const userId = user.id || user.email;
      const response = await axios.get(`${API}/users/${encodeURIComponent(userId)}/email-preferences`);
      setEmailPreferences(response.data);
    } catch (error) {
      console.error('Error fetching email preferences:', error);
    }
  };

  const handleEmailPreferencesUpdate = async (e) => {
    e.preventDefault();
    if (!isOwnProfile) return;
    
    setSaving(true);
    try {
      const userId = user.id || user.email;
      await axios.put(`${API}/users/${encodeURIComponent(userId)}/email-preferences`, emailPreferences);
      toast.success('Email preferences updated successfully');
    } catch (error) {
      console.error('Error updating email preferences:', error);
      toast.error('Error updating email preferences');
    } finally {
      setSaving(false);
    }
  };

  const markWarningAsRead = async (warningId) => {
    if (!isOwnProfile || !user?.id) return;
    
    try {
      await axios.patch(`${BACKEND_URL}/api/user/${user.id}/warnings/${warningId}/mark-read`);
      if (onRefreshNotifications) {
        onRefreshNotifications();
      }
      toast.success('Warning marked as read');
    } catch (error) {
      console.error('Error marking warning as read:', error);
      toast.error('Error updating notification');
    }
  };

  const fetchFullProfile = async () => {
    try {
      setLoading(true);
      
      // Check if this is a persona (user_id starts with 'persona_')
      if (user?.id && user.id.startsWith('persona_')) {
        // For personas, use the data we already have
        // Location might be in 'location' or 'user_location' depending on source
        const personaLocation = user.location || user.user_location || '';
        const personaBio = user.bio || user.user_bio || '';
        
        setFullProfile({
          id: user.id,
          name: user.name || 'Community Member',
          profile_photo: user.profile_photo,
          bio: personaBio,
          location: personaLocation,
          fun_facts: personaBio, // Use bio as fun_facts for personas
          is_persona: true
        });
        setProfileData({
          name: user.name || 'Community Member',
          bio: personaBio,
          location: personaLocation,
          profile_photo: user.profile_photo || '',
          fun_facts: personaBio, // Use bio as fun_facts for personas
          symptoms: [],
          wellness_goals: [],
          topics_of_interest: [],
          experience_level: ''
        });
        setLoading(false);
        return;
      }
      
      const userId = user.id || user.email;
      const response = await axios.get(`${API}/users/${encodeURIComponent(userId)}/profile`);
      
      setFullProfile(response.data);
      setProfileData({
        name: response.data.name || '',
        bio: response.data.bio || '',
        location: response.data.location || '',
        profile_photo: response.data.profile_photo || '',
        fun_facts: response.data.fun_facts || '',
        symptoms: response.data.symptoms || [],
        wellness_goals: response.data.wellness_goals || [],
        topics_of_interest: response.data.topics_of_interest || [],
        experience_level: response.data.experience_level || ''
      });
    } catch (error) {
      console.error('Error fetching profile:', error);
      
      // Try a secondary lookup using the basic user endpoint
      let secondaryProfile = null;
      try {
        const userId = user.id || user.email;
        const fallbackResponse = await axios.get(`${API}/users/${encodeURIComponent(userId)}`);
        if (fallbackResponse.data) {
          secondaryProfile = fallbackResponse.data;
        }
      } catch (fallbackError) {
        console.log('Secondary profile lookup also failed:', fallbackError);
      }
      
      // For failed fetches, use what we have + any secondary data
      const fallbackData = {
        id: secondaryProfile?.id || user?.id,
        name: secondaryProfile?.name || user?.name || 'Community Member',
        profile_photo: secondaryProfile?.profile_photo || user?.profile_photo,
        bio: secondaryProfile?.bio || '',
        location: secondaryProfile?.location || '',
        subscription_status: secondaryProfile?.subscription_status || user?.subscription_status,
        coupon_used: secondaryProfile?.coupon_used || user?.coupon_used,
      };
      
      
      setFullProfile(fallbackData);
      setProfileData({
        name: fallbackData.name,
        bio: fallbackData.bio,
        location: fallbackData.location,
        profile_photo: fallbackData.profile_photo || '',
        fun_facts: '',
        symptoms: [],
        wellness_goals: [],
        topics_of_interest: [],
        experience_level: ''
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (event) => {
    if (!isOwnProfile) return;
    
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      toast.error('Image file too large (max 5MB)');
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      // Use user.id if available, otherwise use user.email
      const userId = user.id || user.email;
      const response = await axios.post(`${API}/users/${encodeURIComponent(userId)}/upload-photo`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        // photo_url is now a full R2 URL, no need to prepend API
        const newPhotoUrl = response.data.photo_url;
        setProfileData(prev => ({...prev, profile_photo: newPhotoUrl}));
        setFullProfile(prev => prev ? {...prev, profile_photo: newPhotoUrl} : null);
        toast.success('Profile photo updated!');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Error uploading photo');
    } finally {
      setUploading(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!isOwnProfile) return;
    
    setSaving(true);
    try {
      // Use user.id if available, otherwise use user.email
      const userId = user.id || user.email;
      await axios.put(`${API}/users/${encodeURIComponent(userId)}/profile`, profileData);
      toast.success('Profile updated successfully!');
      if (onUpdate) onUpdate(profileData);
      fetchFullProfile(); // Refresh profile data
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error('Error updating profile');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl bg-white max-h-[90vh] overflow-auto">
        <CardHeader className="bg-gradient-to-r from-burgundy to-dark-teal text-white">
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="text-xl font-serif">
                {isOwnProfile ? 'Edit Your Profile' : `${formatDisplayName(user?.name)}'s Profile`}
              </CardTitle>
              <p className="text-white/80 text-sm">
                {isOwnProfile ? 'Share more about yourself with the community' : 'Community member profile'}
              </p>
            </div>
            <Button variant="ghost" onClick={onClose} className="text-white hover:bg-white/20">
              <X className="h-5 w-5" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-4 md:p-6">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-burgundy"></div>
              <span className="ml-2 text-burgundy">Loading profile...</span>
            </div>
          ) : (
            <Tabs value={activeProfileTab} onValueChange={setActiveProfileTab} className="w-full">
              <div className="mb-6">
                <div className={`bg-burgundy/10 rounded-lg p-3 ${isOwnProfile ? 'grid grid-cols-2 gap-2 md:flex md:gap-1' : 'grid grid-cols-2 gap-2'}`}>
                  <button
                    onClick={() => setActiveProfileTab('profile')}
                    className={`text-xs sm:text-sm py-2.5 px-2 rounded transition-colors ${activeProfileTab === 'profile' ? 'bg-white text-burgundy font-medium shadow-sm' : 'text-burgundy/70 hover:bg-white/50'}`}
                  >
                    Profile Info
                  </button>
                  {isOwnProfile && (
                    <>
                      <button
                        onClick={() => setActiveProfileTab('community-guidelines')}
                        className={`text-xs sm:text-sm py-2.5 px-2 rounded transition-colors ${activeProfileTab === 'community-guidelines' ? 'bg-white text-burgundy font-medium shadow-sm' : 'text-burgundy/70 hover:bg-white/50'}`}
                      >
                        Guidelines
                      </button>
                      <button
                        onClick={() => setActiveProfileTab('notifications')}
                        className={`text-xs sm:text-sm py-2.5 px-2 rounded transition-colors relative ${activeProfileTab === 'notifications' ? 'bg-white text-burgundy font-medium shadow-sm' : 'text-burgundy/70 hover:bg-white/50'}`}
                      >
                        Notifications
                        {userNotifications && userNotifications.unread_warnings > 0 && (
                          <Badge className="ml-1 h-4 w-4 p-0 flex items-center justify-center bg-red-500 text-white text-xs rounded-full absolute -top-1 -right-1">
                            {userNotifications.unread_warnings}
                          </Badge>
                        )}
                      </button>
                      <button
                        onClick={() => setActiveProfileTab('email')}
                        className={`text-xs sm:text-sm py-2.5 px-2 rounded transition-colors ${activeProfileTab === 'email' ? 'bg-white text-burgundy font-medium shadow-sm' : 'text-burgundy/70 hover:bg-white/50'}`}
                      >
                        Email
                      </button>
                    </>
                  )}
                </div>
              </div>

              <TabsContent value="profile">
                <div className="space-y-6">
                  {/* Profile Photo Section */}
                  <div className="text-center">
                    <div className="relative inline-block">
                      <div className="w-32 h-32 rounded-full border-4 border-burgundy/20 overflow-hidden bg-burgundy/10 flex items-center justify-center mx-auto">
                        {profileData.profile_photo ? (
                          <img 
                            src={profileData.profile_photo}
                            alt={`${formatDisplayName(profileData.name)}'s profile`}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="text-burgundy/50">
                            <User className="h-16 w-16" />
                          </div>
                        )}
                      </div>
                      {isOwnProfile && (
                        <div className="mt-4">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handlePhotoUpload}
                            disabled={uploading}
                            className="hidden"
                            id="profile-photo-upload"
                          />
                          <label
                            htmlFor="profile-photo-upload"
                            className={`cursor-pointer inline-flex items-center px-4 py-2 bg-burgundy text-cream rounded-lg hover:bg-burgundy/80 text-sm ${
                              uploading ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                          >
                            {uploading ? (
                              <>
                                <div className="animate-spin h-4 w-4 mr-2 border-2 border-white border-t-transparent rounded-full"></div>
                                Uploading...
                              </>
                            ) : (
                              <>
                                <Camera className="w-4 h-4 mr-2" />
                                {profileData.profile_photo ? 'Change Photo' : 'Add Photo'}
                              </>
                            )}
                          </label>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Profile Information */}
                  <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-burgundy mb-2">Name</label>
                  {isOwnProfile ? (
                    <Input
                      value={profileData.name}
                      onChange={(e) => setProfileData(prev => ({...prev, name: e.target.value}))}
                      className="border-burgundy/30 focus:border-gold"
                    />
                  ) : (
                    <p className="text-lg font-semibold text-burgundy">{formatDisplayName(fullProfile?.name)}</p>
                  )}
                  {/* Membership Badge */}
                  {fullProfile?.subscription_status && (
                    <span className={`inline-block mt-1 px-3 py-0.5 text-xs font-semibold rounded-full ${
                      fullProfile.coupon_used === 'FOUNDING48'
                        ? 'bg-gold text-burgundy'
                      : fullProfile.subscription_status === 'trial'
                        ? 'bg-white text-burgundy border border-burgundy/20'
                        : 'bg-dark-teal text-cream'
                    }`}>
                      {fullProfile.coupon_used === 'FOUNDING48' ? 'Founding 48'
                      : fullProfile.subscription_status === 'trial' ? 'Trial Member'
                      : 'Member'}
                    </span>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-burgundy mb-2">Location</label>
                  {isOwnProfile ? (
                    <Input
                      value={profileData.location}
                      onChange={(e) => setProfileData(prev => ({...prev, location: e.target.value}))}
                      placeholder="e.g., San Francisco, CA"
                      className="border-burgundy/30 focus:border-gold"
                    />
                  ) : (
                    <p className="text-burgundy/70">{fullProfile?.location || 'Location not specified'}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-burgundy mb-2">Fun Facts About Me</label>
                  {isOwnProfile ? (
                    <Textarea
                      value={profileData.fun_facts}
                      onChange={(e) => setProfileData(prev => ({...prev, fun_facts: e.target.value}))}
                      placeholder="Share your hobbies, interests, or anything you'd like the community to know..."
                      className="min-h-[100px] border-burgundy/30 focus:border-gold"
                    />
                  ) : (
                    <p className="text-burgundy/70 whitespace-pre-wrap">
                      {fullProfile?.fun_facts || 'Not shared yet.'}
                    </p>
                  )}
                </div>

                {/* Only show these sections for own profile */}
                {isOwnProfile && (
                  <>
                    <div className="border-t border-burgundy/20 pt-4">
                      <h4 className="text-lg font-bold text-burgundy mb-3">Current Symptoms</h4>
                      <div className="grid grid-cols-2 gap-2">
                        {['Hot flashes', 'Weight gain', 'Skin and hair changes', 'Mood changes', 'Lack of mental clarity', 'Increased anxiety', 'Sleep quality changes', 'Fluctuating energy', 'Low libido'].map(symptom => (
                          <label key={symptom} className="flex items-center space-x-2 text-sm">
                            <input
                              type="checkbox"
                              checked={(profileData.symptoms || []).includes(symptom)}
                              onChange={(e) => {
                                const symptoms = profileData.symptoms || [];
                                const newSymptoms = e.target.checked 
                                  ? [...symptoms, symptom]
                                  : symptoms.filter(s => s !== symptom);
                                setProfileData({...profileData, symptoms: newSymptoms});
                              }}
                              className="rounded border-burgundy/30 text-burgundy focus:ring-gold"
                            />
                            <span className="text-burgundy/80">{symptom}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div className="border-t border-burgundy/20 pt-4">
                      <h4 className="text-lg font-bold text-burgundy mb-3">Wellness Goals</h4>
                  {isOwnProfile ? (
                    <div className="grid grid-cols-2 gap-2">
                      {['Weight management', 'Energy boost', 'Improved sleep', 'Mental clarity', 'Mood balance', 'Better skin', 'Enhanced libido', 'General wellness'].map(goal => (
                        <label key={goal} className="flex items-center space-x-2 text-sm">
                          <input
                            type="checkbox"
                            checked={(profileData.wellness_goals || []).includes(goal)}
                            onChange={(e) => {
                              const goals = profileData.wellness_goals || [];
                              const newGoals = e.target.checked 
                                ? [...goals, goal]
                                : goals.filter(g => g !== goal);
                              setProfileData({...profileData, wellness_goals: newGoals});
                            }}
                            className="rounded border-burgundy/30 text-burgundy focus:ring-gold"
                          />
                          <span className="text-burgundy/80">{goal}</span>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <p className="text-burgundy/70">
                      {fullProfile?.wellness_goals && fullProfile.wellness_goals.length > 0
                        ? fullProfile.wellness_goals.join(', ')
                        : 'Not shared.'}
                    </p>
                  )}
                </div>

                <div className="border-t border-burgundy/20 pt-4">
                  <h4 className="text-lg font-bold text-burgundy mb-3">Topics of Interest</h4>
                  {isOwnProfile ? (
                    <div className="grid grid-cols-2 gap-2">
                      {['Hormone optimization', 'Nutrition', 'Exercise', 'Sleep optimization', 'Supplements', 'Stress management', 'Mental health', 'Longevity'].map(topic => (
                        <label key={topic} className="flex items-center space-x-2 text-sm">
                          <input
                            type="checkbox"
                            checked={(profileData.topics_of_interest || []).includes(topic)}
                            onChange={(e) => {
                              const topics = profileData.topics_of_interest || [];
                              const newTopics = e.target.checked 
                                ? [...topics, topic]
                                : topics.filter(t => t !== topic);
                              setProfileData({...profileData, topics_of_interest: newTopics});
                            }}
                            className="rounded border-burgundy/30 text-burgundy focus:ring-gold"
                          />
                          <span className="text-burgundy/80">{topic}</span>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <p className="text-burgundy/70">
                      {fullProfile?.topics_of_interest && fullProfile.topics_of_interest.length > 0
                        ? fullProfile.topics_of_interest.join(', ')
                        : 'Not shared.'}
                    </p>
                  )}
                </div>

                <div className="border-t border-burgundy/20 pt-4">
                  <label className="block text-sm font-medium text-burgundy mb-2">Experience Level</label>
                  {isOwnProfile ? (
                    <select
                      value={profileData.experience_level || ''}
                      onChange={(e) => setProfileData({...profileData, experience_level: e.target.value})}
                      className="w-full p-2 border border-burgundy/30 rounded-md focus:border-gold focus:ring-1 focus:ring-gold"
                    >
                      <option value="">Select experience level...</option>
                      <option value="beginner">Beginner - New to wellness optimization</option>
                      <option value="intermediate">Intermediate - Some knowledge and experience</option>
                      <option value="advanced">Advanced - Experienced with protocols</option>
                    </select>
                  ) : (
                    <p className="text-burgundy/70">
                      {fullProfile?.experience_level
                        ? fullProfile.experience_level.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
                        : 'Not shared.'}
                    </p>
                  )}
                </div>

                    {/* Community Stats */}
                    <div className="bg-burgundy/5 rounded-lg p-4">
                      <h5 className="font-semibold text-burgundy mb-3">Community Activity</h5>
                      {/* Activity tracker section removed */}
                      {fullProfile?.joined_date && (
                        <div className="mt-3 text-center">
                          <p className="text-xs text-burgundy/50">
                            Member since {new Date(fullProfile.joined_date).toLocaleDateString('en-US', {
                              month: 'long',
                              year: 'numeric'
                            })}
                          </p>
                        </div>
                      )}
                    </div>
                  </>
                )}
                
              </div>

                  {/* Action Buttons */}
                  {isOwnProfile && (
                    <div className="flex space-x-2 pt-4">
                      <Button 
                        onClick={handleSaveProfile}
                        disabled={saving}
                        className="flex-1 bg-burgundy hover:bg-burgundy/80 text-cream"
                      >
                        {saving ? (
                          <>
                            <div className="animate-spin h-4 w-4 mr-2 border-2 border-white border-t-transparent rounded-full"></div>
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save className="w-4 h-4 mr-2" />
                            Save Profile
                          </>
                        )}
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={onClose}
                        className="border-burgundy/30 text-burgundy hover:bg-burgundy/10"
                      >
                        Cancel
                      </Button>
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* Community Guidelines Tab */}
              <TabsContent value="community-guidelines">
                <div className="space-y-6">
                  <div className="text-center mb-6">
                    <h4 className="text-2xl font-bold text-burgundy font-serif mb-2">Community Guidelines</h4>
                    <p className="text-burgundy/70">Our house vibes for a supportive wellness community</p>
                  </div>

                  <Card className="bg-gold/5 border-gold/30">
                    <CardContent className="p-6">
                      <div className="space-y-4">
                        <h5 className="text-lg font-semibold text-burgundy mb-4">
                          Before you jump in, a few house vibes:
                        </h5>
                        
                        <div className="space-y-3">
                          <div className="flex items-start space-x-3">
                            <div className="w-2 h-2 bg-burgundy rounded-full mt-2 flex-shrink-0"></div>
                            <div>
                              <p className="text-burgundy font-medium">Be Kind</p>
                              <p className="text-burgundy/70 text-sm">Lift each other up. No snark, no shade.</p>
                            </div>
                          </div>
                          
                          <div className="flex items-start space-x-3">
                            <div className="w-2 h-2 bg-burgundy rounded-full mt-2 flex-shrink-0"></div>
                            <div>
                              <p className="text-burgundy font-medium">Share, Don't Prescribe</p>
                              <p className="text-burgundy/70 text-sm">"Here's what helped me" &gt; "You should do this."</p>
                            </div>
                          </div>
                          
                          <div className="flex items-start space-x-3">
                            <div className="w-2 h-2 bg-burgundy rounded-full mt-2 flex-shrink-0"></div>
                            <div>
                              <p className="text-burgundy font-medium">No Spam or Sales</p>
                              <p className="text-burgundy/70 text-sm">We're here to support, not to sell.</p>
                            </div>
                          </div>
                          
                          <div className="flex items-start space-x-3">
                            <div className="w-2 h-2 bg-burgundy rounded-full mt-2 flex-shrink-0"></div>
                            <div>
                              <p className="text-burgundy font-medium">Stay Curious, Not Combative</p>
                              <p className="text-burgundy/70 text-sm">Questions are gold. Be cool in the comments.</p>
                            </div>
                          </div>
                          
                          <div className="flex items-start space-x-3">
                            <div className="w-2 h-2 bg-burgundy rounded-full mt-2 flex-shrink-0"></div>
                            <div>
                              <p className="text-burgundy font-medium">Keep It Private</p>
                              <p className="text-burgundy/70 text-sm">What's shared here, stays here.</p>
                            </div>
                          </div>
                          
                          <div className="flex items-start space-x-3">
                            <div className="w-2 h-2 bg-burgundy rounded-full mt-2 flex-shrink-0"></div>
                            <div>
                              <p className="text-burgundy font-medium">We're Not Your Doctor</p>
                              <p className="text-burgundy/70 text-sm">Educational only, not medical advice.</p>
                            </div>
                          </div>
                        </div>

                        <div className="border-t border-gold/30 pt-4 mt-6">
                          <p className="text-burgundy font-medium text-center">
                            By posting, you agree to keep this space kind, supportive, and spam-free.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <div className="text-center text-sm text-burgundy/60">
                    <p>Questions about our guidelines? Contact our support team.</p>
                  </div>

                  <div className="flex justify-center pt-4">
                    <Button 
                      variant="outline"
                      onClick={onClose}
                      className="border-burgundy/30 text-burgundy hover:bg-burgundy/10"
                    >
                      Close
                    </Button>
                  </div>
                </div>
              </TabsContent>

              {/* Notifications/Warnings Tab */}
              {isOwnProfile && (
                <TabsContent value="notifications">
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <h4 className="text-lg font-semibold text-burgundy">Your Notifications</h4>
                      <Badge className="bg-burgundy/10 text-burgundy">
                        {userNotifications ? userNotifications.total_warnings : 0} total
                      </Badge>
                    </div>

                    {userNotifications && userNotifications.warnings.length > 0 ? (
                      <div className="space-y-3">
                        {userNotifications.warnings.map((warning) => (
                          <Card key={warning.id} className={`border-2 transition-all duration-200 ${
                            warning.read ? 'border-gray-200 bg-gray-50' : 'border-yellow-300 bg-yellow-50'
                          }`}>
                            <CardContent className="p-4">
                              <div className="flex justify-between items-start mb-2">
                                <div className="flex items-center gap-2">
                                  <AlertTriangle className={`h-4 w-4 ${
                                    warning.read ? 'text-gray-400' : 'text-yellow-600'
                                  }`} />
                                  <span className={`text-sm font-medium ${
                                    warning.read ? 'text-gray-600' : 'text-yellow-800'
                                  }`}>
                                    Community Warning
                                  </span>
                                  {!warning.read && (
                                    <Badge className="h-4 w-4 p-0 bg-red-500 text-white text-xs rounded-full">
                                      !
                                    </Badge>
                                  )}
                                </div>
                                <span className="text-xs text-gray-500">
                                  {new Date(warning.created_at).toLocaleDateString()}
                                </span>
                              </div>
                              <p className={`text-sm mb-3 ${
                                warning.read ? 'text-gray-600' : 'text-gray-800'
                              }`}>
                                {warning.message}
                              </p>
                              <div className="flex justify-between items-center">
                                <span className="text-xs text-gray-400">
                                  From: Admin Team
                                </span>
                                {!warning.read && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => markWarningAsRead(warning.id)}
                                    className="text-xs border-gray-300 text-gray-600 hover:bg-gray-100"
                                  >
                                    Mark as Read
                                  </Button>
                                )}
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <div className="mb-4">
                          <Shield className="h-12 w-12 text-gray-400 mx-auto" />
                        </div>
                        <h5 className="text-lg font-medium text-gray-600 mb-2">No Notifications</h5>
                        <p className="text-sm text-gray-500">
                          You're all caught up! Any important messages from our admin team will appear here.
                        </p>
                      </div>
                    )}

                    {/* Close Button for Notifications Tab */}
                    <div className="flex justify-end pt-4">
                      <Button 
                        variant="outline"
                        onClick={onClose}
                        className="border-burgundy/30 text-burgundy hover:bg-burgundy/10"
                      >
                        Close
                      </Button>
                    </div>
                  </div>
                </TabsContent>
              )}

              {/* Email Settings Tab */}
              {isOwnProfile && (
                <TabsContent value="email">
                  <form onSubmit={handleEmailPreferencesUpdate} className="space-y-6">
                    <div className="bg-gradient-to-r from-burgundy/5 to-dark-teal/5 rounded-lg p-6 border border-burgundy/20">
                      <h4 className="font-semibold text-burgundy mb-4">Email Notification Preferences</h4>
                      <p className="text-sm text-burgundy/70 mb-4">
                        Choose which emails you'd like to receive from Luminelle.
                      </p>
                      
                      <div className="space-y-4">
                        {/* Master Toggle */}
                        <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-burgundy/10">
                          <div>
                            <label className="font-medium text-burgundy">All Email Notifications</label>
                            <p className="text-sm text-burgundy/60">Enable or disable all email notifications at once</p>
                          </div>
                          <input
                            type="checkbox"
                            checked={emailPreferences.email_notifications_enabled}
                            onChange={(e) => setEmailPreferences({...emailPreferences, email_notifications_enabled: e.target.checked})}
                            className="h-5 w-5 text-burgundy border-burgundy/30 rounded focus:ring-gold"
                          />
                        </div>

                        {/* Individual Preferences */}
                        <div className={`space-y-3 ${!emailPreferences.email_notifications_enabled && 'opacity-50 pointer-events-none'}`}>
                          <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-burgundy/10">
                            <div>
                              <label className="font-medium text-burgundy">New Content Alerts</label>
                              <p className="text-sm text-burgundy/60">Get notified when new articles and videos are published</p>
                            </div>
                            <input
                              type="checkbox"
                              checked={emailPreferences.email_new_content}
                              onChange={(e) => setEmailPreferences({...emailPreferences, email_new_content: e.target.checked})}
                              className="h-5 w-5 text-burgundy border-burgundy/30 rounded focus:ring-gold"
                              disabled={!emailPreferences.email_notifications_enabled}
                            />
                          </div>

                          <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-burgundy/10">
                            <div>
                              <label className="font-medium text-burgundy">Community Replies</label>
                              <p className="text-sm text-burgundy/60">Get notified when someone replies to your discussions</p>
                            </div>
                            <input
                              type="checkbox"
                              checked={emailPreferences.email_community_replies}
                              onChange={(e) => setEmailPreferences({...emailPreferences, email_community_replies: e.target.checked})}
                              className="h-5 w-5 text-burgundy border-burgundy/30 rounded focus:ring-gold"
                              disabled={!emailPreferences.email_notifications_enabled}
                            />
                          </div>

                          <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-burgundy/10">
                            <div>
                              <label className="font-medium text-burgundy">Trial Reminders</label>
                              <p className="text-sm text-burgundy/60">Receive reminders before your trial ends</p>
                            </div>
                            <input
                              type="checkbox"
                              checked={emailPreferences.email_trial_reminders}
                              onChange={(e) => setEmailPreferences({...emailPreferences, email_trial_reminders: e.target.checked})}
                              className="h-5 w-5 text-burgundy border-burgundy/30 rounded focus:ring-gold"
                              disabled={!emailPreferences.email_notifications_enabled}
                            />
                          </div>

                          <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-burgundy/10">
                            <div>
                              <label className="font-medium text-burgundy">Weekly Digest</label>
                              <p className="text-sm text-burgundy/60">Get a summary of the week's top content and discussions</p>
                            </div>
                            <input
                              type="checkbox"
                              checked={emailPreferences.email_weekly_digest}
                              onChange={(e) => setEmailPreferences({...emailPreferences, email_weekly_digest: e.target.checked})}
                              className="h-5 w-5 text-burgundy border-burgundy/30 rounded focus:ring-gold"
                              disabled={!emailPreferences.email_notifications_enabled}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-end gap-3">
                      <Button 
                        type="button"
                        variant="outline"
                        onClick={onClose}
                        className="border-burgundy/30 text-burgundy hover:bg-burgundy/10"
                      >
                        Close
                      </Button>
                      <Button 
                        type="submit" 
                        disabled={saving}
                        className="bg-burgundy hover:bg-burgundy/80 text-white"
                      >
                        {saving ? 'Saving...' : 'Save Preferences'}
                      </Button>
                    </div>
                  </form>
                </TabsContent>
              )}
            </Tabs>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Need Help Form Component - Dashboard only, non-floating
const NeedHelpForm = ({ user, showOnDashboard = true }) => {
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    subject: 'General Support',
    message: '',
    priority: 'normal'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);

  // Don't show if not on dashboard
  if (!showOnDashboard) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/support/request`, {
        name: formData.name,
        email: formData.email,
        subject: formData.subject,
        message: formData.message,
        priority: formData.priority,
        user_id: user?.id || 'anonymous'
      });
      
      toast.success('✅ Support request received! Your message has been logged for the admin team.');
      setFormData(prev => ({
        ...prev,
        message: '',
        subject: 'General Support'
      }));
      setShowForm(false);
    } catch (error) {
      console.error('Support request error:', error);
      toast.error('Error sending request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!showForm) {
    return (
      <div className="mt-6 flex justify-center">
        <button 
          onClick={() => {
            setShowForm(true);
          }}
          className="bg-teal-700 hover:bg-teal-800 text-white rounded-lg px-6 py-3 shadow-lg hover:shadow-xl transition-all flex items-center gap-2 font-medium"
        >
          <MessageCircle className="h-5 w-5" />
          <span>Need Help?</span>
        </button>
      </div>
    );
  }


  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4" style={{zIndex: 100000}}>
      <Card className="bg-white border-burgundy/20 shadow-2xl w-full max-w-md">
        <CardHeader className="pb-4">
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="text-lg text-burgundy">Need Help?</CardTitle>
              <CardDescription>We're here to support you!</CardDescription>
            </div>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setShowForm(false)}
              className="h-8 w-8 p-0 text-burgundy/60 hover:text-burgundy"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 gap-3">
              <div>
                <label className="block text-sm font-medium text-burgundy mb-1">Name</label>
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({...prev, name: e.target.value}))}
                  className="border-burgundy/30 focus:border-gold text-sm"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-burgundy mb-1">Email</label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({...prev, email: e.target.value}))}
                  className="border-burgundy/30 focus:border-gold text-sm"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-burgundy mb-1">Subject</label>
                <select 
                  value={formData.subject}
                  onChange={(e) => setFormData(prev => ({...prev, subject: e.target.value}))}
                  className="w-full border border-burgundy/30 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold/50 focus:border-gold"
                >
                  <option value="General Support">General Support</option>
                  <option value="Technical Issue">Technical Issue</option>
                  <option value="Account Problem">Account Problem</option>
                  <option value="Billing Question">Billing Question</option>
                  <option value="Content Request">Content Request</option>
                  <option value="Feature Suggestion">Feature Suggestion</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-burgundy mb-1">How can we help?</label>
                <Textarea
                  placeholder="Please describe your issue or question in detail..."
                  value={formData.message}
                  onChange={(e) => setFormData(prev => ({...prev, message: e.target.value}))}
                  className="min-h-[80px] border-burgundy/30 focus:border-gold text-sm"
                  required
                />
              </div>
            </div>

            <div className="flex space-x-2 pt-2">
              <Button 
                type="submit" 
                disabled={isSubmitting}
                className="flex-1 bg-burgundy hover:bg-burgundy/80 text-cream text-sm"
              >
                {isSubmitting ? 'Sending...' : 'Send Request'}
              </Button>
              <Button 
                type="button"
                variant="outline"
                onClick={() => setShowForm(false)}
                className="border-burgundy/30 text-burgundy hover:bg-burgundy/10 text-sm"
              >
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// Education Section Component  
const EducationSection = ({ resetTrigger, user, contentToOpen, setContentToOpen, categoryToOpen, setCategoryToOpen, setShowUpgradeModal, setUpgradeFeature }) => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [viewMode, setViewMode] = useState('categories'); // 'categories', 'subcategories', 'content', or 'article'
  const [subcategories, setSubcategories] = useState([]);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [selectedAudio, setSelectedAudio] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [showLocalUpgradeModal, setShowLocalUpgradeModal] = useState(false);
  const [upgradeFeatureName, setUpgradeFeatureName] = useState('');
  
  // Browser back/forward is handled by React Router for tab-level navigation
  // Within-section back/forward uses pushState and this custom handler
  const isNavigatingBack = useRef(false);
  
  // Refs for popstate handler (stable references, no re-registration)
  const categoriesRef = useRef(categories);
  const selectedCategoryRef = useRef(selectedCategory);
  useEffect(() => { categoriesRef.current = categories; }, [categories]);
  useEffect(() => { selectedCategoryRef.current = selectedCategory; }, [selectedCategory]);

  useEffect(() => {
    const handlePopState = async () => {
      const path = window.location.pathname;
      // Only handle classroom sub-navigation (pushState entries)
      if (!path.startsWith('/classroom')) return;
      
      isNavigatingBack.current = true;
      
      const parts = path.split('/').filter(p => p);
      // parts: ['classroom'] or ['classroom', categorySlug] or ['classroom', categorySlug, subcategorySlug] etc.
      const urlDepth = parts.length;
      
      if (urlDepth === 1) {
        // /classroom → categories view
        setSelectedCategory(null);
        setSelectedSubcategory(null);
        setSubcategories([]);
        setContent([]);
        setSelectedArticle(null);
        setSelectedVideo(null);
        setSelectedAudio(null);
        setViewMode('categories');
      } else if (urlDepth === 2) {
        // /classroom/categorySlug → subcategories view
        const categorySlug = parts[1];
        setSelectedSubcategory(null);
        setContent([]);
        setSelectedArticle(null);
        setSelectedVideo(null);
        setSelectedAudio(null);
        
        // Find matching category from already-loaded categories
        const cats = categoriesRef.current;
        const matchedCategory = cats.find(c => 
          c.title.toLowerCase().replace(/\s+/g, '-') === categorySlug
        );
        if (matchedCategory) {
          const curCat = selectedCategoryRef.current;
          if (!curCat || curCat.id !== matchedCategory.id) {
            setSelectedCategory(matchedCategory);
            try {
              const response = await axios.get(`${API}/content-subcategories/by-category/${encodeURIComponent(matchedCategory.title)}`);
              setSubcategories(response.data || []);
            } catch (error) {
              console.error('Error loading subcategories:', error);
            }
          }
        }
        setViewMode('subcategories');
      } else if (urlDepth === 3) {
        // /classroom/categorySlug/subcategorySlug → content list view
        const categorySlug = parts[1];
        const subcategorySlug = parts[2];
        setSelectedArticle(null);
        setSelectedVideo(null);
        setSelectedAudio(null);
        
        // Find matching category
        const cats = categoriesRef.current;
        const matchedCategory = cats.find(c => 
          c.title.toLowerCase().replace(/\s+/g, '-') === categorySlug
        );
        if (matchedCategory) {
          const curCat = selectedCategoryRef.current;
          if (!curCat || curCat.id !== matchedCategory.id) {
            setSelectedCategory(matchedCategory);
          }
          try {
            setLoading(true);
            const subResponse = await axios.get(`${API}/content-subcategories/by-category/${encodeURIComponent(matchedCategory.title)}`);
            const subs = subResponse.data || [];
            setSubcategories(subs);
            
            const matchedSub = subs.find(s => 
              (s.slug || s.name.toLowerCase().replace(/\s+/g, '-')) === subcategorySlug
            );
            if (matchedSub) {
              setSelectedSubcategory(matchedSub);
            }
            
            const contentResponse = await axios.get(`${API}/content?category=${encodeURIComponent(matchedCategory.title)}`);
            const allContent = contentResponse.data || [];
            const filteredContent = matchedSub 
              ? allContent.filter(item => item.subcategory === matchedSub.name)
              : allContent;
            setContent(filteredContent);
          } catch (error) {
            console.error('Error loading content:', error);
          } finally {
            setLoading(false);
          }
        }
        setViewMode('content');
      } else if (urlDepth >= 4) {
        // /classroom/categorySlug/subcategorySlug/contentId → article view
        const categorySlug = parts[1];
        const subcategorySlug = parts[2];
        const contentId = parts[3];
        setContentToOpen({ categorySlug, subcategorySlug, contentId });
      }
      
      setTimeout(() => {
        isNavigatingBack.current = false;
      }, 100);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []); // Empty deps - uses refs for current state

  // Forward navigation pushState is handled by each navigation function (fetchContentByCategory, fetchContentBySubcategory, handleContentView)
  // No generic viewMode pushState needed — it would create duplicate history entries

  // Fix bullet lists in displayed content
  useEffect(() => {
    const fixBulletLists = () => {
      // Target both UL (new format) and ol[data-list="bullet"] (legacy)
      const bulletLists = document.querySelectorAll('.rich-text-content ul, .prose ul, .rich-text-content ol[data-list="bullet"], .prose ol[data-list="bullet"]');
      bulletLists.forEach(list => {
        list.style.listStyleType = 'disc';
        list.style.listStylePosition = 'outside';
        list.style.paddingLeft = '1.5em';
        list.style.counterReset = 'none';
        list.querySelectorAll('li').forEach(li => {
          li.style.listStyleType = 'disc';
          li.style.counterIncrement = 'none';
        });
      });
    };
    
    if (selectedArticle) {
      setTimeout(fixBulletLists, 100);
      
      // Track article view for monthly points
      if (user?.id && selectedArticle.id) {
        const trackView = async () => {
          try {
            await axios.post(`${BACKEND_URL}/api/content/${selectedArticle.id}/view?user_id=${user.id}`);
          } catch (error) {
            console.error('Error tracking article view:', error);
          }
        };
        trackView();
        trackContentView('article', selectedArticle.id, selectedArticle.title, localStorage.getItem('token'));
      }
    }
  }, [selectedArticle, user?.id]);

  // Handle categoryToOpen from URL
  useEffect(() => {
    const handleCategoryToOpen = async () => {
      if (!categoryToOpen) return;
      
      try {
        // Fetch all categories
        const categoriesResponse = await axios.get(`${API}/content-categories`);
        const allCategories = categoriesResponse.data;
        
        // Find category by slug (lowercase title)
        const category = allCategories.find(cat => 
          cat.title.toLowerCase().replace(/\s+/g, '-') === categoryToOpen.toLowerCase()
        );
        
        if (category) {
          await fetchContentByCategory(category);
        } else {
          toast.error('Category not found');
          setViewMode('categories');
        }
      } catch (error) {
        console.error('Error loading category:', error);
        toast.error('Could not load category');
        setViewMode('categories');
      } finally {
        setCategoryToOpen(null);
      }
    };
    
    handleCategoryToOpen();
  }, [categoryToOpen]);

  // Handle contentToOpen from HomeSection or deep linking
  useEffect(() => {
    const handleContentToOpen = async () => {
      if (!contentToOpen) return;
      
      try {
        // If contentToOpen has categorySlug and contentId (from URL deep link)
        if (contentToOpen.categorySlug && contentToOpen.contentId) {
          
          // Fetch all categories and find by slug
          const categoriesResponse = await axios.get(`${API}/content-categories`);
          const allCategories = categoriesResponse.data;
          const category = allCategories.find(cat => 
            cat.title.toLowerCase().replace(/\s+/g, '-') === contentToOpen.categorySlug.toLowerCase()
          );
          
          // Fetch the content regardless of category match
          const contentResponse = await axios.get(`${API}/content/${contentToOpen.contentId}`);
          const contentItem = contentResponse.data;
          
          if (!contentItem) {
            toast.error('Content not found');
            setViewMode('categories');
            return;
          }
          
          // Try to use matched category, or find category from content item
          let effectiveCategory = category;
          if (!effectiveCategory && contentItem.category) {
            // Try to find category by content's category field
            effectiveCategory = allCategories.find(cat => cat.title === contentItem.category);
          }
          
          if (effectiveCategory) {
            // Set the category
            setSelectedCategory(effectiveCategory);
            
            // Fetch all content for this category
            const encodedCategory = encodeURIComponent(effectiveCategory.title);
            const allContentResponse = await axios.get(`${API}/content?category=${encodedCategory}`);
            setContent(allContentResponse.data || []);
            
            // Handle subcategory from URL or from content item
            const subcategoriesResponse = await axios.get(`${API}/content-subcategories/by-category/${encodeURIComponent(effectiveCategory.title)}`);
            const subcategoriesList = subcategoriesResponse.data || [];
            setSubcategories(subcategoriesList);
            
            // Try to match subcategory from URL slug first, then from content item
            let matchingSubcategory = null;
            if (contentToOpen.subcategorySlug) {
              matchingSubcategory = subcategoriesList.find(sc => 
                sc.slug === contentToOpen.subcategorySlug || 
                sc.name.toLowerCase().replace(/\s+/g, '-') === contentToOpen.subcategorySlug.toLowerCase()
              );
            }
            if (!matchingSubcategory && contentItem.subcategory) {
              matchingSubcategory = subcategoriesList.find(sc => sc.name === contentItem.subcategory);
            }
            
            if (matchingSubcategory) {
              setSelectedSubcategory(matchingSubcategory);
              // Filter content to just this subcategory
              const filteredContent = allContentResponse.data.filter(item => item.subcategory === matchingSubcategory.name);
              setContent(filteredContent);
            }
          }
          
          // Open the content based on type - even if category wasn't found
          if (contentItem.content_type === 'video') {
            setSelectedVideo(contentItem);
          } else {
            setSelectedArticle(contentItem);
          }
          setViewMode('article');
          
        }
        // Handle subcategory-only deep link (no content ID)
        else if (contentToOpen.categorySlug && contentToOpen.subcategorySlug && !contentToOpen.contentId) {
          
          // Fetch all categories and find by slug
          const categoriesResponse = await axios.get(`${API}/content-categories`);
          const allCategories = categoriesResponse.data;
          const category = allCategories.find(cat => 
            cat.title.toLowerCase().replace(/\s+/g, '-') === contentToOpen.categorySlug.toLowerCase()
          );
          
          if (!category) {
            toast.error('Category not found');
            setViewMode('categories');
            return;
          }
          
          setSelectedCategory(category);
          
          // Fetch subcategories and find by slug
          const subcategoriesResponse = await axios.get(`${API}/content-subcategories/by-category/${encodeURIComponent(category.title)}`);
          const subcategoriesList = subcategoriesResponse.data || [];
          setSubcategories(subcategoriesList);
          
          const matchingSubcategory = subcategoriesList.find(sc => 
            sc.slug === contentToOpen.subcategorySlug || 
            sc.name.toLowerCase().replace(/\s+/g, '-') === contentToOpen.subcategorySlug.toLowerCase()
          );
          
          if (matchingSubcategory) {
            setSelectedSubcategory(matchingSubcategory);
            
            // Fetch and filter content for this subcategory
            const encodedCategory = encodeURIComponent(category.title);
            const contentResponse = await axios.get(`${API}/content?category=${encodedCategory}`);
            const filteredContent = (contentResponse.data || []).filter(item => item.subcategory === matchingSubcategory.name);
            setContent(filteredContent);
            setViewMode('content');
          } else {
            // Subcategory not found, show subcategories view
            setViewMode('subcategories');
          }
          
        }
        // If contentToOpen is the actual content object (from HomeSection or dashboard)
        else if (contentToOpen.content_type === 'article') {
          
          // The content object should have a category property
          if (contentToOpen.category) {
            // Fetch the category by title (need to find it in the categories list)
            const categoriesResponse = await axios.get(`${API}/content-categories`);
            const allCategories = categoriesResponse.data;
            const category = allCategories.find(cat => cat.title === contentToOpen.category);
            
            if (category) {
              setSelectedCategory(category);
              
              // Fetch all content for this category
              const encodedCategory = encodeURIComponent(category.title);
              const allContentResponse = await axios.get(`${API}/content?category=${encodedCategory}`);
              setContent(allContentResponse.data || []);
              
              console.log('Category loaded from content.category:', category);
            }
          }
          
          setSelectedArticle(contentToOpen);
          setViewMode('article');
        } else if (contentToOpen.content_type === 'video' && contentToOpen.url) {
          setSelectedVideo(contentToOpen);
          setViewMode('article');
        } else if (contentToOpen.content_type === 'audio') {
          // Navigate to audio detail page
          setSelectedArticle(null);  // Clear other selections
          setSelectedVideo(null);
          setSelectedAudio(contentToOpen);
          setViewMode('article');
        } else if (contentToOpen.content_type === 'document') {
          // Open document in new tab if URL available
          if (contentToOpen.url) {
            window.open(contentToOpen.url, '_blank');
            toast.success('Opening document');
          } else {
            toast.error('Document URL not available');
          }
          setContentToOpen(null);
          return;
        }
        // Handle contentToOpen with only contentId (from search results fallback)
        else if (contentToOpen.contentId && !contentToOpen.categorySlug) {
          
          // Fetch the content by ID
          const contentResponse = await axios.get(`${API}/content/${contentToOpen.contentId}`);
          const contentItem = contentResponse.data;
          
          if (contentItem) {
            // Set up the category context
            if (contentItem.category) {
              const categoriesResponse = await axios.get(`${API}/content-categories`);
              const allCategories = categoriesResponse.data;
              const category = allCategories.find(cat => cat.title === contentItem.category);
              
              if (category) {
                setSelectedCategory(category);
                
                // Fetch all content for this category
                const encodedCategory = encodeURIComponent(category.title);
                const allContentResponse = await axios.get(`${API}/content?category=${encodedCategory}`);
                setContent(allContentResponse.data || []);
              }
            }
            
            // Open the content based on type
            if (contentItem.content_type === 'video') {
              setSelectedVideo(contentItem);
              setViewMode('article');
            } else if (contentItem.content_type === 'audio') {
              setSelectedArticle(null);  // Clear other selections
              setSelectedVideo(null);
              setSelectedAudio(contentItem);
              setViewMode('article');
            } else if (contentItem.content_type === 'document') {
              if (contentItem.url) {
                window.open(contentItem.url, '_blank');
                toast.success('Opening document');
              } else {
                toast.error('Document URL not available');
              }
              return;
            } else {
              setSelectedAudio(null);  // Clear audio selection for articles
              setSelectedVideo(null);
              setSelectedArticle(contentItem);
              setViewMode('article');
            }
            console.log('Content loaded by ID:', contentItem);
          }
        }
      } catch (error) {
        console.error('Error loading content:', error);
        toast.error('Could not load content');
        setViewMode('categories');
      } finally {
        // Clear the contentToOpen after processing
        setContentToOpen(null);
      }
    };
    
    handleContentToOpen();
  }, [contentToOpen]);

  // Reset to categories when resetTrigger changes
  useEffect(() => {
    if (resetTrigger) {
      setViewMode('categories');
      setSelectedCategory(null);
      setContent([]);
    }
  }, [resetTrigger]);

  useEffect(() => {
    fetchCategories();
    if (user?.id) {
      fetchFavorites();
    }
  }, [user]);

  const fetchFavorites = async () => {
    try {
      const authToken = localStorage.getItem('auth_token');
      if (!authToken || !user?.id) return;

      const response = await axios.get(`${API}/users/${user.id}/saved-items`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      
      // Filter for content type saved items to use as favorites
      const contentFavorites = response.data.filter(item => item.type === 'content');
      setFavorites(contentFavorites);
    } catch (error) {
      console.error('Error fetching favorites:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/content-categories`);
      setCategories(response.data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Error loading categories');
    } finally {
      setLoading(false);
    }
  };

  const fetchContentByCategory = async (category) => {
    try {
      setLoading(true);
      setSelectedCategory(category);
      
      const subcategoriesResponse = await axios.get(`${API}/content-subcategories/by-category/${encodeURIComponent(category.title)}`);
      const fetchedSubcategories = subcategoriesResponse.data || [];
      setSubcategories(fetchedSubcategories);
      
      // Always go to subcategories view (even if no subcategories exist)
      setViewMode('subcategories');
      setContent([]); // Clear any previous content
      
      // Update URL with category slug (skip if already at this URL — prevents duplicate entries during popstate)
      const categorySlug = category.title.toLowerCase().replace(/\s+/g, '-');
      const targetUrl = `/classroom/${categorySlug}`;
      if (window.location.pathname !== targetUrl) {
        window.history.pushState({}, '', targetUrl);
      }
    } catch (error) {
      console.error('Error fetching content:', error);
      toast.error('Error loading content');
    } finally {
      setLoading(false);
    }
  };

  const fetchContentBySubcategory = async (subcategory) => {
    try {
      setLoading(true);
      setSelectedSubcategory(subcategory);
      
      // Fetch content for this category and subcategory
      const encodedCategory = encodeURIComponent(selectedCategory.title);
      const response = await axios.get(`${API}/content?category=${encodedCategory}`);
      const allContent = response.data || [];
      
      // Filter by subcategory name
      const filteredContent = allContent.filter(item => item.subcategory === subcategory.name);
      setContent(filteredContent);
      setViewMode('content');
      
      // Update URL with category and subcategory slugs (skip if already at this URL)
      const categorySlug = selectedCategory.title.toLowerCase().replace(/\s+/g, '-');
      const subcategorySlug = subcategory.slug || subcategory.name.toLowerCase().replace(/\s+/g, '-');
      const targetUrl = `/classroom/${categorySlug}/${subcategorySlug}`;
      if (window.location.pathname !== targetUrl) {
        window.history.pushState({}, '', targetUrl);
      }
    } catch (error) {
      console.error('Error fetching content:', error);
      toast.error('Error loading content');
    } finally {
      setLoading(false);
    }
  };

  const backToCategories = () => {
    setViewMode('categories');
    setSelectedCategory(null);
    setSelectedSubcategory(null);
    setSubcategories([]);
    setContent([]);
    
    // Use replaceState when going back (don't add new history entry)
    window.history.replaceState({}, '', '/classroom');
  };
  
  const backToSubcategories = () => {
    // Simply go back to subcategories view
    // All content now belongs to subcategories, so no need to fetch "loose" content
    setViewMode('subcategories');
    setSelectedSubcategory(null);
    setContent([]); // Clear content since we're going back to subcategory selection
    
    // Use replaceState when going back (don't add new history entry)
    if (selectedCategory) {
      const categorySlug = selectedCategory.title.toLowerCase().replace(/\s+/g, '-');
      window.history.replaceState({}, '', `/classroom/${categorySlug}`);
    }
  };

  const handleContentView = (item) => {
    // Check if content is member-only and user is not a member
    const isCurrentMember = isMember(user?.subscription_status);
    
    if (item.member_only && !isCurrentMember) {
      // Show local upgrade modal that overlays the classroom
      setUpgradeFeatureName(item.title);
      setShowLocalUpgradeModal(true);
      return;
    }
    
    // Helper function to build URL with subcategory
    const buildContentUrl = (item) => {
      const categorySlug = selectedCategory?.title?.toLowerCase().replace(/\s+/g, '-');
      const subcategorySlug = selectedSubcategory?.slug || selectedSubcategory?.name?.toLowerCase().replace(/\s+/g, '-');
      const contentSlug = item.slug || item.id;
      
      if (categorySlug && subcategorySlug) {
        return `/classroom/${categorySlug}/${subcategorySlug}/${contentSlug}`;
      } else if (categorySlug) {
        return `/classroom/${categorySlug}/${contentSlug}`;
      }
      return null;
    };
    
    if (item.content_type === 'url' && item.url) {
      window.open(item.url, '_blank');
    } else if (item.content_type === 'video' && item.url) {
      // For videos, navigate to full page instead of modal
      setSelectedVideo(item);
      setViewMode('article');
      const url = buildContentUrl(item);
      if (url && window.location.pathname !== url) {
        window.history.pushState({}, '', url);
      }
    } else if (item.content_type === 'audio') {
      // Navigate to audio detail page (similar to article)
      setSelectedArticle(null);  // Clear other selections
      setSelectedVideo(null);
      setSelectedAudio(item);
      setViewMode('article');
      const url = buildContentUrl(item);
      if (url && window.location.pathname !== url) {
        window.history.pushState({}, '', url);
      }
    } else if (item.content_type === 'document') {
      // Open document URL in new tab if available
      if (item.url) {
        window.open(item.url, '_blank');
        toast.success('Opening document');
      } else {
        toast.error('Document URL not available');
      }
    } else if (item.content_type === 'article') {
      // Navigate to full article page instead of modal
      setSelectedArticle(item);
      setViewMode('article');
      const url = buildContentUrl(item);
      if (url && window.location.pathname !== url) {
        window.history.pushState({}, '', url);
      }
    } else {
      toast.error('Content not available');
    }
  };
  
  const backToContent = async () => {
    setSelectedArticle(null);
    setSelectedVideo(null);
    setSelectedAudio(null);
    
    // If no category is selected, go back to categories
    if (!selectedCategory) {
      setViewMode('categories');
      window.history.replaceState({}, '', '/classroom');
      return;
    }
    
    // If we have a selected subcategory, go back to the content list view
    if (selectedSubcategory) {
      // Check if content is empty (e.g., when coming from deep link/dashboard)
      // If so, re-fetch the content for this subcategory
      if (content.length === 0) {
        try {
          setLoading(true);
          const encodedCategory = encodeURIComponent(selectedCategory.title);
          const response = await axios.get(`${API}/content?category=${encodedCategory}`);
          const allContent = response.data || [];
          const filteredContent = allContent.filter(item => item.subcategory === selectedSubcategory.name);
          setContent(filteredContent);
        } catch (error) {
          console.error('Error fetching content:', error);
        } finally {
          setLoading(false);
        }
      }
      setViewMode('content');
      
      // Use replaceState when going back (don't add new history entry)
      const categorySlug = selectedCategory.title.toLowerCase().replace(/\s+/g, '-');
      const subcategorySlug = selectedSubcategory.slug || selectedSubcategory.name.toLowerCase().replace(/\s+/g, '-');
      window.history.replaceState({}, '', `/classroom/${categorySlug}/${subcategorySlug}`);
    } else {
      // No subcategory selected, go back to subcategories view
      setViewMode('subcategories');
      
      // Use replaceState when going back (don't add new history entry)
      const categorySlug = selectedCategory.title.toLowerCase().replace(/\s+/g, '-');
      window.history.replaceState({}, '', `/classroom/${categorySlug}`);
    }
  };

  const handleSaveContent = async (item) => {
    try {
      const authToken = localStorage.getItem('auth_token');
      const userData = localStorage.getItem('user_data');
      
      if (!authToken || !userData) {
        toast.error('Please log in to save content');
        return;
      }

      const user = JSON.parse(userData);
      
      // Use the unified saved items system - save ALL content data for viewing
      const savedData = {
        type: 'content',
        name: item.title,
        data: {
          content_id: item.id,
          description: item.description || '',
          category: item.category || '',
          author: item.author || '',
          content_type: item.content_type || '',
          url: item.url || '',
          content: item.content || '',
          title: item.title,
          // Store the entire item for proper viewing
          original_item: item
        }
      };

      const response = await axios.post(`${API}/users/${user.id}/saved-items`, savedData, {
        headers: { Authorization: `Bearer ${authToken}` }
      });

      if (response.data.success) {
        toast.success('Content saved successfully!');
      } else {
        toast.error(response.data.message || 'Content already saved');
      }
    } catch (error) {
      console.error('Error saving content:', error);
      if (error.response?.status === 400) {
        toast.error('Content already saved to your collection');
      } else {
        toast.error('Error saving content - please try again');
      }
    }
  };

  const toggleContentFavorite = async (item) => {
    if (!user?.id) {
      toast.error('Please log in to save favorites');
      return;
    }

    try {
      const authToken = localStorage.getItem('auth_token');
      const isCurrentlyFavorited = isContentFavorited(item.id);
      
      if (isCurrentlyFavorited) {
        // Remove from favorites
        const savedItem = favorites.find(fav => 
          fav.data.content_id === item.id || fav.data.original_item?.id === item.id
        );
        
        if (savedItem) {
          await axios.delete(`${API}/users/${user.id}/saved-items/${savedItem.id}`, {
            headers: { Authorization: `Bearer ${authToken}` }
          });
          
          setFavorites(prev => prev.filter(fav => fav.id !== savedItem.id));
          toast.success('Removed from favorites');
        }
      } else {
        // Add to favorites (same as save content)
        const savedData = {
          type: 'content',
          name: item.title,
          data: {
            content_id: item.id,
            description: item.description || '',
            category: item.category || '',
            author: item.author || '',
            content_type: item.content_type || '',
            url: item.url || '',
            content: item.content || '',
            title: item.title,
            original_item: item
          }
        };

        const response = await axios.post(`${API}/users/${user.id}/saved-items`, savedData, {
          headers: { Authorization: `Bearer ${authToken}` }
        });

        if (response.data.success) {
          await fetchFavorites(); // Refresh favorites list
          toast.success('Added to favorites!');
        }
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      toast.error('Error updating favorites');
    }
  };

  const isContentFavorited = (contentId) => {
    return favorites.some(fav => 
      fav.data.content_id === contentId || fav.data.original_item?.id === contentId
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-burgundy mx-auto mb-4"></div>
        <p className="text-burgundy/70">Loading classroom...</p>
      </div>
    );
  }

  return (
    <div data-testid="education-section" className="space-y-6 px-2 sm:px-0">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div className="flex items-center space-x-2 sm:space-x-4">
          <h2 className="text-2xl sm:text-3xl font-bold text-burgundy font-serif">Classroom</h2>
        </div>
      </div>

      {/* Categories View */}
      {viewMode === 'categories' && (
        <>
          <div className="text-center mb-8">
            <p className="text-burgundy/80 text-lg">Explore our comprehensive learning categories</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {categories.length === 0 ? (
              <div className="col-span-full text-center py-12">
                <BookOpen className="h-16 w-16 text-burgundy/30 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-burgundy/60 mb-2 font-serif">No Categories Available</h3>
                <p className="text-burgundy/50">Content categories will appear here when created by administrators.</p>
              </div>
            ) : (
              categories.map((category) => {
                  // Check both member_only field AND "The Lab" title (for backward compatibility)
                  const isLocked = (category.member_only || category.title === "The Lab") && !isMember(user?.subscription_status);
                  return (
                <Card 
                  key={category.id} 
                  className={`hover:shadow-xl transition-all duration-300 bg-white border-burgundy/20 ${!isLocked ? 'cursor-pointer' : 'cursor-not-allowed'} group overflow-hidden relative`}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    if (isLocked) {
                      if (setUpgradeFeature && setShowUpgradeModal) {
                        setUpgradeFeature("The Lab - advanced wellness content");
                        setShowUpgradeModal(true);
                      } else {
                        toast.error('This content is available to members only. Please upgrade to access The Lab!');
                      }
                      return;
                    }
                    fetchContentByCategory(category);
                  }}
                >
                  {/* Lock Overlay for Member-Only Content */}
                  {isLocked && (
                    <div className="absolute inset-0 bg-burgundy/10 backdrop-blur-sm z-10 flex items-center justify-center">
                      <div className="text-center p-4">
                        <Lock className="h-12 w-12 text-burgundy mx-auto mb-2" />
                        <p className="text-burgundy font-semibold">Members Only</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Category Image */}
                  {category.image_url ? (
                    <div className="aspect-video w-full overflow-hidden">
                      <img 
                        src={category.image_url} 
                        alt={category.image_alt || category.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                    </div>
                  ) : (
                    <div className="aspect-video w-full bg-gradient-to-br from-burgundy/20 to-dark-teal/20 flex items-center justify-center">
                      <BookOpen className="h-16 w-16 text-burgundy/40" />
                    </div>
                  )}
                  
                  <CardHeader>
                    <CardTitle className="text-xl font-serif text-burgundy group-hover:text-burgundy/80 transition-colors">
                      {category.title}
                    </CardTitle>
                  </CardHeader>
                  
                  <CardContent>
                    <p className="text-burgundy/70 mb-4 line-clamp-3">{category.description}</p>
                    
                    <div className="flex justify-between items-center">
                      <Badge variant="outline" className="border-burgundy text-burgundy">
                        {category.content_count} {category.content_count === 1 ? 'item' : 'items'}
                      </Badge>
                      
                      <Button 
                        variant="ghost"
                        size="sm"
                        className="text-burgundy hover:bg-burgundy/10 group-hover:bg-burgundy group-hover:text-cream transition-all"
                        onClick={(e) => {
                          e.stopPropagation();
                          fetchContentByCategory(category);
                        }}
                      >
                        Explore
                      </Button>
                    </div>
                  </CardContent>
                </Card>
                  );
              })
            )}
          </div>
        </>
      )}

      {/* Subcategories View */}
      {viewMode === 'subcategories' && (
        <>
          <div className="mb-6">
            <button 
              onClick={backToCategories}
              className="flex items-center text-burgundy hover:text-burgundy/80 mb-4 text-sm"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Categories
            </button>
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-burgundy font-serif">{selectedCategory?.title || 'Category'}</h3>
              <p className="text-burgundy/70">Choose a topic to explore</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {subcategories.map((subcategory) => {
              const isLocked = subcategory.member_only && !isMember(user?.subscription_status);
              
              return (
              <Card 
                key={subcategory.id} 
                className="hover:shadow-xl transition-all duration-300 bg-white border-burgundy/20 cursor-pointer group relative overflow-hidden"
                onClick={() => {
                  if (isLocked) {
                    if (setUpgradeFeature && setShowUpgradeModal) {
                      setUpgradeFeature(subcategory.name);
                      setShowUpgradeModal(true);
                    }
                    return;
                  }
                  fetchContentBySubcategory(subcategory);
                }}
              >
                {isLocked && (
                  <div className="absolute inset-0 bg-white/90 backdrop-blur-md z-10 flex items-center justify-center rounded-lg cursor-pointer hover:bg-white/95 transition-colors">
                    <div className="text-center">
                      <Lock className="h-8 w-8 text-burgundy mx-auto mb-1" />
                      <p className="text-xs text-burgundy font-semibold">Members Only</p>
                      <p className="text-xs text-burgundy/70 mt-1">Tap to upgrade</p>
                    </div>
                  </div>
                )}
                
                <div className={isLocked ? 'filter blur-sm' : ''}>
                  {subcategory.image_url ? (
                    <div className="aspect-video w-full overflow-hidden">
                      <img 
                        src={subcategory.image_url} 
                        alt={subcategory.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  ) : (
                    <div className="aspect-video w-full bg-gradient-to-br from-burgundy/10 to-dark-teal/10 flex items-center justify-center">
                      <BookOpen className="h-12 w-12 text-burgundy/40" />
                    </div>
                  )}
                  
                  <CardHeader>
                    <CardTitle className="text-lg font-serif text-burgundy group-hover:text-burgundy/80 transition-colors">
                      {subcategory.name}
                    </CardTitle>
                  </CardHeader>
                  
                  <CardContent>
                    <Button 
                      className="w-full bg-gradient-to-r from-burgundy to-dark-teal hover:from-burgundy/80 hover:to-dark-teal/80 text-cream"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isLocked) {
                          if (setUpgradeFeature && setShowUpgradeModal) {
                            setUpgradeFeature(subcategory.name);
                            setShowUpgradeModal(true);
                          }
                          return;
                        }
                        fetchContentBySubcategory(subcategory);
                      }}
                    >
                      Explore
                    </Button>
                  </CardContent>
                </div>
              </Card>
              );
            })}
          </div>

        </>
      )}

      {/* Content View */}
      {viewMode === 'content' && (
        <>
          <div className="mb-6">
            <button 
              onClick={backToSubcategories}
              className="flex items-center text-burgundy hover:text-burgundy/80 mb-4 text-sm"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to {selectedCategory?.title || 'Category'}
            </button>
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-burgundy font-serif">
                {selectedSubcategory?.name || 'Content'}
              </h3>
              <p className="text-burgundy/70">Explore content in this topic</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {content.length === 0 ? (
              <div className="col-span-full text-center py-12">
                <BookOpen className="h-16 w-16 text-burgundy/30 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-burgundy/60 mb-2 font-serif">No Content Available</h3>
                <p className="text-burgundy/50">Content for this topic will appear here when added by administrators.</p>
                <p className="text-burgundy/40 text-sm mt-2">Use your browser's back button to return</p>
              </div>
            ) : (
              content.map((item) => {
                const isCurrentMember = isMember(user?.subscription_status);
                const isLocked = item.member_only && !isCurrentMember;
                
                return (
                <Card 
                  key={item.id} 
                  className="hover:shadow-lg transition-all duration-300 bg-white border-burgundy/20 overflow-hidden relative"
                >
                  {/* Thumbnail Image or Placeholder */}
                  {item.image_url ? (
                    <div className="relative h-48 overflow-hidden bg-gray-100">
                      <img 
                        src={item.image_url} 
                        alt={item.image_alt || item.title}
                        className={`w-full h-full object-cover ${isLocked ? 'opacity-50' : ''}`}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                      {/* Lock Icon Overlay */}
                      {isLocked && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                          <Lock className="h-16 w-16 text-white" />
                          <p className="absolute bottom-4 text-white text-sm font-semibold">Tap to upgrade</p>
                        </div>
                      )}
                      {/* Content Type Badge on Image */}
                      <Badge 
                        variant="outline" 
                        className="absolute top-2 right-2 bg-white/90 border-gold text-gold text-xs"
                      >
                        {item.content_type === 'article' ? 'Article' : item.content_type === 'video' ? 'Video' : item.content_type === 'audio' ? 'Audio' : item.content_type === 'document' ? 'Document' : 'Resource'}
                      </Badge>
                    </div>
                  ) : (
                    <div className="relative h-48 bg-gradient-to-br from-burgundy/10 to-dark-teal/10 flex items-center justify-center">
                      <BookOpen className="h-16 w-16 text-burgundy/40" />
                      {isLocked && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/10">
                          <Lock className="h-16 w-16 text-burgundy" />
                        </div>
                      )}
                      <Badge 
                        variant="outline" 
                        className="absolute top-2 right-2 bg-white/90 border-gold text-gold text-xs"
                      >
                        {item.content_type === 'article' ? 'Article' : item.content_type === 'video' ? 'Video' : item.content_type === 'audio' ? 'Audio' : item.content_type === 'document' ? 'Document' : 'Resource'}
                      </Badge>
                    </div>
                  )}
                  
                  <CardHeader className="overflow-hidden">
                    <div className="flex items-start justify-between gap-2">
                      <CardTitle className="text-lg font-serif text-gray-900 flex-1 break-words">{item.title}</CardTitle>
                      {!item.image_url && (
                        <Badge variant="outline" className="border-gold text-gold text-xs flex-shrink-0">
                          {item.content_type === 'article' ? 'Article' : item.content_type === 'video' ? 'Video' : item.content_type === 'audio' ? 'Audio' : item.content_type === 'document' ? 'Document' : item.content_type?.charAt(0).toUpperCase() + item.content_type?.slice(1)}
                        </Badge>
                      )}
                    </div>
                    <div 
                      className="text-gray-700 text-sm rich-text-content line-clamp-3 overflow-hidden"
                      dangerouslySetInnerHTML={{ __html: item.description }}
                    />
                  </CardHeader>
                  
                  <CardContent>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {item.tags.map((tag) => (
                        <Badge key={tag} variant="secondary" className="text-xs bg-burgundy/10 text-burgundy border-none">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                    
                    <div className="flex gap-2">
                      <Button 
                        className="flex-1 bg-gradient-to-r from-burgundy to-dark-teal hover:from-burgundy/80 hover:to-dark-teal/80"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isLocked) {
                            if (setUpgradeFeature && setShowUpgradeModal) {
                              setUpgradeFeature(item.title);
                              setShowUpgradeModal(true);
                            }
                            return;
                          }
                          handleContentView(item);
                        }}
                      >
                        View Content
                      </Button>
                      {user && (
                        <Button
                          variant="outline"
                          size="sm"
                          className={`border-burgundy/30 hover:bg-burgundy/10 ${
                            isContentFavorited(item.id) ? 'bg-red-50 border-red-300' : ''
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleContentFavorite(item);
                          }}
                        >
                          <Heart className={`h-4 w-4 ${
                            isContentFavorited(item.id) ? 'fill-red-500 text-red-500' : 'text-burgundy'
                          }`} />
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
                );
              })
            )}
          </div>
        </>
      )}

      {/* Article Full Page View */}
      {viewMode === 'article' && selectedArticle && (
        <div className="max-w-4xl mx-auto px-2 sm:px-4">
          <button 
            onClick={backToContent}
            className="flex items-center text-burgundy hover:text-burgundy/80 mb-4 text-sm"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to {selectedSubcategory?.name || 'Content'}
          </button>
          <Card className="bg-white border-burgundy/20">
            <CardHeader className="px-4 sm:px-6">
              <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                <div className="flex-1">
                  <CardTitle className="text-2xl sm:text-3xl font-serif text-gray-900 mb-3">{selectedArticle.title}</CardTitle>
                  <div 
                    className="text-gray-700 text-base sm:text-lg rich-text-content"
                    dangerouslySetInnerHTML={{ __html: selectedArticle.description }}
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    onClick={() => toggleContentFavorite(selectedArticle)}
                    className="h-10 w-10 sm:h-12 sm:w-12 p-0 flex-shrink-0 min-h-[44px] min-w-[44px]"
                    title={favorites.some(f => f.id === selectedArticle.id) ? "Remove from favorites" : "Add to favorites"}
                  >
                    <Heart 
                      className={`h-5 w-5 ${favorites.some(f => f.id === selectedArticle.id) ? 'fill-burgundy text-burgundy' : 'text-burgundy'}`}
                    />
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      navigator.clipboard.writeText(window.location.href);
                      toast.success('Link copied to clipboard!');
                    }}
                    className="h-10 w-10 sm:h-12 sm:w-12 p-0 flex-shrink-0 min-h-[44px] min-w-[44px]"
                    title="Copy link"
                  >
                    <Link2 className="h-5 w-5" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="px-4 sm:px-6">
              <div className="flex flex-wrap gap-2 mb-6">
                {selectedArticle.tags?.map((tag) => (
                  <Badge key={tag} variant="secondary" className="text-xs bg-burgundy/10 text-burgundy border-none">
                    {tag}
                  </Badge>
                ))}
              </div>
              
              {/* Display content image if available */}
              {selectedArticle.image_url && (
                <div className="mb-6">
                  <img 
                    src={selectedArticle.image_url} 
                    alt={selectedArticle.image_alt || selectedArticle.title}
                    className="w-full max-w-2xl mx-auto rounded-lg shadow-lg"
                    loading="lazy"
                    onError={(e) => {
                      console.error('Image failed to load:', selectedArticle.image_url);
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}
              
              <div 
                className="rich-text-content prose prose-sm sm:prose-base lg:prose-lg max-w-none text-gray-800 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: selectedArticle.content || 'No content available.' }}
              />

              {/* Quiz Component */}
              {user?.id && (
                <QuizComponent 
                  contentId={selectedArticle.id}
                  userId={user.id}
                  onClose={() => {}}
                />
              )}

              {/* Optional Action Buttons */}
              {(selectedArticle.dig_deeper_url || selectedArticle.marketplace_url) && (
                <div className="flex flex-wrap gap-3 mt-8 pt-6 border-t">
                  {selectedArticle.dig_deeper_url && (
                    <Button
                      onClick={() => window.open(selectedArticle.dig_deeper_url, '_blank')}
                      className="bg-burgundy hover:bg-burgundy/80 text-cream"
                    >
                      <BookOpen className="h-4 w-4 mr-2" />
                      Dig Deeper
                    </Button>
                  )}
                  {selectedArticle.marketplace_url && (
                    <Button
                      onClick={() => {
                        setSelectedArticle(null);
                        window.open(selectedArticle.marketplace_url, '_blank');
                      }}
                      className="bg-dark-teal hover:bg-dark-teal/80 text-cream"
                    >
                      <ShoppingBag className="h-4 w-4 mr-2" />
                      Marketplace
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Video Full Page View */}
      {viewMode === 'article' && selectedVideo && (
        <div className="max-w-4xl mx-auto px-2 sm:px-4">
          <button 
            onClick={backToContent}
            className="flex items-center text-burgundy hover:text-burgundy/80 mb-4 text-sm"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to {selectedSubcategory?.name || 'Content'}
          </button>
          <Card className="bg-white border-burgundy/20">
            <CardHeader className="px-4 sm:px-6">
              <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                <div className="flex-1">
                  <CardTitle className="text-2xl sm:text-3xl font-serif text-burgundy mb-3">{selectedVideo.title}</CardTitle>
                  <div 
                    className="text-gray-700 text-base sm:text-lg rich-text-content"
                    dangerouslySetInnerHTML={{ __html: selectedVideo.description }}
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    onClick={() => toggleContentFavorite(selectedVideo)}
                    className="h-10 w-10 sm:h-12 sm:w-12 p-0 flex-shrink-0 min-h-[44px] min-w-[44px]"
                    title={favorites.some(f => f.id === selectedVideo.id) ? "Remove from favorites" : "Add to favorites"}
                  >
                    <Heart 
                      className={`h-5 w-5 ${favorites.some(f => f.id === selectedVideo.id) ? 'fill-burgundy text-burgundy' : 'text-burgundy'}`}
                    />
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      navigator.clipboard.writeText(window.location.href);
                      toast.success('Link copied to clipboard!');
                    }}
                    className="h-10 w-10 sm:h-12 sm:w-12 p-0 flex-shrink-0 min-h-[44px] min-w-[44px]"
                    title="Copy link"
                  >
                    <Link2 className="h-5 w-5" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="px-4 sm:px-6">
              <div className="flex flex-wrap gap-2 mb-6">
                {selectedVideo.tags?.map((tag) => (
                  <Badge key={tag} variant="secondary" className="text-xs bg-burgundy/10 text-burgundy border-none">
                    {tag}
                  </Badge>
                ))}
              </div>
              
              {/* Display embedded video */}
              {selectedVideo.url && (
                <div className="mb-6">
                  <div className="relative w-full" style={{paddingBottom: '56.25%'}}>
                    <iframe 
                      src={(() => {
                        const url = selectedVideo.url;
                        if (url.includes('youtube.com') || url.includes('youtu.be')) {
                          const videoId = url.includes('youtu.be') 
                            ? url.split('youtu.be/')[1]?.split('?')[0]
                            : url.split('v=')[1]?.split('&')[0];
                          return `https://www.youtube.com/embed/${videoId}`;
                        } else if (url.includes('vimeo.com')) {
                          const videoId = url.split('vimeo.com/')[1]?.split('?')[0];
                          return `https://player.vimeo.com/video/${videoId}`;
                        }
                        return url;
                      })()}
                      className="absolute top-0 left-0 w-full h-full rounded-lg"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Audio Full Page View */}
      {viewMode === 'article' && selectedAudio && (
        <div className="max-w-4xl mx-auto px-2 sm:px-4">
          <button 
            onClick={backToContent}
            className="flex items-center text-burgundy hover:text-burgundy/80 mb-4 text-sm"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to {selectedSubcategory?.name || 'Content'}
          </button>
          <Card className="bg-white border-burgundy/20">
            <CardHeader className="px-4 sm:px-6">
              <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                <div className="flex-1">
                  <CardTitle className="text-2xl sm:text-3xl font-serif text-gray-900 mb-3">{selectedAudio.title}</CardTitle>
                  <div 
                    className="text-gray-700 text-base sm:text-lg rich-text-content"
                    dangerouslySetInnerHTML={{ __html: selectedAudio.description }}
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    onClick={() => toggleContentFavorite(selectedAudio)}
                    className="h-10 w-10 sm:h-12 sm:w-12 p-0 flex-shrink-0 min-h-[44px] min-w-[44px]"
                    title={favorites.some(f => f.id === selectedAudio.id) ? "Remove from favorites" : "Add to favorites"}
                  >
                    <Heart 
                      className={`h-5 w-5 ${favorites.some(f => f.id === selectedAudio.id) ? 'fill-burgundy text-burgundy' : 'text-burgundy'}`}
                    />
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      navigator.clipboard.writeText(window.location.href);
                      toast.success('Link copied to clipboard!');
                    }}
                    className="h-10 w-10 sm:h-12 sm:w-12 p-0 flex-shrink-0 min-h-[44px] min-w-[44px]"
                    title="Copy link"
                  >
                    <Link2 className="h-5 w-5" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="px-4 sm:px-6">
              <div className="flex flex-wrap gap-2 mb-6">
                {selectedAudio.tags?.map((tag) => (
                  <Badge key={tag} variant="secondary" className="text-xs bg-burgundy/10 text-burgundy border-none">
                    {tag}
                  </Badge>
                ))}
              </div>
              
              {/* Display content image if available */}
              {selectedAudio.image_url && (
                <div className="mb-6">
                  <img 
                    src={selectedAudio.image_url} 
                    alt={selectedAudio.image_alt || selectedAudio.title}
                    className="w-full max-w-2xl mx-auto rounded-lg shadow-lg"
                    loading="lazy"
                    onError={(e) => {
                      console.error('Image failed to load:', selectedAudio.image_url);
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}
              
              {/* Display any additional content text */}
              {selectedAudio.content && (
                <div 
                  className="rich-text-content prose prose-sm sm:prose-base lg:prose-lg max-w-none text-gray-800 leading-relaxed mb-8"
                  dangerouslySetInnerHTML={{ __html: selectedAudio.content }}
                />
              )}

              {/* Visit Button - Primary action for audio content */}
              {(selectedAudio.url || selectedAudio.visit_url) ? (
                <div className="flex flex-wrap gap-3 mt-8 pt-6 border-t">
                  <Button
                    onClick={() => window.open(selectedAudio.visit_url || selectedAudio.url, '_blank')}
                    className="bg-gradient-to-r from-gold to-gold-600 hover:from-gold-600 hover:to-gold-700 text-burgundy font-semibold px-8 py-3 text-lg rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 border border-gold-300"
                  >
                    <ExternalLink className="h-5 w-5 mr-2" />
                    Visit
                  </Button>
                </div>
              ) : null}

              {/* Optional Action Buttons */}
              {(selectedAudio.dig_deeper_url || selectedAudio.marketplace_url || selectedAudio.advanced_support_url) && (
                <div className="flex flex-wrap gap-3 mt-4">
                  {selectedAudio.dig_deeper_url && (
                    <Button
                      onClick={() => window.open(selectedAudio.dig_deeper_url, '_blank')}
                      variant="outline"
                      className="border-burgundy text-burgundy hover:bg-burgundy/10"
                    >
                      <BookOpen className="h-4 w-4 mr-2" />
                      Dig Deeper
                    </Button>
                  )}
                  {selectedAudio.marketplace_url && (
                    <Button
                      onClick={() => window.open(selectedAudio.marketplace_url, '_blank')}
                      variant="outline"
                      className="border-dark-teal text-dark-teal hover:bg-dark-teal/10"
                    >
                      <ShoppingBag className="h-4 w-4 mr-2" />
                      Marketplace
                    </Button>
                  )}
                  {selectedAudio.advanced_support_url && (
                    <Button
                      onClick={() => window.open(selectedAudio.advanced_support_url, '_blank')}
                      variant="outline"
                      className="border-gold text-gold hover:bg-gold/10"
                    >
                      <Users className="h-4 w-4 mr-2" />
                      Advanced Support
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Upgrade Modal - Overlays the classroom */}
      <UpgradeModal 
        isOpen={showLocalUpgradeModal}
        onClose={() => setShowLocalUpgradeModal(false)}
        feature={upgradeFeatureName || "this premium content"}
      />
    </div>
  );
};

// User Avatar Component
const UserAvatar = ({ user, size = "sm", onClick, showInitialsOnly = false }) => {
  const sizeClasses = {
    xs: "w-6 h-6 min-w-[24px] min-h-[24px]",
    sm: "w-8 h-8 min-w-[32px] min-h-[32px]", 
    md: "w-12 h-12 min-w-[48px] min-h-[48px]",
    lg: "w-16 h-16 min-w-[64px] min-h-[64px]"
  };

  // Generate initials from user name - matches formatDisplayName logic
  const getInitials = (name) => {
    if (!name) return 'U';
    const nameParts = name.trim().split(' ').filter(part => part.length > 0);
    
    if (nameParts.length === 0) {
      return 'U';
    } else if (nameParts.length === 1) {
      return nameParts[0].charAt(0).toUpperCase();
    } else {
      // First initial + last initial (matching "FirstName L." format)
      const firstInitial = nameParts[0].charAt(0).toUpperCase();
      const lastInitial = nameParts[nameParts.length - 1].charAt(0).toUpperCase();
      return `${firstInitial}${lastInitial}`;
    }
  };

  const textSizeClasses = {
    xs: "text-[10px] leading-none",
    sm: "text-xs leading-none", 
    md: "text-sm leading-none",
    lg: "text-base leading-none"
  };

  // Force initials if showInitialsOnly is true
  const shouldShowPhoto = user?.profile_photo && !showInitialsOnly;

  return (
    <div 
      className={`
        ${sizeClasses[size]} 
        rounded-full 
        border-2 
        border-burgundy/20 
        bg-burgundy/10 
        flex 
        items-center 
        justify-center 
        font-semibold 
        text-burgundy/70
        hover:border-burgundy/40 
        transition-colors 
        flex-shrink-0
        aspect-square
        ${onClick ? 'cursor-pointer' : ''}
      `}
      onClick={onClick}
      title={`View ${user?.name || 'User'}'s profile`}
      style={{ aspectRatio: '1' }}
    >
      {shouldShowPhoto ? (
        <img 
          src={user.profile_photo}
          alt={`${user.name}'s profile`}
          className="w-full h-full object-cover rounded-full"
        />
      ) : (
        <span className={`${textSizeClasses[size]} font-semibold text-burgundy/70 select-none`}>
          {getInitials(user?.name)}
        </span>
      )}
    </div>
  );
};

// Chat Section Component  
const ChatSection = ({ user, onViewProfile, onShowProfileModal, discussionToOpen, setDiscussionToOpen, resetTrigger }) => {
  const navigate = useNavigate();
  const [discussions, setDiscussions] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);  // Start with no room selected
  const [selectedDiscussion, setSelectedDiscussion] = useState(null);
  const [replies, setReplies] = useState([]);
  const [chatRooms, setChatRooms] = useState([]);
  const [viewMode, setViewMode] = useState('rooms'); // 'rooms', 'discussions' or 'thread'
  
  // New discussion form
  const [showNewDiscussion, setShowNewDiscussion] = useState(false);
  const [newDiscussion, setNewDiscussion] = useState({
    headline: '',
    content: ''
  });
  
  // Collapse/expand state for discussions
  const [expandedDiscussions, setExpandedDiscussions] = useState(new Set());
  
  // Reply form
  const [newReply, setNewReply] = useState('');

  useEffect(() => {
    fetchChatRooms();
  }, []);

  // Auto-select first room when chatRooms load and no room is selected
  // This makes Community look active on first visit
  useEffect(() => {
    if (chatRooms.length > 0 && !selectedRoom && viewMode === 'rooms') {
      setSelectedRoom(chatRooms[0].id);
      setViewMode('discussions');
    }
  }, [chatRooms]);

  // Handle reset trigger - auto-select first room and discussion to make Community look active
  // NOTE: Do NOT use pushState here - handleTabChange already calls navigate('/community')
  useEffect(() => {
    if (resetTrigger > 0) {
      // If we have rooms, select the first one instead of showing empty state
      if (chatRooms && chatRooms.length > 0) {
        setSelectedRoom(chatRooms[0].id);
        setViewMode('discussions');
      } else {
        // Fallback to rooms view if no rooms exist
        setSelectedRoom(null);
        setSelectedDiscussion(null);
        setViewMode('rooms');
      }
      // Removed: window.history.pushState - this was causing double history entries
      // Tab navigation already updates URL via navigate() in handleTabChange
    }
  }, [resetTrigger, chatRooms]);

  useEffect(() => {
    fetchDiscussions();
  }, [selectedRoom]);

  // Handle opening a specific discussion from external navigation
  useEffect(() => {
    const handleDiscussionToOpen = async () => {
      if (!discussionToOpen) return;
      
      // Handle different formats:
      // 1. String ID (legacy)
      // 2. Object with id (legacy)
      // 3. Object with messageId and roomId (new format from search)
      
      let targetMessageId;
      let targetRoomId;
      
      if (typeof discussionToOpen === 'string') {
        targetMessageId = discussionToOpen;
      } else if (discussionToOpen.messageId && discussionToOpen.roomId) {
        // New format from search results
        targetMessageId = discussionToOpen.messageId;
        targetRoomId = discussionToOpen.roomId;
      } else {
        targetMessageId = discussionToOpen.id;
      }
      
      // If we have a room ID, we can directly fetch and open
      if (targetRoomId) {
        try {
          // Fetch rooms if not loaded
          let rooms = chatRooms;
          if (!rooms || rooms.length === 0) {
            const roomsResponse = await axios.get(`${API}/chat/rooms`);
            rooms = roomsResponse.data;
            setChatRooms(rooms);
          }
          
          // Set the room
          setSelectedRoom(targetRoomId);
          
          // Fetch messages for this room
          const messagesResponse = await axios.get(`${API}/chat/messages/${targetRoomId}`);
          const roomMessages = messagesResponse.data;
          
          // Transform raw messages to discussion format
          const transformedDiscussions = roomMessages.map(transformMessageToDiscussion);
          setDiscussions(transformedDiscussions);
          
          // Find the target message
          const targetMessage = roomMessages.find(m => m.id === targetMessageId);
          
          if (targetMessage) {
            // Transform to discussion format and open
            const transformedDiscussion = transformMessageToDiscussion(targetMessage);
            
            // Open the discussion after state updates
            setTimeout(() => {
              setSelectedDiscussion(transformedDiscussion);
              setViewMode('thread');
              // Set replies directly from the transformed data
              setReplies(transformedDiscussion.replies || []);
              window.history.pushState({}, '', `/community/${transformedDiscussion.id}`);
            }, 150);
          }
          
          setDiscussionToOpen(null);
          return;
        } catch (error) {
          console.error('Error opening discussion with room context:', error);
        }
      }
      
      // Fallback: Find the discussion in the current discussions list
      const discussionInList = discussions.find(d => d.id === targetMessageId);
      if (discussionInList) {
        openDiscussion(discussionInList);
      } else if (typeof discussionToOpen === 'object' && discussionToOpen.message) {
        // If not found but we have a full message object, use it directly
        setSelectedDiscussion(discussionToOpen);
        setViewMode('thread');
        fetchReplies(discussionToOpen.id);
        window.history.pushState({}, '', `/community/${discussionToOpen.id}`);
      } else {
        // Last resort: search through all rooms
        try {
          let rooms = chatRooms;
          if (!rooms || rooms.length === 0) {
            const roomsResponse = await axios.get(`${API}/chat/rooms`);
            rooms = roomsResponse.data;
            setChatRooms(rooms);
          }
          
          for (const room of rooms) {
            try {
              const messagesResponse = await axios.get(`${API}/chat/messages/${room.id}`);
              const roomMessages = messagesResponse.data;
              const found = roomMessages.find(m => m.id === targetMessageId);
              
              if (found) {
                setSelectedRoom(room.id);
                
                // Transform raw messages to discussion format
                const transformedDiscussions = roomMessages.map(transformMessageToDiscussion);
                setDiscussions(transformedDiscussions);
                
                // Transform the found message
                const transformedDiscussion = transformMessageToDiscussion(found);
                
                setTimeout(() => {
                  setSelectedDiscussion(transformedDiscussion);
                  setViewMode('thread');
                  setReplies(transformedDiscussion.replies || []);
                  window.history.pushState({}, '', `/community/${transformedDiscussion.id}`);
                }, 150);
                break;
              }
            } catch (e) {
              // Room might not have messages, continue
            }
          }
        } catch (error) {
          console.error('Error finding discussion:', error);
          toast.error('Could not find the discussion');
        }
      }
      
      setDiscussionToOpen(null);
    };
    
    handleDiscussionToOpen();
  }, [discussionToOpen, discussions]);

  // Handle room selection with URL updates
  const handleRoomSelect = async (room) => {
    setSelectedRoom(room.id);
    setViewMode('discussions');
    // Update URL for room-level navigation
    window.history.pushState({}, '', `/community/${room.name.toLowerCase().replace(/\s+/g, '-')}`);
  };

  const fetchChatRooms = async () => {
    try {
      const response = await axios.get(`${API}/chat/rooms`);
      const rooms = response.data;
      setChatRooms(rooms);
    } catch (error) {
      console.error('Error fetching chat rooms:', error);
    }
  };

  const fetchDiscussions = async () => {
    // Don't fetch if no room is selected
    if (!selectedRoom) {
      setDiscussions([]);
      return;
    }
    
    try {
      // Fetch threaded messages from backend
      const response = await axios.get(`${API}/chat/messages/${selectedRoom}`);
      const messages = response.data || [];
      
      // Backend now returns messages with nested structure
      // Each message has: id, message, user_name, replies[], reply_count, reactions
      const discussions = messages.map(msg => {
        // Parse message to extract headline and content
        const lines = msg.message.split('\n');
        let headline, content;
        
        if (lines.length > 2 && lines[1] === '') {
          // Formatted as "Headline\n\nContent"
          headline = lines[0];
          content = lines.slice(2).join('\n');
        } else {
          // Single line or no formatting - use first line as headline
          headline = lines[0];
          content = lines.slice(1).join('\n') || lines[0];
        }
        
        return {
          id: msg.id,
          headline: headline,
          content: content,
          user_name: msg.user_name,
          user_id: msg.user_id,
          user_profile_photo: msg.user_profile_photo,
          user_photo: msg.user_photo,
          user_subscription_status: msg.user_subscription_status,
          user_coupon_used: msg.user_coupon_used,
          user_bio: msg.user_bio,
          user_location: msg.user_location,
          is_expert: msg.is_expert,
          is_persona: msg.is_persona,
          created_at: msg.created_at,
          reply_count: msg.reply_count || 0,
          replies: msg.replies || [],
          reactions: msg.reactions || { thumbs_up: 0, heart: 0 }
        };
      });
      
      setDiscussions(discussions);
    } catch (error) {
      console.error('Error fetching discussions:', error);
    }
  };

  // Helper function to transform a raw chat message into the expected discussion format
  const transformMessageToDiscussion = (msg) => {
    const lines = msg.message.split('\n');
    let headline, content;
    
    if (lines.length > 2 && lines[1] === '') {
      // Formatted as "Headline\n\nContent"
      headline = lines[0];
      content = lines.slice(2).join('\n');
    } else {
      // Single line or no formatting - use first line as headline
      headline = lines[0];
      content = lines.slice(1).join('\n') || lines[0];
    }
    
    return {
      id: msg.id,
      headline: headline,
      content: content,
      user_name: msg.user_name,
      user_id: msg.user_id,
      user_profile_photo: msg.user_profile_photo || msg.user_photo,
      user_subscription_status: msg.user_subscription_status,
      user_coupon_used: msg.user_coupon_used,
      user_bio: msg.user_bio,
      user_location: msg.user_location,
      is_expert: msg.is_expert,
      created_at: msg.created_at,
      reply_count: msg.reply_count || 0,
      replies: msg.replies || [],
      reactions: msg.reactions || { thumbs_up: 0, heart: 0 }
    };
  };

  const fetchReplies = async (discussionId) => {
    try {
      // Find the discussion and get its replies
      const discussion = discussions.find(d => d.id === discussionId);
      if (discussion && discussion.replies) {
        setReplies(discussion.replies);
      } else {
        // If not found in current discussions list, fetch fresh data
        const response = await axios.get(`${API}/chat/messages/${selectedRoom}`);
        const messages = response.data || [];
        const discussionData = messages.find(m => m.id === discussionId);
        if (discussionData) {
          setReplies(discussionData.replies || []);
        }
      }
    } catch (error) {
      console.error('Error fetching replies:', error);
    }
  };

  const createDiscussion = async (e) => {
    e.preventDefault();
    if (!newDiscussion.headline.trim() || !newDiscussion.content.trim()) {
      toast.error('Please fill in both headline and content');
      return;
    }

    try {
      // For now, post as a message with formatted content
      const formattedMessage = `${newDiscussion.headline}\n\n${newDiscussion.content}`;
      
      await axios.post(`${API}/chat/messages`, {
        room_id: selectedRoom,
        user_id: user?.id || 'demo-user',
        user_name: user?.name || 'Demo User',
        message: formattedMessage,
        is_expert: false
      });
      
      setNewDiscussion({ headline: '', content: '' });
      setShowNewDiscussion(false);
      // Refresh discussions immediately to show new discussion
      await fetchDiscussions();
      toast.success("Discussion created!");
    } catch (error) {
      console.error('Error creating discussion:', error);
      toast.error("Failed to create discussion");
    }
  };

  const addReply = async (e) => {
    e.preventDefault();
    if (!newReply.trim()) return;

    try {
      // Post as a reply with parent_message_id for proper threading
      await axios.post(`${API}/chat/messages`, {
        room_id: selectedRoom,
        user_id: user?.id || 'demo-user',
        user_name: user?.name || 'Demo User', 
        message: newReply,
        is_expert: false,
        parent_message_id: selectedDiscussion.id  // Link to parent discussion
      });
      
      setNewReply('');
      
      // Fetch fresh data from server
      const response = await axios.get(`${API}/chat/messages/${selectedRoom}`);
      const messages = response.data || [];
      
      // Parse and update discussions
      const updatedDiscussions = messages.map(msg => {
        const lines = msg.message.split('\n');
        let headline, content;
        
        if (lines.length > 2 && lines[1] === '') {
          headline = lines[0];
          content = lines.slice(2).join('\n');
        } else {
          headline = lines[0];
          content = lines.slice(1).join('\n') || lines[0];
        }
        
        return {
          id: msg.id,
          headline: headline,
          content: content,
          user_name: msg.user_name,
          user_id: msg.user_id,
          is_expert: msg.is_expert,
          created_at: msg.created_at,
          reply_count: msg.reply_count || 0,
          replies: msg.replies || [],
          reactions: msg.reactions || { thumbs_up: 0, heart: 0 }
        };
      });
      
      setDiscussions(updatedDiscussions);
      
      // Update the selectedDiscussion with fresh data including new reply
      const updatedDiscussion = updatedDiscussions.find(d => d.id === selectedDiscussion.id);
      if (updatedDiscussion) {
        setSelectedDiscussion(updatedDiscussion);
        setReplies(updatedDiscussion.replies || []);
      }
      
      toast.success("Reply added!");
    } catch (error) {
      console.error('Error adding reply:', error);
      toast.error("Failed to add reply");
    }
  };

  const addReaction = async (messageId, reactionType) => {
    try {
      await axios.post(`${API}/chat/messages/${messageId}/react?reaction_type=${reactionType}`);
      
      // Fetch fresh data from server
      const response = await axios.get(`${API}/chat/messages/${selectedRoom}`);
      const messages = response.data || [];
      
      // Parse and update discussions
      const updatedDiscussions = messages.map(msg => {
        const lines = msg.message.split('\n');
        let headline, content;
        
        if (lines.length > 2 && lines[1] === '') {
          headline = lines[0];
          content = lines.slice(2).join('\n');
        } else {
          headline = lines[0];
          content = lines.slice(1).join('\n') || lines[0];
        }
        
        return {
          id: msg.id,
          headline: headline,
          content: content,
          user_name: msg.user_name,
          user_id: msg.user_id,
          is_expert: msg.is_expert,
          created_at: msg.created_at,
          reply_count: msg.reply_count || 0,
          replies: msg.replies || [],
          reactions: msg.reactions || { thumbs_up: 0, heart: 0 }
        };
      });
      
      setDiscussions(updatedDiscussions);
      
      // If viewing a thread, update the selectedDiscussion with fresh data
      if (selectedDiscussion && viewMode === 'thread') {
        const updatedDiscussion = updatedDiscussions.find(d => d.id === selectedDiscussion.id);
        if (updatedDiscussion) {
          setSelectedDiscussion(updatedDiscussion);
          setReplies(updatedDiscussion.replies || []);
        }
      }
      
      toast.success(`Reaction added!`);
    } catch (error) {
      console.error('Error adding reaction:', error);
      toast.error('Error adding reaction');
    }
  };

  const openDiscussion = (discussion) => {
    setSelectedDiscussion(discussion);
    setViewMode('thread');
    fetchReplies(discussion.id);
    // Update URL for sharing without adding to history
    window.history.replaceState({}, '', `/community/${discussion.id}`);
    // Track discussion view
    trackContentView('discussion', discussion.id, discussion.content?.substring(0, 80), localStorage.getItem('token'));
  };
  
  const toggleDiscussionExpand = (discussionId, e) => {
    e.stopPropagation();
    setExpandedDiscussions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(discussionId)) {
        newSet.delete(discussionId);
      } else {
        newSet.add(discussionId);
      }
      return newSet;
    });
  };

  const backToDiscussions = () => {
    setViewMode('discussions');
    setSelectedDiscussion(null);
    setReplies([]);
    // Reset URL back to community tab
    window.history.replaceState({}, '', '/community');
  };

  return (
    <div data-testid="chat-section" className="space-y-6 mb-16">
      {/* Header with Navigation */}
      <div className="mb-6">
        {/* Title and action button row */}
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-3xl font-bold text-burgundy font-serif">Community</h2>
            {viewMode === 'discussions' && selectedRoom && (
              <p className="text-burgundy/70">{selectedRoom.name}</p>
            )}
          </div>
          
          {viewMode === 'discussions' && (
            <Button
              onClick={() => setShowNewDiscussion(true)}
              className="bg-burgundy hover:bg-burgundy/80 text-cream"
            >
              <Plus className="h-4 w-4 mr-2" />
              Start Discussion
            </Button>
          )}
        </div>
        
        {/* Back button container - fixed height to prevent layout shift */}
        <div className="h-8 flex items-center">
          {viewMode === 'discussions' && (
            <button
              onClick={() => {
                setSelectedRoom(null);
                setSelectedDiscussion(null);
                setViewMode('rooms');
                window.history.pushState({}, '', '/community');
              }}
              className="flex items-center text-burgundy hover:text-burgundy/80 text-sm"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Rooms
            </button>
          )}
          {viewMode === 'thread' && (
            <button
              onClick={backToDiscussions}
              className="flex items-center text-burgundy hover:text-burgundy/80 text-sm"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Discussions
            </button>
          )}
        </div>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Mobile Tab Switcher for Community */}
        <div className="lg:hidden mb-4">
          <div className="flex rounded-lg overflow-hidden border border-burgundy/20">
            <button
              onClick={() => setViewMode('rooms')}
              className={`flex-1 py-2 px-4 text-sm font-medium transition-colors ${
                viewMode === 'rooms' 
                  ? 'bg-burgundy text-cream' 
                  : 'bg-white text-burgundy hover:bg-burgundy/5'
              }`}
            >
              Rooms
            </button>
            <button
              onClick={() => setViewMode('discussions')}
              className={`flex-1 py-2 px-4 text-sm font-medium transition-colors ${
                viewMode === 'discussions' 
                  ? 'bg-burgundy text-cream' 
                  : 'bg-white text-burgundy hover:bg-burgundy/5'
              }`}
            >
              Discussions
            </button>
          </div>
        </div>
        {/* Discussion Rooms Sidebar */}
        <div className={`lg:col-span-1 ${viewMode === 'rooms' ? 'block' : 'hidden lg:block'}`}>
          <Card className="bg-white border-burgundy/20">
            <CardHeader className="bg-gradient-to-r from-burgundy to-dark-teal text-cream">
              <CardTitle className="font-serif">Discussion Rooms</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {chatRooms.map((room) => (
                <button
                  key={room.id}
                  onClick={() => handleRoomSelect(room)}
                  className={`w-full text-left p-4 border-b border-burgundy/10 hover:bg-cream/50 transition-colors ${
                    selectedRoom === room.id ? 'bg-burgundy/10' : ''
                  }`}
                >
                  <h4 className="font-semibold text-burgundy text-sm">{room.name}</h4>
                  <p className="text-xs text-burgundy/70 mt-1">{room.description}</p>
                </button>
              ))}
            </CardContent>
          </Card>
        </div>

        {/* Main Content Area */}
        <div className={`lg:col-span-3 ${viewMode === 'discussions' || viewMode === 'thread' ? 'block' : 'hidden lg:block'}`}>
          {viewMode === 'discussions' ? (
            /* Discussions List View */
            <Card className="bg-white border-burgundy/20">
              <CardHeader className="bg-gradient-to-r from-burgundy to-dark-teal text-cream">
                <CardTitle className="font-serif">
                  {chatRooms.find(room => room.id === selectedRoom)?.name || 'Discussions'}
                </CardTitle>
                <CardDescription className="text-cream/90">
                  Join the conversation and share your experiences
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <div className="max-h-[600px] overflow-y-auto">
                  {!selectedRoom ? (
                    <div className="text-center text-burgundy/50 py-12">
                      <MessageCircle className="h-16 w-16 mx-auto mb-4 opacity-50" />
                      <h3 className="text-xl font-semibold mb-2">Select a Discussion Room</h3>
                      <p>Click on any discussion room to join the conversation</p>
                    </div>
                  ) : discussions.length === 0 ? (
                    <div className="text-center text-burgundy/50 py-12 px-6">
                      <MessageCircle className="h-16 w-16 mx-auto mb-4 opacity-50" />
                      <h3 className="text-xl font-semibold mb-2 text-burgundy">Welcome to {chatRooms.find(room => room.id === selectedRoom)?.name || 'Discussion Room'}</h3>
                      <p className="mb-4 text-base">This discussion room is ready for meaningful conversations. Be the first to share your thoughts, questions, or experiences!</p>
                      <Button
                        onClick={() => setShowNewDiscussion(true)}
                        className="bg-burgundy hover:bg-burgundy/80 text-cream"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Start a New Discussion
                      </Button>
                    </div>
                  ) : (
                    <div className="divide-y divide-burgundy/10">
                      {discussions.map((discussion) => {
                        const isExpanded = expandedDiscussions.has(discussion.id);
                        return (
                        <div
                          key={discussion.id}
                          className="p-4 sm:p-6 hover:bg-cream/30 transition-colors"
                        >
                          <div className="flex items-start space-x-3 w-full overflow-hidden">
                            <UserAvatar 
                              user={{
                                name: discussion.user_name,
                                profile_photo: discussion.user_profile_photo || discussion.user_photo,
                                id: discussion.user_id
                              }} 
                              size="sm"
                              showInitialsOnly={true}
                              onClick={(e) => {
                                e.stopPropagation();
                                onViewProfile({
                                  name: discussion.user_name,
                                  profile_photo: discussion.user_profile_photo || discussion.user_photo,
                                  id: discussion.user_id,
                                  bio: discussion.user_bio,
                                  location: discussion.user_location,
                                  subscription_status: discussion.user_subscription_status,
                                  coupon_used: discussion.user_coupon_used
                                });
                                onShowProfileModal(true);
                              }}
                            />
                            <div className="flex-1 min-w-0 overflow-hidden">
                              <div className="flex items-center gap-2 mb-1 overflow-hidden">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onViewProfile({
                                      name: discussion.user_name,
                                      profile_photo: discussion.user_profile_photo || discussion.user_photo,
                                      id: discussion.user_id,
                                      bio: discussion.user_bio,
                                      location: discussion.user_location,
                                      subscription_status: discussion.user_subscription_status,
                                      coupon_used: discussion.user_coupon_used
                                    });
                                    onShowProfileModal(true);
                                  }}
                                  className="font-medium text-sm text-gray-800 hover:text-gray-600 hover:underline truncate flex-shrink-0"
                                >
                                  {formatDisplayName(discussion.user_name)}
                                </button>
                                {discussion.is_expert && (
                                  <Badge className="text-xs bg-gold/20 text-gold border-gold/30 flex-shrink-0">Expert</Badge>
                                )}
                                <span className="text-xs text-burgundy/50 flex-shrink-0">
                                  {new Date(discussion.created_at).toLocaleDateString()}
                                </span>
                              </div>
                              
                              <div className="flex items-start gap-2">
                                <button
                                  onClick={() => openDiscussion(discussion)}
                                  className="text-burgundy hover:text-burgundy/70 transition-colors flex-shrink-0 mt-1"
                                  title="View Thread"
                                >
                                  <ChevronDown className="h-5 w-5 -rotate-90" />
                                </button>
                                
                                <div className="flex-1 min-w-0">
                                  <h3 
                                    onClick={() => openDiscussion(discussion)}
                                    className="text-sm font-bold text-gray-800 mb-1 hover:text-gray-600 cursor-pointer break-words"
                                    style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                                  >
                                    {discussion.headline}
                                  </h3>
                                  
                                  <div className="flex items-center gap-3 text-xs text-burgundy/70">
                                    <button 
                                      onClick={(e) => { e.stopPropagation(); addReaction(discussion.id, 'thumbs_up'); }}
                                      className="flex items-center gap-1 hover:text-burgundy transition-colors"
                                    >
                                      <span>👍</span>
                                      <span>{discussion.reactions?.thumbs_up || 0}</span>
                                    </button>
                                    <button 
                                      onClick={(e) => { e.stopPropagation(); addReaction(discussion.id, 'heart'); }}
                                      className="flex items-center gap-1 hover:text-burgundy transition-colors"
                                    >
                                      <span>❤️</span>
                                      <span>{discussion.reactions?.heart || 0}</span>
                                    </button>
                                    <span className="flex items-center gap-1">
                                      <MessageCircle className="h-3 w-3" />
                                      <span>{discussion.reply_count} {discussion.reply_count === 1 ? 'reply' : 'replies'}</span>
                                    </span>
                                    <button
                                      onClick={() => openDiscussion(discussion)}
                                      className="text-dark-teal hover:text-dark-teal/70 font-semibold flex items-center gap-1"
                                    >
                                      View Thread
                                      <ChevronDown className="h-3 w-3 -rotate-90" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )})}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ) : selectedDiscussion ? (
            /* Individual Discussion Thread View */
            <Card className="bg-white border-burgundy/20">
              <CardHeader className="bg-gradient-to-r from-burgundy to-dark-teal text-cream">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <CardTitle className="font-serif text-lg">
                      {selectedDiscussion?.headline}
                    </CardTitle>
                    <CardDescription className="text-cream/90">
                      Started by {formatDisplayName(selectedDiscussion?.user_name)} • {selectedDiscussion && new Date(selectedDiscussion.created_at).toLocaleDateString()}
                    </CardDescription>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(window.location.href);
                      toast.success('Discussion link copied to clipboard!');
                    }}
                    className="text-cream hover:text-gold hover:bg-cream/10"
                    title="Copy discussion link"
                  >
                    <Link2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {/* Original Discussion Post */}
                <div className="p-6 border-b border-burgundy/10 bg-cream/20">
                  <div className="flex items-start space-x-4 mb-4">
                    <UserAvatar 
                      user={{
                        name: selectedDiscussion?.user_name,
                        profile_photo: selectedDiscussion?.user_profile_photo,
                        id: selectedDiscussion?.user_id
                      }} 
                      size="md"
                      showInitialsOnly={true}
                      onClick={(e) => {
                        e.stopPropagation();
                        onViewProfile({
                          name: selectedDiscussion?.user_name,
                          profile_photo: selectedDiscussion?.user_profile_photo,
                          id: selectedDiscussion?.user_id,
                          subscription_status: selectedDiscussion?.user_subscription_status,
                          coupon_used: selectedDiscussion?.user_coupon_used
                        });
                        onShowProfileModal(true);
                      }}
                    />
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onViewProfile({
                              name: selectedDiscussion?.user_name,
                              profile_photo: selectedDiscussion?.user_profile_photo,
                              id: selectedDiscussion?.user_id,
                              subscription_status: selectedDiscussion?.user_subscription_status,
                              coupon_used: selectedDiscussion?.user_coupon_used
                            });
                            onShowProfileModal(true);
                          }}
                          className="font-semibold text-burgundy hover:text-burgundy/80 hover:underline"
                        >
                          {formatDisplayName(selectedDiscussion?.user_name)}
                        </button>
                        {selectedDiscussion?.is_expert && (
                          <Badge className="text-xs bg-gold/20 text-gold border-gold/30">Expert</Badge>
                        )}
                        <span className="text-xs text-burgundy/50">•</span>
                        <span className="text-xs text-burgundy/50">Original Post</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-gray-700 whitespace-pre-wrap pl-16 mb-3">
                    {selectedDiscussion?.content}
                  </div>
                  <div className="flex items-center gap-4 pl-16 text-sm text-burgundy/70">
                    <button 
                      onClick={() => addReaction(selectedDiscussion?.id, 'thumbs_up')}
                      className="flex items-center gap-1 hover:text-burgundy transition-colors"
                    >
                      <span>👍</span>
                      <span>{selectedDiscussion?.reactions?.thumbs_up || 0}</span>
                    </button>
                    <button 
                      onClick={() => addReaction(selectedDiscussion?.id, 'heart')}
                      className="flex items-center gap-1 hover:text-burgundy transition-colors"
                    >
                      <span>❤️</span>
                      <span>{selectedDiscussion?.reactions?.heart || 0}</span>
                    </button>
                    <span className="flex items-center gap-1">
                      <MessageCircle className="h-4 w-4" />
                      <span>{selectedDiscussion?.reply_count || 0} {selectedDiscussion?.reply_count === 1 ? 'reply' : 'replies'}</span>
                    </span>
                  </div>
                </div>
                
                {/* Replies Section */}
                <div className="max-h-[400px] overflow-y-auto">
                  {replies.length === 0 ? (
                    <div className="text-center text-burgundy/50 py-8">
                      <MessageCircle className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p>No replies yet. Be the first to respond!</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-burgundy/5">
                      {replies.map((reply) => (
                        <div key={reply.id} className="p-4">
                          <div className="flex items-start space-x-3">
                            <UserAvatar 
                              user={{
                                name: reply.user_name,
                                profile_photo: reply.user_profile_photo,
                                id: reply.user_id
                              }} 
                              size="sm"
                              showInitialsOnly={true}
                              onClick={(e) => {
                                e.stopPropagation();
                                onViewProfile({
                                  name: reply.user_name,
                                  profile_photo: reply.user_profile_photo,
                                  id: reply.user_id,
                                  subscription_status: reply.user_subscription_status,
                                  coupon_used: reply.user_coupon_used
                                });
                                onShowProfileModal(true);
                              }}
                            />
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-2">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onViewProfile({
                                      name: reply.user_name,
                                      profile_photo: reply.user_profile_photo,
                                      id: reply.user_id,
                                      subscription_status: reply.user_subscription_status,
                                      coupon_used: reply.user_coupon_used
                                    });
                                    onShowProfileModal(true);
                                  }}
                                  className="font-medium text-sm text-gray-800 hover:text-gray-600 hover:underline"
                                >
                                  {formatDisplayName(reply.user_name)}
                                </button>
                                {reply.is_expert && (
                                  <Badge className="text-xs bg-gold/20 text-gold border-gold/30">Expert</Badge>
                                )}
                                <span className="text-xs text-burgundy/50">
                                  {new Date(reply.created_at).toLocaleTimeString()}
                                </span>
                              </div>
                              <p className="text-gray-700 mb-2">{reply.message}</p>
                              <div className="flex items-center gap-3 text-xs text-burgundy/70">
                                <button 
                                  onClick={(e) => { e.stopPropagation(); addReaction(reply.id, 'thumbs_up'); }}
                                  className="flex items-center gap-1 hover:text-burgundy transition-colors"
                                >
                                  <span>👍</span>
                                  <span>{reply.reactions?.thumbs_up || 0}</span>
                                </button>
                                <button 
                                  onClick={(e) => { e.stopPropagation(); addReaction(reply.id, 'heart'); }}
                                  className="flex items-center gap-1 hover:text-burgundy transition-colors"
                                >
                                  <span>❤️</span>
                                  <span>{reply.reactions?.heart || 0}</span>
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                {/* Reply Form */}
                <div className="p-4 border-t border-burgundy/20 bg-cream/50">
                  <form onSubmit={addReply}>
                    <textarea
                      value={newReply}
                      onChange={(e) => setNewReply(e.target.value)}
                      placeholder="Add your reply..."
                      className="w-full min-h-[80px] border border-burgundy/30 rounded-md px-3 py-2 focus:border-gold focus:ring-gold mb-3 resize-none"
                      required
                    />
                    <div className="flex justify-end">
                      <button 
                        type="submit" 
                        className="bg-burgundy hover:bg-burgundy/80 text-white px-4 py-2 rounded-md disabled:opacity-50"
                        disabled={!newReply.trim()}
                      >
                        Post Reply
                      </button>
                    </div>
                  </form>
                </div>
              </CardContent>
            </Card>
          ) : (
            /* No discussion selected - show empty state */
            <Card className="bg-white border-burgundy/20">
              <CardContent className="p-12">
                <div className="text-center text-burgundy/50">
                  <MessageCircle className="h-16 w-16 mx-auto mb-4 opacity-50" />
                  <h3 className="text-xl font-semibold mb-2">No Discussion Selected</h3>
                  <p className="mb-4">Please select a discussion room and open a discussion to view it</p>
                  <Button
                    onClick={() => setViewMode('discussions')}
                    className="bg-burgundy hover:bg-burgundy/80 text-cream"
                  >
                    Back to Discussions
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* New Discussion Modal */}
      {showNewDiscussion && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl bg-white border-burgundy/20">
            <CardHeader>
              <CardTitle className="font-serif text-burgundy flex justify-between items-center">
                Start New Discussion
                <Button variant="ghost" onClick={() => setShowNewDiscussion(false)} className="h-8 w-8 p-0">
                  <X className="h-4 w-4" />
                </Button>
              </CardTitle>
              <CardDescription>
                Share your thoughts and experiences with the community
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={createDiscussion} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-burgundy mb-2">
                    Discussion Title
                  </label>
                  <Input
                    value={newDiscussion.headline}
                    onChange={(e) => setNewDiscussion({...newDiscussion, headline: e.target.value})}
                    placeholder="What would you like to discuss?"
                    className="border-burgundy/30 focus:border-gold"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-burgundy mb-2">
                    Content
                  </label>
                  <Textarea
                    value={newDiscussion.content}
                    onChange={(e) => setNewDiscussion({...newDiscussion, content: e.target.value})}
                    placeholder="Share your thoughts, experiences, or questions..."
                    className="border-burgundy/30 focus:border-gold"
                    rows={6}
                    required
                  />
                </div>
                
                <div className="flex justify-end space-x-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowNewDiscussion(false)}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-burgundy hover:bg-burgundy/80">
                    Start Discussion
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

// Peptide Calculator Component with Tools Navigation
// Product Log Component
const ProductLog = ({ user }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingLog, setEditingLog] = useState(null);
  const [formData, setFormData] = useState({
    product_name: '',
    dosage: '',
    frequency: '',
    date_started: '',
    date_ended: '',
    effects: '',
    rating: '',
    side_effects: '',
    notes: ''
  });

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/product-logs`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      if (response.data.success) {
        setLogs(response.data.logs);
      }
    } catch (error) {
      console.error('Error fetching product logs:', error);
      toast.error('Failed to load product logs');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.product_name.trim()) {
      toast.error('Product name is required');
      return;
    }

    try {
      const submitData = {
        product_name: formData.product_name.trim(),
        dosage: formData.dosage.trim() || null,
        frequency: formData.frequency.trim() || null,
        date_started: formData.date_started || null,
        date_ended: formData.date_ended || null,
        effects: formData.effects.trim() || null,
        rating: formData.rating ? parseInt(formData.rating) : null,
        side_effects: formData.side_effects.trim() || null,
        notes: formData.notes.trim() || null
      };

      if (editingLog) {
        await axios.put(
          `${BACKEND_URL}/api/product-logs/${editingLog.id}`,
          submitData,
          { headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` } }
        );
        toast.success('Product log updated!');
      } else {
        await axios.post(
          `${BACKEND_URL}/api/product-logs`,
          submitData,
          { headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` } }
        );
        toast.success('Product log added!');
      }

      fetchLogs();
      handleCloseForm();
    } catch (error) {
      console.error('Error saving product log:', error);
      toast.error('Failed to save product log');
    }
  };

  const handleEdit = (log) => {
    setEditingLog(log);
    setFormData({
      product_name: log.product_name || '',
      dosage: log.dosage || '',
      frequency: log.frequency || '',
      date_started: log.date_started ? log.date_started.split('T')[0] : '',
      date_ended: log.date_ended ? log.date_ended.split('T')[0] : '',
      effects: log.effects || '',
      rating: log.rating || '',
      side_effects: log.side_effects || '',
      notes: log.notes || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (logId) => {
    if (!confirm('Are you sure you want to delete this product log?')) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/product-logs/${logId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      toast.success('Product log deleted');
      fetchLogs();
    } catch (error) {
      console.error('Error deleting product log:', error);
      toast.error('Failed to delete product log');
    }
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingLog(null);
    setFormData({
      product_name: '',
      dosage: '',
      frequency: '',
      date_started: '',
      date_ended: '',
      effects: '',
      rating: '',
      side_effects: '',
      notes: ''
    });
  };

  if (loading) {
    return (
      <Card className="bg-white border-burgundy/20">
        <CardContent className="pt-6">
          <div className="text-center py-8 text-burgundy/60">Loading...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="bg-white border-burgundy/20">
        <CardHeader className="bg-gradient-to-r from-burgundy to-dark-teal text-cream">
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="font-serif">Product Log</CardTitle>
              <CardDescription className="text-cream/90">
                Track wellness products, supplements, and their effects on your health
              </CardDescription>
            </div>
            <Button
              onClick={() => setShowForm(true)}
              className="bg-cream text-burgundy hover:bg-cream/90"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Product
            </Button>
          </div>
        </CardHeader>

        <CardContent className="pt-6">
          {logs.length === 0 ? (
            <div className="text-center py-12 text-burgundy/60">
              <p className="text-lg mb-2">No products logged yet</p>
              <p className="text-sm">Start tracking your wellness products to monitor their effects</p>
            </div>
          ) : (
            <div className="space-y-4">
              {logs.map((log) => (
                <div
                  key={log.id}
                  className="border border-burgundy/20 rounded-lg p-4 hover:border-burgundy/40 transition-colors"
                >
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-burgundy">{log.product_name}</h3>
                      <div className="flex gap-4 text-sm text-burgundy/60 mt-1">
                        {log.dosage && <span>Dosage: {log.dosage}</span>}
                        {log.frequency && <span>• {log.frequency}</span>}
                        {log.rating && (
                          <span>• Rating: {log.rating}/10</span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(log)}
                        className="text-burgundy hover:text-burgundy hover:bg-burgundy/10"
                      >
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(log.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        Delete
                      </Button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 text-sm">
                    {log.date_started && (
                      <div>
                        <span className="text-burgundy/60">Started:</span>
                        <span className="ml-2 text-burgundy">
                          {new Date(log.date_started).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                    {log.date_ended && (
                      <div>
                        <span className="text-burgundy/60">Ended:</span>
                        <span className="ml-2 text-burgundy">
                          {new Date(log.date_ended).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                  </div>

                  {log.effects && (
                    <div className="mt-3">
                      <p className="text-sm text-burgundy/60 mb-1">Effects:</p>
                      <p className="text-sm text-burgundy">{log.effects}</p>
                    </div>
                  )}

                  {log.side_effects && (
                    <div className="mt-2">
                      <p className="text-sm text-burgundy/60 mb-1">Side Effects:</p>
                      <p className="text-sm text-burgundy">{log.side_effects}</p>
                    </div>
                  )}

                  {log.notes && (
                    <div className="mt-2">
                      <p className="text-sm text-burgundy/60 mb-1">Notes:</p>
                      <p className="text-sm text-burgundy">{log.notes}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-burgundy">
                  {editingLog ? 'Edit Product Log' : 'Add Product Log'}
                </h2>
                <Button variant="ghost" onClick={handleCloseForm} className="h-8 w-8 p-0">
                  <X className="h-4 w-4" />
                </Button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-burgundy mb-2">
                    Product Name *
                  </label>
                  <Input
                    value={formData.product_name}
                    onChange={(e) => setFormData({ ...formData, product_name: e.target.value })}
                    placeholder="e.g., Magnesium, Vitamin D, etc."
                    required
                    className="border-burgundy/30"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-burgundy mb-2">
                      Dosage
                    </label>
                    <Input
                      value={formData.dosage}
                      onChange={(e) => setFormData({ ...formData, dosage: e.target.value })}
                      placeholder="e.g., 500mg"
                      className="border-burgundy/30"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-burgundy mb-2">
                      Frequency
                    </label>
                    <Input
                      value={formData.frequency}
                      onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
                      placeholder="e.g., Daily, Twice daily"
                      className="border-burgundy/30"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-burgundy mb-2">
                      Date Started
                    </label>
                    <Input
                      type="date"
                      value={formData.date_started}
                      onChange={(e) => setFormData({ ...formData, date_started: e.target.value })}
                      className="border-burgundy/30"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-burgundy mb-2">
                      Date Ended
                    </label>
                    <Input
                      type="date"
                      value={formData.date_ended}
                      onChange={(e) => setFormData({ ...formData, date_ended: e.target.value })}
                      className="border-burgundy/30"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-burgundy mb-2">
                    Rating (1-10)
                  </label>
                  <Input
                    type="number"
                    min="1"
                    max="10"
                    value={formData.rating}
                    onChange={(e) => setFormData({ ...formData, rating: e.target.value })}
                    placeholder="How effective? (1-10)"
                    className="border-burgundy/30"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-burgundy mb-2">
                    Effects
                  </label>
                  <Textarea
                    value={formData.effects}
                    onChange={(e) => setFormData({ ...formData, effects: e.target.value })}
                    placeholder="What effects did you notice?"
                    rows={3}
                    className="border-burgundy/30"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-burgundy mb-2">
                    Side Effects
                  </label>
                  <Textarea
                    value={formData.side_effects}
                    onChange={(e) => setFormData({ ...formData, side_effects: e.target.value })}
                    placeholder="Any side effects experienced?"
                    rows={2}
                    className="border-burgundy/30"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-burgundy mb-2">
                    Notes
                  </label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Additional notes..."
                    rows={3}
                    className="border-burgundy/30"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    type="submit"
                    className="flex-1 bg-burgundy hover:bg-burgundy/80 text-cream"
                  >
                    {editingLog ? 'Update' : 'Add'} Product Log
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCloseForm}
                    className="border-burgundy text-burgundy hover:bg-burgundy/10"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
const PeptideCalculator = ({ user, defaultTab = 'dosing' }) => {
  const navigate = useNavigate();
  const [activeToolTab, setActiveToolTab] = useState(defaultTab);
  
  // Update active tab when defaultTab changes
  useEffect(() => {
    setActiveToolTab(defaultTab);
  }, [defaultTab]);
  
  // Handle tool tab changes with URL updates
  const handleToolTabChange = (tab) => {
    setActiveToolTab(tab);
    // Update URL for sub-level navigation
    let subPath = '/tools';
    if (tab === 'tracker') subPath = '/tools/daily-wellness-tracker';
    else if (tab === 'weight') subPath = '/tools/weight-tracker';
    else if (tab === 'period') subPath = '/tools/period-tracker';
    else if (tab === 'dosing') subPath = '/tools/dosing-calculator';
    else if (tab === 'product-log') subPath = '/tools/product-log';
    else if (tab === 'overview') subPath = '/tools';
    
    // Use pushState for within-section navigation
    window.history.pushState({}, '', subPath);
  };
  
  // Dosing Database states
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [dosingResults, setDosingResults] = useState([]);
  const [selectedCompound, setSelectedCompound] = useState(null);
  const [calculation, setCalculation] = useState({
    dose_mg: '',
    peptide_strength_mg: '',
    water_volume_ml: ''
  });
  const [result, setResult] = useState(null);
  
  // Calculator states
  const [saving, setSaving] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveName, setSaveName] = useState('');

  // Daily Tracker states
  const [trackerData, setTrackerData] = useState({
    // Radiant Body
    hotFlashes: 5,
    weightChanges: 5,
    skinHair: 5,
    // Balanced Mind
    moodSwings: 5,
    brainFog: 5,
    anxiety: 5,
    // Enduring Vitality
    sleepQuality: 5,
    fatigue: 5,
    libido: 5
  });
  const [lastSaved, setLastSaved] = useState(null);
  const [isSavingTracker, setIsSavingTracker] = useState(false);
  const [showTrackerHistory, setShowTrackerHistory] = useState(false);
  const [trackerHistory, setTrackerHistory] = useState([]);
  const [trackerDate, setTrackerDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    if (activeToolTab === 'tracker') {
      fetchTodayTracker();
    }
  }, [activeToolTab]);

  const fetchTodayTracker = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const response = await axios.get(`${API}/users/${user.id}/wellness-tracker/${today}`);
      if (response.data && response.data.data) {
        setTrackerData(response.data.data);
        setLastSaved(new Date(response.data.updated_at));
      }
    } catch (error) {
      console.error('Error fetching today tracker:', error);
    }
  };

  const saveTrackerData = async () => {
    setIsSavingTracker(true);
    try {
      const payload = {
        date: trackerDate,
        data: trackerData
      };
      
      const response = await axios.post(`${API}/users/${user.id}/wellness-tracker`, payload, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      
      if (response.data.success) {
        setLastSaved(new Date());
        toast.success(`Wellness tracking saved for ${trackerDate}!`);
        // Trigger refresh of progress dashboard by updating a timestamp
        setLastSaved(new Date());
      }
    } catch (error) {
      console.error('Error saving tracker:', error);
      toast.error('Error saving wellness tracking');
    } finally {
      setIsSavingTracker(false);
    }
  };

  const fetchTrackerHistory = async () => {
    try {
      const historyData = [];
      const today = new Date();
      
      // Get last 30 days of data
      for (let daysBack = 29; daysBack >= 0; daysBack--) {
        const checkDate = new Date(today);
        checkDate.setDate(today.getDate() - daysBack);
        const dateStr = checkDate.toISOString().split('T')[0];
        
        try {
          const response = await axios.get(`${API}/users/${user.id}/wellness-tracker/${dateStr}`);
          if (response.data && response.data.data) {
            historyData.push({
              date: dateStr,
              ...response.data
            });
          }
        } catch (error) {
          // Skip dates without data
        }
      }
      
      setTrackerHistory(historyData.reverse()); // Most recent first
    } catch (error) {
      console.error('Error fetching tracker history:', error);
    }
  };

  const updateTrackerValue = (key, value) => {
    setTrackerData(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const calculateDosage = async () => {
    try {
      const url = user?.id 
        ? `${API}/peptide-calculator?user_id=${user.id}`
        : `${API}/peptide-calculator`;
      const response = await axios.post(url, calculation);
      setResult(response.data);
    } catch (error) {
      console.error('Error calculating dosage:', error);
    }
  };

  const searchDosing = async (query) => {
    if (query.length < 2) {
      setDosingResults([]);
      return;
    }
    
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(`${API}/dosing-database/search?q=${encodeURIComponent(query)}`, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : ''
        }
      });
      setDosingResults(response.data.results);
    } catch (error) {
      console.error('Error searching dosing database:', error);
      if (error.response?.status === 403) {
        toast.error('Dosing calculator is available to members only. Please upgrade to access this feature.');
      }
    }
  };

  const selectCompound = (compound) => {
    setSelectedCompound(compound);
    setShowSearch(false);
    setSearchQuery(compound.compound);
    // Update URL for sharing without adding to history
    window.history.replaceState({}, '', `/dosing/${encodeURIComponent(compound.compound)}`);
    
    // Try to parse dosing range for auto-fill
    const dosing = compound.dosing_range.toLowerCase();
    if (dosing.includes('mg')) {
      const mgMatch = dosing.match(/(\d+(?:\.\d+)?)\s*mg/);
      if (mgMatch) {
        setCalculation({
          ...calculation,
          peptide_strength_mg: parseFloat(mgMatch[1])
        });
      }
    } else if (dosing.includes('mcg')) {
      const mcgMatch = dosing.match(/(\d+(?:\.\d+)?)\s*mcg/);
      if (mcgMatch) {
        setCalculation({
          ...calculation,
          dose_mg: parseFloat(mcgMatch[1]) / 1000 // Convert mcg to mg
        });
      }
    }
  };

  const saveCalculation = async () => {
    // Ensure we have a valid calculation first
    if (calculation.dose_mg <= 0 || calculation.peptide_strength_mg <= 0 || calculation.water_volume_ml <= 0) {
      toast.error('Please fill in all calculation fields before saving');
      return;
    }
    
    // If no result yet, calculate first
    if (!result) {
      console.log('No result found, calculating first...');
      try {
        const url = user?.id 
          ? `${API}/peptide-calculator?user_id=${user.id}`
          : `${API}/peptide-calculator`;
        const response = await axios.post(url, calculation);
        setResult(response.data);
        console.log('Calculation result:', response.data);
      } catch (error) {
        console.error('Error calculating dosage:', error);
        toast.error('Error calculating dosage. Please try again.');
        return;
      }
    }
    
    // Show modal to get name from user
    const defaultName = selectedCompound?.compound ? 
      `${selectedCompound.compound} - ${calculation.dose_mg}mg` : 
      `Custom - ${calculation.dose_mg}mg`;
    
    setSaveName(defaultName);
    setShowSaveModal(true);
  };

  const confirmSaveCalculation = async () => {
    if (!saveName.trim()) {
      toast.error('Please enter a name for this calculation');
      return;
    }

    if (saveName.length > 20) {
      toast.error('Name must be 20 characters or less');
      return;
    }
    
    setSaving(true);
    try {
      const authToken = localStorage.getItem('auth_token');
      const userData = localStorage.getItem('user_data');
      
      console.log('Save attempt - authToken:', !!authToken, 'userData:', !!userData);
      
      if (!authToken || !userData) {
        toast.error('Please log in to save calculations');
        setShowSaveModal(false);
        return;
      }

      const user = JSON.parse(userData);
      console.log('User ID:', user.id);

      // Ensure we have calculation results
      let currentResult = result;
      if (!currentResult) {
        console.log('No result found in confirmSaveCalculation, calculating now...');
        try {
          const url = user.id 
            ? `${API}/peptide-calculator?user_id=${user.id}`
            : `${API}/peptide-calculator`;
          const calcResponse = await axios.post(url, calculation);
          currentResult = calcResponse.data;
          setResult(currentResult);
        } catch (error) {
          console.error('Error calculating dosage during save:', error);
          toast.error('Error calculating dosage. Please try again.');
          setSaving(false);
          return;
        }
      }

      const savedData = {
        type: 'calculation',
        name: saveName.trim(),
        data: {
          peptide: selectedCompound?.compound || 'Custom',
          dosage: calculation.dose_mg,
          unit: 'mg',
          vialSize: calculation.peptide_strength_mg,
          waterAmount: calculation.water_volume_ml,
          tickMarks: currentResult.syringe_units.toFixed(1),
          concentration: currentResult.concentration_mg_per_ml.toFixed(2),
          totalDoses: currentResult.total_doses
        }
      };

      console.log('Saving to URL:', `${API}/users/${user.id}/saved-items`);
      console.log('Save data:', savedData);

      const response = await axios.post(`${API}/users/${user.id}/saved-items`, savedData, {
        headers: { Authorization: `Bearer ${authToken}` }
      });

      console.log('Save response:', response.data);

      if (response.data.success) {
        toast.success('Calculation saved successfully!');
        setShowSaveModal(false);
        setSaveName('');
      } else {
        toast.error(response.data.message || 'Error saving calculation');
      }
    } catch (error) {
      console.error('Detailed save error:', error);
      if (error.response) {
        console.error('Error response:', error.response.data);
        console.error('Error status:', error.response.status);
        toast.error(`Error saving: ${error.response.data.detail || error.response.statusText}`);
      } else {
        toast.error('Network error. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (calculation.dose_mg > 0 && calculation.peptide_strength_mg > 0 && calculation.water_volume_ml > 0) {
      calculateDosage();
    }
  }, [calculation]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchDosing(searchQuery);
    }, 300);
    
    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  return (
    <div data-testid="peptide-calculator" className="space-y-6">
      <h2 className="text-3xl font-bold text-burgundy font-serif">Wellness Tools</h2>
      
      {/* Tools Navigation */}
      <Tabs value={activeToolTab} onValueChange={handleToolTabChange} className="w-full">
        {/* Tools Landing Page */}
        {activeToolTab === 'overview' && (
          <div className="space-y-6">
            <p className="text-burgundy/70 text-base max-w-2xl">Track your wellness journey with our personalized tools — from daily symptom logging to weight and period tracking.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { key: 'tracker', title: 'Daily Wellness Tracker', desc: 'Log your daily symptoms across all three pillars — Body, Mind, and Vitality. See trends over time to understand what works for you.', img: '/icons/tools/daily wellness tracker neutral.png' },
                { key: 'weight', title: 'Weight Tracker', desc: 'Track weight changes alongside your wellness journey. Visualize trends and understand how your body is responding.', img: '/icons/tools/weight tracker neutral.png' },
                { key: 'period', title: 'Period Tracker', desc: 'Monitor your cycle patterns as they shift during perimenopause. Track flow, symptoms, and cycle length changes.', img: '/icons/tools/period tracker neutral.png' },
                ...(isMember(user?.subscription_status) ? [
                  { key: 'dosing', title: 'Dosing Calculator', desc: 'Professional peptide and supplement dosing calculator with our comprehensive compound database and safety protocols.', img: '/icons/tools/dosing and calculator neutral.png' },
                  { key: 'product-log', title: 'Product Log', desc: 'Keep track of all the supplements, peptides, and wellness products in your routine. Log doses and notes.', img: '/icons/tools/product log neutral.png' },
                ] : [])
              ].map(tool => (
                <button
                  key={tool.key}
                  onClick={() => handleToolTabChange(tool.key)}
                  className="bg-white rounded-xl border border-burgundy/10 hover:border-burgundy/25 hover:shadow-md transition-all p-5 text-left group"
                  data-testid={`tool-card-${tool.key}`}
                >
                  <img src={tool.img} alt={tool.title} className="w-full h-auto rounded-lg shadow-sm mb-4 group-hover:shadow-md transition-shadow" />
                  <h3 className="font-serif font-bold text-burgundy text-base mb-1.5">{tool.title}</h3>
                  <p className="text-burgundy/60 text-sm leading-relaxed">{tool.desc}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {activeToolTab !== 'overview' && (
          <>
            {/* Back to overview + Tab navigation */}
            <div className="flex items-center gap-3 mb-4">
              <button
                onClick={() => handleToolTabChange('overview')}
                className="text-burgundy/60 hover:text-burgundy text-sm font-medium flex items-center gap-1 transition-colors"
                data-testid="tools-back-overview"
              >
                ← All Tools
              </button>
            </div>
            <TabsList className="bg-transparent p-0 h-auto mb-8 w-full md:w-3/4" style={{ display: 'flex', flexWrap: 'wrap', gap: '0px' }}>
          <TabsTrigger 
            value="tracker" 
            className="bg-transparent border-none shadow-none hover:opacity-90 transition-opacity duration-200 h-auto data-[state=active]:bg-transparent data-[state=active]:shadow-none w-1/3 md:w-[18%]"
            style={{ padding: 0, margin: 0 }}
          >
            <img 
              src={activeToolTab === 'tracker' ? '/icons/tools/daily wellness tracker selected.png' : '/icons/tools/daily wellness tracker neutral.png'}
              alt="Daily Wellness Tracker"
              className="w-full h-auto rounded-lg shadow-md"
            />
          </TabsTrigger>
          <TabsTrigger 
            value="weight" 
            className="bg-transparent border-none shadow-none hover:opacity-90 transition-opacity duration-200 h-auto data-[state=active]:bg-transparent data-[state=active]:shadow-none w-1/3 md:w-[18%]"
            style={{ padding: 0, margin: 0 }}
          >
            <img 
              src={activeToolTab === 'weight' ? '/icons/tools/weight tracker selected.png' : '/icons/tools/weight tracker neutral.png'}
              alt="Weight Tracker"
              className="w-full h-auto rounded-lg shadow-md"
            />
          </TabsTrigger>
          <TabsTrigger 
            value="period" 
            className="bg-transparent border-none shadow-none hover:opacity-90 transition-opacity duration-200 h-auto data-[state=active]:bg-transparent data-[state=active]:shadow-none w-1/3 md:w-[18%]"
            style={{ padding: 0, margin: 0 }}
          >
            <img 
              src={activeToolTab === 'period' ? '/icons/tools/period tracker selected.png' : '/icons/tools/period tracker neutral.png'}
              alt="Period Tracker"
              className="w-full h-auto rounded-lg shadow-md"
            />
          </TabsTrigger>
          {/* Dosing Calculator - Members Only */}
          {isMember(user?.subscription_status) && (
            <TabsTrigger 
              value="dosing" 
              className="bg-transparent border-none shadow-none hover:opacity-90 transition-opacity duration-200 h-auto data-[state=active]:bg-transparent data-[state=active]:shadow-none w-1/3 md:w-[18%]"
              style={{ padding: 0, margin: 0 }}
            >
              <img 
                src={activeToolTab === 'dosing' ? '/icons/tools/dosing and calculator selected.png' : '/icons/tools/dosing and calculator neutral.png'}
                alt="Dosing & Calculator"
                className="w-full h-auto rounded-lg shadow-md"
              />
            </TabsTrigger>
          )}
          {/* Product Log - Members Only */}
          {isMember(user?.subscription_status) && (
            <TabsTrigger 
              value="product-log" 
              className="bg-transparent border-none shadow-none hover:opacity-90 transition-opacity duration-200 h-auto data-[state=active]:bg-transparent data-[state=active]:shadow-none w-1/3 md:w-[18%]"
              style={{ padding: 0, margin: 0 }}
            >
              <img 
                src={activeToolTab === 'product-log' ? '/icons/tools/product log selected.png' : '/icons/tools/product log neutral.png'}
                alt="Product Log"
                className="w-full h-auto rounded-lg shadow-md"
              />
            </TabsTrigger>
          )}

        </TabsList>

        <TabsContent value="dosing" className="space-y-6">
          {/* Dosing Database Search */}
          <Card className="bg-white border-burgundy/20">
            <CardHeader className="bg-gradient-to-r from-burgundy to-dark-teal text-cream">
              <CardTitle className="font-serif">Peptide / Nootropic / Supplement Dosing Database</CardTitle>
              <CardDescription className="text-cream/90">Search our comprehensive dosing guide. Click the result for more information.</CardDescription>
            </CardHeader>
        <CardContent className="pt-6">
          <div className="relative">
            <Input
              type="text"
              placeholder="Search compounds (e.g., Retatrutide, Semaglutide, BPC-157...)"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setShowSearch(e.target.value.length > 0);
              }}
              className="border-burgundy/30 focus:border-gold focus:ring-gold pr-10"
            />
            {searchQuery && (
              <Button
                variant="ghost"
                size="sm"
                className="absolute right-2 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0"
                onClick={() => {
                  setSearchQuery('');
                  setDosingResults([]);
                  setShowSearch(false);
                  setSelectedCompound(null);
                }}
              >
                ×
              </Button>
            )}
          </div>
          
          {showSearch && dosingResults.length > 0 && (
            <div className="mt-4 space-y-2 max-h-60 overflow-y-auto">
              {dosingResults.map((compound, index) => (
                <Card 
                  key={compound.compound || `dosing-${index}`}
                  className="p-3 cursor-pointer hover:bg-burgundy/5 border-burgundy/20 transition-colors"
                  onClick={() => selectCompound(compound)}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-semibold text-burgundy">{compound.compound}</h4>
                      <p className="text-sm text-burgundy/70">Dosing: {compound.dosing_range}</p>
                      <p className="text-xs text-burgundy/60">{compound.uses}</p>
                    </div>
                    <Badge variant="outline" className="border-gold text-gold">
                      {compound.category}
                    </Badge>
                  </div>
                </Card>
              ))}
            </div>
          )}
          
          {selectedCompound && (
            <div className="mt-4 p-4 bg-burgundy/5 border border-burgundy/20 rounded-lg">
              <div className="flex justify-between items-start mb-3">
                <h4 className="font-semibold text-burgundy text-lg">{selectedCompound.compound}</h4>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(window.location.href);
                    toast.success('Dosing info link copied to clipboard!');
                  }}
                  className="h-7 text-xs"
                  title="Copy dosing info link"
                >
                  <Link2 className="h-3 w-3 mr-1" />
                  Share
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-burgundy/80">Dosing Range:</span>
                  <p className="text-burgundy/70">{selectedCompound.dosing_range}</p>
                </div>
                <div>
                  <span className="font-medium text-burgundy/80">Frequency:</span>
                  <p className="text-burgundy/70">{selectedCompound.frequency}</p>
                </div>
                <div>
                  <span className="font-medium text-burgundy/80">Cycle Length:</span>
                  <p className="text-burgundy/70">{selectedCompound.cycle_length}</p>
                </div>
                <div>
                  <span className="font-medium text-burgundy/80">Time Off Between Cycles:</span>
                  <p className="text-burgundy/70">{selectedCompound.time_off || 'N/A'}</p>
                </div>
                <div>
                  <span className="font-medium text-burgundy/80">Schedule:</span>
                  <p className="text-burgundy/70">{selectedCompound.schedule || 'Daily'}</p>
                </div>
                <div>
                  <span className="font-medium text-burgundy/80">Fasted:</span>
                  <p className="text-burgundy/70">{selectedCompound.fasted}</p>
                </div>
                <div>
                  <span className="font-medium text-burgundy/80">Time of Day:</span>
                  <p className="text-burgundy/70">{selectedCompound.time_of_day || selectedCompound.timing || 'N/A'}</p>
                </div>
                <div>
                  <span className="font-medium text-burgundy/80">Reconstitution:</span>
                  <p className="text-burgundy/70">{selectedCompound.reconstitution || 'N/A'}</p>
                </div>
              </div>
              
              {/* Titration Strategy */}
              {selectedCompound.titration && (
                <div className="mt-4 p-3 bg-gold/10 border border-gold/30 rounded-lg">
                  <span className="font-medium text-burgundy/80 block mb-1">Titration Strategy:</span>
                  <p className="text-sm text-burgundy/70 leading-relaxed">{selectedCompound.titration}</p>
                </div>
              )}
              
              {/* Uses */}
              {selectedCompound.uses && (
                <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <span className="font-medium text-emerald-800 block mb-1">Uses & Benefits:</span>
                  <p className="text-sm text-emerald-700 leading-relaxed">{selectedCompound.uses}</p>
                </div>
              )}
              
              {/* Watchouts */}
              {selectedCompound.watchouts && selectedCompound.watchouts.trim() && (
                <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                  <span className="font-medium text-orange-800 block mb-1">⚠️ Watchouts & Precautions:</span>
                  <p className="text-sm text-orange-700 leading-relaxed">{selectedCompound.watchouts}</p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <Card className="bg-white border-burgundy/20">
          <CardHeader className="bg-gradient-to-r from-burgundy to-dark-teal text-cream">
            <CardTitle className="font-serif">Peptide / Nootropic / Supplement Calculator</CardTitle>
            <CardDescription className="text-cream/90">Enter your specifications</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6 pt-6">
            <div>
              <label className="block text-sm font-medium text-burgundy mb-2">
                Desired Dose (mg)
              </label>
              <Input
                data-testid="dose-input"
                type="text"
                inputMode="decimal"
                step="0.01"
                value={calculation.dose_mg}
                onChange={(e) => {
                  const val = e.target.value;
                  // Allow empty, numbers, and decimals including leading dot
                  if (val === '' || /^\.?\d*\.?\d*$/.test(val)) {
                    setCalculation({...calculation, dose_mg: val === '' ? '' : val});
                  }
                }}
                onBlur={(e) => {
                  // Convert to number on blur, handling empty and decimal-only strings
                  const val = e.target.value;
                  const num = parseFloat(val) || 0;
                  setCalculation({...calculation, dose_mg: num});
                }}
                className="border-burgundy/30 focus:border-gold focus:ring-gold"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-burgundy mb-2">
                Peptide Vial Strength (mg)
              </label>
              <Input
                data-testid="strength-input"
                type="text"
                inputMode="decimal"
                value={calculation.peptide_strength_mg}
                onChange={(e) => {
                  const val = e.target.value;
                  // Allow empty, numbers, and decimals including leading dot
                  if (val === '' || /^\.?\d*\.?\d*$/.test(val)) {
                    setCalculation({...calculation, peptide_strength_mg: val === '' ? '' : val});
                  }
                }}
                onBlur={(e) => {
                  // Convert to number on blur, handling empty and decimal-only strings
                  const val = e.target.value;
                  const num = parseFloat(val) || 0;
                  setCalculation({...calculation, peptide_strength_mg: num});
                }}
                className="border-burgundy/30 focus:border-gold focus:ring-gold"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-burgundy mb-2">
                BAC Water Volume (mL)
              </label>
              <Input
                data-testid="water-input"
                type="text"
                inputMode="decimal"
                step="0.1"
                value={calculation.water_volume_ml}
                onChange={(e) => {
                  const val = e.target.value;
                  // Allow empty, numbers, and decimals including leading dot
                  if (val === '' || /^\.?\d*\.?\d*$/.test(val)) {
                    setCalculation({...calculation, water_volume_ml: val === '' ? '' : val});
                  }
                }}
                onBlur={(e) => {
                  // Convert to number on blur, handling empty and decimal-only strings
                  const val = e.target.value;
                  const num = parseFloat(val) || 0;
                  setCalculation({...calculation, water_volume_ml: num});
                }}
                className="border-burgundy/30 focus:border-gold focus:ring-gold"
              />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-burgundy/20">
          <CardHeader className="bg-gradient-to-r from-gold to-gold/80 text-burgundy">
            <CardTitle className="font-serif">Results</CardTitle>
            <CardDescription className="text-burgundy/80">Your calculated dosage information</CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            {result ? (
              <div className="space-y-6">
                <div className="bg-gradient-to-r from-burgundy to-dark-teal p-6 rounded-xl text-cream">
                  <h3 className="font-semibold text-gold mb-2 font-serif">Syringe Units to Draw</h3>
                  <p className="text-3xl font-bold">{result.syringe_units.toFixed(1)} units</p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-cream p-4 rounded-lg border border-burgundy/20">
                    <h4 className="font-medium text-burgundy font-serif">Concentration</h4>
                    <p className="text-lg font-semibold text-dark-teal">{result.concentration_mg_per_ml.toFixed(2)} mg/mL</p>
                  </div>
                  <div className="bg-cream p-4 rounded-lg border border-burgundy/20">
                    <h4 className="font-medium text-burgundy font-serif">Total Doses</h4>
                    <p className="text-lg font-semibold text-dark-teal">{result.total_doses}</p>
                  </div>
                </div>
                
                {/* Visual Syringe Guide */}
                <div className="bg-gradient-to-r from-gold/20 to-gold/10 p-6 rounded-xl border border-gold/30">
                  <h4 className="font-medium text-burgundy mb-3 font-serif">Visual Syringe Guide</h4>
                  <div className="relative">
                    <div className="w-full h-8 bg-white border-2 border-burgundy/30 rounded-lg relative overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-burgundy to-dark-teal transition-all duration-500"
                        style={{ width: `${Math.min(result.syringe_units, 100)}%` }}
                      />
                      <div className="absolute inset-0 flex items-center justify-center text-sm font-medium text-burgundy">
                        {result.syringe_units.toFixed(1)} / 100 units
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Save Calculation Button */}
                <div className="text-center">
                  <Button
                    onClick={saveCalculation}
                    disabled={saving}
                    className="bg-gradient-to-r from-gold to-gold-600 hover:from-gold-600 hover:to-gold-700 text-burgundy font-semibold px-6 py-2"
                  >
                    {saving ? 'Saving...' : 'Save This Calculation'}
                  </Button>
                  {selectedCompound && (
                    <p className="text-xs text-burgundy/60 mt-2">
                      Save as: {selectedCompound.compound} - {calculation.peptide_strength_mg}mg vial, {calculation.water_volume_ml}ml BAC, {calculation.dose_mg}mg dose, draw {result?.syringe_units.toFixed(1)} units
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center text-burgundy/50 py-8">
                <Calculator className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>Enter parameters to calculate results</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Save Calculation Modal */}
      {showSaveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md bg-white border-burgundy/20">
            <CardHeader>
              <CardTitle className="text-burgundy font-serif">Save Calculation</CardTitle>
              <CardDescription>Give this calculation a memorable name</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-burgundy mb-2">
                  Calculation Name (max 20 characters)
                </label>
                <Input
                  value={saveName}
                  onChange={(e) => setSaveName(e.target.value)}
                  placeholder="e.g., Morning NAD+"
                  maxLength={20}
                  className="border-burgundy/30 focus:border-gold"
                />
                <p className="text-xs text-burgundy/50 mt-1">
                  {saveName.length}/20 characters
                </p>
              </div>
              
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => setShowSaveModal(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={confirmSaveCalculation}
                  disabled={saving}
                  className="flex-1 bg-burgundy hover:bg-burgundy/80 text-white"
                >
                  {saving ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
        </TabsContent>

        <TabsContent value="tracker" className="space-y-6">
          <Card className="bg-white border-burgundy/20">
            <CardHeader className="bg-gradient-to-r from-burgundy to-dark-teal text-cream">
              <CardTitle className="font-serif">Daily Wellness Tracker</CardTitle>
              <CardDescription className="text-cream/90">Track your journey across the three pillars of wellness</CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              <style jsx>{`
                .slider-emerald::-webkit-slider-thumb {
                  appearance: none;
                  width: 20px;
                  height: 20px;
                  border-radius: 50%; 
                  background: #134e4a;
                  cursor: pointer;
                  border: 2px solid white;
                  box-shadow: 0 0 0 1px rgba(19, 78, 74, 0.3);
                }
                .slider-emerald::-moz-range-thumb {
                  width: 20px;
                  height: 20px;
                  border-radius: 50%;
                  background: #134e4a;
                  cursor: pointer;
                  border: 2px solid white;
                  box-shadow: 0 0 0 1px rgba(19, 78, 74, 0.3);
                }
                .slider-emerald:focus::-webkit-slider-thumb {
                  outline: 2px solid #134e4a;
                  outline-offset: 2px;
                }
              `}</style>
              <div className="space-y-8">
                
                {/* Radiant Body */}
                <div className="space-y-4">
                  <h3 className="text-xl font-semibold text-burgundy font-serif flex items-center">
                    <Sparkles className="h-5 w-5 mr-2" />
                    Radiant Body
                  </h3>
                  <p className="text-sm text-burgundy/70 mb-4">Track physical shifts and sensations — how your body feels day to day.</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-burgundy">Hot Flashes</label>
                      <div className="px-0 py-2">
                        <input
                          type="range"
                          min="1"
                          max="10"
                          value={trackerData.hotFlashes}
                          onChange={(e) => updateTrackerValue('hotFlashes', parseInt(e.target.value))}
                          className="w-full h-2 bg-burgundy/20 rounded-lg appearance-none cursor-pointer slider-emerald"
                        />
                        <div className="flex justify-between text-xs text-burgundy/60 mt-1">
                          <span>Lots of heat</span>
                          <span className="font-bold text-burgundy">{trackerData.hotFlashes}/10</span>
                          <span>No heat</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-burgundy">Weight</label>
                      <div className="px-0 py-2">
                        <input
                          type="range"
                          min="1"
                          max="10"
                          value={trackerData.weightChanges}
                          onChange={(e) => updateTrackerValue('weightChanges', parseInt(e.target.value))}
                          className="w-full h-2 bg-burgundy/20 rounded-lg appearance-none cursor-pointer slider-emerald"
                        />
                        <div className="flex justify-between text-xs text-burgundy/60 mt-1">
                          <span>Gaining</span>
                          <span className="font-bold text-burgundy">{trackerData.weightChanges}/10</span>
                          <span>Losing</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-burgundy">Skin & Hair</label>
                      <div className="px-0 py-2">
                        <input
                          type="range"
                          min="1"
                          max="10"
                          value={trackerData.skinHair}
                          onChange={(e) => updateTrackerValue('skinHair', parseInt(e.target.value))}
                          className="w-full h-2 bg-burgundy/20 rounded-lg appearance-none cursor-pointer slider-emerald"
                        />
                        <div className="flex justify-between text-xs text-burgundy/60 mt-1">
                          <span>Dull</span>
                          <span className="font-bold text-burgundy">{trackerData.skinHair}/10</span>
                          <span>Glowing</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Balanced Mind */}
                <div className="space-y-4">
                  <h3 className="text-xl font-semibold text-burgundy font-serif flex items-center">
                    <Heart className="h-5 w-5 mr-2" />
                    Balanced Mind
                  </h3>
                  <p className="text-sm text-burgundy/70 mb-4">Capture your emotional equilibrium.</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-burgundy">Mood</label>
                      <div className="px-0 py-2">
                        <input
                          type="range"
                          min="1"
                          max="10"
                          value={trackerData.moodSwings}
                          onChange={(e) => updateTrackerValue('moodSwings', parseInt(e.target.value))}
                          className="w-full h-2 bg-burgundy/20 rounded-lg appearance-none cursor-pointer slider-emerald"
                        />
                        <div className="flex justify-between text-xs text-burgundy/60 mt-1">
                          <span>Bad mood</span>
                          <span className="font-bold text-burgundy">{trackerData.moodSwings}/10</span>
                          <span>Good mood</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-burgundy">Mental Clarity</label>
                      <div className="px-0 py-2">
                        <input
                          type="range"
                          min="1"
                          max="10"
                          value={trackerData.brainFog}
                          onChange={(e) => updateTrackerValue('brainFog', parseInt(e.target.value))}
                          className="w-full h-2 bg-burgundy/20 rounded-lg appearance-none cursor-pointer slider-emerald"
                        />
                        <div className="flex justify-between text-xs text-burgundy/60 mt-1">
                          <span>Foggy</span>
                          <span className="font-bold text-burgundy">{trackerData.brainFog}/10</span>
                          <span>Clear</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-burgundy">Anxiety</label>
                      <div className="px-0 py-2">
                        <input
                          type="range"
                          min="1"
                          max="10"
                          value={trackerData.anxiety}
                          onChange={(e) => updateTrackerValue('anxiety', parseInt(e.target.value))}
                          className="w-full h-2 bg-burgundy/20 rounded-lg appearance-none cursor-pointer slider-emerald"
                        />
                        <div className="flex justify-between text-xs text-burgundy/60 mt-1">
                          <span>Anxious</span>
                          <span className="font-bold text-burgundy">{trackerData.anxiety}/10</span>
                          <span>Calm</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Enduring Vitality */}
                <div className="space-y-4">
                  <h3 className="text-xl font-semibold text-burgundy font-serif flex items-center">
                    <TrendingUp className="h-5 w-5 mr-2" />
                    Enduring Vitality
                  </h3>
                  <p className="text-sm text-burgundy/70 mb-4">Measure energy, drive, and rest — your overall life force.</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-burgundy">Sleep Quality</label>
                      <div className="px-0 py-2">
                        <input
                          type="range"
                          min="1"
                          max="10"
                          value={trackerData.sleepQuality}
                          onChange={(e) => updateTrackerValue('sleepQuality', parseInt(e.target.value))}
                          className="w-full h-2 bg-burgundy/20 rounded-lg appearance-none cursor-pointer slider-emerald"
                        />
                        <div className="flex justify-between text-xs text-burgundy/60 mt-1">
                          <span>Tired</span>
                          <span className="font-bold text-burgundy">{trackerData.sleepQuality}/10</span>
                          <span>Rested</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-burgundy">Energy</label>
                      <div className="px-0 py-2">
                        <input
                          type="range"
                          min="1"
                          max="10"
                          value={trackerData.fatigue}
                          onChange={(e) => updateTrackerValue('fatigue', parseInt(e.target.value))}
                          className="w-full h-2 bg-burgundy/20 rounded-lg appearance-none cursor-pointer slider-emerald"
                        />
                        <div className="flex justify-between text-xs text-burgundy/60 mt-1">
                          <span>Exhausted</span>
                          <span className="font-bold text-burgundy">{trackerData.fatigue}/10</span>
                          <span>Energetic</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-burgundy">Libido</label>
                      <div className="px-0 py-2">
                        <input
                          type="range"
                          min="1"
                          max="10"
                          value={trackerData.libido}
                          onChange={(e) => updateTrackerValue('libido', parseInt(e.target.value))}
                          className="w-full h-2 bg-burgundy/20 rounded-lg appearance-none cursor-pointer slider-emerald"
                        />
                        <div className="flex justify-between text-xs text-burgundy/60 mt-1">
                          <span>No thanks</span>
                          <span className="font-bold text-burgundy">{trackerData.libido}/10</span>
                          <span>Va voom</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Save Button and Status */}
                <div className="pt-6 border-t border-burgundy/20">
                  <div className="mb-3 flex flex-col sm:flex-row sm:items-center gap-2">
                    <div className="flex items-center gap-2">
                      <label className="text-xs sm:text-sm font-medium text-burgundy whitespace-nowrap">Date:</label>
                      <Input
                        type="date"
                        value={trackerDate}
                        onChange={(e) => setTrackerDate(e.target.value)}
                        className="text-xs sm:text-sm w-32 sm:w-auto border-burgundy/30 focus:border-gold py-1 px-2"
                      />
                    </div>
                    <span className="hidden sm:inline text-xs text-burgundy/60">Select a date to add or update wellness data</span>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
                    <div className="text-xs sm:text-sm text-burgundy/70 hidden sm:block">
                      {lastSaved && (
                        <span>Last saved: {lastSaved.toLocaleString()}</span>
                      )}
                    </div>
                    <div className="flex gap-2 sm:gap-3">
                      <Button
                        onClick={() => {
                          setShowTrackerHistory(!showTrackerHistory);
                        }}
                        variant="outline"
                        className="border-burgundy text-burgundy hover:bg-burgundy hover:text-white text-xs sm:text-sm px-3 py-2"
                      >
                        {showTrackerHistory ? 'Hide' : 'View History'}
                      </Button>
                      <Button
                        onClick={saveTrackerData}
                        disabled={isSavingTracker}
                        className="bg-burgundy hover:bg-burgundy/80 text-white text-xs sm:text-sm px-3 py-2"
                      >
                        {isSavingTracker ? 'Saving...' : 'Save'}
                      </Button>
                    </div>
                  </div>

                  {/* Progress Dashboard */}
                  {showTrackerHistory && (
                    <ProgressDashboard user={user} refreshTrigger={lastSaved} />
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="weight" className="space-y-6">
          <WeightTracker user={user} />
        </TabsContent>

        <TabsContent value="period" className="space-y-6">
          <PeriodTracker user={user} />
        </TabsContent>

        <TabsContent value="product-log" className="space-y-6">
          <ProductLog user={user} />
        </TabsContent>

          </>
        )}
      </Tabs>
    </div>
  );
};


// Events Section Component
const EventsSection = () => {
  const [events, setEvents] = useState([]);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/events`);
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  return (
    <div data-testid="events-section" className="space-y-6">
      <h2 className="text-3xl font-bold text-burgundy font-serif">Exclusive Events</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {events.length === 0 ? (
          <div className="col-span-full text-center py-12">
            <Calendar className="h-16 w-16 text-burgundy/30 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-burgundy/60 mb-2 font-serif">Upcoming Events</h3>
            <p className="text-burgundy/50">Premium events and workshops coming soon!</p>
          </div>
        ) : (
          events.map((event) => (
            <Card key={event.id} className="hover:shadow-lg transition-all duration-300 bg-white border-burgundy/20">
              <CardHeader>
                <CardTitle className="text-lg font-serif text-burgundy">{event.title}</CardTitle>
                <CardDescription className="text-burgundy/70">
                  {new Date(event.date).toLocaleDateString()} at {new Date(event.date).toLocaleTimeString()}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-burgundy/70 mb-4">{event.description}</p>
                <div className="flex justify-between items-center">
                  <Badge variant="outline" className="border-gold text-gold">
                    {event.duration_minutes} minutes
                  </Badge>
                  <Button data-testid={`register-event-${event.id}`} size="sm" className="bg-burgundy hover:bg-burgundy/80">
                    Register
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

// Shop Section Component
const ShopSection = ({ user, productToOpen, setProductToOpen, setShowUpgradeModal, setUpgradeFeature }) => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [selectedBenefit, setSelectedBenefit] = useState('');
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showProductModal, setShowProductModal] = useState(false);
  const [productTypes, setProductTypes] = useState([]);
  const [productBenefits, setProductBenefits] = useState([]);
  const [categories, setCategories] = useState({});
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showLocalUpgradeModal, setShowLocalUpgradeModal] = useState(false);
  const [upgradeFeatureName, setUpgradeFeatureName] = useState('');

  // Track screen size for responsive recently viewed
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchProducts();
    fetchFeaturedProducts();
    fetchTypesAndBenefits();
    fetchMarketplaceCategories();
    if (user?.id) {
      fetchUserFavorites();
      fetchRecentlyViewed();
    }
  }, [user?.id]);

  // Handle browser back button to close product modal
  useEffect(() => {
    const handlePopState = () => {
      const path = window.location.pathname;
      if (path === '/marketplace' && showProductModal) {
        setShowProductModal(false);
        setSelectedProduct(null);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [showProductModal]);

  // Handle productToOpen from deep linking
  useEffect(() => {
    if (productToOpen && products.length > 0) {
      const product = products.find(p => p.id === productToOpen);
      if (product) {
        handleProductClick(product);
      }
      // Clear productToOpen after processing
      setProductToOpen(null);
    }
  }, [productToOpen, products]);

  // Filter products when category/type/benefit changes
  useEffect(() => {
    let filtered = products;
    
    // DON'T filter out member_only products - they should be visible but locked
    // The UI will show a lock icon and prevent access for non-members
    
    if (selectedCategory) {
      filtered = filtered.filter(product => product.category === selectedCategory);
    }
    if (selectedType) {
      filtered = filtered.filter(product => product.type === selectedType);
    }
    if (selectedBenefit) {
      filtered = filtered.filter(product => product.benefit === selectedBenefit);
    }
    setFilteredProducts(filtered);
  }, [products, selectedCategory, selectedType, selectedBenefit]);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data || []);
      setFilteredProducts(response.data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Error loading products');
    } finally {
      setLoading(false);
    }
  };

  const fetchFeaturedProducts = async () => {
    try {
      const response = await axios.get(`${API}/products?featured_only=true`);
      // Show all featured products including member_only ones
      // The UI will show a lock icon for member_only products
      setFeaturedProducts(response.data || []);
    } catch (error) {
      console.error('Error fetching featured products:', error);
    }
  };

  const fetchTypesAndBenefits = async () => {
    try {
      const [typesResponse, benefitsResponse] = await Promise.all([
        axios.get(`${API}/admin/product-types`),
        axios.get(`${API}/admin/product-benefits`)
      ]);
      setProductTypes(typesResponse.data || []);
      setProductBenefits(benefitsResponse.data || []);
    } catch (error) {
      console.error('Error fetching types and benefits:', error);
    }
  };

  const fetchMarketplaceCategories = async () => {
    try {
      const response = await axios.get(`${API}/admin/marketplace-categories`);
      setCategories(response.data || {});
    } catch (error) {
      console.error('Error fetching marketplace categories:', error);
      // Set default categories if fetch fails
      setCategories({
        body: { name: 'Body', subcategories: [] },
        mind: { name: 'Mind', subcategories: [] },
        vitality: { name: 'Vitality', subcategories: [] }
      });
    }
  };

  const fetchUserFavorites = async () => {
    if (!user?.id) return;
    
    try {
      const authToken = localStorage.getItem('auth_token');
      const response = await axios.get(`${API}/users/${user.id}/saved-items`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      
      // Filter for product type saved items and convert to favorites format
      const productSaves = response.data.filter(item => item.type === 'product');
      const favoritesData = productSaves.map(save => ({
        id: save.data.product_id,
        name: save.name,
        ...save.data
      }));
      
      setFavorites(favoritesData);
    } catch (error) {
      console.error('Error fetching favorites:', error);
    }
  };

  const fetchRecentlyViewed = async () => {
    if (!user?.id) return;
    try {
      const response = await axios.get(`${API}/user/${user.id}/recently-viewed`);
      setRecentlyViewed(response.data || []);
    } catch (error) {
      console.error('Error fetching recently viewed:', error);
    }
  };

  const handleProductClick = async (product) => {
    // Check if product is member-only and user is not a member
    // Use the global isMember function for consistency
    const isCurrentMember = isMember(user?.subscription_status);
    
    if (product.member_only && !isCurrentMember) {
      // Show local upgrade modal that overlays the marketplace
      setUpgradeFeatureName(product.name);
      setShowLocalUpgradeModal(true);
      return;
    }
    
    // Track view for ALL logged-in users (including trial, forever_free, etc.)
    if (user?.id) {
      try {
        await axios.post(`${API}/products/${product.id}/view?user_id=${user.id}`);
      } catch (error) {
        console.error('Error tracking view:', error);
      }
      trackContentView('product', product.id, product.name, localStorage.getItem('token'));
    }
    
    // Open product modal
    setSelectedProduct(product);
    setShowProductModal(true);
    // Push URL so back button can close the modal
    window.history.pushState({ productOpen: true }, '', `/marketplace/${product.id}`);
    
    // Refresh recently viewed for ALL logged-in users
    if (user?.id) {
      fetchRecentlyViewed();
    }
  };

  const handleAffiliateClick = async (product) => {
    // Check if product is member-only and user is not a member
    // Use the global isMember function for consistency
    const isCurrentMember = isMember(user?.subscription_status);
    
    if (product.member_only && !isCurrentMember) {
      // Show local upgrade modal that overlays the marketplace
      setUpgradeFeatureName(product.name);
      setShowLocalUpgradeModal(true);
      return;
    }
    
    try {
      // Track click
      await axios.post(`${API}/products/${product.id}/click`);
      
      // Open affiliate link
      window.open(product.link, '_blank');
      
      toast.success('Opening product page...');
    } catch (error) {
      console.error('Error tracking click:', error);
      // Still open the link even if tracking fails
      window.open(product.link, '_blank');
    }
  };

  const toggleFavorite = async (product) => {
    if (!user?.id) {
      toast.error('Please log in to save favorites');
      return;
    }

    try {
      // Use the new saved items system instead of the old favorites system
      const authToken = localStorage.getItem('auth_token');
      
      // Check if already saved
      const isCurrentlyFavorited = isProductFavorited(product.id);
      
      if (isCurrentlyFavorited) {
        // Remove from saved items
        const savedItemsResponse = await axios.get(`${API}/users/${user.id}/saved-items`, {
          headers: { Authorization: `Bearer ${authToken}` }
        });
        
        const savedProduct = savedItemsResponse.data.find(item => 
          item.type === 'product' && item.data.product_id === product.id
        );
        
        if (savedProduct) {
          await axios.delete(`${API}/users/${user.id}/saved-items/${savedProduct.id}`, {
            headers: { Authorization: `Bearer ${authToken}` }
          });
          toast.success('Removed from favorites');
        }
      } else {
        // Add to saved items
        const response = await axios.post(`${API}/products/${product.id}/save`, {
          user_id: user.id
        }, {
          headers: { Authorization: `Bearer ${authToken}` }
        });

        if (response.data.success) {
          toast.success('Added to favorites!');
        } else {
          toast.error(response.data.message || 'Product already saved');
        }
      }
      
      // Refresh favorites list
      fetchUserFavorites();
    } catch (error) {
      console.error('Error toggling favorite:', error);
      toast.error('Error updating favorites');
    }
  };

  const isProductFavorited = (productId) => {
    return favorites.some(fav => fav.id === productId || fav.product_id === productId);
  };

  const handleCategorySelect = (category) => {
    // Toggle: if clicking the same category, deselect it
    if (selectedCategory === category) {
      setSelectedCategory('');
    } else {
      setSelectedCategory(category);
    }
  };

  const handleTypeSelect = (type) => {
    // Toggle: if clicking the same type, deselect it
    if (selectedType === type) {
      setSelectedType('');
    } else {
      setSelectedType(type);
    }
  };

  const handleBenefitSelect = (benefit) => {
    // Toggle: if clicking the same benefit, deselect it
    if (selectedBenefit === benefit) {
      setSelectedBenefit('');
    } else {
      setSelectedBenefit(benefit);
    }
  };

  const clearFilters = () => {
    setSelectedCategory('');
    setSelectedType('');
    setSelectedBenefit('');
  };

  return (
    <div data-testid="shop-section" className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-bold text-burgundy font-serif">Curated Marketplace</h2>
        
        {/* Filter Controls */}
        <div className="flex items-center space-x-4">
          {(selectedCategory || selectedType || selectedBenefit) && (
            <Button
              variant="outline"
              onClick={clearFilters}
              className="border-burgundy/30 text-burgundy hover:bg-burgundy/10"
            >
              Clear Filters
            </Button>
          )}
        </div>
      </div>

      {/* Category Filter Buttons */}
      <div className="flex flex-wrap gap-3">
        {Object.entries(categories).map(([key, category]) => (
          <Button
            key={key}
            variant={selectedCategory === key ? "default" : "outline"}
            onClick={() => handleCategorySelect(key)}
            className={selectedCategory === key 
              ? "bg-burgundy text-cream hover:bg-burgundy/80" 
              : "border-burgundy/30 text-burgundy hover:bg-burgundy/10"
            }
          >
            {category.name}
          </Button>
        ))}
      </div>

      {/* Type Filter Buttons */}
      {productTypes.length > 0 && (
        <div className="flex flex-wrap gap-2">
          <span className="text-sm font-medium text-burgundy/70 self-center mr-2">
            Product Type:
          </span>
          {productTypes.map((type) => (
            <Button
              key={type.id}
              variant={selectedType === type.name ? "default" : "outline"}
              size="sm"
              onClick={() => handleTypeSelect(type.name)}
              className={selectedType === type.name
                ? "bg-dark-teal text-cream hover:bg-dark-teal/80"
                : "border-dark-teal/30 text-dark-teal hover:bg-dark-teal/10"
              }
            >
              {type.name}
            </Button>
          ))}
        </div>
      )}

      {/* Benefit Filter Buttons */}
      {productBenefits.length > 0 && (
        <div className="flex flex-wrap gap-2">
          <span className="text-sm font-medium text-burgundy/70 self-center mr-2">
            Benefit:
          </span>
          {productBenefits.map((benefit) => (
            <Button
              key={benefit.id}
              variant={selectedBenefit === benefit.name ? "default" : "outline"}
              size="sm"
              onClick={() => handleBenefitSelect(benefit.name)}
              className={selectedBenefit === benefit.name
                ? "bg-gold-dark text-white hover:bg-gold-dark/80"
                : "border-gold-dark/50 text-gold-dark hover:bg-gold/10"
              }
            >
              {benefit.name}
            </Button>
          ))}
        </div>
      )}

      {/* Featured Products Section */}
      {!selectedCategory && !selectedType && !selectedBenefit && featuredProducts.length > 0 && (
        <Card className="bg-gradient-to-r from-gold/20 to-burgundy/10 border-gold/30">
          <CardHeader>
            <CardTitle className="font-serif text-burgundy flex items-center">
              <Star className="h-5 w-5 mr-2 text-gold" />
              Featured Products
            </CardTitle>
            <CardDescription>Hand-picked premium wellness products</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {featuredProducts.slice(0, 3).map((product) => {
                const isCurrentMember = isMember(user?.subscription_status);
                const isLocked = product.member_only && !isCurrentMember;
                
                return (
                <div 
                  key={product.id} 
                  className="bg-white rounded-lg p-4 border border-burgundy/20 cursor-pointer hover:shadow-md transition-shadow relative overflow-hidden"
                  onClick={() => handleProductClick(product)}
                >
                  {/* Blur overlay for locked products - clicking opens upgrade modal */}
                  {isLocked && (
                    <div 
                      className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white/60 backdrop-blur-sm rounded-lg cursor-pointer hover:bg-white/70 transition-colors"
                      onClick={() => {
                        setUpgradeFeatureName(product.name);
                        setShowLocalUpgradeModal(true);
                      }}
                    >
                      <Lock className="h-10 w-10 text-burgundy mb-2" />
                      <span className="text-burgundy font-semibold text-sm">Members Only</span>
                    </div>
                  )}
                  <div className={`relative ${isLocked ? 'blur-md' : ''}`}>
                    {product.image_url && (
                      <img 
                        src={product.image_url} 
                        alt={product.name}
                        className="w-full h-32 object-cover rounded-lg mb-3"
                        loading="lazy"
                      />
                    )}
                  </div>
                  <div className={isLocked ? 'blur-md' : ''}>
                    <h4 className="font-semibold text-gray-800 mb-1">{product.name}</h4>
                    <div className="flex justify-between items-center">
                      {product.price > 0 && (
                        <span className="text-red-600 font-bold">${product.price}</span>
                      )}
                      <Badge className="bg-gold/20 text-gold border-gold/30">Featured</Badge>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recently Viewed Products */}
      {user && recentlyViewed.length > 0 && (
        <Card className="bg-white border-burgundy/20">
          <CardHeader>
            <CardTitle className="font-serif text-burgundy flex items-center">
              <Clock className="h-5 w-5 mr-2 text-burgundy" />
              Recently Viewed
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {recentlyViewed.slice(0, isMobile ? 2 : 6).map((product) => {
                const isCurrentMember = isMember(user?.subscription_status);
                const isLocked = product.member_only && !isCurrentMember;
                
                return (
                <div key={product.id} className="relative overflow-hidden rounded-lg">
                  {/* Blur overlay for locked products - clicking opens upgrade modal */}
                  {isLocked && (
                    <div 
                      className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white/60 backdrop-blur-sm rounded-lg cursor-pointer hover:bg-white/70 transition-colors"
                      onClick={() => {
                        setUpgradeFeatureName(product.name);
                        setShowLocalUpgradeModal(true);
                      }}
                    >
                      <Lock className="h-6 w-6 text-burgundy mb-1" />
                      <span className="text-burgundy font-semibold text-xs">Members Only</span>
                    </div>
                  )}
                  <div 
                    className={`cursor-pointer hover:opacity-80 transition-opacity ${isLocked ? 'blur-md' : ''}`}
                    onClick={() => handleProductClick(product)}
                  >
                    <div className="aspect-square bg-gray-100 rounded-lg mb-2 overflow-hidden relative">
                      {product.image_url ? (
                        <img 
                          src={product.image_url} 
                          alt={product.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-burgundy/10">
                          <ShoppingBag className="h-8 w-8 text-burgundy/40" />
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-gray-800 font-medium line-clamp-2">{product.name}</p>
                    {product.price > 0 && (
                      <p className="text-xs text-burgundy">${product.price}</p>
                    )}
                  </div>
                </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-burgundy mx-auto mb-4"></div>
          <p className="text-burgundy/70">Loading products...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProducts.length === 0 ? (
            <div className="col-span-full text-center py-12">
              <ShoppingBag className="h-16 w-16 text-burgundy/30 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-burgundy/60 mb-2 font-serif">
                {products.length === 0 ? 'Premium Products' : 'No Products Found'}
              </h3>
              <p className="text-burgundy/50">
                {products.length === 0 
                  ? 'Vetted wellness products coming soon.' 
                  : 'Try adjusting your filters to see more products.'
                }
              </p>
            </div>
          ) : (
            filteredProducts.map((product) => {
              const isProductLocked = product.member_only && !(user && (user.subscription_status === 'member' || user.subscription_status === 'forever_free' || user.subscription_status === 'referral_free'));
              
              return (
            <Card key={product.id} className="hover:shadow-xl transition-all duration-300 bg-white border-burgundy/20 group relative overflow-hidden">
              {/* Blur overlay for locked products - clicking opens upgrade modal */}
              {isProductLocked && (
                <div 
                  className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white/60 backdrop-blur-sm rounded-lg cursor-pointer hover:bg-white/70 transition-colors"
                  onClick={() => {
                    setUpgradeFeatureName(product.name);
                    setShowLocalUpgradeModal(true);
                  }}
                >
                  <Lock className="h-12 w-12 text-burgundy mb-2" />
                  <span className="text-burgundy font-semibold text-sm">Members Only</span>
                  <span className="text-burgundy/70 text-xs mt-1">Tap to upgrade</span>
                </div>
              )}
              
              {/* Product Image with Favorite Button */}
              <div className={`relative ${isProductLocked ? 'blur-md' : ''}`}>
                {product.image_url ? (
                  <div className="aspect-square w-full h-32 overflow-hidden rounded-t-lg cursor-pointer" onClick={() => handleProductClick(product)}>
                    <img 
                      src={product.image_url} 
                      alt={product.name}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                ) : (
                  <div className="aspect-square w-full h-32 bg-burgundy/10 rounded-t-lg flex items-center justify-center cursor-pointer" onClick={() => handleProductClick(product)}>
                    <ShoppingBag className="h-8 w-8 text-burgundy/30" />
                  </div>
                )}
                
                {/* Favorite Button - Always Visible */}
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute top-2 right-2 bg-white/80 hover:bg-white p-2 rounded-full transition-opacity"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!user) {
                      toast.error('Please log in to save favorites');
                      return;
                    }
                    toggleFavorite(product);
                  }}
                >
                  <Heart 
                    className={`h-4 w-4 ${user && isProductFavorited(product.id) ? 'fill-red-500 text-red-500' : 'text-burgundy'}`}
                  />
                </Button>
                
                {/* Featured Badge */}
                {product.featured && (
                  <Badge className="absolute top-2 left-2 bg-gold text-burgundy border-none">
                    <Star className="h-3 w-3 mr-1" />
                    Featured
                  </Badge>
                )}
              </div>
              
              <CardHeader className={`cursor-pointer ${isProductLocked ? 'blur-md' : ''}`} onClick={() => handleProductClick(product)}>
                <CardTitle className="text-lg font-serif text-burgundy group-hover:text-burgundy/80 transition-colors">
                  {product.name}
                </CardTitle>
                <CardDescription className="text-gray-700">
                  <p className="text-sm leading-tight line-clamp-3 overflow-hidden">
                    {(() => {
                      const plainText = (product.description || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
                      const sentences = plainText.match(/[^.!?]+[.!?]+/g) || [plainText];
                      return sentences.slice(0, 3).join(' ').substring(0, 200);
                    })()}
                  </p>
                </CardDescription>
              </CardHeader>
              
              <CardContent className={isProductLocked ? 'blur-md' : ''}>
                <div className="flex justify-between items-center mb-4">
                  {product.price > 0 && (
                    <span className="text-2xl font-bold text-burgundy">${product.price}</span>
                  )}
                  <div className="flex items-center space-x-2">
                    <Badge variant="outline" className="border-burgundy/30 text-burgundy text-xs">
                      {product.category}
                    </Badge>
                    {product.click_count > 0 && (
                      <Badge variant="outline" className="border-blue-300 text-blue-600 text-xs">
                        {product.click_count} views
                      </Badge>
                    )}
                  </div>
                </div>
                
                {/* Product Status */}
                {/* Stock status removed - direct selling only */}
                
                {/* Coupon codes removed per user request */}
                
                {/* Action Buttons */}
                <div className="space-y-2">
                  <Button 
                    className="w-full bg-gradient-to-r from-burgundy to-dark-teal hover:from-burgundy/80 hover:to-dark-teal/80" 
                    onClick={() => handleAffiliateClick(product)}
                  >
                    Buy Now
                  </Button>
                  
                  <Button 
                    variant="outline"
                    className="w-full border-burgundy/30 text-burgundy hover:bg-burgundy/10"
                    onClick={() => handleProductClick(product)}
                  >
                    View Details
                  </Button>
                </div>
              </CardContent>
            </Card>
              );
            })
        )}
        </div>
      )}

      {/* Product Detail Modal */}
      {showProductModal && selectedProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-4xl max-h-[90vh] overflow-auto bg-white border-burgundy/20">
            <CardHeader>
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <CardTitle className="text-2xl font-serif text-gray-900 mb-2">
                    {selectedProduct.name}
                  </CardTitle>
                  <div className="flex items-center space-x-3">
                    <Badge variant="outline" className="border-burgundy/30 text-burgundy">
                      {selectedProduct.category}
                    </Badge>
                    {selectedProduct.featured && (
                      <Badge className="bg-gold/20 text-gold border-gold/30">
                        <Star className="h-3 w-3 mr-1" />
                        Featured
                      </Badge>
                    )}
                    {selectedProduct.click_count > 0 && (
                      <Badge variant="outline" className="border-blue-300 text-blue-600 text-xs">
                        {selectedProduct.click_count} views
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    onClick={() => {
                      navigator.clipboard.writeText(window.location.href);
                      toast.success('Product link copied to clipboard!');
                    }}
                    className="h-8 w-8 p-0"
                    title="Copy product link"
                  >
                    <Link2 className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setShowProductModal(false);
                      setSelectedProduct(null);
                      // Go back in history to restore marketplace URL
                      window.history.back();
                    }}
                    className="h-8 w-8 p-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            
            <CardContent>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Product Image */}
                <div>
                  {selectedProduct.image_url ? (
                    <img 
                      src={selectedProduct.image_url} 
                      alt={selectedProduct.name}
                      className="w-full rounded-lg border-2 border-burgundy/20"
                    />
                  ) : (
                    <div className="w-full aspect-square bg-burgundy/10 rounded-lg flex items-center justify-center">
                      <ShoppingBag className="h-24 w-24 text-burgundy/30" />
                    </div>
                  )}
                </div>
                
                {/* Product Details */}
                <div className="space-y-6">
                  <div>
                    {selectedProduct.price > 0 && (
                      <h3 className="text-3xl font-bold text-red-600 mb-2">${selectedProduct.price}</h3>
                    )}
                    <div 
                      className="text-gray-800 leading-relaxed rich-text-content prose prose-lg max-w-none"
                      dangerouslySetInnerHTML={{ __html: selectedProduct.description || 'No description available.' }}
                    />
                  </div>
                  
                  {/* Coupon codes removed per user request */}
                  
                  {/* Product Status */}
                  {selectedProduct.status && (
                    <div>
                      {/* Stock status removed - direct selling only */}
                    </div>
                  )}
                  
                  {/* Action Buttons */}
                  <div className="space-y-3">
                    <Button 
                      className="w-full bg-gradient-to-r from-burgundy to-dark-teal hover:from-burgundy/80 hover:to-dark-teal/80 text-lg py-6" 
                      onClick={() => handleAffiliateClick(selectedProduct)}
                    >
                      Buy Now
                    </Button>
                    
                    {selectedProduct.learn_more_url && (
                      <Button 
                        variant="outline"
                        className="w-full border-dark-teal text-dark-teal hover:bg-dark-teal/10"
                        onClick={() => {
                          setSelectedProduct(null);
                          // If it's an internal link, navigate to it
                          if (selectedProduct.learn_more_url.startsWith('/') || selectedProduct.learn_more_url.startsWith('#')) {
                            window.location.href = selectedProduct.learn_more_url;
                          } else {
                            window.open(selectedProduct.learn_more_url, '_blank');
                          }
                        }}
                      >
                        Learn More
                      </Button>
                    )}
                    
                    {user && (
                      <Button 
                        variant="outline"
                        className="w-full border-burgundy/30 text-burgundy hover:bg-burgundy/10"
                        onClick={() => toggleFavorite(selectedProduct)}
                      >
                        <Heart className={`h-4 w-4 mr-2 ${isProductFavorited(selectedProduct.id) ? 'fill-red-500 text-red-500' : 'text-burgundy'}`} />
                        {isProductFavorited(selectedProduct.id) ? 'Remove from Favorites' : 'Add to Favorites'}
                      </Button>
                    )}
                  </div>
                  
                  {/* Additional Product Info */}
                  <div className="text-sm text-burgundy/60 space-y-1">
                    <p>Category: {selectedProduct.category} › {selectedProduct.subcategory}</p>
                    {selectedProduct.rating && (
                      <p>Rating: {selectedProduct.rating}/5 ⭐ ({selectedProduct.review_count} reviews)</p>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Upgrade Modal - Overlays the marketplace */}
      <UpgradeModal 
        isOpen={showLocalUpgradeModal}
        onClose={() => setShowLocalUpgradeModal(false)}
        feature={upgradeFeatureName || "this premium product"}
      />
    </div>
  );
};

// Subscription Status Component
const SubscriptionStatus = ({ user, setActiveTab }) => {
  // Removed showUpgradeModal - now uses subscription tab navigation
  const [showTrialExpiryModal, setShowTrialExpiryModal] = useState(false);
  
  // Check if trial is expiring (last day)
  useEffect(() => {
    if (user?.subscription_status === 'trial' && user?.days_remaining === 1) {
      setShowTrialExpiryModal(true);
    }
  }, [user]);

  if (!user?.subscription_status) return null;

  const isTrialUser = user.subscription_status === 'trial';
  const isPremium = user.subscription_status === 'active';
  const isForeverFree = user.subscription_status === 'forever_free' || user.subscription_status === 'referral_free';
  const isCanceled = user.subscription_status === 'canceled';

  const getBadgeColors = () => {
    if (isPremium) return 'bg-emerald-100 text-emerald-800 border-emerald-300 hover:bg-emerald-100 hover:text-emerald-800';
    if (isForeverFree) return 'bg-purple-100 text-purple-800 border-purple-300 hover:bg-purple-100 hover:text-purple-800';
    if (isCanceled) return 'bg-red-100 text-red-800 border-red-300 hover:bg-red-100 hover:text-red-800';
    return 'bg-blue-100 text-blue-800 border-blue-300 hover:bg-blue-100 hover:text-blue-800'; // Trial
  };

  const getStatusText = () => {
    if (isPremium) return '✨ Premium Member';
    if (isForeverFree) return '🎉 Forever Free Member';
    if (isCanceled) return '❌ Canceled Membership';
    
    // Calculate trial length based on trial_end_date
    if (isTrialUser && user?.trial_end_date && user?.created_at) {
      try {
        const trialEnd = new Date(user.trial_end_date);
        const createdAt = new Date(user.created_at);
        const trialDays = Math.ceil((trialEnd - createdAt) / (1000 * 60 * 60 * 24));
        return `${trialDays}-Day Free Trial`;
      } catch (e) {
        return '7-Day Free Trial';
      }
    }
    
    return '7-Day Free Trial';
  };

  return (
    <>
      <div className="space-y-4">
        {/* Current Status Badge */}
        <Badge className={`text-xs px-3 py-1.5 cursor-default ${getBadgeColors()}`}>
          {getStatusText()}
          {user?.days_remaining && isTrialUser && ` - ${user.days_remaining} days remaining`}
        </Badge>

        {/* Trial Benefits & Upgrade */}
        {isTrialUser && !isForeverFree && (
          <div className="bg-white rounded-lg p-6 border border-gold/30 max-w-2xl mx-auto">
            <h3 className="font-semibold text-burgundy mb-3">Your Free Trial Includes:</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4 text-sm text-burgundy/80">
              <div className="flex items-center"><span className="mr-2">✓</span>Access to more content</div>
              <div className="flex items-center"><span className="mr-2">✓</span>All community discussions</div>
              <div className="flex items-center"><span className="mr-2">✓</span>Health and wellness trackers</div>
              <div className="flex items-center"><span className="mr-2">✓</span>Curated marketplace</div>
            </div>
            <Button 
              onClick={() => setActiveTab('subscription')}
              className="w-full bg-gradient-to-r from-burgundy to-dark-teal hover:from-burgundy/80 hover:to-dark-teal/80"
            >
              Upgrade to Premium
            </Button>
          </div>
        )}

        {/* Premium Status */}
        {isPremium && (
          <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
            <p className="text-emerald-800 font-medium">Thank you for being a Premium Member! 🎉</p>
            <p className="text-emerald-700 text-sm">Enjoying unlimited access to all wellness resources.</p>
          </div>
        )}
      </div>

      {/* Removed upgrade modal - now uses subscription tab navigation */}

      {/* Trial Expiry Modal */}
      {showTrialExpiryModal && (
        <TrialExpiryModal 
          onClose={() => setShowTrialExpiryModal(false)}
          onUpgrade={() => {
            setShowTrialExpiryModal(false);
            setActiveTab('subscription');
          }}
        />
      )}
    </>
  );
};

// Trial Expiry Modal Component
const TrialExpiryModal = ({ onClose, onUpgrade }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md bg-white border-burgundy/20">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-serif text-burgundy">Your Trial Expires Tomorrow! 🕒</CardTitle>
          <CardDescription>
            Don't lose access to your personalized wellness journey
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="text-center text-burgundy/80">
            <p className="mb-4">Continue enjoying:</p>
            <div className="space-y-2 text-sm">
              <div className="flex items-center"><span className="mr-2">✓</span>Expert wellness content</div>
              <div className="flex items-center"><span className="mr-2">✓</span>Premium community support</div>
              <div className="flex items-center"><span className="mr-2">✓</span>Exclusive peptide resources</div>
              <div className="flex items-center"><span className="mr-2">✓</span>Curated product recommendations</div>
            </div>
          </div>
          
          <div className="space-y-2">
            <Button 
              onClick={onUpgrade}
              className="w-full bg-gradient-to-r from-burgundy to-dark-teal hover:from-burgundy/80 hover:to-dark-teal/80"
            >
              Upgrade to Premium Now
            </Button>
            <Button 
              variant="outline" 
              onClick={onClose}
              className="w-full border-burgundy/30 text-burgundy hover:bg-burgundy/10"
            >
              Remind Me Later
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Saved Items Modal Component
const SavedItemsModal = ({ user, onClose, onNavigate }) => {
  const [savedItems, setSavedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('calculations');
  const [showArticleModal, setShowArticleModal] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);

  useEffect(() => {
    fetchSavedItems();
  }, []);

  const fetchSavedItems = async () => {
    try {
      const response = await axios.get(`${API}/users/${user.id}/saved-items`);
      setSavedItems(response.data || []);
    } catch (error) {
      console.error('Error fetching saved items:', error);
      setSavedItems([]);
    } finally {
      setLoading(false);
    }
  };

  const removeSavedItem = async (itemId) => {
    if (!window.confirm('Remove this saved item?')) return;

    try {
      await axios.delete(`${API}/users/${user.id}/saved-items/${itemId}`);
      setSavedItems(savedItems.filter(item => item.id !== itemId));
      toast.success('Item removed from saved items');
    } catch (error) {
      console.error('Error removing saved item:', error);
      toast.error('Error removing saved item');
    }
  };

  const handleViewSavedProduct = (savedItem) => {
    // Navigate to marketplace tab and show product details
    onNavigate('shop'); // Switch to marketplace tab
    onClose(); // Close the saved items modal
    // You can add product detail modal functionality here if needed
    toast(`Navigated to ${savedItem.name} in Marketplace`);
  };

  const handleViewSavedCalculation = (savedItem) => {
    // Navigate to tools tab and load the calculation
    onNavigate('calculator'); // Switch to tools tab
    onClose(); // Close the saved items modal
    toast(`Navigated to ${savedItem.name} in Tools`);
  };

  const handleViewSavedContent = (savedItem) => {
    const contentData = savedItem.data;
    
    // Try to reconstruct the original item from saved data
    const originalItem = contentData.original_item || {
      id: contentData.content_id,
      title: contentData.title || savedItem.name,
      description: contentData.description,
      content_type: contentData.content_type,
      url: contentData.url,
      content: contentData.content,
      category: contentData.category,
      author: contentData.author
    };

    // Handle URL content type
    if (originalItem.content_type === 'url' && originalItem.url) {
      window.open(originalItem.url, '_blank');
      onClose(); // Close the modal when opening external URL
      return;
    } 
    
    // Handle video content type
    if (originalItem.content_type === 'video' && originalItem.url) {
      window.open(originalItem.url, '_blank');
      onClose(); // Close the modal when opening external URL
      return;
    }
    
    // Handle audio content type
    if (originalItem.content_type === 'audio' && originalItem.url) {
      window.open(originalItem.url, '_blank');
      toast.success('Opening audio');
      onClose(); // Close the modal when opening external URL
      return;
    }
    
    // Handle document content type
    if (originalItem.content_type === 'document' && originalItem.url) {
      window.open(originalItem.url, '_blank');
      toast.success('Opening document');
      onClose(); // Close the modal when opening external URL
      return;
    }
    
    // Handle article content type with proper content
    if (originalItem.content_type === 'article' && originalItem.content) {
      setSelectedArticle(originalItem);
      setShowArticleModal(true);
      onClose(); // Close the saved items modal
      return;
    }
    
    // Handle legacy data or fallback cases
    if (originalItem.url) {
      // If there's a URL but no content_type specified, treat as URL
      window.open(originalItem.url, '_blank');
      onClose(); // Close the modal when opening external URL
      return;
    }
    
    // For legacy article data without content, show info message
    if (originalItem.content_type === 'article' || savedItem.type === 'content') {
      toast(`Content "${originalItem.title || savedItem.name}" is not available. This may be a legacy saved item with incomplete data.`);
      console.log('Legacy content data:', contentData);
      return;
    }
    
    // Final fallback
    toast.error('Content not available - missing content data');
    console.log('Content data available:', contentData);
  };

  const calculations = savedItems.filter(item => item.type === 'calculation');
  const content = savedItems.filter(item => item.type === 'content');
  const products = savedItems.filter(item => item.type === 'product');

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-4xl max-h-[90vh] overflow-auto bg-white border-burgundy/20">
        <CardHeader>
          <div className="flex justify-between items-start">
            <div>
              <CardTitle className="text-2xl font-serif text-burgundy">Saved Items</CardTitle>
              <CardDescription>Your saved calculations, content, and products</CardDescription>
            </div>
            <Button variant="ghost" onClick={onClose} className="h-8 w-8 p-0">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Tab Navigation */}
          <div className="flex space-x-1 mb-6 bg-burgundy/10 rounded-lg p-1">
            {[
              { key: 'calculations', label: 'Calcs', count: calculations.length },
              { key: 'content', label: 'Classroom', count: content.length },
              { key: 'products', label: 'Products', count: products.length }
            ].map(tab => (
              <button
                key={tab.key}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  activeTab === tab.key 
                    ? 'bg-burgundy text-white' 
                    : 'text-burgundy hover:bg-burgundy/20'
                }`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label} ({tab.count})
              </button>
            ))}
          </div>

          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-burgundy mx-auto mb-4"></div>
              <p className="text-burgundy">Loading saved items...</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Calculations Tab */}
              {activeTab === 'calculations' && (
                <div className="space-y-3">
                  {calculations.length === 0 ? (
                    <div className="text-center py-8 text-burgundy/60">
                      <Calculator className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>No saved calculations yet</p>
                      <p className="text-sm">Use the Peptide Calculator to save your dosing protocols</p>
                    </div>
                  ) : (
                    calculations.map(item => (
                      <div key={item.id} className="border border-burgundy/20 rounded-lg p-4">
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex-1">
                            <h4 className="font-medium text-burgundy mb-2">{item.name}</h4>
                            <div className="text-sm text-burgundy/70 grid grid-cols-2 gap-2">
                              <span><strong>Peptide:</strong> {item.data.peptide}</span>
                              <span><strong>Dosage:</strong> {item.data.dosage} {item.data.unit}</span>
                              <span><strong>Vial Size:</strong> {item.data.vialSize} mg</span>
                              <span><strong>Water:</strong> {item.data.waterAmount} ml</span>
                              {item.data.tickMarks && <span className="col-span-2"><strong>Tick Marks:</strong> {item.data.tickMarks}</span>}
                            </div>
                            <p className="text-xs text-burgundy/50 mt-2">
                              Saved: {new Date(item.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => removeSavedItem(item.id)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => handleViewSavedCalculation(item)}
                          className="w-full bg-burgundy hover:bg-burgundy/80 text-white"
                        >
                          View in Tools
                        </Button>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Content Tab */}
              {activeTab === 'content' && (
                <div className="space-y-3">
                  {content.length === 0 ? (
                    <div className="text-center py-8 text-burgundy/60">
                      <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>No saved content yet</p>
                      <p className="text-sm">Save articles and content for easy access later</p>
                    </div>
                  ) : (
                    content.map(item => (
                      <div key={item.id} className="border border-burgundy/20 rounded-lg p-4">
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex-1">
                            <h4 className="font-medium text-burgundy mb-1">{item.name}</h4>
                            <div 
                              className="text-sm text-burgundy/70 mb-2 line-clamp-2"
                              dangerouslySetInnerHTML={{ __html: item.data.description || '' }}
                            />
                            <p className="text-xs text-burgundy/50">
                              Saved: {new Date(item.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => removeSavedItem(item.id)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => handleViewSavedContent(item)}
                          className="w-full bg-burgundy hover:bg-burgundy/80 text-white"
                        >
                          View Content
                        </Button>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Products Tab */}
              {activeTab === 'products' && (
                <div className="space-y-3">
                  {products.length === 0 ? (
                    <div className="text-center py-8 text-burgundy/60">
                      <ShoppingBag className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>No saved products yet</p>
                      <p className="text-sm">Save products from the marketplace for later viewing</p>
                    </div>
                  ) : (
                    products.map(item => (
                      <div key={item.id} className="border border-burgundy/20 rounded-lg p-4">
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex items-start space-x-3 flex-1">
                            {item.data.image_url && (
                              <img 
                                src={item.data.image_url} 
                                alt={item.name}
                                className="w-16 h-16 object-cover rounded-lg"
                              />
                            )}
                            <div className="flex-1">
                              <h4 className="font-medium text-burgundy mb-1">{item.name}</h4>
                              <div 
                                className="text-sm text-burgundy/70 mb-2 line-clamp-2"
                                dangerouslySetInnerHTML={{ __html: item.data.description || '' }}
                              />
                              <p className="text-lg font-semibold text-burgundy">${item.data.price}</p>
                              <p className="text-xs text-burgundy/50">
                                Saved: {new Date(item.created_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => removeSavedItem(item.id)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => handleViewSavedProduct(item)}
                          className="w-full bg-burgundy hover:bg-burgundy/80 text-white"
                        >
                          View in Marketplace
                        </Button>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Article Modal for Saved Content */}
      {showArticleModal && selectedArticle && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-4xl max-h-[90vh] overflow-auto bg-white border-burgundy/20">
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="text-2xl font-serif text-gray-900">{selectedArticle.title}</CardTitle>
                  <div 
                    className="text-gray-700 rich-text-content"
                    dangerouslySetInnerHTML={{ __html: selectedArticle.description }}
                  />
                </div>
                <Button 
                  variant="ghost" 
                  onClick={() => setShowArticleModal(false)}
                  className="h-8 w-8 p-0"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Debug info for content images */}
              {selectedArticle && (
                <div className="bg-yellow-100 border border-yellow-400 p-3 mb-4 text-xs">
                  <div>DEBUG INFO:</div>
                  <div>Title: {selectedArticle.title}</div>
                  <div>Has image_url: {selectedArticle.image_url ? 'YES' : 'NO'}</div>
                  <div>image_url: {selectedArticle.image_url || 'MISSING'}</div>
                  <div>image_url length: {selectedArticle.image_url ? selectedArticle.image_url.length : 0}</div>
                </div>
              )}
              
              {/* Display image if available */}
              {selectedArticle.image_url && (
                <div className="mb-6 border-2 border-red-500 p-4">
                  <div className="text-red-600 text-sm mb-2">IMAGE SHOULD BE BELOW:</div>
                  <img
                    src={selectedArticle.image_url}
                    alt={selectedArticle.title}
                    className="w-full max-w-2xl mx-auto rounded-lg shadow-md border-4 border-blue-500"
                    onError={(e) => {
                      console.error('Image failed to load:', selectedArticle.image_url);
                      alert(`Image failed to load: ${selectedArticle.image_url}`);
                      e.target.style.border = '4px solid red';
                      e.target.style.background = 'pink';
                    }}
                    onLoad={() => {
                      console.log('Image loaded successfully:', selectedArticle.image_url);
                      alert(`Image loaded successfully: ${selectedArticle.image_url}`);
                    }}
                    style={{minHeight: '200px', backgroundColor: 'lightblue'}}
                  />
                  <div className="text-red-600 text-sm mt-2">IMAGE SHOULD BE ABOVE</div>
                </div>
              )}
              
              {/* Show when no image */}
              {!selectedArticle.image_url && selectedArticle && (
                <div className="bg-red-100 border border-red-400 p-3 mb-4 text-xs text-red-700">
                  NO IMAGE_URL FIELD FOUND FOR THIS CONTENT
                </div>
              )}
              
              <div 
                className="prose max-w-none text-burgundy prose-headings:text-burgundy prose-links:text-dark-teal prose-strong:text-burgundy"
                dangerouslySetInnerHTML={{ __html: selectedArticle.content || selectedArticle.description }}
              />
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

// Profile Modal Component  
const ProfileModal = ({ user, onClose, onUserUpdate, userNotifications = null, onRefreshNotifications = null, setMainActiveTab = null }) => {
  // Email preferences feature added - v2
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [giftCode, setGiftCode] = useState('');
  const [redeemingGift, setRedeemingGift] = useState(false);
  const [profileForm, setProfileForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    age: user?.age || '',
    location: user?.location || '',
    fun_facts: user?.fun_facts || '',
    symptoms: user?.symptoms || [],
    wellness_goals: user?.wellness_goals || [],
    topics_of_interest: user?.topics_of_interest || [],
    experience_level: user?.experience_level || '',
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [emailPreferences, setEmailPreferences] = useState({
    email_notifications_enabled: true,
    email_new_content: true,
    email_community_replies: true,
    email_trial_reminders: true,
    email_weekly_digest: true
  });
  const [uploading, setUploading] = useState(false);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const userId = user.id || user.email;
      const response = await axios.post(`${API}/users/${encodeURIComponent(userId)}/upload-photo`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        // photo_url is now a full R2 URL, no need to prepend API
        const newPhotoUrl = response.data.photo_url;
        onUserUpdate({...user, profile_photo: newPhotoUrl});
        toast.success('Profile photo updated!');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Error uploading photo');
    } finally {
      setUploading(false);
    }
  };

  // Update profile form when user data loads or changes
  useEffect(() => {
    if (user) {
      setProfileForm({
        name: user.name || '',
        email: user.email || '',
        age: user.age || '',
        location: user.location || '',
        fun_facts: user.fun_facts || '',
        symptoms: user.symptoms || [],
        wellness_goals: user.wellness_goals || [],
        topics_of_interest: user.topics_of_interest || [],
        experience_level: user.experience_level || '',
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    }
  }, [user]);

  useEffect(() => {
    if (activeTab === 'payment') {
      fetchSubscriptionInfo();
    } else if (activeTab === 'email') {
      fetchEmailPreferences();
    }
  }, [activeTab]);

  const fetchEmailPreferences = async () => {
    try {
      const response = await axios.get(`${API}/users/${user.id}/email-preferences`);
      setEmailPreferences(response.data);
    } catch (error) {
      console.error('Error fetching email preferences:', error);
    }
  };

  const handleEmailPreferencesUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.put(`${API}/users/${user.id}/email-preferences`, emailPreferences);
      toast.success('Email preferences updated successfully');
    } catch (error) {
      console.error('Error updating email preferences:', error);
      toast.error('Error updating email preferences');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriptionInfo = async () => {
    try {
      const response = await axios.get(`${API}/subscription/user/${user.id}/status`);
      setSubscriptionInfo(response.data);
    } catch (error) {
      console.error('Error fetching subscription info:', error);
      // Set basic info from user object if API fails
      setSubscriptionInfo({
        subscription_status: user?.subscription_status,
        plan_name: user?.subscription_status === 'trial' ? 'Free Trial' : 
                   user?.subscription_status === 'forever_free' ? 'Forever Free' :
                   user?.subscription_status === 'active' ? 'Premium Member' : 'Unknown',
        next_billing_date: user?.trial_ends_at || null
      });
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    
    if (profileForm.new_password && profileForm.new_password !== profileForm.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }

    setLoading(true);
    
    try {
      const updateData = {
        name: profileForm.name,
        email: profileForm.email,
        age: profileForm.age ? parseInt(profileForm.age) : null,
        location: profileForm.location || null,
        profile_photo: user.profile_photo || null,  // Preserve existing profile photo
        fun_facts: profileForm.fun_facts || '',
        symptoms: profileForm.symptoms || [],
        wellness_goals: profileForm.wellness_goals || [],
        topics_of_interest: profileForm.topics_of_interest || [],
        experience_level: profileForm.experience_level || ''
      };

      if (profileForm.new_password) {
        updateData.current_password = profileForm.current_password;
        updateData.new_password = profileForm.new_password;
      }

      const userId = user.id || user.email;
      const response = await axios.put(`${API}/users/${encodeURIComponent(userId)}/profile`, updateData);
      
      // Preserve profile_photo if not in response
      const updatedUser = {
        ...user,
        ...response.data.user,
        profile_photo: response.data.user.profile_photo || user.profile_photo
      };
      
      console.log('Profile update response:', response.data.user);
      console.log('Updated user with preserved photo:', updatedUser);
      
      onUserUpdate(updatedUser);
      toast.success('Profile updated successfully!');
      
      // Clear password fields
      setProfileForm({
        ...profileForm,
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (error) {
      console.error('Error updating profile:', error);
      if (error.response?.status === 400) {
        toast.error(error.response.data.detail || 'Current password is incorrect');
      } else {
        toast.error('Error updating profile');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? You will retain access until the end of your billing period.')) {
      return;
    }

    setLoading(true);
    
    try {
      const token = localStorage.getItem('auth_token');
      await axios.post(
        `${API}/subscription/cancel`, 
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      // Track cancellation event
      Analytics.trackCancellation(user);
      
      toast.success('Subscription cancelled successfully. You retain access until your period ends.');
      fetchSubscriptionInfo(); // Refresh subscription info
      onUserUpdate({ ...user, subscription_status: 'canceled' });
      
      // Trigger cancellation survey
      window.dispatchEvent(new CustomEvent('show-survey', { detail: { type: 'cancellation' } }));
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      const errorMsg = error.response?.data?.detail || 'Error cancelling subscription. Please try again.';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleReactivateSubscription = async () => {
    if (!window.confirm('Reactivate your subscription? You will continue to be billed at the end of your current period.')) {
      return;
    }

    setLoading(true);
    
    try {
      const token = localStorage.getItem('auth_token');
      await axios.post(
        `${API}/subscription/reactivate`, 
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      toast.success('Subscription reactivated successfully! Welcome back!');
      fetchSubscriptionInfo(); // Refresh subscription info
      onUserUpdate({ ...user, subscription_status: 'active' });
    } catch (error) {
      console.error('Error reactivating subscription:', error);
      const errorMsg = error.response?.data?.detail || 'Error reactivating subscription. Please try again.';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-auto bg-white border-burgundy/20">
        <CardHeader>
          <div className="flex justify-between items-start">
            <div>
              <CardTitle className="text-2xl font-serif text-burgundy">Profile & Settings</CardTitle>
              <CardDescription>Manage your account and subscription</CardDescription>
            </div>
            <Button variant="ghost" onClick={onClose} className="h-8 w-8 p-0">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Tab Navigation */}
          <div className="flex space-x-1 mb-6 bg-burgundy/10 rounded-lg p-1 flex-wrap">
            <button
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'profile' 
                  ? 'bg-burgundy text-white' 
                  : 'text-burgundy hover:bg-burgundy/20'
              }`}
              onClick={() => setActiveTab('profile')}
            >
              Profile Info
            </button>
            <button
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'payment' 
                  ? 'bg-burgundy text-white' 
                  : 'text-burgundy hover:bg-burgundy/20'
              }`}
              onClick={() => setActiveTab('payment')}
            >
              Payment
            </button>
            <button
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'email' 
                  ? 'bg-burgundy text-white' 
                  : 'text-burgundy hover:bg-burgundy/20'
              }`}
              onClick={() => setActiveTab('email')}
            >
              Email
            </button>
            <button
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'guidelines' 
                  ? 'bg-burgundy text-white' 
                  : 'text-burgundy hover:bg-burgundy/20'
              }`}
              onClick={() => setActiveTab('guidelines')}
            >
              Guidelines
            </button>
            <button
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors relative ${
                activeTab === 'notifications' 
                  ? 'bg-burgundy text-white' 
                  : 'text-burgundy hover:bg-burgundy/20'
              }`}
              onClick={() => setActiveTab('notifications')}
            >
              Notifications
              {userNotifications && userNotifications.unread_warnings > 0 && (
                <Badge className="ml-1 h-4 w-4 p-0 flex items-center justify-center bg-red-500 text-white text-xs rounded-full absolute -top-1 -right-1">
                  {userNotifications.unread_warnings}
                </Badge>
              )}
            </button>
          </div>

          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <form onSubmit={handleProfileUpdate} className="space-y-4">
              {/* Profile Photo Section */}
              <div className="text-center pb-4 border-b border-burgundy/20">
                <div className="relative inline-block">
                  <div className="w-32 h-32 rounded-full border-4 border-burgundy/20 overflow-hidden bg-burgundy/10 flex items-center justify-center mx-auto">
                    {user?.profile_photo ? (
                      <img 
                        key={user.profile_photo}
                        src={user.profile_photo}
                        alt={`${user.name}'s profile`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          console.error('Image load error:', e.target.src);
                        }}
                      />
                    ) : (
                      <div className="text-burgundy/50">
                        <User className="w-12 h-12" />
                      </div>
                    )}
                  </div>
                  <div className="mt-3">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoUpload}
                      disabled={uploading}
                      className="hidden"
                      id="profile-photo-upload-settings"
                    />
                    <label
                      htmlFor="profile-photo-upload-settings"
                      className={`cursor-pointer inline-flex items-center px-4 py-2 bg-burgundy text-cream rounded-lg hover:bg-burgundy/80 text-sm ${
                        uploading ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      {uploading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Camera className="w-4 h-4 mr-2" />
                          {user?.profile_photo ? 'Change Photo' : 'Add Photo'}
                        </>
                      )}
                    </label>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-burgundy mb-2">Name</label>
                  <Input
                    value={profileForm.name}
                    onChange={(e) => setProfileForm({...profileForm, name: e.target.value})}
                    className="border-burgundy/30 focus:border-gold"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-burgundy mb-2">Email</label>
                  <Input
                    type="email"
                    value={profileForm.email}
                    onChange={(e) => setProfileForm({...profileForm, email: e.target.value})}
                    className="border-burgundy/30 focus:border-gold"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-burgundy mb-2">Age (optional)</label>
                <Input
                  type="number"
                  value={profileForm.age}
                  onChange={(e) => setProfileForm({...profileForm, age: e.target.value})}
                  className="border-burgundy/30 focus:border-gold w-32"
                  placeholder="e.g., 45"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-burgundy mb-2">Location (optional)</label>
                <Input
                  type="text"
                  value={profileForm.location}
                  onChange={(e) => setProfileForm({...profileForm, location: e.target.value})}
                  className="border-burgundy/30 focus:border-gold"
                  placeholder="City, State"
                />
              </div>

              {/* Onboarding Fields - Now Editable */}
              <div className="border-t border-burgundy/20 pt-4">
                <h4 className="font-medium text-burgundy mb-3">🦋 Wellness Profile</h4>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-burgundy mb-2">Fun Facts About Me (optional)</label>
                    <textarea
                      value={profileForm.fun_facts || ''}
                      onChange={(e) => setProfileForm({...profileForm, fun_facts: e.target.value})}
                      className="w-full p-2 border border-burgundy/30 rounded-md focus:border-gold focus:ring-1 focus:ring-gold"
                      rows="3"
                      placeholder="Share something fun about yourself..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-burgundy mb-2">Current Symptoms (optional)</label>
                    <div className="grid grid-cols-2 gap-2">
                      {['Hot flashes', 'Weight gain', 'Skin and hair changes', 'Mood changes', 'Lack of mental clarity', 'Increased anxiety', 'Sleep quality changes', 'Fluctuating energy', 'Low libido'].map(symptom => (
                        <label key={symptom} className="flex items-center space-x-2 text-sm">
                          <input
                            type="checkbox"
                            checked={(profileForm.symptoms || []).includes(symptom)}
                            onChange={(e) => {
                              const symptoms = profileForm.symptoms || [];
                              if (e.target.checked) {
                                setProfileForm({...profileForm, symptoms: [...symptoms, symptom]});
                              } else {
                                setProfileForm({...profileForm, symptoms: symptoms.filter(s => s !== symptom)});
                              }
                            }}
                            className="rounded border-burgundy/30 text-burgundy focus:ring-gold"
                          />
                          <span className="text-burgundy/80">{symptom}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-burgundy mb-2">Wellness Goals (optional)</label>
                    <div className="grid grid-cols-2 gap-2">
                      {['Weight management', 'Energy boost', 'Improved sleep', 'Mental clarity', 'Mood balance', 'Better skin', 'Enhanced libido', 'General wellness'].map(goal => (
                        <label key={goal} className="flex items-center space-x-2 text-sm">
                          <input
                            type="checkbox"
                            checked={(profileForm.wellness_goals || []).includes(goal)}
                            onChange={(e) => {
                              const goals = profileForm.wellness_goals || [];
                              if (e.target.checked) {
                                setProfileForm({...profileForm, wellness_goals: [...goals, goal]});
                              } else {
                                setProfileForm({...profileForm, wellness_goals: goals.filter(g => g !== goal)});
                              }
                            }}
                            className="rounded border-burgundy/30 text-burgundy focus:ring-gold"
                          />
                          <span className="text-burgundy/80">{goal}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-burgundy mb-2">Topics of Interest (optional)</label>
                    <div className="grid grid-cols-2 gap-2">
                      {['Hormone optimization', 'Nutrition', 'Exercise', 'Sleep optimization', 'Supplements', 'Stress management', 'Mental health', 'Longevity'].map(topic => (
                        <label key={topic} className="flex items-center space-x-2 text-sm">
                          <input
                            type="checkbox"
                            checked={(profileForm.topics_of_interest || []).includes(topic)}
                            onChange={(e) => {
                              const topics = profileForm.topics_of_interest || [];
                              if (e.target.checked) {
                                setProfileForm({...profileForm, topics_of_interest: [...topics, topic]});
                              } else {
                                setProfileForm({...profileForm, topics_of_interest: topics.filter(t => t !== topic)});
                              }
                            }}
                            className="rounded border-burgundy/30 text-burgundy focus:ring-gold"
                          />
                          <span className="text-burgundy/80">{topic}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-burgundy mb-2">Experience Level (optional)</label>
                    <select
                      value={profileForm.experience_level || ''}
                      onChange={(e) => setProfileForm({...profileForm, experience_level: e.target.value})}
                      className="w-full p-2 border border-burgundy/30 rounded-md focus:border-gold focus:ring-1 focus:ring-gold"
                    >
                      <option value="">Select experience level...</option>
                      <option value="beginner">Beginner - New to wellness optimization</option>
                      <option value="intermediate">Intermediate - Some knowledge and experience</option>
                      <option value="advanced">Advanced - Experienced with protocols</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Password Change Section */}
              <div className="border-t border-burgundy/20 pt-4">
                <h4 className="font-medium text-burgundy mb-3">Change Password (optional)</h4>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-burgundy mb-2">Current Password</label>
                    <Input
                      type="password"
                      value={profileForm.current_password}
                      onChange={(e) => setProfileForm({...profileForm, current_password: e.target.value})}
                      className="border-burgundy/30 focus:border-gold"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-burgundy mb-2">New Password</label>
                    <Input
                      type="password"
                      value={profileForm.new_password}
                      onChange={(e) => setProfileForm({...profileForm, new_password: e.target.value})}
                      className="border-burgundy/30 focus:border-gold"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-burgundy mb-2">Confirm New Password</label>
                    <Input
                      type="password"
                      value={profileForm.confirm_password}
                      onChange={(e) => setProfileForm({...profileForm, confirm_password: e.target.value})}
                      className="border-burgundy/30 focus:border-gold"
                    />
                  </div>
                </div>
              </div>

              <Button 
                type="submit" 
                disabled={loading}
                className="w-full bg-burgundy hover:bg-burgundy/80 text-white"
              >
                {loading ? 'Updating...' : 'Update Profile'}
              </Button>
            </form>
          )}

          {/* Payment Tab */}
          {activeTab === 'payment' && (
            <div className="space-y-6">
              {/* Current Subscription Status */}
              <div className="bg-gradient-to-r from-burgundy/5 to-dark-teal/5 rounded-lg p-6 border border-burgundy/20">
                <h4 className="font-semibold text-burgundy mb-3">Current Subscription</h4>
                <div className="flex justify-between items-start">
                  <div>
                    <Badge className={`mb-2 cursor-default ${
                      user?.subscription_status === 'active' 
                        ? 'bg-emerald-100 text-emerald-800 border-emerald-300 hover:bg-emerald-100 hover:text-emerald-800'
                      : user?.subscription_status === 'forever_free' || user?.subscription_status === 'referral_free'
                        ? 'bg-gold/20 text-gold border-gold/30 hover:bg-gold/20 hover:text-gold'
                        : user?.subscription_status === 'trial'
                        ? 'bg-gold/20 text-gold border-gold/30 hover:bg-gold/20 hover:text-gold'
                        : 'bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-100 hover:text-gray-600'
                    }`}>
                      {user?.subscription_status === 'active' && '✨ Premium Member'}
                      {user?.subscription_status === 'forever_free' && '🎉 Forever Free'}
                      {user?.subscription_status === 'referral_free' && '🎁 Referral Free'}
                      {user?.subscription_status === 'trial' && 'Free Trial'}
                      {user?.subscription_status === 'canceled' && '⚠️ Cancelled'}
                    </Badge>
                    
                    <div className="text-sm text-burgundy/80 space-y-1 mt-2">
                      {user?.subscription_status === 'forever_free' && (
                        <>
                          <p><strong>Plan:</strong> Forever Free Access</p>
                          <p><strong>Status:</strong> Lifetime membership - no billing</p>
                        </>
                      )}
                      
                      {user?.subscription_status === 'referral_free' && (
                        <>
                          <p><strong>Plan:</strong> Referral Free Access</p>
                          <p><strong>Status:</strong> Earned through referrals - no billing</p>
                        </>
                      )}
                      
                      {user?.subscription_status === 'active' && subscriptionInfo && (
                        <>
                          <p><strong>Plan:</strong> {subscriptionInfo.plan_name || 'Premium Membership'}</p>
                          {subscriptionInfo.price && (
                            <p><strong>Amount:</strong> ${subscriptionInfo.price}/{subscriptionInfo.interval || 'month'}</p>
                          )}
                          {subscriptionInfo.current_period_end && (
                            <p><strong>Next billing:</strong> {new Date(subscriptionInfo.current_period_end).toLocaleDateString()}</p>
                          )}
                          {subscriptionInfo.cancel_at_period_end && (
                            <p className="text-orange-600"><strong>Status:</strong> Will cancel at period end</p>
                          )}
                        </>
                      )}
                      
                      {user?.subscription_status === 'trial' && (
                        <>
                          <p><strong>Plan:</strong> Free Trial</p>
                          {user?.trial_ends_at && (
                            <p><strong>Trial ends:</strong> {new Date(user.trial_ends_at).toLocaleDateString()}</p>
                          )}
                        </>
                      )}
                    </div>
                    
                    {user?.subscription_status === 'trial' && user?.days_remaining && (
                      <p className="text-sm text-burgundy/80 mt-2">
                        <strong>Trial ends in:</strong> {user.days_remaining} days
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="space-y-4">
                {user?.subscription_status === 'active' && !subscriptionInfo?.cancel_at_period_end && (
                  <Button 
                    variant="destructive"
                    onClick={handleCancelSubscription}
                    disabled={loading}
                    className="w-full"
                  >
                    {loading ? 'Processing...' : 'Cancel Subscription'}
                  </Button>
                )}

                {user?.subscription_status === 'trial' && (
                  <div className="text-center">
                    <p className="text-burgundy/80 mb-3">Ready to upgrade your trial?</p>
                    <Button 
                      onClick={() => {
                        onClose();
                        if (setMainActiveTab) {
                          setMainActiveTab('subscription');
                        }
                      }}
                      className="bg-gradient-to-r from-burgundy to-dark-teal hover:from-burgundy/80 hover:to-dark-teal/80"
                    >
                      Upgrade to Premium
                    </Button>
                  </div>
                )}

                {user?.subscription_status === 'canceled' && (
                  <div className="text-center bg-orange-50 rounded-lg p-4 border border-orange-200">
                    <p className="text-orange-800 mb-3">
                      Your subscription is cancelled but you retain access until your billing period ends.
                    </p>
                    <Button 
                      onClick={handleReactivateSubscription}
                      disabled={loading}
                      className="bg-gradient-to-r from-burgundy to-dark-teal hover:from-burgundy/80 hover:to-dark-teal/80"
                    >
                      {loading ? 'Processing...' : 'Reactivate Subscription'}
                    </Button>
                  </div>
                )}
              </div>

              {/* Billing History Placeholder */}
              <div className="border-t border-burgundy/20 pt-4">
                <h4 className="font-medium text-burgundy mb-3">Billing History</h4>
                <p className="text-burgundy/60 text-sm">
                  Billing history and receipts will be available here. For now, please check your email for Stripe receipts.
                </p>
              </div>
            </div>
          )}

          {/* Email Settings Tab */}
          {activeTab === 'email' && (
            <form onSubmit={handleEmailPreferencesUpdate} className="space-y-6">
              <div className="bg-gradient-to-r from-burgundy/5 to-dark-teal/5 rounded-lg p-6 border border-burgundy/20">
                <h4 className="font-semibold text-burgundy mb-4">Email Notification Preferences</h4>
                <p className="text-sm text-burgundy/70 mb-4">
                  Choose which emails you'd like to receive from Luminelle.
                </p>
                
                <div className="space-y-4">
                  {/* Master Toggle */}
                  <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-burgundy/10">
                    <div>
                      <label className="font-medium text-burgundy">All Email Notifications</label>
                      <p className="text-sm text-burgundy/60">Enable or disable all email notifications at once</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={emailPreferences.email_notifications_enabled}
                      onChange={(e) => setEmailPreferences({...emailPreferences, email_notifications_enabled: e.target.checked})}
                      className="h-5 w-5 text-burgundy border-burgundy/30 rounded focus:ring-gold"
                    />
                  </div>

                  {/* Individual Preferences */}
                  <div className={`space-y-3 ${!emailPreferences.email_notifications_enabled && 'opacity-50 pointer-events-none'}`}>
                    <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-burgundy/10">
                      <div>
                        <label className="font-medium text-burgundy">New Content Alerts</label>
                        <p className="text-sm text-burgundy/60">Get notified when new articles and videos are published</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={emailPreferences.email_new_content}
                        onChange={(e) => setEmailPreferences({...emailPreferences, email_new_content: e.target.checked})}
                        className="h-5 w-5 text-burgundy border-burgundy/30 rounded focus:ring-gold"
                        disabled={!emailPreferences.email_notifications_enabled}
                      />
                    </div>

                    <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-burgundy/10">
                      <div>
                        <label className="font-medium text-burgundy">Community Replies</label>
                        <p className="text-sm text-burgundy/60">Get notified when someone replies to your discussions</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={emailPreferences.email_community_replies}
                        onChange={(e) => setEmailPreferences({...emailPreferences, email_community_replies: e.target.checked})}
                        className="h-5 w-5 text-burgundy border-burgundy/30 rounded focus:ring-gold"
                        disabled={!emailPreferences.email_notifications_enabled}
                      />
                    </div>

                    <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-burgundy/10">
                      <div>
                        <label className="font-medium text-burgundy">Trial Reminders</label>
                        <p className="text-sm text-burgundy/60">Receive reminders before your trial ends</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={emailPreferences.email_trial_reminders}
                        onChange={(e) => setEmailPreferences({...emailPreferences, email_trial_reminders: e.target.checked})}
                        className="h-5 w-5 text-burgundy border-burgundy/30 rounded focus:ring-gold"
                        disabled={!emailPreferences.email_notifications_enabled}
                      />
                    </div>

                    <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-burgundy/10">
                      <div>
                        <label className="font-medium text-burgundy">Weekly Digest</label>
                        <p className="text-sm text-burgundy/60">Get a summary of the week's top content and discussions</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={emailPreferences.email_weekly_digest}
                        onChange={(e) => setEmailPreferences({...emailPreferences, email_weekly_digest: e.target.checked})}
                        className="h-5 w-5 text-burgundy border-burgundy/30 rounded focus:ring-gold"
                        disabled={!emailPreferences.email_notifications_enabled}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <Button 
                type="submit" 
                disabled={loading}
                className="w-full bg-burgundy hover:bg-burgundy/80 text-white"
              >
                {loading ? 'Saving...' : 'Save Email Preferences'}
              </Button>
            </form>
          )}

          {/* Community Guidelines Tab */}
          {activeTab === 'guidelines' && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <h4 className="text-2xl font-bold text-burgundy font-serif mb-2">Community Guidelines</h4>
                <p className="text-burgundy/70">Our house vibes for a supportive wellness community</p>
              </div>

              <Card className="bg-gold/5 border-gold/30">
                <CardContent className="p-6">
                  <div className="space-y-4">
                    <h5 className="text-lg font-semibold text-burgundy mb-4">
                      Before you jump in, a few house vibes:
                    </h5>
                    
                    <div className="space-y-3">
                      <div className="flex items-start space-x-3">
                        <div className="w-2 h-2 bg-burgundy rounded-full mt-2 flex-shrink-0"></div>
                        <div>
                          <p className="text-burgundy font-medium">Be Kind</p>
                          <p className="text-burgundy/70 text-sm">Lift each other up. No snark, no shade.</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start space-x-3">
                        <div className="w-2 h-2 bg-burgundy rounded-full mt-2 flex-shrink-0"></div>
                        <div>
                          <p className="text-burgundy font-medium">Share, Don't Prescribe</p>
                          <p className="text-burgundy/70 text-sm">"Here's what helped me" &gt; "You should do this."</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start space-x-3">
                        <div className="w-2 h-2 bg-burgundy rounded-full mt-2 flex-shrink-0"></div>
                        <div>
                          <p className="text-burgundy font-medium">No Spam or Sales</p>
                          <p className="text-burgundy/70 text-sm">We're here to support, not to sell.</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start space-x-3">
                        <div className="w-2 h-2 bg-burgundy rounded-full mt-2 flex-shrink-0"></div>
                        <div>
                          <p className="text-burgundy font-medium">Stay Curious, Not Combative</p>
                          <p className="text-burgundy/70 text-sm">Questions are gold. Be cool in the comments.</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start space-x-3">
                        <div className="w-2 h-2 bg-burgundy rounded-full mt-2 flex-shrink-0"></div>
                        <div>
                          <p className="text-burgundy font-medium">Keep It Private</p>
                          <p className="text-burgundy/70 text-sm">What's shared here, stays here.</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start space-x-3">
                        <div className="w-2 h-2 bg-burgundy rounded-full mt-2 flex-shrink-0"></div>
                        <div>
                          <p className="text-burgundy font-medium">We're Not Your Doctor</p>
                          <p className="text-burgundy/70 text-sm">Educational only, not medical advice.</p>
                        </div>
                      </div>
                    </div>

                    <div className="border-t border-gold/30 pt-4 mt-6">
                      <p className="text-burgundy font-medium text-center">
                        By posting, you agree to keep this space kind, supportive, and spam-free.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="text-center text-sm text-burgundy/60">
                <p>Questions about our guidelines? Contact our support team.</p>
              </div>
            </div>
          )}

          {/* Notifications Tab */}
          {activeTab === 'notifications' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h4 className="text-lg font-semibold text-burgundy">Your Notifications</h4>
                <Badge className="bg-burgundy/10 text-burgundy">
                  {userNotifications ? userNotifications.total_warnings : 0} total
                </Badge>
              </div>

              {userNotifications && userNotifications.warnings.length > 0 ? (
                <div className="space-y-3">
                  {userNotifications.warnings.map((warning) => (
                    <Card key={warning.id} className={`border-2 transition-all duration-200 ${
                      warning.read ? 'border-gray-200 bg-gray-50' : 'border-yellow-300 bg-yellow-50'
                    }`}>
                      <CardContent className="p-4">
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex items-center gap-2">
                            <AlertTriangle className={`h-4 w-4 ${
                              warning.read ? 'text-gray-400' : 'text-yellow-600'
                            }`} />
                            <span className={`text-sm font-medium ${
                              warning.read ? 'text-gray-600' : 'text-yellow-800'
                            }`}>
                              Community Warning
                            </span>
                            {!warning.read && (
                              <Badge className="h-4 w-4 p-0 bg-red-500 text-white text-xs rounded-full">
                                !
                              </Badge>
                            )}
                          </div>
                          <span className="text-xs text-gray-500">
                            {new Date(warning.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <p className={`text-sm mb-3 ${
                          warning.read ? 'text-gray-600' : 'text-gray-800'
                        }`}>
                          {warning.message}
                        </p>
                        <div className="flex justify-between items-center">
                          <span className={`text-xs ${
                            warning.read ? 'text-gray-500' : 'text-yellow-700'
                          }`}>
                            From: {warning.from_admin ? 'Admin' : 'System'}
                          </span>
                          {!warning.read && (
                            <Button 
                              size="sm"
                              variant="outline"
                              onClick={async () => {
                                try {
                                  await axios.put(`${API}/users/${user.id}/warnings/${warning.id}/read`);
                                  if (onRefreshNotifications) {
                                    await onRefreshNotifications();
                                  }
                                  toast.success('Marked as read');
                                } catch (error) {
                                  console.error('Error marking as read:', error);
                                  toast.error('Failed to mark as read');
                                }
                              }}
                              className="h-7 text-xs border-burgundy/30 text-burgundy hover:bg-burgundy/10"
                            >
                              Mark as Read
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-burgundy/10 mb-4">
                    <AlertTriangle className="w-8 h-8 text-burgundy/50" />
                  </div>
                  <p className="text-burgundy/70">No notifications yet</p>
                  <p className="text-sm text-burgundy/50 mt-2">You're all good! No warnings or alerts.</p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// SubscriptionUpgradeModal removed - now uses subscription tab navigation

// Trial Expired Modal Component
const TrialExpiredModal = ({ onClose, user, setActiveTab }) => {
  const handleUpgrade = () => {
    onClose();
    // Navigate to subscription tab
    setActiveTab('subscription');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md mx-4 border-0 shadow-2xl bg-cream">
        <CardHeader className="text-center pb-6">
          <div className="w-16 h-16 bg-gradient-to-r from-red-500 to-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="h-8 w-8 text-white" />
          </div>
          <CardTitle className="text-2xl font-serif text-burgundy">
            Trial Period Ended
          </CardTitle>
          <CardDescription className="text-burgundy/70">
            Your 14-day trial has expired. Upgrade to continue your wellness journey.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-burgundy/5 border border-burgundy/20 rounded-lg p-4">
            <h4 className="font-semibold text-burgundy mb-2">What you'll get with Premium:</h4>
            <ul className="text-sm text-burgundy/80 space-y-1">
              <li>• 🔓 Unlock all premium content & articles</li>
              <li>• 🔓 Access member-only marketplace products</li>
              <li>• Full access to all wellness tools & trackers</li>
              <li>• Community discussions and expert guidance</li>
              <li>• Personal progress tracking</li>
            </ul>
          </div>
          
          <div className="flex flex-col gap-3">
            <Button
              onClick={handleUpgrade}
              className="w-full bg-gradient-to-r from-burgundy to-dark-teal hover:from-burgundy/80 hover:to-dark-teal/80 text-cream font-semibold py-3 rounded-lg shadow-lg"
            >
              Upgrade to Premium
            </Button>
            
            <Button
              variant="outline"
              onClick={onClose}
              className="w-full border-burgundy/30 text-burgundy hover:bg-burgundy/10"
            >
              Continue Browsing (Limited Access)
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Trial expiration utility function
const checkTrialExpiration = (user) => {
  if (!user) return false;
  
  // Check if user has active subscription (premium users never expire)
  if (user.subscription_status === 'active' || user.subscription_tier === 'premium') {
    return false;
  }
  
  // Check if user is on trial
  if (user.subscription_tier === 'trial' || user.subscription_tier === 'free') {
    // Check if trial_start_date exists and calculate expiration
    if (user.trial_start_date) {
      const trialStart = new Date(user.trial_start_date);
      const now = new Date();
      const daysDifference = Math.floor((now - trialStart) / (1000 * 60 * 60 * 24));
      
      // Trial period is 14 days
      return daysDifference > 14;
    }
  }
  
  return false;
};

// Onboarding Modal Component
const OnboardingModal = ({ user, isOpen, onClose, onComplete }) => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    fun_facts: '',
    symptoms: [],
    wellness_goals: [],
    topics_of_interest: [],
    experience_level: ''
  });
  const [saving, setSaving] = useState(false);

  const symptomsOptions = [
    'Hot flashes', 'Weight gain', 'Skin and hair changes', 'Mood changes',
    'Lack of mental clarity', 'Increased anxiety', 'Sleep quality changes',
    'Fluctuating energy', 'Low libido'
  ];

  const goalsOptions = [
    'Symptom relief', 'Community support', 'Lifestyle optimization',
    'Education and learning', 'Guidance and coaching', 'Overall wellbeing',
    'Movement and fitness advice'
  ];

  const topicsOptions = [
    'Peptides', 'Supplements', 'Skincare', 'Exercise and fitness',
    'Sleep optimization', 'Alternative therapies', 'Sexual health',
    'Brain optimization', 'Product ideas'
  ];

  const experienceLevels = [
    { value: 'just_starting', label: 'Just starting my journey' },
    { value: 'researching', label: 'Researching options' },
    { value: 'on_treatment', label: 'Currently on treatment' },
    { value: 'experienced', label: 'Experienced & optimizing' }
  ];

  const handleCheckboxChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(item => item !== value)
        : [...prev[field], value]
    }));
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const response = await axios.post(`${API}/users/${user.id}/onboarding`, formData);
      if (response.data.success) {
        toast.success('Welcome! Your profile is all set up.');
        onComplete();
      }
    } catch (error) {
      console.error('Error saving onboarding:', error);
      toast.error('Error saving your preferences');
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = async () => {
    try {
      await axios.post(`${API}/users/${user.id}/skip-onboarding`);
      toast('You can complete your profile anytime from settings');
      onComplete();
    } catch (error) {
      console.error('Error skipping onboarding:', error);
      onComplete(); // Still close even if API fails
    }
  };

  if (!isOpen) return null;

  const totalSteps = 5;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl bg-cream border-burgundy/20 max-h-[90vh] overflow-y-auto">
        <CardHeader className="border-b border-burgundy/20">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-2xl font-serif text-burgundy">Welcome to Luminelle!</CardTitle>
              <p className="text-burgundy/70 text-sm mt-1">Question {step} of {totalSteps}</p>
            </div>
            <button onClick={handleSkip} className="text-burgundy/50 hover:text-burgundy text-sm underline">
              Skip for now
            </button>
          </div>
        </CardHeader>

        <CardContent className="p-6">
          {step === 1 && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-burgundy">Tell us a few fun facts about yourself</h3>
              <p className="text-burgundy/70 text-sm">Help the community get to know you better!</p>
              <textarea
                value={formData.fun_facts}
                onChange={(e) => setFormData({...formData, fun_facts: e.target.value})}
                placeholder="Share your hobbies, interests, or anything you'd like the community to know..."
                className="w-full min-h-[120px] p-3 border border-burgundy/30 rounded-lg focus:border-gold focus:ring-1 focus:ring-gold resize-none"
              />
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-burgundy">Are you experiencing any symptoms?</h3>
              <p className="text-burgundy/70 text-sm">Select all that apply (optional)</p>
              <div className="grid grid-cols-2 gap-3">
                {symptomsOptions.map(symptom => (
                  <label key={symptom} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.symptoms.includes(symptom)}
                      onChange={() => handleCheckboxChange('symptoms', symptom)}
                      className="h-4 w-4 text-burgundy border-burgundy/30 rounded focus:ring-gold"
                    />
                    <span className="text-sm text-burgundy">{symptom}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-burgundy">What are your wellness goals?</h3>
              <p className="text-burgundy/70 text-sm">Select all that apply (optional)</p>
              <div className="grid grid-cols-2 gap-3">
                {goalsOptions.map(goal => (
                  <label key={goal} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.wellness_goals.includes(goal)}
                      onChange={() => handleCheckboxChange('wellness_goals', goal)}
                      className="h-4 w-4 text-burgundy border-burgundy/30 rounded focus:ring-gold"
                    />
                    <span className="text-sm text-burgundy">{goal}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-burgundy">What topics interest you most?</h3>
              <p className="text-burgundy/70 text-sm">Select all that apply (optional)</p>
              <div className="grid grid-cols-2 gap-3">
                {topicsOptions.map(topic => (
                  <label key={topic} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.topics_of_interest.includes(topic)}
                      onChange={() => handleCheckboxChange('topics_of_interest', topic)}
                      className="h-4 w-4 text-burgundy border-burgundy/30 rounded focus:ring-gold"
                    />
                    <span className="text-sm text-burgundy">{topic}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {step === 5 && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-burgundy">What is your experience level in the world of wellness?</h3>
              <p className="text-burgundy/70 text-sm">This helps us tailor content to your needs</p>
              <div className="space-y-2">
                {experienceLevels.map(level => (
                  <label key={level.value} className="flex items-center gap-3 p-3 border border-burgundy/20 rounded-lg cursor-pointer hover:bg-burgundy/5 transition-colors">
                    <input
                      type="radio"
                      name="experience_level"
                      value={level.value}
                      checked={formData.experience_level === level.value}
                      onChange={(e) => setFormData({...formData, experience_level: e.target.value})}
                      className="h-4 w-4 text-burgundy border-burgundy/30 focus:ring-gold"
                    />
                    <span className="text-sm text-burgundy">{level.label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-between mt-8 pt-6 border-t border-burgundy/20">
            {step > 1 && (
              <Button
                variant="outline"
                onClick={() => setStep(step - 1)}
                className="border-burgundy/30 text-burgundy hover:bg-burgundy/10"
              >
                Back
              </Button>
            )}
            {step < totalSteps ? (
              <Button
                onClick={() => setStep(step + 1)}
                className="ml-auto bg-burgundy hover:bg-burgundy/80 text-white"
              >
                Continue
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={saving}
                className="ml-auto bg-gold hover:bg-gold/80 text-burgundy font-medium"
              >
                {saving ? 'Saving...' : 'Complete Setup'}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Need Help Modal Component
const NeedHelpModal = ({ user, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="border-b border-burgundy/20">
          <div className="flex justify-between items-center">
            <CardTitle className="text-2xl font-serif text-burgundy">Need Help?</CardTitle>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="h-6 w-6" />
            </button>
          </div>
          <CardDescription>
            We're here to support you on your wellness journey
          </CardDescription>
        </CardHeader>
        <CardContent className="p-6">
          <div className="space-y-6">
            {/* Quick Help Section */}
            <div>
              <h3 className="text-lg font-semibold text-burgundy mb-3 flex items-center">
                <MessageCircle className="h-5 w-5 mr-2" />
                Quick Help
              </h3>
              <div className="grid gap-3">
                <div className="p-4 bg-burgundy/5 rounded-lg border border-burgundy/20">
                  <h4 className="font-medium text-burgundy mb-2">Getting Started</h4>
                  <p className="text-sm text-gray-600 mb-2">
                    New to Luminelle? Start with our Dashboard to explore all features and take the platform tour.
                  </p>
                </div>
                <div className="p-4 bg-gold/5 rounded-lg border border-gold/20">
                  <h4 className="font-medium text-burgundy mb-2">Community Guidelines</h4>
                  <p className="text-sm text-gray-600 mb-2">
                    Join our supportive community discussions. Remember to be kind, share experiences (not medical advice), and keep conversations respectful.
                  </p>
                </div>
                <div className="p-4 bg-dark-teal/5 rounded-lg border border-dark-teal/20">
                  <h4 className="font-medium text-burgundy mb-2">Educational Content</h4>
                  <p className="text-sm text-gray-600 mb-2">
                    Access our Classroom for expert-curated wellness content, research articles, and educational resources.
                  </p>
                </div>
              </div>
            </div>

            {/* Contact Support Section */}
            <div>
              <h3 className="text-lg font-semibold text-burgundy mb-3 flex items-center">
                <MessageSquare className="h-5 w-5 mr-2" />
                Contact Support
              </h3>
              <div className="p-4 bg-cream border border-burgundy/20 rounded-lg">
                <p className="text-sm text-gray-600 mb-3">
                  Need personalized help? Our support team is here for you.
                </p>
                <div className="space-y-2">
                  <div className="flex items-center text-sm">
                    <span className="font-medium text-burgundy mr-2">Email:</span>
                    <a href="mailto:hello@luminellewellness.com" className="text-dark-teal hover:underline">
                      hello@luminellewellness.com
                    </a>
                  </div>
                  <div className="flex items-center text-sm">
                    <span className="font-medium text-burgundy mr-2">Response Time:</span>
                    <span className="text-gray-600">Within 24 hours</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Important Disclaimer */}
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start">
                <AlertTriangle className="h-5 w-5 text-amber-600 mr-2 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-medium text-amber-800 mb-1">Medical Disclaimer</h4>
                  <p className="text-sm text-amber-700">
                    Luminelle provides educational content only. Always consult with your healthcare provider 
                    before making any medical decisions or changes to your wellness routine.
                  </p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <Button
                onClick={() => {
                  window.open('mailto:hello@luminellewellness.com?subject=Support Request from ' + (user?.name || 'Luminelle User'), '_blank');
                }}
                className="flex-1 bg-burgundy hover:bg-burgundy/80 text-cream"
              >
                Email Support
              </Button>
              <Button
                onClick={onClose}
                variant="outline"
                className="flex-1"
              >
                Close
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Feedback/Bug Report Modal Component
const FeedbackModal = ({ user, onClose, currentPage }) => {
  // Convert internal tab names to user-friendly display names
  const getPageDisplayName = (page) => {
    const pageNames = {
      'dashboard': 'Dashboard',
      'education': 'Classroom',
      'classroom': 'Classroom',
      'chat': 'Community',
      'community': 'Community',
      'marketplace': 'Marketplace',
      'tools': 'Tools',
      'dosing': 'Dosing Database'
    };
    return pageNames[page] || page || window.location.pathname;
  };

  const [feedbackData, setFeedbackData] = useState({
    type: 'bug',
    subject: '',
    description: '',
    page: getPageDisplayName(currentPage),
    userEmail: user?.email || ''
  });
  const [sending, setSending] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);

    try {
      // Send feedback via support request endpoint
      await axios.post(`${API}/support/request`, {
        name: user?.name || 'Anonymous User',
        email: feedbackData.userEmail,
        subject: `[${feedbackData.type.toUpperCase()}] ${feedbackData.subject}`,
        message: `Page: ${feedbackData.page}\n\nUser: ${user?.name || 'Unknown'} (${user?.email || 'No email'})\n\nDescription:\n${feedbackData.description}`,
        priority: 'normal',
        user_id: user?.id
      });

      setSubmitted(true);
      toast.success('Feedback submitted! We\'ll review it shortly.');
      
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast.error('Failed to submit feedback. Please try again.');
    } finally {
      setSending(false);
    }
  };

  if (submitted) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-6 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-burgundy mb-2">Thank You!</h3>
            <p className="text-gray-600">Your feedback has been submitted successfully.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="border-b border-burgundy/20">
          <div className="flex justify-between items-center">
            <CardTitle className="text-2xl font-serif text-burgundy">Send Feedback</CardTitle>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="h-6 w-6" />
            </button>
          </div>
          <CardDescription>
            Help us improve Luminelle by reporting bugs or suggesting features
          </CardDescription>
        </CardHeader>
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Feedback Type *
              </label>
              <Select 
                value={feedbackData.type} 
                onValueChange={(value) => setFeedbackData({...feedbackData, type: value})}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bug">🐛 Bug Report</SelectItem>
                  <SelectItem value="feature">💡 Feature Request</SelectItem>
                  <SelectItem value="feedback">💬 General Feedback</SelectItem>
                  <SelectItem value="question">❓ Question/Help</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subject *
              </label>
              <Input
                type="text"
                value={feedbackData.subject}
                onChange={(e) => setFeedbackData({...feedbackData, subject: e.target.value})}
                placeholder="Brief description of the issue or suggestion"
                required
                className="w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description *
              </label>
              <Textarea
                value={feedbackData.description}
                onChange={(e) => setFeedbackData({...feedbackData, description: e.target.value})}
                placeholder="Please provide details about your feedback. For bugs, include steps to reproduce if possible."
                rows={6}
                required
                className="w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Current Page
              </label>
              <Input
                type="text"
                value={feedbackData.page}
                onChange={(e) => setFeedbackData({...feedbackData, page: e.target.value})}
                className="w-full bg-gray-50"
                readOnly
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="submit"
                disabled={sending}
                className="flex-1 bg-burgundy hover:bg-burgundy/80 text-cream"
              >
                {sending ? 'Sending...' : 'Submit Feedback'}
              </Button>
              <Button
                type="button"
                onClick={onClose}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// Main App Component

// Upgrade Page Component - Standalone version of subscription page
const UpgradePage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const response = await axios.get(`${API}/user/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        }
      } catch (error) {
        console.error('Error fetching user:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-burgundy" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      {/* Header */}
      <header className="bg-white border-b border-burgundy/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
            <img src="/assets/luminelle-logo.png" alt="Luminelle" className="h-8" />
          </div>
          <Button variant="ghost" onClick={() => navigate('/')} className="text-burgundy">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to {user ? 'Dashboard' : 'Home'}
          </Button>
        </div>
      </header>

      {/* Main Content - Use existing SubscriptionSection */}
      <div className="py-8">
        <SubscriptionSection user={user} />
      </div>
    </div>
  );
};


function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminToken, setAdminToken] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTrialExpiredModal, setShowTrialExpiredModal] = useState(false);
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState("this premium content");
  const [showTrialReminderModal, setShowTrialReminderModal] = useState(false);

  
  // Password reset states
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [resetToken, setResetToken] = useState(null);
  const [resetEmail, setResetEmail] = useState(null);
  const [forgotPasswordIsAdmin, setForgotPasswordIsAdmin] = useState(false);

  // Check for authentication tokens on load
  useEffect(() => {
    const checkAuth = async () => {
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      // Handle Google OAuth callback - check for session_id in URL hash fragment
      const hash = window.location.hash;
      if (hash && hash.includes('session_id=')) {
        try {
          const sessionId = hash.split('session_id=')[1]?.split('&')[0];
          if (sessionId) {
            // Clean the hash immediately to prevent re-processing
            window.history.replaceState(null, null, window.location.pathname + window.location.search);
            
            // Exchange session_id for user data via backend
            const response = await axios.post(`${API}/auth/google/session`, {
              session_id: sessionId
            });
            
            if (response.data.success) {
              const userData = response.data.user;
              localStorage.setItem('auth_token', response.data.token);
              localStorage.setItem('user_data', JSON.stringify(userData));
              
              // Track analytics
              if (response.data.is_new_user) {
                Analytics.trackSignUp({ ...userData, method: 'google' });
                if (!userData.onboarding_completed) {
                  localStorage.setItem('show_onboarding', 'true');
                }
              } else {
                Analytics.trackLogin({ ...userData, method: 'google' });
              }
              
              setIsAuthenticated(true);
              setUser(userData);
              setLoading(false);
              
              toast.success(response.data.message);
              return;
            }
          }
        } catch (error) {
          console.error('Google auth error:', error);
          toast.error(error.response?.data?.detail || 'Google sign-in failed. Please try again.');
        }
        // Clean hash on failure too
        window.history.replaceState(null, null, window.location.pathname + window.location.search);
      }

      // Check admin token
      const adminToken = localStorage.getItem('admin_token');
      if (adminToken) {
        setIsAdmin(true);
        setAdminToken(adminToken);
        setLoading(false);
        return;
      }

      // Check user auth token
      const authToken = localStorage.getItem('auth_token');
      if (authToken) {
        try {
          const response = await axios.get(`${API}/auth/verify`, {
            headers: { Authorization: `Bearer ${authToken}` }
          });
          
          if (response.data.success) {
            setIsAuthenticated(true);
            setUser(response.data.user);
            localStorage.setItem('user_data', JSON.stringify(response.data.user));
          } else {
            // Invalid token, clear it
            localStorage.removeItem('auth_token');
            localStorage.removeItem('user_data');
          }
        } catch (error) {
          console.error('Auth verification failed:', error);
          localStorage.removeItem('auth_token');
          localStorage.removeItem('user_data');
        }
      } else {
        // Check for stored user data
        const userData = localStorage.getItem('user_data');
        if (userData) {
          try {
            const parsedUser = JSON.parse(userData);
            setUser(parsedUser);
            setIsAuthenticated(true);
          } catch (error) {
            localStorage.removeItem('user_data');
          }
        }
      }
      
      setLoading(false);
    };

    checkAuth();
    // Prevent horizontal scrolling at document level
    document.body.style.overflowX = 'hidden';
    return () => {
      document.body.style.overflowX = '';
    };
  }, []);

  // Helper function to check and show trial reminder modal
  // forceShow parameter bypasses the sessionStorage check (used when called from onboarding close)
  const checkAndShowTrialReminder = useCallback((forceShow = false) => {
    if (!user) return;
    
    // Only check sessionStorage if not forcing
    if (!forceShow && sessionStorage.getItem('trial_reminder_shown')) return;
    
    console.log('Trial check - User:', user?.email, 'Status:', user?.subscription_status, 'Trial End:', user?.trial_end_date);
    
    const isTrial = user.subscription_status === 'trial';
    const trialEndDate = user.trial_end_date ? new Date(user.trial_end_date) : null;
    const now = new Date();
    
    console.log('Trial check - isTrial:', isTrial, 'trialEndDate:', trialEndDate, 'now:', now, 'isActive:', trialEndDate && trialEndDate > now);
    
    if (isTrial && trialEndDate && trialEndDate > now) {
      // Show reminder for active trial users
      console.log('Showing trial reminder modal');
      setShowTrialReminderModal(true);
      sessionStorage.setItem('trial_reminder_shown', 'true');
    }
  }, [user]);

  // Show trial reminder modal once per session for trial users
  // Only show if onboarding modal is NOT being shown AND no pending onboarding flag
  useEffect(() => {
    // Check if there's a pending onboarding flag - if so, don't show trial reminder yet
    const hasPendingOnboarding = localStorage.getItem('show_onboarding') === 'true';
    
    if (isAuthenticated && user && !sessionStorage.getItem('trial_reminder_shown') && !showOnboardingModal && !hasPendingOnboarding && user.onboarding_completed !== false) {
      // Small delay to ensure user data is fully loaded and onboarding check completes first
      setTimeout(checkAndShowTrialReminder, 800);
    }
  }, [isAuthenticated, user, showOnboardingModal, checkAndShowTrialReminder]);


  // Check for trial expiration and onboarding flags after authentication
  useEffect(() => {
    if (isAuthenticated && user) {
      const showTrialExpired = localStorage.getItem('show_trial_expired');
      if (showTrialExpired === 'true') {
        localStorage.removeItem('show_trial_expired');
        setShowTrialExpiredModal(true);
      }
      
      // Check for onboarding flag after registration - show FIRST
      // Also show if user hasn't completed onboarding (e.g., logged out and back in)
      const showOnboarding = localStorage.getItem('show_onboarding');
      if ((showOnboarding === 'true' || !user.onboarding_completed) && !showOnboardingModal) {
        localStorage.removeItem('show_onboarding');
        setShowOnboardingModal(true);
      }
    }
  }, [isAuthenticated, user]);

  // Check for Stripe subscription payment return
  useEffect(() => {
    const checkStripeReturn = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const sessionId = urlParams.get('session_id');
      
      if (sessionId && user) {
        toast('Verifying your payment...');
        
        try {
          const response = await axios.get(`${API}/subscription/status/${sessionId}`);
          
          if (response.data.payment_status === 'paid' || response.data.status === 'paid') {
            toast.success('Subscription activated! Welcome to Premium Membership!');
            
            // Trigger upgrade survey
            window.dispatchEvent(new CustomEvent('show-survey', { detail: { type: 'upgrade' } }));
            
            // Refresh user data
            setTimeout(() => window.location.href = '/', 2000);
          } else {
            toast.error('Payment verification failed. Please contact support.');
            console.log('Payment status:', response.data);
          }
        } catch (error) {
          console.error('Error verifying subscription:', error);
          toast.error('Error verifying payment. Please contact support if you were charged.');
        }
        
        // Clean URL
        window.history.replaceState({}, document.title, '/');
      }
    };
    
    if (isAuthenticated && user) {
      checkStripeReturn();
    }
  }, [isAuthenticated, user]);

  // Handle password reset URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('reset_token');
    const email = urlParams.get('email');
    
    if (token && email) {
      setResetToken(token);
      setResetEmail(email);
      setShowResetPasswordModal(true);
      
      // Clear URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Handle forgot password event from login forms
  useEffect(() => {
    const handleForgotPassword = (event) => {
      const isAdmin = event.detail?.isAdmin || false;
      setForgotPasswordIsAdmin(isAdmin);
      setShowForgotPasswordModal(true);
    };

    window.addEventListener('openForgotPassword', handleForgotPassword);
    return () => {
      window.removeEventListener('openForgotPassword', handleForgotPassword);
    };
  }, []);


  const handleAdminLogin = async (username, password) => {
    try {
      const response = await axios.post(`${API}/admin/login`, { username, password });
      if (response.data.token) {
        localStorage.setItem('admin_token', response.data.token);
        setIsAdmin(true);
        setAdminToken(response.data.token);
        return true;
      }
    } catch (error) {
      console.error('Admin login failed:', error);
      return false;
    }
  };

  const handleAdminLogout = () => {
    localStorage.removeItem('admin_token');
    setIsAdmin(false);
    setAdminToken(null);
  };

  const handleUserLogout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_data');
    setIsAuthenticated(false);
    setUser(null);
    // Navigate to home page after logout
    window.location.href = '/';
  };

  // Admin login component
  const AdminLogin = () => {
    const [credentials, setCredentials] = useState({ username: '', password: '' });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
      e.preventDefault();
      setLoading(true);
      setError('');

      const success = await handleAdminLogin(credentials.username, credentials.password);
      if (!success) {
        setError('Invalid credentials');
      }
      setLoading(false);
    };

    return (
      <div className="min-h-screen bg-gradient-to-br from-cream via-warm-white to-gold-50 flex items-center justify-center">
        <Card className="w-full max-w-md border-burgundy/20">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-serif text-burgundy">Admin Login</CardTitle>
            <CardDescription>Access Luminelle Admin Panel</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-burgundy mb-2">Username</label>
                <Input
                  type="text"
                  value={credentials.username}
                  onChange={(e) => setCredentials({...credentials, username: e.target.value})}
                  className="border-burgundy/30 focus:border-gold"
                  required
                />
              </div>
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-burgundy">Password</label>
                  <button
                    type="button"
                    onClick={() => window.dispatchEvent(new CustomEvent('openForgotPassword', { detail: { isAdmin: true } }))}
                    className="text-xs text-burgundy/70 hover:text-gold transition-colors"
                  >
                    Forgot Password?
                  </button>
                </div>
                <Input
                  type="password"
                  value={credentials.password}
                  onChange={(e) => setCredentials({...credentials, password: e.target.value})}
                  className="border-burgundy/30 focus:border-gold"
                  required
                />
              </div>
              {error && <p className="text-red-600 text-sm">{error}</p>}
              <Button type="submit" disabled={loading} className="w-full bg-burgundy hover:bg-burgundy/80">
                {loading ? 'Logging in...' : 'Login'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cream via-warm-white to-gold-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-burgundy mx-auto mb-4"></div>
          <p className="text-burgundy font-serif">Loading Luminelle...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="App overflow-x-hidden">
      {/* Custom CSS to fix Emergent branding overlay issue and mobile display issues */}
      <style dangerouslySetInnerHTML={{
        __html: `
          /* AGGRESSIVE Emergent branding removal */
          div[style*="position: fixed"][style*="z-index: 9999"],
          div[style*="position:fixed"][style*="z-index:9999"],
          div[style*="z-index: 9999"],
          div[style*="z-index:9999"],
          [class*="emergent"], [id*="emergent"], [data-emergent],
          .emergent-branding, .emergent-header, [class*="emergent-sticky"] {
            display: none !important;
            visibility: hidden !important;
            opacity: 0 !important;
            pointer-events: none !important;
          }
          
          /* Force checkbox containers to be visible and sized properly on mobile */
          .community-guidelines-container,
          div[class*="bg-gold/5"],
          div[class*="bg-burgundy/5"] {
            display: block !important;
            visibility: visible !important;
            opacity: 1 !important;
            width: 100% !important;
            min-height: 120px !important;
            box-sizing: border-box !important;
          }
          
          /* Ensure all checkbox inputs are visible */
          input[type="checkbox"] {
            display: inline-block !important;
            visibility: visible !important;
            opacity: 1 !important;
            width: 16px !important;
            height: 16px !important;
            margin: 0 !important;
            flex-shrink: 0 !important;
          }
          
          /* Mobile-specific fixes */
          @media (max-width: 768px) {
            .community-guidelines-container,
            div[class*="bg-gold/5"] {
              display: block !important;
              width: 100% !important;
              min-height: 200px !important;
              padding: 16px !important;
              margin-top: 16px !important;
              border: 1px solid rgba(217, 119, 6, 0.3) !important;
              background-color: rgba(217, 119, 6, 0.05) !important;
            }
            
            /* Force show checkbox and label */
            #community-guidelines-checkbox {
              display: inline-block !important;
              width: 16px !important;
              height: 16px !important;
            }
          }
        `
      }} />
      
      <BrowserRouter>
        {/* Toast Notifications with high z-index to appear above modals */}
        <ToastContainer 
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={true}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
          style={{ zIndex: 99999 }}
        />
        
        <Routes>
          <Route 
            path="/" 
            element={
              isAdmin ? (
                <AdminPanel onLogout={handleAdminLogout} />
              ) : (
                isAuthenticated ? <Dashboard user={user} onLogout={handleUserLogout} onUserUpdate={setUser} setShowUpgradeModal={setShowUpgradeModal} setUpgradeFeature={setUpgradeFeature} /> : <HomePage />
              )
            } 
          />
          {/* Admin route - shows AdminLogin if not authenticated as admin */}
          <Route 
            path="/admin" 
            element={
              isAdmin ? (
                <AdminPanel onLogout={handleAdminLogout} />
              ) : (
                <AdminLogin />
              )
            } 
          />
          <Route 
            path="/dashboard" 
            element={
              isAuthenticated ? (
                <Dashboard user={user} onLogout={handleUserLogout} onUserUpdate={setUser} setShowUpgradeModal={setShowUpgradeModal} setUpgradeFeature={setUpgradeFeature} />
              ) : (
                <HomePage />
              )
            } 
          />
          <Route 
            path="/admin" 
            element={
              isAdmin ? (
                <AdminPanel onLogout={handleAdminLogout} />
              ) : (
                <AdminLogin />
              )
            } 
          />
          <Route 
            path="/gift-success" 
            element={<GiftSuccessPage />} 
          />
          {/* Classroom routes with subcategory support */}
          <Route 
            path="/classroom/:categorySlug/:subcategorySlug/:contentId" 
            element={
              isAuthenticated ? (
                <Dashboard user={user} onLogout={handleUserLogout} onUserUpdate={setUser} initialView="education" setShowUpgradeModal={setShowUpgradeModal} setUpgradeFeature={setUpgradeFeature} />
              ) : (
                <HomePage />
              )
            } 
          />
          <Route 
            path="/classroom/:categorySlug/:subcategorySlug" 
            element={
              isAuthenticated ? (
                <Dashboard user={user} onLogout={handleUserLogout} onUserUpdate={setUser} initialView="education" setShowUpgradeModal={setShowUpgradeModal} setUpgradeFeature={setUpgradeFeature} />
              ) : (
                <HomePage />
              )
            } 
          />
          <Route 
            path="/classroom/:categorySlug" 
            element={
              isAuthenticated ? (
                <Dashboard user={user} onLogout={handleUserLogout} onUserUpdate={setUser} initialView="education" setShowUpgradeModal={setShowUpgradeModal} setUpgradeFeature={setUpgradeFeature} />
              ) : (
                <HomePage />
              )
            } 
          />
          <Route 
            path="/classroom" 
            element={
              isAuthenticated ? (
                <Dashboard user={user} onLogout={handleUserLogout} onUserUpdate={setUser} initialView="education" setShowUpgradeModal={setShowUpgradeModal} setUpgradeFeature={setUpgradeFeature} />
              ) : (
                <HomePage />
              )
            } 
          />
          <Route 
            path="/community" 
            element={
              isAuthenticated ? (
                <Dashboard user={user} onLogout={handleUserLogout} onUserUpdate={setUser} initialView="chat" />
              ) : (
                <HomePage />
              )
            } 
          />
          <Route 
            path="/community/:roomOrDiscussionId" 
            element={
              isAuthenticated ? (
                <Dashboard user={user} onLogout={handleUserLogout} onUserUpdate={setUser} initialView="chat" />
              ) : (
                <HomePage />
              )
            } 
          />
          <Route 
            path="/marketplace" 
            element={
              isAuthenticated ? (
                <Dashboard user={user} onLogout={handleUserLogout} onUserUpdate={setUser} initialView="shop" />
              ) : (
                <HomePage />
              )
            } 
          />
          <Route 
            path="/tools" 
            element={
              isAuthenticated ? (
                <Dashboard user={user} onLogout={handleUserLogout} onUserUpdate={setUser} initialView="calculator" />
              ) : (
                <HomePage />
              )
            } 
          />
          <Route 
            path="/tools/daily-wellness-tracker" 
            element={
              isAuthenticated ? (
                <Dashboard user={user} onLogout={handleUserLogout} onUserUpdate={setUser} initialView="calculator" />
              ) : (
                <HomePage />
              )
            } 
          />
          <Route 
            path="/tools/weight-tracker" 
            element={
              isAuthenticated ? (
                <Dashboard user={user} onLogout={handleUserLogout} onUserUpdate={setUser} initialView="calculator" />
              ) : (
                <HomePage />
              )
            } 
          />
          <Route 
            path="/tools/period-tracker" 
            element={
              isAuthenticated ? (
                <Dashboard user={user} onLogout={handleUserLogout} onUserUpdate={setUser} initialView="calculator" />
              ) : (
                <HomePage />
              )
            } 
          />
          <Route 
            path="/tools/dosing-calculator" 
            element={
              isAuthenticated ? (
                <Dashboard user={user} onLogout={handleUserLogout} onUserUpdate={setUser} initialView="calculator" />
              ) : (
                <HomePage />
              )
            } 
          />
          <Route 
            path="/marketplace/:productId" 
            element={
              isAuthenticated ? (
                <Dashboard user={user} onLogout={handleUserLogout} onUserUpdate={setUser} initialView="shop" />
              ) : (
                <HomePage />
              )
            } 
          />
          <Route 
            path="/subscription" 
            element={
              isAuthenticated ? (
                <Dashboard user={user} onLogout={handleUserLogout} onUserUpdate={setUser} initialView="subscription" setShowUpgradeModal={setShowUpgradeModal} setUpgradeFeature={setUpgradeFeature} />
              ) : (
                <HomePage />
              )
            } 
          />
          <Route 
            path="/community/:discussionId" 
            element={
              isAuthenticated ? (
                <Dashboard user={user} onLogout={handleUserLogout} onUserUpdate={setUser} initialView="chat" />
              ) : (
                <HomePage />
              )
            } 
          />
          <Route 
            path="/dosing/:compoundName" 
            element={
              isAuthenticated ? (
                <Dashboard user={user} onLogout={handleUserLogout} onUserUpdate={setUser} initialView="calculator" />
              ) : (
                <HomePage />
              )
            } 
          />
          {/* Blog Pillar Pages */}
          <Route path="/blog/:pillar" element={<BlogPillarPage />} />
          <Route path="/blog/:pillar/:articleSlug" element={<BlogPillarPage />} />
          {/* FAQ Page */}
          <Route path="/faq" element={<FAQPage />} />
          {/* Wellness Assessment Standalone Page */}
          <Route path="/wellness-assessment" element={<WellnessAssessmentStandalone />} />
          {/* Founding 48 Landing Page */}
          <Route path="/founding48" element={<Founding48Page />} />
          {/* Upgrade Page */}
          <Route path="/upgrade" element={<UpgradePage />} />
          {/* Legal Pages */}
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          {/* 404 - Catch-all route */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
      
      {/* Trial Expired Modal */}
      {showTrialExpiredModal && (
        <TrialExpiredModal 
          onClose={() => setShowTrialExpiredModal(false)}
          user={user}
          setActiveTab={(tab) => { window.location.href = `/${tab}`; }}
        />
      )}
      
      {/* Progressive Onboarding Flow */}
      {showOnboardingModal && user && (
        <ProgressiveOnboarding 
          user={user}
          isOpen={showOnboardingModal}
          onClose={() => {
            setShowOnboardingModal(false);
            setTimeout(() => checkAndShowTrialReminder(true), 300);
          }}
          onComplete={(navigateTo) => {
            setShowOnboardingModal(false);
            const updatedUser = {...user, onboarding_completed: true};
            setUser(updatedUser);
            localStorage.setItem('user_data', JSON.stringify(updatedUser));
            if (navigateTo === 'classroom') {
              window.location.href = '/classroom';
            }
            setTimeout(() => checkAndShowTrialReminder(true), 300);
          }}
        />
      )}
      
      {/* Upgrade to Member Modal */}
      <UpgradeModal 
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        feature={upgradeFeature}
      />
      
      {/* Forgot Password Modal */}
      {showForgotPasswordModal && (
        <ForgotPasswordModal 
          onClose={() => setShowForgotPasswordModal(false)}
          isAdmin={forgotPasswordIsAdmin}
        />
      )}
      
      {/* Reset Password Modal */}
      {showResetPasswordModal && (
        <ResetPasswordModal 
          token={resetToken}
          email={resetEmail}
          onClose={() => {
            setShowResetPasswordModal(false);
            setResetToken(null);
            setResetEmail(null);
          }}
          onSuccess={() => {
            setShowResetPasswordModal(false);
            setResetToken(null);
            setResetEmail(null);
            toast.success('Password reset successful! You can now log in with your new password.');
          }}
        />
      )}

      {/* Trial Reminder Modal */}
      {showTrialReminderModal && user?.subscription_status === 'trial' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md border-0 shadow-2xl bg-cream">
            <CardHeader className="bg-gradient-to-r from-burgundy to-burgundy/90 text-cream">
              <div className="flex items-center justify-between">
                <CardTitle className="text-2xl font-serif flex items-center gap-2">
                  <span className="text-3xl">⏰</span>
                  Trial Membership
                </CardTitle>
                <Button 
                  variant="ghost" 
                  onClick={() => setShowTrialReminderModal(false)}
                  className="text-cream hover:bg-cream/20"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </CardHeader>
            
            <CardContent className="p-6 space-y-4">
              <div className="text-center">
                <p className="text-lg text-burgundy mb-2">
                  You have <span className="font-bold text-2xl text-gold">
                    {Math.ceil((new Date(user.trial_end_date) - new Date()) / (1000 * 60 * 60 * 24))}
                  </span> days left in your free trial
                </p>
                <p className="text-sm text-burgundy/70 mb-3">
                  Trial ends: {new Date(user.trial_end_date).toLocaleDateString('en-US', { 
                    month: 'long', 
                    day: 'numeric', 
                    year: 'numeric' 
                  })}
                </p>
                {user.stripe_subscription_id && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                    <p className="text-amber-800">
                      <span className="font-semibold">Note:</span> Your card will be charged <span className="font-bold">$20/month</span> after your trial ends.
                    </p>
                    <p className="text-amber-700 mt-1">
                      Cancel anytime before <span className="font-semibold">{new Date(user.trial_end_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}</span> to avoid charges.
                    </p>
                  </div>
                )}
              </div>
              
              <div className="bg-gradient-to-r from-burgundy/10 to-dark-teal/10 rounded-lg p-4 border border-burgundy/20">
                <h4 className="font-semibold text-burgundy mb-2">Your trial includes:</h4>
                <ul className="space-y-2 text-sm text-burgundy/80">
                  <li className="flex items-start gap-2">
                    <span className="text-gold mt-0.5">✓</span>
                    <span>Full access to all content & courses</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-gold mt-0.5">✓</span>
                    <span>Premium marketplace products</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-gold mt-0.5">✓</span>
                    <span>Advanced tracking tools</span>
                  </li>
                </ul>
              </div>
              
              <div className="flex gap-3">
                <Button 
                  onClick={() => {
                    setShowTrialReminderModal(false);
                    // Set flag for Dashboard to open subscription tab
                    sessionStorage.setItem('openSubscriptionTab', 'true');
                    // Navigate to dashboard (will trigger the subscription tab)
                    window.location.href = '/dashboard';
                  }}
                  className="flex-1 bg-gradient-to-r from-gold to-gold-600 hover:from-gold-600 hover:to-gold-700 text-burgundy font-semibold"
                >
                  {user.stripe_subscription_id ? 'Manage Subscription' : 'Upgrade Now'}
                </Button>
                <Button 
                  onClick={() => setShowTrialReminderModal(false)}
                  variant="outline"
                  className="flex-1 border-burgundy/30 text-burgundy hover:bg-burgundy/5"
                >
                  Remind Me Later
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

    </div>
  );
}

// Subscription Section Component
const SubscriptionSection = ({ user }) => {
  const [plans, setPlans] = useState({});
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [cancellingSubscription, setCancellingSubscription] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  useEffect(() => {
    fetchSubscriptionPlans();
    checkReturnFromStripe();
  }, []);

  const fetchSubscriptionPlans = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/subscription/plans`);
      // Convert array format to object format for compatibility
      if (response.data.plans && Array.isArray(response.data.plans)) {
        const plansObj = {};
        response.data.plans.forEach(plan => {
          plansObj[plan.id] = {
            name: plan.name,
            monthly_price: plan.interval === 'year' ? Math.round(plan.price / 12) : plan.price,
            annual_price: plan.interval === 'year' ? plan.price : plan.price * 12,
            interval: plan.interval,
            features: [
              "Exclusive wellness content & courses",
              "Advanced peptide calculator & guides", 
              "Progress tracking & analytics",
              "Priority email support",
              "Premium community access",
              "Curated wellness marketplace",
              "Expert Q&A sessions",
              "Exclusive member perks"
            ]
          };
        });
        setPlans(plansObj);
      } else {
        setPlans(response.data);
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const getUrlParameter = (name) => {
    name = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');
    const regex = new RegExp('[\\?&]' + name + '=([^&#]*)');
    const results = regex.exec(location.search);
    return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error('Payment verification timed out. Please contact support if payment was processed.');
      return;
    }

    try {
      const response = await axios.get(`${BACKEND_URL}/api/subscription/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        // Track successful purchase
        Analytics.trackPurchase({ 
          subscription_id: sessionId,
          plan: response.data.plan || 'monthly',
          amount: response.data.amount || 19
        });
        toast.success('🎉 Welcome to Luminelle Wellness! Your subscription is now active.');
        // Refresh user data
        window.location.reload();
        return;
      } else if (response.data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        return;
      }

      // Continue polling if pending
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      toast.error('Error verifying payment. Please contact support.');
    }
  };

  const checkReturnFromStripe = () => {
    const sessionId = getUrlParameter('session_id');
    if (sessionId) {
      toast('Verifying your payment...');
      pollPaymentStatus(sessionId);
    }
  };

  const initiateSubscription = async (planId) => {
    try {
      setProcessingPayment(true);
      
      // Track checkout initiated
      Analytics.trackCheckoutInitiated({ plan: planId, amount: planId === 'annual' ? 180 : 19 });
      
      const requestBody = {
        plan_id: planId,
        success_url: `${window.location.origin}/?session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${window.location.origin}/`,
        coupon_code: couponCode.trim() || null
      };

      const response = await axios.post(`${BACKEND_URL}/api/subscription/checkout`, requestBody, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      
      if (response.data.free_access) {
        // Forever free coupon - no payment needed!
        Analytics.trackPurchase({ plan: planId, amount: 0 });
        toast.success(response.data.message || 'Free access granted!');
        setTimeout(() => window.location.reload(), 2000);
      } else if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Subscription error:', error);
      toast.error('Failed to start subscription process. Please try again.');
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      setCancellingSubscription(true);
      const token = localStorage.getItem('auth_token');
      
      const response = await axios.post(`${BACKEND_URL}/api/subscription/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success('Subscription cancelled. You\'ll retain access until your current period ends.');
        setShowCancelConfirm(false);
        // Refresh page to update user data
        setTimeout(() => window.location.reload(), 2000);
      }
    } catch (error) {
      console.error('Cancel error:', error);
      toast.error(error.response?.data?.detail || 'Failed to cancel subscription. Please try again.');
    } finally {
      setCancellingSubscription(false);
    }
  };

  const getSubscriptionStatus = () => {
    if (!user) return 'trial';
    
    const status = user.subscription_status;
    const endDate = user.subscription_end ? new Date(user.subscription_end) : null;
    const now = new Date();
    
    if (status === 'forever_free') return 'forever_free';
    if (status === 'active' && endDate && endDate > now) return 'active';
    if (status === 'trial') return 'trial';
    return 'expired';
  };

  const subscriptionStatus = getSubscriptionStatus();

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-burgundy mx-auto"></div>
        <span className="mt-2 text-burgundy">Loading subscription plans...</span>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-8">
      <div className="text-center">
        <h1 className="text-4xl font-bold text-burgundy mb-4 font-serif">Wellness Membership</h1>
        <p className="text-burgundy/70 text-lg">
          Join our community and get access to personalized wellness coaching, expert support, and comprehensive health tracking.
        </p>
      </div>

      {/* Current Status */}
      {subscriptionStatus === 'active' && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
          <div className="text-center mb-4">
            <div className="text-green-700 font-semibold text-lg mb-2">
              ✅ Your Membership is Active
            </div>
            <p className="text-green-600">
              Subscription expires: {user?.subscription_end ? new Date(user.subscription_end).toLocaleDateString() : 'N/A'}
            </p>
          </div>
          
          {/* Cancel Subscription Option */}
          {user?.stripe_subscription_id && !user?.subscription_canceled_at && (
            <div className="border-t border-green-200 pt-4 mt-4">
              {!showCancelConfirm ? (
                <button
                  onClick={() => setShowCancelConfirm(true)}
                  className="text-sm text-gray-500 hover:text-red-600 underline"
                >
                  Cancel subscription
                </button>
              ) : (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-700 text-sm mb-3">
                    Are you sure you want to cancel? You'll retain access until your current billing period ends.
                  </p>
                  <div className="flex gap-3">
                    <button
                      onClick={handleCancelSubscription}
                      disabled={cancellingSubscription}
                      className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 text-sm"
                    >
                      {cancellingSubscription ? 'Cancelling...' : 'Yes, Cancel'}
                    </button>
                    <button
                      onClick={() => setShowCancelConfirm(false)}
                      className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 text-sm"
                    >
                      Keep Subscription
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {user?.subscription_canceled_at && (
            <div className="border-t border-amber-200 pt-4 mt-4 bg-amber-50 rounded-lg p-3">
              <p className="text-amber-700 text-sm">
                ⚠️ Your subscription has been cancelled and will not renew. You'll retain access until {user?.subscription_end ? new Date(user.subscription_end).toLocaleDateString() : 'your current period ends'}.
              </p>
            </div>
          )}
        </div>
      )}
      
      {/* Trial Status with Cancel Option */}
      {subscriptionStatus === 'trial' && user?.stripe_subscription_id && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
          <div className="text-center mb-4">
            <div className="text-amber-700 font-semibold text-lg mb-2">
              ⏰ Free Trial Active
            </div>
            <p className="text-amber-600">
              Trial ends: {user?.trial_end_date ? new Date(user.trial_end_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : 'N/A'}
            </p>
            <p className="text-amber-600 text-sm mt-2">
              Your card will be charged <span className="font-bold">$20/month</span> after your trial ends.
            </p>
          </div>
          
          {/* Cancel Option for Trial Users */}
          {!user?.subscription_canceled_at && (
            <div className="border-t border-amber-200 pt-4 mt-4">
              {!showCancelConfirm ? (
                <button
                  onClick={() => setShowCancelConfirm(true)}
                  className="text-sm text-gray-500 hover:text-red-600 underline"
                >
                  Cancel before trial ends to avoid charges
                </button>
              ) : (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-700 text-sm mb-3">
                    Are you sure you want to cancel? You'll retain trial access until {user?.trial_end_date ? new Date(user.trial_end_date).toLocaleDateString() : 'your trial ends'}, and your card will not be charged.
                  </p>
                  <div className="flex gap-3">
                    <button
                      onClick={handleCancelSubscription}
                      disabled={cancellingSubscription}
                      className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 text-sm"
                    >
                      {cancellingSubscription ? 'Cancelling...' : 'Yes, Cancel'}
                    </button>
                    <button
                      onClick={() => setShowCancelConfirm(false)}
                      className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 text-sm"
                    >
                      Keep Trial
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {user?.subscription_canceled_at && (
            <div className="border-t border-green-200 pt-4 mt-4 bg-green-50 rounded-lg p-3">
              <p className="text-green-700 text-sm">
                ✅ Your trial has been cancelled and your card will not be charged. You'll retain trial access until {user?.trial_end_date ? new Date(user.trial_end_date).toLocaleDateString() : 'your trial ends'}.
              </p>
            </div>
          )}
        </div>
      )}

      {subscriptionStatus === 'forever_free' && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-6 text-center">
          <div className="text-purple-700 font-semibold text-lg mb-2">
            🎉 Forever Free Member
          </div>
          <p className="text-purple-600">
            You have lifetime access to all wellness features!
          </p>
        </div>
      )}

      {/* Subscription Plans */}
      {subscriptionStatus !== 'active' && subscriptionStatus !== 'forever_free' && (
        <div className="space-y-6">
          {/* Coupon Code Input */}
          <div className="bg-cream/30 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-burgundy mb-4">Have a Coupon Code?</h3>
            <div className="flex gap-3">
              <input
                type="text"
                placeholder="Enter coupon code"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value)}
                className="flex-1 px-4 py-2 border border-burgundy/30 rounded-md focus:outline-none focus:ring-2 focus:ring-gold"
              />
            </div>
            {couponCode.toUpperCase() === 'FOREVERFREE' && (
              <div className="mt-2 text-green-600 font-medium">
                🎉 Forever Free coupon applied! Your membership will be completely free.
              </div>
            )}
          </div>

          {/* Plan Cards - Side by Side Layout */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Monthly Plan */}
            {plans.monthly_20 && (
              <div className="border-2 rounded-xl p-6 bg-white shadow-lg hover:shadow-xl transition-all border-burgundy/20 hover:border-burgundy/50">
                <div className="text-center mb-4">
                  <h3 className="text-xl font-bold text-burgundy mb-2 font-serif">Monthly Membership</h3>
                  <div className="text-3xl font-bold text-burgundy mb-2">
                    {couponCode.toUpperCase() === 'FOREVERFREE' ? (
                      <span className="text-green-600">FREE</span>
                    ) : (
                      <span>${plans.monthly_20.monthly_price}/month</span>
                    )}
                  </div>
                  <div className="bg-burgundy/10 text-burgundy text-xs font-medium px-2 py-1 rounded-full inline-block">
                    Most Flexible
                  </div>
                </div>
                
                <div className="space-y-2 mb-6">
                  {plans.monthly_20.features.slice(0, 4).map((feature, index) => (
                    <div key={`monthly-${feature.slice(0,20)}`} className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-burgundy rounded-full flex items-center justify-center flex-shrink-0">
                        <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                        </svg>
                      </div>
                      <span className="text-sm text-burgundy">{feature}</span>
                    </div>
                  ))}
                </div>

                {/* Special Offers for Monthly */}
                {subscriptionStatus === 'trial' && user?.referral_email && (
                  <div className="bg-gold/10 border border-gold/30 rounded-lg p-3 mb-4">
                    <div className="text-gold font-semibold mb-1 text-sm">🎁 Referral Bonus Applied!</div>
                    <p className="text-gold/80 text-xs">
                      You were referred by someone special - enjoy your extended 14-day trial and special pricing!
                    </p>
                  </div>
                )}

                <button
                  onClick={() => initiateSubscription('monthly_20')}
                  disabled={processingPayment}
                  className="w-full bg-gradient-to-r from-burgundy to-burgundy/80 hover:from-burgundy/80 hover:to-burgundy/60 text-white font-semibold py-3 px-4 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {processingPayment ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      Processing...
                    </div>
                  ) : couponCode.toUpperCase() === 'FOREVERFREE' ? 'Claim Free Membership' :
                   subscriptionStatus === 'trial' ? 'Upgrade Now' : 'Start Membership'}
                </button>

                <p className="text-center text-xs text-burgundy/60 mt-3">
                  Secure payment via Stripe • Cancel anytime
                </p>
              </div>
            )}

            {/* Yearly Plan */}
            {plans.yearly_192 && (
              <div className="border-2 rounded-xl p-6 bg-white shadow-lg hover:shadow-xl transition-all relative border-gold/20 hover:border-gold/50">
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-gold text-burgundy font-medium text-xs px-3 py-1 rounded-full">
                  Save $48/year
                </div>
                <div className="text-center mb-4">
                  <h3 className="text-xl font-bold text-burgundy mb-2 font-serif">Yearly Membership</h3>
                  <div className="text-3xl font-bold text-burgundy mb-1">
                    {couponCode.toUpperCase() === 'FOREVERFREE' ? (
                      <span className="text-green-600">FREE</span>
                    ) : (
                      <span>${plans.yearly_192.monthly_price}/month</span>
                    )}
                  </div>
                  {couponCode.toUpperCase() !== 'FOREVERFREE' && (
                    <p className="text-sm text-burgundy/60">Billed annually at ${plans.yearly_192.annual_price}</p>
                  )}
                  <div className="bg-gold/20 text-burgundy text-xs font-medium px-2 py-1 rounded-full inline-block mt-1">
                    Best Value
                  </div>
                </div>
                
                <div className="space-y-2 mb-6">
                  {[...plans.yearly_192.features.slice(0, 3), '2 months free'].map((feature, index) => (
                    <div key={`yearly-${feature.slice(0,20)}`} className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-gold rounded-full flex items-center justify-center flex-shrink-0">
                        <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                        </svg>
                      </div>
                      <span className="text-sm text-burgundy">{feature}</span>
                    </div>
                  ))}
                </div>

                {/* Special Offers for Yearly */}
                {subscriptionStatus === 'trial' && user?.referral_email && (
                  <div className="bg-gold/10 border border-gold/30 rounded-lg p-3 mb-4">
                    <div className="text-gold font-semibold mb-1 text-sm">🎁 Referral Bonus Applied!</div>
                    <p className="text-gold/80 text-xs">
                      You were referred by someone special - enjoy your extended 14-day trial and special pricing!
                    </p>
                  </div>
                )}

                <button
                  onClick={() => initiateSubscription('yearly_192')}
                  disabled={processingPayment}
                  className="w-full bg-gradient-to-r from-gold to-gold/80 hover:from-gold/80 hover:to-gold/60 text-white font-semibold py-3 px-4 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {processingPayment ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      Processing...
                    </div>
                  ) : couponCode.toUpperCase() === 'FOREVERFREE' ? 'Claim Free Membership' :
                   subscriptionStatus === 'trial' ? 'Upgrade Now' : 'Start Membership'}
                </button>

                <p className="text-center text-xs text-burgundy/60 mt-3">
                  Secure payment via Stripe • Cancel anytime
                </p>
              </div>
            )}
          </div>

          {/* Referral Benefits */}
          <div className="bg-gradient-to-r from-burgundy/5 to-gold/5 rounded-xl p-6 border border-burgundy/20">
            <h3 className="text-xl font-semibold text-burgundy mb-4 font-serif">Referral Program</h3>
            <p className="text-burgundy/80 mb-4 text-sm">
              Become a referral partner. Once you have signed up for a membership, share your email address with a friend, family member or colleague. When they sign up, if they use your email address you will both be rewarded.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="font-medium text-burgundy">For Referrers:</div>
                <ul className="text-sm text-burgundy/70 space-y-1">
                  <li>• One month free for every referral that signs up for a paid account</li>
                  <li>• Unlimited number of free months</li>
                </ul>
              </div>
              <div className="space-y-2">
                <div className="font-medium text-burgundy">For New Members:</div>
                <ul className="text-sm text-burgundy/70 space-y-1">
                  <li>• Get 14-day trial (instead of 7 days) with referral</li>
                  <li>• Access to most features during trial</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default App;